import React, { useState, useRef, useEffect, useCallback, useMemo, memo, useDeferredValue } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Form, Dropdown, /* Alert, */ Modal } from 'react-bootstrap';
import * as pdfjsLib from 'pdfjs-dist';
import JSZip from 'jszip';
import {
  loadDropboxScript,
  loadGooglePicker,
  loadOneDriveScript,
  loadBoxScript,
} from '../../utils/cloudStorage';
import DocumentsSection from './DocumentsSection';
import RecipientsSection from './RecipientsSection';
import { isValidEmailAddress } from '../../utils/emailValidation';
import MessageSection from './MessageSection';
import {
  getDraftEnvelope,
  getEnvelope,
  sendEnvelope,
  preSendEnvelopeSync,
  createDocument,
  createDraftEnvelope,
  // bulkCreateRecipients,
  bulkCreateFields,
  bulkUpdateDocumentFields,
  bulkDeleteDocumentFields,
  deleteEnvelopeRecipient,
  // createDocumentField,
  updateDocumentField,
  deleteDocumentField,
  deleteDocument,
  deleteEnvelope,
  getDocumentFileBatchZipUrls,
  copyEnvelope,
} from '../../services/envelopeService';
import UpgradeQuotaModal from '../billing/UpgradeQuotaModal';
import PayPerEnvelopeModal from '../billing/PayPerEnvelopeModal';
import { syncEnvelopeQuotaFromApi } from '../../features/subscription/subscriptionSlice';
import {
  isQuotaExceededError,
  isRevokedEnvelopeBlockedError,
  getQuotaExceededMessage,
  isEnvelopeSendQuotaExceeded,
  getEnvelopeQuotaExceededMessage,
  isPayPerEnvelopeEnabled,
  getPayPerEnvelopeOptions,
  getPayPerEnvelopeFromError,
  syncSubscriptionFromQuotaError,
} from '../../utils/subscription';
import { getCopiedEnvelopeId } from '../../utils/getCopiedEnvelopeId';
import {
  createTemplate,
  deleteTemplate,
  getTemplate,
  removeDocumentFromTemplate,
  // listTemplates,
} from '../../services/templateService';
import {
  clearBackendDocumentIdStamp,
  createEnvelopeDocumentRow,
  mapFilesAndIdsToEnvelopeRows,
  newEnvelopeRowId,
  normalizeRowServerDocumentId,
  reorderEnvelopeDocumentRows,
} from '../../models/envelopeDocumentRows';
import { normalizeToDateDDMMYYYY, normalizeToDateISO } from '../../utils/dateFormat';
import ReadOnlyInlineRichEditor from './ReadOnlyInlineRichEditor';
import {
  FIELD_FONT_SIZE_OPTIONS,
  getReadOnlyFieldValueHtml,
  hasHtmlMarkup,
  measureReadOnlyHtmlBlockHeight,
  prepareReadOnlyValueHtml,
  buildReadOnlyFieldValueForApi,
  getFieldTypographyFromApiValue,
  unwrapReadOnlyFieldValueWrapper,
  stripHtmlForMeasure,
} from '../../utils/readOnlyRichText';
import { isHiddenFieldType } from '../../utils/fieldTypes';
import { getContactList } from '../../services/contactService';
import {
  extractContactsFromApiResponse,
  extractOrganizationIdFromContactListResponse,
  getActiveOrganizationId,
  isContactListApiFailure,
  loadStoredRecipientContacts,
  mergeContactsIntoStore,
  saveStoredRecipientContacts,
  setActiveOrganizationId,
} from '../../utils/recipientContactsStore';
import { getOrganizationIdFromSubscription } from '../../utils/subscription';

// Set up PDF.js worker - use local file to avoid CORS issues
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const RECIPIENT_COLORS = [
  '#d73a49', '#e818e8', '#18e8e8', '#e81818', '#183898',
  '#e89878', '#7878e8', '#185818', '#7c3aed', '#981878',
  '#78d898', '#e8d818', '#5818e8', '#783818', '#d87818',
  '#d878d8', '#009688', '#389878', '#987858', '#e83878',
  '#78e8e8', '#ff6f00', '#6d4c41', '#785898', '#b838b8',
  '#38b8b8', '#581898', '#587818', '#0057d9', '#b83838',
  '#183858', '#1838d8', '#c2185b', '#9818e8', '#987818',
  '#3878b8', '#38e818', '#58b8e8', '#98e818', '#b8b818',
  '#187858', '#385878', '#389838', '#783858', '#b85878',
  '#18b858', '#18b8e8', '#5858d8', '#58e8b8', '#9858d8',
  '#98e878', '#d81858', '#d81898', '#d87858', '#d87898',
  '#e838b8', '#e85838', '#e89838', '#1858b8', '#187898',
  '#1898b8', '#18b898', '#383818', '#3838b8', '#3898d8',
  '#38d878', '#583878', '#587898', '#589858', '#5898b8',
  '#58b838', '#58b878', '#781838', '#7818b8', '#7838d8',
  '#785838', '#789838', '#78b858', '#78d838', '#981818',
  '#983898', '#98b838', '#b838e8', '#b85818', '#b87838',
  '#b89858', '#d8b858', '#d8d878', '#38d8d8', '#e858e8',
  '#18d838', '#18d8b8', '#3838e8', '#8b4513', '#1565c0',
  '#6a1b9a', '#7898d8', '#78e858', '#b818d8', '#b878e8',
];

const RECIPIENT_COLOR_MIN_DISTANCE = 120;

function normalizeHexColor(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return '';
}

function hexToRgbTuple(hex) {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return null;
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

function colorDistance(a, b) {
  const c1 = hexToRgbTuple(a);
  const c2 = hexToRgbTuple(b);
  if (!c1 || !c2) return Number.POSITIVE_INFINITY;
  const dr = c1.r - c2.r;
  const dg = c1.g - c2.g;
  const db = c1.b - c2.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function isTooCloseToUsedColors(candidate, usedColors, minDistance = RECIPIENT_COLOR_MIN_DISTANCE) {
  return usedColors.some((used) => colorDistance(candidate, used) < minDistance);
}

function pickMostDistinctColor(candidates, usedColors) {
  const normalizedCandidates = (candidates || []).map(normalizeHexColor).filter(Boolean);
  const normalizedUsed = (usedColors || []).map(normalizeHexColor).filter(Boolean);
  if (normalizedCandidates.length === 0) return '';
  if (normalizedUsed.length === 0) {
    return normalizedCandidates[0];
  }

  let bestScore = -1;
  let best = [];
  normalizedCandidates.forEach((candidate) => {
    const minDistanceToUsed = Math.min(
      ...normalizedUsed.map((used) => colorDistance(candidate, used))
    );
    if (minDistanceToUsed > bestScore) {
      bestScore = minDistanceToUsed;
      best = [candidate];
    } else if (minDistanceToUsed === bestScore) {
      best.push(candidate);
    }
  });
  return best[0] || normalizedCandidates[0];
}

function randomHexColor() {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

function generateDistinctRecipientColor(usedColors) {
  const normalizedUsed = (usedColors || []).map(normalizeHexColor).filter(Boolean);
  const sampledCandidates = [];
  for (let attempt = 0; attempt < 180; attempt += 1) {
    sampledCandidates.push(randomHexColor());
  }
  const sufficientlyDistinct = sampledCandidates.filter(
    (candidate) => !isTooCloseToUsedColors(candidate, normalizedUsed)
  );
  if (sufficientlyDistinct.length > 0) {
    return pickMostDistinctColor(sufficientlyDistinct, normalizedUsed);
  }
  // If all random samples are still close, pick the farthest available candidate.
  return pickMostDistinctColor(sampledCandidates, normalizedUsed);
}

function buildInitialRecipientColorPool(baseColors) {
  const remaining = (baseColors || []).map(normalizeHexColor).filter(Boolean);
  if (remaining.length === 0) return [];
  const pool = [remaining.shift()];
  while (remaining.length > 0) {
    const picked = pickMostDistinctColor(remaining, pool) || remaining[0];
    pool.push(picked);
    const pickedIndex = remaining.indexOf(picked);
    remaining.splice(pickedIndex >= 0 ? pickedIndex : 0, 1);
  }
  return pool;
}

/** Random unused color; generates new colors when palette is exhausted. */
function getNextUnusedRecipientColor(currentRecipients, preferredPool = RECIPIENT_COLORS) {
  const usedColors = currentRecipients
    .map((r) => normalizeHexColor(r?.color))
    .filter(Boolean);
  const pool = (preferredPool || []).map(normalizeHexColor).filter(Boolean);
  if (usedColors.length === 0 && pool.length > 0) return pool[0];
  const available = pool.filter((color) => !usedColors.includes(color));
  if (available.length > 0) {
    const distinct = available.filter((color) => !isTooCloseToUsedColors(color, usedColors));
    const source = distinct.length > 0 ? distinct : available;
    return pickMostDistinctColor(source, usedColors);
  }
  return generateDistinctRecipientColor(usedColors);
}

function pickUniqueRecipientColor(preferredColor, existingRecipients, preferredPool = RECIPIENT_COLORS) {
  const normalizedPreferred = normalizeHexColor(preferredColor);
  const usedColors = (existingRecipients || [])
    .map((r) => normalizeHexColor(r?.color))
    .filter(Boolean);
  if (normalizedPreferred && !usedColors.includes(normalizedPreferred)) {
    return normalizedPreferred;
  }
  return getNextUnusedRecipientColor(existingRecipients || [], preferredPool);
}

/** Document-space → overlay pixel layout (matches placed-field rendering). */
function layoutFieldOnOverlay(field) {
  let fieldY = 0;
  let fieldX = field.x;
  if (field?.__pageInfo) {
    fieldY = field.__pageInfo.y + field.y;
    fieldX = (field.__pageInfo.offsetX || 0) + field.x;
  }
  return {
    fieldX,
    fieldY,
    width: field.width || 150,
    height: field.height || 40,
  };
}

function applyPlacedFieldDomGeometry(el, field) {
  if (!el) return;
  const { fieldX, fieldY, width, height } = layoutFieldOnOverlay(field);
  el.style.left = `${fieldX}px`;
  el.style.top = `${fieldY}px`;
  el.style.width = `${width}px`;
  el.style.height = `${height}px`;
}

/**
 * Isolated from CreateEnvelope so selecting fields does not re-render / re-parse huge SVG innerHTML.
 */
/** Field draft autosave: wait 2s after last change, then confirm idle before API (reduces duplicate creates). */
const FIELD_AUTOSAVE_DEBOUNCE_MS = 2000;
/** Pending create guard timeout: if create mapping never resolves, release to avoid infinite autosave retry loop. */
const FIELD_PENDING_CREATE_MAX_MS = 15000;
/** Keep verbose autosave logs off in production interactions. */
const FIELD_AUTOSAVE_DEBUG = false;
/** Keep verbose field-save run logs off by default. */
const FIELD_SAVE_DEBUG = false;
/** Debug logs for visible-page scroll tracking. */
const FIELD_VISIBLE_PAGE_DEBUG = false;
/** Debug logs for Step-2 SVG cache hydration status. */
const SVG_LOAD_STATUS_DEBUG = false;
/** Upload documents in chunks to avoid oversized requests/session expiry. */
const DOCUMENT_UPLOAD_BATCH_SIZE = 10;
const MAX_ENVELOPE_TOTAL_BYTES = 200 * 1024 * 1024; // 200 MB (fail when >=)
const MAX_DOCUMENT_BYTES = 50 * 1024 * 1024; // 50 MB (fail when >=)
const MAX_PAGES_PER_DOCUMENT = 2000; // fail when >=
const MAX_TOTAL_PAGES_PER_ENVELOPE = 5000; // fail when >=
const MAX_DOCUMENTS_PER_ENVELOPE = 130; // fail when >=
const MAX_RECIPIENTS_PER_ENVELOPE = 100; // fail when >

function isEnvelopeDocumentCountLimitMessage(raw) {
  const text = String(raw || '').toLowerCase();
  if (!text) return false;
  return (
    text.includes('envelope document count limit exceeded')
    || text.includes('fewer than 130 documents')
    || (text.includes('document') && text.includes('130') && text.includes('limit exceeded'))
  );
}

function isEnvelopeDocumentCountLimitError(error) {
  const responseData = error?.response?.data || error?.originalError?.response?.data || null;
  const messages = [
    error?.message,
    responseData?.message,
  ];
  if (Array.isArray(responseData?.errors?.documents)) {
    messages.push(...responseData.errors.documents);
  }
  return messages.some((msg) => isEnvelopeDocumentCountLimitMessage(msg));
}

function formatMegabytes(bytes) {
  const value = Number(bytes) || 0;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
}

/** Monotonic client-only id for correlating uploads (resets on full page reload). */
let browserClientDocumentIdSeq = 0;

/** Next stable id for correlating uploads with envelope-create / document-create responses. */
function createBrowserClientDocumentId() {
  browserClientDocumentIdSeq += 1;
  return String(browserClientDocumentIdSeq);
}

function getFileClientDocumentId(file) {
  if (!file || file.fromApi) return '';
  const raw = file.__clientDocumentId;
  return raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';
}

const READONLY_INLINE_EDIT_FIELD_TYPES = new Set(['text', 'title', 'company', 'number', 'email']);

function isInlineTextEditFieldType(type) {
  return READONLY_INLINE_EDIT_FIELD_TYPES.has(type);
}

function fieldSupportsInlineTextEdit(field) {
  return isInlineTextEditFieldType(field?.type);
}

function fieldUsesContentHeightFitting(field) {
  return isReadOnlyField(field) || isInlineTextEditFieldType(field?.type);
}

const READONLY_FIELD_BADGE_V_PADDING = 16;
const READONLY_FIELD_BADGE_H_PADDING = 24;
/** Horizontal padding inside rich HTML display/editor (2px + 4px each side). */
const READONLY_FIELD_RICH_HTML_H_PADDING = 8;
const READONLY_FIELD_RICH_HTML_V_PADDING = 4;
const READONLY_FIELD_ICON_SLOT = 24;
const READONLY_FIELD_CHOICE_SLOT = 20;
const READONLY_FIELD_TEXT_LINE_HEIGHT = 1.2;
const READONLY_FIELD_MIN_HEIGHT = 15;
const READONLY_FIELD_MEASURE_PROBE_ID = 'volksign-readonly-field-measure';

function isReadOnlyField(field) {
  return Boolean(field?.readOnly ?? field?.read_only);
}

function getReadOnlyFieldTextForMeasure(field) {
  if (!field) return '';
  if (field.value != null && String(field.value).trim() !== '') {
    return String(field.value);
  }
  if (field.valueHtml) return stripHtmlForMeasure(field.valueHtml);
  return '';
}

function measureReadOnlyTextBlockHeight({
  text = '',
  contentWidth,
  fontFamily = 'Arial',
  fontSize = 12,
  fontWeight = 'normal',
  fontStyle = 'normal',
  textDecoration = 'none',
  textAlign = 'left',
}) {
  if (typeof document === 'undefined') return 0;
  const width = Math.max(1, Number(contentWidth) || 1);
  let el = document.getElementById(READONLY_FIELD_MEASURE_PROBE_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = READONLY_FIELD_MEASURE_PROBE_ID;
    el.setAttribute('aria-hidden', 'true');
    Object.assign(el.style, {
      position: 'absolute',
      visibility: 'hidden',
      pointerEvents: 'none',
      left: '-9999px',
      top: '0',
      margin: '0',
      padding: '0',
      border: '0',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      boxSizing: 'border-box',
      lineHeight: String(READONLY_FIELD_TEXT_LINE_HEIGHT),
    });
    document.body.appendChild(el);
  }
  const sizePx = Math.max(2, parseInt(String(fontSize), 10) || 12);
  el.style.width = `${width}px`;
  el.style.fontFamily = fontFamily || 'Arial';
  el.style.fontSize = `${sizePx}px`;
  el.style.fontWeight = fontWeight || 'normal';
  el.style.fontStyle = fontStyle || 'normal';
  el.style.textDecoration = textDecoration || 'none';
  el.style.textAlign = textAlign || 'left';
  el.textContent = String(text || '');
  return el.scrollHeight || Math.ceil(sizePx * READONLY_FIELD_TEXT_LINE_HEIGHT);
}

function readOnlyFieldHasDisplayContent(field) {
  if (isInlineTextEditFieldType(field?.type)) {
    return Boolean(getReadOnlyFieldTextForMeasure(field)?.trim());
  }
  if (!isReadOnlyField(field)) return false;
  if (fieldTypeAllowsSingleOptionOnly(field.type)) return true;
  if (isDateFieldType(field.type)) {
    return Boolean(getReadOnlyFieldCanvasDisplayValue(field)?.trim());
  }
  return Boolean(getReadOnlyFieldTextForMeasure(field)?.trim());
}

/** Text fields and read-only values: show formatted text (no field-type icon or label). */
function readOnlyFieldShowsValueOnly(field) {
  if (isInlineTextEditFieldType(field?.type)) return true;
  if (!isReadOnlyField(field)) return false;
  if (fieldTypeAllowsSingleOptionOnly(field.type)) return false;
  return Boolean(getReadOnlyFieldValueHtml(field) || String(field.value || '').trim());
}

function getReadOnlyFieldMeasureText(field, textOverride) {
  if (textOverride != null) return String(textOverride);
  if (fieldTypeAllowsSingleOptionOnly(field?.type)) {
    return getFieldChoiceOptionLabel(field);
  }
  if (isDateFieldType(field?.type)) {
    return getReadOnlyFieldCanvasDisplayValue(field);
  }
  const plain = getReadOnlyFieldTextForMeasure(field);
  if (plain.trim()) return plain;
  return getReadOnlyFieldCanvasDisplayValue(field);
}

function getReadOnlyFieldContentWidth(field, widthOverride) {
  const w = widthOverride ?? field?.width ?? 150;
  let horizontalChrome = READONLY_FIELD_BADGE_H_PADDING;
  if (fieldTypeAllowsSingleOptionOnly(field?.type)) {
    horizontalChrome += READONLY_FIELD_CHOICE_SLOT;
  } else if (!readOnlyFieldShowsValueOnly(field)) {
    horizontalChrome += READONLY_FIELD_ICON_SLOT;
  }
  return Math.max(30, w - horizontalChrome);
}

function getReadOnlyFieldRichHtmlContentWidth(field, widthOverride) {
  return Math.max(30, (widthOverride ?? field?.width ?? 150) - READONLY_FIELD_RICH_HTML_H_PADDING);
}

function buildReadOnlyMeasureHtmlWithBaseStyle(html, field, styleOverride = {}) {
  const rawFontFamily = String(styleOverride?.fontFamily ?? field?.fontFamily ?? 'Arial');
  const safeFontFamily = rawFontFamily.replace(/"/g, '\\"');
  const sizePx = Math.max(2, parseInt(String(styleOverride?.fontSize ?? field?.fontSize ?? '12'), 10) || 12);
  const fontWeight = styleOverride?.fontWeight ?? field?.fontWeight ?? 'normal';
  const fontStyle = styleOverride?.fontStyle ?? field?.fontStyle ?? 'normal';
  const textDecoration = styleOverride?.textDecoration ?? field?.textDecoration ?? 'none';
  const textAlign = styleOverride?.textAlign ?? field?.textAlign ?? 'left';
  const fontColor = styleOverride?.fontColor ?? field?.fontColor ?? '#000000';
  return `<div style="font-family:\\"${safeFontFamily}\\";font-size:${sizePx}px;font-weight:${fontWeight};font-style:${fontStyle};text-decoration:${textDecoration};text-align:${textAlign};color:${fontColor};line-height:1.2;">${String(html || '')}</div>`;
}

/** Minimum height to fit read-only content at the given width and font (all read-only field types). */
function computeReadOnlyFieldNeededHeight(field, { textOverride, widthOverride, styleOverride } = {}) {
  if (!fieldUsesContentHeightFitting(field)) return READONLY_FIELD_MIN_HEIGHT;

  if (
    readOnlyFieldShowsValueOnly(field)
    && !fieldTypeAllowsSingleOptionOnly(field.type)
  ) {
    const htmlSource =
      textOverride != null && hasHtmlMarkup(textOverride)
        ? textOverride
        : getReadOnlyFieldValueHtml(field);
    if (htmlSource && hasHtmlMarkup(htmlSource)) {
      const prepared = prepareReadOnlyValueHtml(htmlSource);
      const contentWidth = getReadOnlyFieldRichHtmlContentWidth(field, widthOverride);
      const measureField = { ...field, ...styleOverride };
      const textHeight = measureReadOnlyHtmlBlockHeight({
        html: buildReadOnlyMeasureHtmlWithBaseStyle(prepared, measureField, styleOverride),
        contentWidth,
        baseStyle: {
          fontFamily: measureField.fontFamily || 'Arial',
          fontSize: String(measureField.fontSize || '12'),
          fontColor: measureField.fontColor || '#000000',
          textAlign: measureField.textAlign || 'left',
        },
      });
      return Math.max(
        READONLY_FIELD_MIN_HEIGHT,
        Math.ceil(textHeight + READONLY_FIELD_RICH_HTML_V_PADDING)
      );
    }
  }

  const fontFamily = styleOverride?.fontFamily ?? field.fontFamily ?? 'Arial';
  const fontSize = styleOverride?.fontSize ?? field.fontSize ?? '12';
  const fontWeight = styleOverride?.fontWeight ?? field.fontWeight ?? 'normal';
  const fontStyle = styleOverride?.fontStyle ?? field.fontStyle ?? 'normal';
  const textDecoration = styleOverride?.textDecoration ?? field.textDecoration ?? 'none';
  const textAlign = styleOverride?.textAlign ?? field.textAlign ?? 'left';
  const sizePx = Math.max(2, parseInt(String(fontSize), 10) || 12);
  const text = getReadOnlyFieldMeasureText(field, textOverride);
  const contentWidth = getReadOnlyFieldContentWidth(field, widthOverride);

  let textHeight = 0;
  if (text.trim()) {
    textHeight = measureReadOnlyTextBlockHeight({
      text,
      contentWidth,
      fontFamily,
      fontSize,
      fontWeight,
      fontStyle,
      textDecoration,
      textAlign,
    });
  } else if (fieldTypeAllowsSingleOptionOnly(field.type)) {
    textHeight = Math.ceil(sizePx * READONLY_FIELD_TEXT_LINE_HEIGHT);
  } else {
    return READONLY_FIELD_MIN_HEIGHT;
  }

  const verticalPad =
    readOnlyFieldShowsValueOnly(field) || isInlineTextEditFieldType(field?.type)
      ? READONLY_FIELD_RICH_HTML_V_PADDING
      : READONLY_FIELD_BADGE_V_PADDING;
  return Math.max(READONLY_FIELD_MIN_HEIGHT, Math.ceil(textHeight + verticalPad));
}

/** Grow read-only field height when content overflows; never shrink when text is removed. */
function growReadOnlyFieldToFitContent(field, textOverride, styleOverride = {}) {
  if (!fieldUsesContentHeightFitting(field)) return field;
  if (!readOnlyFieldHasDisplayContent(field) && !String(textOverride ?? '').trim()) return field;

  const neededHeight = computeReadOnlyFieldNeededHeight(field, {
    textOverride,
    styleOverride,
    widthOverride: field.width,
  });
  const currentHeight = field.height || 40;
  if (neededHeight <= currentHeight) return field;
  return { ...field, height: neededHeight };
}

/** Set read-only field height to fit content (grow or shrink). Keeps display/edit in sync. */
/** Quill inserts .ql-toolbar as a sibling of the editor; remove any left on the placed-field. */
function purgeOrphanQuillToolbars(placedFieldEl) {
  if (!(placedFieldEl instanceof Element)) return;
  placedFieldEl.querySelectorAll('.ql-toolbar').forEach((toolbar) => {
    if (!toolbar.closest('[data-readonly-quill-root]')) {
      toolbar.remove();
    }
  });
}

function fitReadOnlyFieldToContent(field, textOverride, styleOverride = {}) {
  if (!fieldUsesContentHeightFitting(field)) return field;
  if (
    !readOnlyFieldHasDisplayContent(field)
    && !String(textOverride ?? '').trim()
  ) {
    return field;
  }

  const neededHeight = computeReadOnlyFieldNeededHeight(field, {
    textOverride,
    styleOverride,
    widthOverride: field.width,
  });
  const currentHeight = field.height || 40;
  if (neededHeight === currentHeight) return field;
  return { ...field, height: neededHeight };
}

function clampReadOnlyFieldResizeGeometry(field, geom, resizeHandle, resizeStart) {
  if (!fieldUsesContentHeightFitting(field) || !readOnlyFieldHasDisplayContent(field)) return geom;

  let { width, height, x, y } = geom;
  const minHeight = computeReadOnlyFieldNeededHeight(
    { ...field, width },
    { widthOverride: width }
  );

  if (height >= minHeight) return geom;

  height = minHeight;
  if (resizeHandle?.includes('n') && resizeStart) {
    y = resizeStart.y + (resizeStart.height - height);
  }
  return { width, height, x, y };
}

function computeFieldResizeGeometry(resizeStart, resizeHandle, docDeltaX, docDeltaY) {
  let newWidth = resizeStart.width;
  let newHeight = resizeStart.height;
  let newX = resizeStart.x;
  let newY = resizeStart.y;

  if (resizeHandle.includes('e')) {
    newWidth = Math.max(25, resizeStart.width + docDeltaX);
  }
  if (resizeHandle.includes('w')) {
    newWidth = Math.max(25, resizeStart.width - docDeltaX);
    newX = resizeStart.x + (resizeStart.width - newWidth);
  }
  if (resizeHandle.includes('s')) {
    newHeight = Math.max(15, resizeStart.height + docDeltaY);
  }
  if (resizeHandle.includes('n')) {
    newHeight = Math.max(15, resizeStart.height - docDeltaY);
    newY = resizeStart.y + (resizeStart.height - newHeight);
  }

  return { width: newWidth, height: newHeight, x: newX, y: newY };
}

function getResizeScaleFactors(resizeHandle, primaryStart, primaryGeom) {
  const scaleX =
    resizeHandle.includes('e') || resizeHandle.includes('w')
      ? primaryGeom.width / Math.max(1, primaryStart.width)
      : 1;
  const scaleY =
    resizeHandle.includes('n') || resizeHandle.includes('s')
      ? primaryGeom.height / Math.max(1, primaryStart.height)
      : 1;
  return { scaleX, scaleY };
}

function applyProportionalResizeGeometry(fStart, resizeHandle, scaleX, scaleY) {
  let fWidth = Math.max(25, fStart.width * scaleX);
  let fHeight = Math.max(15, fStart.height * scaleY);
  let fX = fStart.x;
  let fY = fStart.y;
  if (resizeHandle.includes('w')) {
    fX = fStart.x + fStart.width - fWidth;
  }
  if (resizeHandle.includes('n')) {
    fY = fStart.y + fStart.height - fHeight;
  }
  return { width: fWidth, height: fHeight, x: fX, y: fY };
}

const FIELD_TYPE_LABELS = {
  signature: 'Signature',
  initial: 'Initial',
  stamp: 'Stamp',
  dateSigned: 'Date Signed',
  date: 'Date',
  name: 'Name',
  email: 'Email',
  company: 'Company',
  title: 'Title',
  text: 'Text',
  number: 'Number',
  checkbox: 'Checkbox',
  dropdown: 'Dropdown',
  radio: 'Radio',
};

function getFieldTypeLabel(type) {
  const key = String(type || '').trim();
  if (FIELD_TYPE_LABELS[key]) return FIELD_TYPE_LABELS[key];
  if (!key) return '';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function buildAutoFieldLabel(fieldType, recipientDisplayName) {
  return `${getFieldTypeLabel(fieldType)} - ${recipientDisplayName}`;
}

function getRecipientDisplayNameForFieldLabel(recipient, index, isTemplateMode, getVariableSigneePlaceholder) {
  if (!recipient) return '';
  const safeIndex = index >= 0 ? index : 0;
  const placeholder = getVariableSigneePlaceholder(recipient, safeIndex);
  if (isTemplateMode && recipient.isVariable && placeholder) return placeholder;
  return String(recipient.name || recipient.email || placeholder || '').trim();
}

function toRecipientIdSelectionKey(recipientId) {
  if (recipientId == null || recipientId === '') return '';
  return `id:${recipientId}`;
}

function parseRecipientIdFromSelectionKey(key) {
  if (key == null || key === '') return null;
  const keyStr = String(key);
  if (keyStr.startsWith('id:')) {
    const parsed = keyStr.slice(3);
    return parsed || null;
  }
  if (!keyStr.includes(':') && /^\d+$/.test(keyStr)) return keyStr;
  return null;
}

function resolveEnvelopeRecipientId(recipient, emailToIdMap = {}) {
  if (!recipient) return null;
  if (recipient.id != null && recipient.id !== '') return recipient.id;
  const email = String(recipient.email || '').trim();
  if (email && emailToIdMap[email] != null) return emailToIdMap[email];
  return null;
}

function fieldBelongsToEnvelopeRecipient(field, recipient, selectionKey = '') {
  if (!field || !recipient) return false;

  const recipientApiId = resolveEnvelopeRecipientId(recipient, {});
  if (recipientApiId != null) {
    if (field.apiRecipientId != null && String(field.apiRecipientId) === String(recipientApiId)) {
      return true;
    }
    const idKey = toRecipientIdSelectionKey(recipientApiId);
    if (idKey && String(field.recipientId || '') === idKey) return true;
    if (String(field.recipientId || '') === String(recipientApiId)) return true;
  }

  const fieldKey = field.recipientId != null ? String(field.recipientId) : '';
  if (selectionKey && fieldKey === String(selectionKey)) return true;
  if (recipient.localKey && fieldKey === String(recipient.localKey)) return true;

  const recipientEmail = String(recipient.email || '').trim();
  if (recipientEmail && String(field.recipientEmail || '').trim() === recipientEmail) return true;
  if (recipientEmail && fieldKey === recipientEmail) return true;
  if (recipientEmail && fieldKey === `email:${recipientEmail}`) return true;

  return false;
}

/** @deprecated use fieldBelongsToEnvelopeRecipient */
function fieldMatchesRecipientForSync(field, oldRecipient, oldSelectionKey) {
  return fieldBelongsToEnvelopeRecipient(field, oldRecipient, oldSelectionKey);
}

function shouldSyncFieldLabelForRecipient(field, oldDisplayName, oldEmail) {
  const trimmedOldName = String(oldDisplayName || '').trim();
  const trimmedOldEmail = String(oldEmail || '').trim();
  const autoLabel = buildAutoFieldLabel(field.type, trimmedOldName);
  const label = String(field.label || '').trim();
  const dataLabel = String(field.dataLabel || '').trim();
  if (label === autoLabel || dataLabel === autoLabel) return true;
  const storedName = String(field.recipientName || '').trim();
  if (trimmedOldName && storedName === trimmedOldName) return true;
  if (trimmedOldEmail && (label.includes(trimmedOldEmail) || dataLabel.includes(trimmedOldEmail))) {
    return true;
  }
  return false;
}

function resolveFieldNameForApi(field) {
  const name = String(field?.dataLabel ?? field?.label ?? field?.type ?? '').trim();
  return name || String(field?.type || 'field');
}

function isDateFieldType(type) {
  return String(type || '').toLowerCase() === 'date';
}

function mapApiFieldValueToLocal(field) {
  const rawValue = field.field_value || '';
  const raw = /<[^>]+>/.test(String(rawValue))
    ? String(rawValue)
        .replace(/<\/(p|div|h[1-6]|li)>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    : rawValue;
  if (isDateFieldType(field.field_type)) return normalizeToDateISO(raw);
  return raw;
}

/** DD/MM/YYYY on the placed field badge (stored value may be ISO from the date picker). */
function getReadOnlyFieldCanvasDisplayValue(field) {
  if (!field) return '';
  const raw = String(field.value ?? '').trim();
  if (!raw) return '';
  if (isDateFieldType(field.type ?? field.field_type)) {
    return normalizeToDateDDMMYYYY(raw) || raw;
  }
  return raw;
}

function fieldTypeAllowsSingleOptionOnly(type) {
  return type === 'checkbox' || type === 'radio';
}

/** Mouse or touch client coordinates for field drag/resize. */
function getClientPoint(e) {
  if (e?.touches && e.touches.length > 0) {
    return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
  }
  if (e?.changedTouches && e.changedTouches.length > 0) {
    return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
  }
  return { clientX: e?.clientX ?? 0, clientY: e?.clientY ?? 0 };
}

/** Resolve displayed signing order for a recipient row. */
function resolveRecipientSigningOrder(recipient, index, sequenceInputs) {
  const rawInput = sequenceInputs?.[index];
  if (rawInput !== undefined && rawInput !== '') {
    const parsed = parseInt(String(rawInput), 10);
    if (!Number.isNaN(parsed) && parsed >= 1) return parsed;
  }
  const stored = recipient?.signature_order;
  if (stored != null && stored !== '') {
    const parsed = Number(stored);
    if (!Number.isNaN(parsed) && parsed >= 1) return parsed;
  }
  return index + 1;
}

/** Next signing order when adding a recipient (max existing + 1). */
function getNextRecipientSigningOrder(recipients, sequenceInputs) {
  if (!Array.isArray(recipients) || recipients.length === 0) return 1;
  let max = 0;
  recipients.forEach((recipient, idx) => {
    const order = resolveRecipientSigningOrder(recipient, idx, sequenceInputs);
    if (order > max) max = order;
  });
  return max + 1;
}

/** Sort list by signing order; stable within the same order (multiple signers per step). */
function sortRecipientsBySigningOrder(recipients, sequenceInputs, orderOverrideByIndex = null) {
  const rows = recipients.map((recipient, origIdx) => {
    const overridden = orderOverrideByIndex?.[origIdx];
    const order =
      overridden != null && !Number.isNaN(overridden)
        ? Math.max(1, overridden)
        : resolveRecipientSigningOrder(recipient, origIdx, sequenceInputs);
    return { recipient, origIdx, order };
  });
  rows.sort((a, b) => (a.order !== b.order ? a.order - b.order : a.origIdx - b.origIdx));
  return {
    recipients: rows.map(({ recipient, order }) => ({ ...recipient, signature_order: order })),
    sequenceInputs: rows.map(({ order }) => order),
  };
}

/** signing order + badge color for every recipient role in sequential workflow payloads. */
function buildRecipientSigningPayloadFields(recipient, origIdx, { signingOrder, sequenceInputs, getRecipientColor }) {
  const signee_color = recipient.color || getRecipientColor(origIdx);
  if (!signingOrder) {
    return { signee_color };
  }
  const rawOrder = sequenceInputs?.[origIdx];
  const fromInput =
    rawOrder !== undefined && rawOrder !== '' ? Number(rawOrder) : NaN;
  const fromRecipient =
    recipient.signature_order != null && recipient.signature_order !== ''
      ? Number(recipient.signature_order)
      : NaN;
  const signature_order = Number.isFinite(fromRecipient)
    ? fromRecipient
    : Number.isFinite(fromInput)
      ? fromInput
      : origIdx + 1;
  return {
    signee_color,
    signature_order,
  };
}

function buildRecipientsSaveFingerprint(list, workflowType) {
  const parts = (list || []).map((r, idx) => [
    String(r.recipient_id ?? r.id ?? `row${idx}`),
    r.type || '',
    String(r.signature_order ?? ''),
    r.signee_color || '',
    r.name || '',
  ].join('|'));
  return `${workflowType}::${parts.join(';;')}`;
}

/** Pre-filled value for read-only checkbox/radio on the signed document. */
const READONLY_CHOICE_FIELD_SELECTED_VALUE = 'selected';

function getFieldChoiceOptionLabel(field) {
  if (!field) return '';
  const opts = Array.isArray(field.options) ? field.options : [];
  if (opts.length > 0) return String(opts[0] ?? '');
  const raw = field.field_options;
  if (raw == null || raw === '') return '';
  if (Array.isArray(raw)) return String(raw[0] ?? '');
  return String(raw);
}

/** Checkbox/radio option label for API (`field_options` string). */
function getFieldChoiceOptionsForApi(field) {
  if (!field) return '';
  const type = field.type ?? field.field_type;
  if (!fieldTypeAllowsSingleOptionOnly(type)) return '';
  return getFieldChoiceOptionLabel(field);
}

function resolveChoiceFieldSourceForApi(source, localFields) {
  if (!source) return source;
  const type = source.type ?? source.field_type;
  if (!fieldTypeAllowsSingleOptionOnly(type)) return source;
  if (getFieldChoiceOptionLabel(source)) return source;
  const localId = source.local_field_id ?? source.id;
  if (localId == null || !Array.isArray(localFields)) return source;
  const local = localFields.find((f) => String(f.id) === String(localId));
  if (!local) return source;
  return {
    ...source,
    type: local.type ?? type,
    options: local.options,
    field_options: local.field_options ?? source.field_options,
  };
}

function withChoiceFieldOptionsForApi(row, field, localFields) {
  const source = resolveChoiceFieldSourceForApi(field ?? row, localFields);
  const type = source?.type ?? source?.field_type ?? row?.field_type;
  if (!fieldTypeAllowsSingleOptionOnly(type)) return row;
  return { ...row, field_options: getFieldChoiceOptionsForApi(source) };
}

function normalizeFieldOptionsForType(field) {
  if (!field || !fieldTypeAllowsSingleOptionOnly(field.type)) return field;
  const opts = Array.isArray(field.options) ? field.options : [];
  const first = opts.length > 0 ? String(opts[0] ?? '') : '';
  const next = { ...field, options: [first] };
  if (Boolean(next.readOnly ?? next.read_only)) {
    next.readOnly = true;
    next.value = READONLY_CHOICE_FIELD_SELECTED_VALUE;
  }
  return next;
}

function parseChoiceFieldOptionsFromApi(field) {
  if (Array.isArray(field?.options) && field.options.length > 0) {
    return [String(field.options[0] ?? '')];
  }
  const raw = field?.field_options;
  if (raw != null && raw !== '') {
    if (Array.isArray(raw)) return [String(raw[0] ?? '')];
    return [String(raw)];
  }
  return [''];
}

function applyReadOnlyChoiceFieldState(field, readOnly) {
  if (!field || !fieldTypeAllowsSingleOptionOnly(field.type)) {
    return { ...field, readOnly: Boolean(readOnly) };
  }
  if (readOnly) {
    return normalizeFieldOptionsForType({
      ...field,
      readOnly: true,
      value: READONLY_CHOICE_FIELD_SELECTED_VALUE,
    });
  }
  const next = normalizeFieldOptionsForType({ ...field, readOnly: false });
  if (String(next.value || '').trim() === READONLY_CHOICE_FIELD_SELECTED_VALUE) {
    next.value = '';
  }
  return next;
}

function isChoiceFieldDisplayChecked(field) {
  if (!field) return false;
  const v = String(field.value ?? field.field_value ?? '').trim().toLowerCase();
  return v === 'true' || v === READONLY_CHOICE_FIELD_SELECTED_VALUE;
}

/** Row index for an upload item — **only** by stable `client_document_id` on the File (no index fallback). */
function resolveUploadRowIndexByClientId(filesSnapshot, item) {
  const cid = String(item?.clientDocumentId || getFileClientDocumentId(item?.file) || '').trim();
  if (!cid) return -1;
  const idx = (filesSnapshot || []).findIndex(
    (f) => String(getFileClientDocumentId(f) || '').trim() === cid
  );
  return idx;
}

function cloneFieldsSnapshot(fields) {
  // Keep snapshots shallow/lightweight to avoid long blocking tasks in pointer handlers.
  return (fields || []).map((field) => ({
    ...field,
    options: Array.isArray(field?.options) ? [...field.options] : field?.options,
  }));
}

/** Backend copy for missing-documents pre-send response; upload/retry only when message matches exactly. */
const PRE_SEND_SYNC_MISSING_DOCUMENTS_MESSAGE =
  'One or more documents are missing from this envelope.';

/** True only for the known "missing documents" pre-send-sync failure (then upload + retry). */
function isPreSendSyncMissingDocumentsOnlyFailure(responseData) {
  if (!responseData || typeof responseData !== 'object') return false;
  const layer =
    responseData.data != null && typeof responseData.data === 'object' ? responseData.data : responseData;
  if (layer.success !== false) return false;
  const messageStr = String(layer.message || '').trim();
  if (messageStr !== PRE_SEND_SYNC_MISSING_DOCUMENTS_MESSAGE) return false;
  const raw = layer.missing_documents;
  return Array.isArray(raw) && raw.length > 0;
}

/** Single customer-facing string for any pre-send failure except the handled missing-documents retry path. */
function formatPreSendSyncCustomerMessage(responseData) {
  const layer =
    responseData?.data != null && typeof responseData.data === 'object'
      ? responseData.data
      : responseData;
  if (!layer || typeof layer !== 'object') {
    return 'Pre-send sync failed. Please try again.';
  }
  const msgParts = [];
  if (layer.message) msgParts.push(String(layer.message));

  const errObj = layer.errors;
  if (errObj && typeof errObj === 'object' && !Array.isArray(errObj)) {
    for (const val of Object.values(errObj)) {
      if (Array.isArray(val)) {
        for (const line of val) {
          if (line != null && String(line).trim()) msgParts.push(String(line).trim());
        }
      } else if (val != null && String(val).trim()) {
        msgParts.push(String(val).trim());
      }
    }
  }

  const hasReadOnlyFieldValueError = msgParts.some((s) =>
    /read-only fields must have a non-null/i.test(s)
  );
  if (hasReadOnlyFieldValueError) {
    msgParts.push(
      'Please add a value for every read-only field (field properties → Field value, or double‑click the field on the canvas), then try Send again.'
    );
  }

  return (
    msgParts.filter(Boolean).join('\n')
    || String(layer.error || '').trim()
    || 'Pre-send sync failed. Please try again.'
  );
}

/** Parse "missing document" hints from POST /envelope/{id}/pre-send-sync (flexible response shapes). */
function extractPreSendSyncMissingDocuments(responseData) {
  const root =
    responseData != null && typeof responseData === 'object' && responseData.data != null
      ? responseData.data
      : responseData;
  if (!root || typeof root !== 'object') return [];

  let raw =
    root.missing_documents
    ?? root.missingDocuments
    ?? root.documents_missing
    ?? root.upload_required_documents
    ?? root.missing_document
    ?? null;

  if (raw == null) return [];
  if (!Array.isArray(raw) && typeof raw === 'object') {
    raw = Object.values(raw);
  }
  if (!Array.isArray(raw)) return [];

  const out = [];
  for (const row of raw) {
    if (row == null) continue;
    if (typeof row === 'number' && Number.isFinite(row)) {
      out.push({ document_id: row });
      continue;
    }
    if (typeof row === 'string') {
      const s = row.trim();
      if (!s) continue;
      const asNum = parseInt(s, 10);
      if (String(asNum) === s && Number.isFinite(asNum) && asNum > 0) {
        out.push({ document_id: asNum });
      } else {
        out.push({ client_document_id: s });
      }
      continue;
    }
    if (typeof row === 'object') {
      const documentIndex = row.document_index ?? row.index ?? row.order ?? row.document_order;
      const clientId =
        row.client_document_id
        ?? row.client_document_uuid
        ?? row.client_id
        ?? row.client_uuid
        ?? undefined;
      out.push({
        document_index:
          documentIndex != null && documentIndex !== '' && Number.isFinite(Number(documentIndex))
            ? Number(documentIndex)
            : undefined,
        document_id: row.document_id ?? row.id ?? undefined,
        client_document_id:
          clientId != null && String(clientId).trim() !== '' ? String(clientId).trim() : undefined,
        title: row.title ?? row.name ?? row.document_title,
      });
    }
  }
  return out;
}

/**
 * Newly created documents from an envelope upload response — sole source for mapping
 * `client_document_id` → server `document_id` when merging upload batches.
 */
function extractDocumentsCreatedRecordsFromEnvelopeResponse(payload) {
  const root =
    payload != null && typeof payload === 'object' && payload.data != null && typeof payload.data === 'object'
      ? payload.data
      : payload;
  if (!root || typeof root !== 'object') return [];

  const normalizeId = (raw) => {
    const n = typeof raw === 'string' ? parseInt(raw, 10) : raw;
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const normalizeClientId = (doc) => {
    const raw =
      doc?.client_document_id
      ?? doc?.client_document_uuid
      ?? doc?.frontend_document_id
      ?? doc?.temp_document_id
      ?? doc?.client_id
      ?? doc?.client_uuid
      ?? doc?.frontend_id
      ?? doc?.upload_client_id
      ?? doc?.correlation_id
      ?? doc?.uuid
      ?? '';
    return String(raw || '').trim();
  };

  const created = root.documents_created;
  if (!Array.isArray(created)) return [];

  const byClient = new Map();
  created.forEach((doc) => {
    const id = normalizeId(doc?.original_document_id ?? doc?.document_id ?? doc?.id);
    const cid = normalizeClientId(doc);
    if (!id || !cid) return;
    byClient.set(cid, { id, clientId: cid });
  });
  return Array.from(byClient.values());
}

/**
 * Apply server document ids to `workingDocumentIds` using only `documents_created` in the response,
 * matched by `client_document_id` (no index fallbacks, no stale-slot skip).
 */
function mergeWorkingDocumentIdsFromEnvelopeResponse(
  workingDocumentIds,
  filesSnapshot,
  uploadItems,
  envelopePayload
) {
  const records = extractDocumentsCreatedRecordsFromEnvelopeResponse(envelopePayload);
  if (uploadItems.length > 0 && records.length === 0) {
    throw new Error(
      'Server did not return documents_created with ids for this upload. Cannot map new documents to rows.'
    );
  }

  const cidToServerId = new Map();
  records.forEach((rec) => {
    const cid = String(rec.clientId || '').trim();
    if (cid && rec.id) cidToServerId.set(cid, rec.id);
  });

  const slotHasValidId = (index) => {
    const raw = workingDocumentIds[index];
    const id = typeof raw === 'string' ? parseInt(raw, 10) : raw;
    return Number.isFinite(id) && id > 0;
  };

    uploadItems.forEach((item) => {
    const cid = String(item?.clientDocumentId || getFileClientDocumentId(item.file) || '').trim();
    if (!cid) {
      throw new Error(
        'Every document upload must send a client_document_id. Re-add the file or refresh the page, then try again.'
      );
    }
    const rowIndex = (filesSnapshot || []).findIndex(
      (f) => String(getFileClientDocumentId(f) || '').trim() === cid
    );
    if (rowIndex < 0) {
      throw new Error(
        `Could not apply upload response: no row matches client_document_id "${cid}" (the file may have been removed).`
      );
    }
    const serverId = cidToServerId.get(cid);
    if (!serverId) {
      throw new Error(
        `documents_created did not include an entry for client_document_id "${cid}".`
      );
    }
    workingDocumentIds[rowIndex] = serverId;
  });

  uploadItems.forEach((item) => {
    const cid = String(item?.clientDocumentId || getFileClientDocumentId(item.file) || '').trim();
    if (!cid) return;
    const rowIndex = (filesSnapshot || []).findIndex(
      (f) => String(getFileClientDocumentId(f) || '').trim() === cid
    );
    if (rowIndex >= 0 && !slotHasValidId(rowIndex)) {
  throw new Error(
        `After mapping, row for client_document_id "${cid}" still has no server document_id.`
  );
    }
  });
}

const FieldEditorSvgLayers = memo(function FieldEditorSvgLayers({
  segments,
  svgMaxWidth,
  svgTotalHeight,
  zoomScale,
}) {
  const safeScale = Number.isFinite(zoomScale) && zoomScale > 0 ? zoomScale : 1;
  const wrapperStyle = {
    position: 'relative',
    width: `${svgMaxWidth * safeScale}px`,
    height: `${svgTotalHeight * safeScale}px`,
    margin: '0',
    overflow: 'hidden',
  };
  const scaleRootStyle = {
    transform: `scale(${safeScale})`,
    transformOrigin: 'top left',
    width: `${svgMaxWidth}px`,
    height: `${svgTotalHeight}px`,
    position: 'absolute',
    left: 0,
    top: 0,
    margin: '0',
  };

  return (
    <div className="merged-svg-wrapper" style={wrapperStyle}>
      <div
        className="field-editor-svg-scale-root merged-svg-container"
        style={scaleRootStyle}
      >
        {segments.map((seg) => (
          <div
            key={`doc-editor-svg-${seg.documentIndex}`}
            className="document-editor-svg-segment"
            style={{
              position: 'absolute',
              left: 0,
              top: seg.top,
              width: `${svgMaxWidth}px`,
              height: `${seg.height}px`,
              pointerEvents: 'none',
            }}
            dangerouslySetInnerHTML={{
              __html: `<svg width="${svgMaxWidth}" height="${seg.height}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${svgMaxWidth} ${seg.height}">${seg.innerMarkup}</svg>`,
            }}
          />
        ))}
      </div>
    </div>
  );
});

/** Sum lengths before join so one enormous `Array#join` does not throw RangeError. */
const MAX_SVG_MARKUP_JOIN_CHARS = 256 * 1024 * 1024;
const ZIP_SVG_DEBUG = true;

function logZipSvgDebug(...args) {
  if (!ZIP_SVG_DEBUG) return;
  console.log(...args);
}

function logFieldAutosaveDebug(...args) {
  if (!FIELD_AUTOSAVE_DEBUG) return;
  console.log(...args);
}

function logFieldSaveDebug(...args) {
  if (!FIELD_SAVE_DEBUG) return;
  console.log(...args);
}

function logVisiblePageDebug(...args) {
  if (!FIELD_VISIBLE_PAGE_DEBUG) return;
  console.log(...args);
}

function joinSvgMarkupParts(parts) {
  let len = 0;
  for (let i = 0; i < parts.length; i++) {
    len += parts[i].length;
    if (len > MAX_SVG_MARKUP_JOIN_CHARS) {
      throw new RangeError('SVG segment markup exceeds safe string length');
    }
  }
  return parts.join('');
}

const CreateEnvelope = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const userSubscription = useSelector((state) => state.subscription?.userSubscription);
  const { user } = useSelector((state) => state.auth);
  const isOrgAdmin = Boolean(
    user?.is_organization_admin ?? user?.is_organization_owner
  );
  const { envelopeId: urlEnvelopeId, templateId: urlTemplateId } = useParams(); // Get IDs from URL params
  const isTemplateMode =
    location.pathname.startsWith('/create-template') ||
    location.pathname.startsWith('/embed/create-template');
  const [searchParams] = useSearchParams();
  const isEmbedMode = useMemo(() => {
    if (location.pathname.startsWith('/embed/')) return true;
    if (searchParams.get('embed') === '1' || searchParams.get('embed') === 'true') return true;
    if (typeof window === 'undefined') return false;
    if (window.ReactNativeWebView) return true;
    try {
      return window.self !== window.top;
    } catch {
      // Cross-origin iframe access can throw — treat as embedded.
      return true;
    }
  }, [location.pathname, searchParams]);
  const [embedExitPending, setEmbedExitPending] = useState(false);
  const notifyEmbedParent = useCallback((payload = {}) => {
    if (typeof window === 'undefined') return;
    const message = { type: 'volksign:done', ...payload };
    const serialized = JSON.stringify(message);
    try {
      if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
        window.ReactNativeWebView.postMessage(serialized);
      }
    } catch {
      // ignore RN bridge errors
    }
    try {
      if (window.parent && window.parent !== window) {
        const origin = process.env.REACT_APP_EMBED_PARENT_ORIGIN || '*';
        window.parent.postMessage(message, origin);
      }
    } catch {
      // ignore cross-origin parent errors
    }
  }, []);
  const resolveEmbedNavigateTo = useCallback((fallbackPath = '', mode) => {
    const path = String(fallbackPath || '');
    if (mode === 'template' || path.includes('template')) return 'templates';
    if (path.includes('draft') || path.includes('envelope')) return 'envelopes';
    if (path.includes('dashboard')) return 'dashboard';
    return 'back';
  }, []);
  const exitEditor = useCallback(
    (fallbackPath, payload = {}) => {
      const navigateTo =
        payload.navigateTo
        || resolveEmbedNavigateTo(fallbackPath, payload.mode);

      if (isEmbedMode) {
        // Never navigate inside the WebView/iframe — host app must close and route natively.
        setEmbedExitPending(true);
        notifyEmbedParent({
          ...payload,
          navigateTo,
          path: fallbackPath,
        });
        return;
      }
      navigate(fallbackPath, { replace: true });
    },
    [isEmbedMode, navigate, notifyEmbedParent, resolveEmbedNavigateTo]
  );
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload/Recipients/Message, 2: Field Placement
  const [documentsExpanded, setDocumentsExpanded] = useState(true);
  const [recipientsExpanded, setRecipientsExpanded] = useState(true);
  const [messageExpanded, setMessageExpanded] = useState(true);
  const [isOnlySigner, setIsOnlySigner] = useState(false);
  // const [showUploadDropdown, setShowUploadDropdown] = useState(false);
  /** Canonical Step 1 documents: stable rowId + file + serverDocumentId (order = array index). */
  const [envelopeDocumentRows, setEnvelopeDocumentRows] = useState([]);
  const envelopeDocumentRowsRef = useRef([]);
  const envelopeRowsCommitRef = useRef(() => {});
  const uploadedFiles = useMemo(
    () => envelopeDocumentRows.map((r) => r.file),
    [envelopeDocumentRows]
  );
  const documentIds = useMemo(
    () => envelopeDocumentRows.map((r) => r.serverDocumentId ?? null),
    [envelopeDocumentRows]
  );
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [recipientFieldErrorsVisible, setRecipientFieldErrorsVisible] = useState(false);
  const [removingRecipientIndex, setRemovingRecipientIndex] = useState(null);
  const [signingOrder, setSigningOrder] = useState(false);
  const [recipients, setRecipients] = useState([{ name: '', email: '', role: 'Needs to Sign', color: '#6366f1', isVariable: false, placeholder: '' }]);
  const [savedRecipientContacts, setSavedRecipientContacts] = useState([]);
  const [sequenceInputs, setSequenceInputs] = useState([1]);
  const [replaceFileIndex, setReplaceFileIndex] = useState(null);
  const [subject, setSubject] = useState('Complete with Volksign:');
  const [message, setMessage] = useState('');
  const [reminderFrequency, setReminderFrequency] = useState('Every day');
  const [templateName, setTemplateName] = useState(''); // Optional template name (defaults to subject)
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [zoomLevel, setZoomLevel] = useState('fitToWidth'); // 'fitToWidth' or percentage number
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [svgMaxWidth, setSvgMaxWidth] = useState(0); // Store max width for fit calculations
  const [svgTotalHeight, setSvgTotalHeight] = useState(0); // Store total height for fit calculations
  const [currentPage, setCurrentPage] = useState(1);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const [documentFields, setDocumentFields] = useState([]); // Fields placed on document
  const [selectedField, setSelectedField] = useState(null); // Currently selected field for editing (legacy, for single field operations)
  const [selectedFields, setSelectedFields] = useState([]); // Array of selected field IDs for multi-select
  const [history, setHistory] = useState({ undo: [], redo: [] }); // Undo/redo history (max 5 operations)
  const maxHistorySize = 5; // Maximum number of undo/redo operations
  // Inline editor for read-only fields (edit value directly on the PDF canvas)
  // { fieldId, draft, fontFamily, fontSize, fontColor, fontWeight, fontStyle, textDecoration, textAlign }
  const [inlineReadOnlyEditor, setInlineReadOnlyEditor] = useState(null);
  const readOnlyRichEditorRef = useRef(null);
  const [copiedField, setCopiedField] = useState(null); // Field data for copy/paste (single field)
  const [copiedFields, setCopiedFields] = useState([]); // Array of copied fields for multi-copy/paste
  const [isSelecting, setIsSelecting] = useState(false); // Whether user is drawing selection box
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, fieldId: null }); // Context menu state
  const [expandedSections, setExpandedSections] = useState({
    formatting: false,
    dataLabel: false,
    tooltip: false,
    location: false,
    options: false
  }); // Track which property sections are expanded
  const [draggedField, setDraggedField] = useState(null); // Field being dragged (primary field)
  const draggedFieldsRef = useRef([]); // All fields being dragged (for multi-select)
  const resizingFieldsRef = useRef([]); // All fields being resized (for multi-select)
  const resizeFieldStartsRef = useRef(new Map()); // field id → { x, y, width, height } at resize start
  const dragOffsetRef = useRef({ x: 0, y: 0 }); // Offset when starting drag
  const dragStartStateRef = useRef(null); // State before drag started (ref avoids mousedown rerender cost)
  const autoScrollIntervalRef = useRef(null); // Auto-scroll interval ref
  const [resizingField, setResizingField] = useState(null); // Field being resized
  const [resizeHandle, setResizeHandle] = useState(null); // Which handle is being used
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0, mouseX: 0, mouseY: 0 }); // Initial resize values
  const resizeStartStateRef = useRef(null); // State before resize started (ref avoids mousedown rerender cost)
  const [searchFields, setSearchFields] = useState('');
  /** Mobile field-placement: fields palette drawer + tap-to-place */
  const [fieldsDrawerOpen, setFieldsDrawerOpen] = useState(false);
  const [pendingPlaceField, setPendingPlaceField] = useState(null); // { type, label } | null
  const [propertiesDrawerOpen, setPropertiesDrawerOpen] = useState(false);
  const ignoreMouseFromTouchRef = useRef(false);
  const [isMobileFieldLayout, setIsMobileFieldLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [expandedDocuments, setExpandedDocuments] = useState({}); // Track which documents are expanded
  const [documentPages, setDocumentPages] = useState({}); // Track page count for each document
  const [, setPdfDocuments] = useState([]); // Store loaded PDF documents
  const [, setMergedSvg] = useState(''); // Shell SVG (full canvas size); real page markup lives in documentEditorSvgSegments
  /** Per-document SVG fragments for field editor (lighter than one giant root SVG). */
  const [documentEditorSvgSegments, setDocumentEditorSvgSegments] = useState([]);
  const [svgPageHeights, setSvgPageHeights] = useState([]); // Store heights of each page in the merged SVG
  const [documentPageSvgs, setDocumentPageSvgs] = useState({}); // Store individual page SVGs per document: { docIndex: { pageNum: svgString } }
  const [documentSvgBundles, setDocumentSvgBundles] = useState([]); // One SVG bundle per document (ordered by uploadedFiles)
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const [showEditMessageModal, setShowEditMessageModal] = useState(false);
  const [showEditRecipientsModal, setShowEditRecipientsModal] = useState(false);
  const [showEditDocumentsModal, setShowEditDocumentsModal] = useState(false);
  const [showDiscardConfirmModal, setShowDiscardConfirmModal] = useState(false);
  const [showRecipientWithoutFieldsModal, setShowRecipientWithoutFieldsModal] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [quotaModalMessage, setQuotaModalMessage] = useState('');
  const [showPayPerEnvelopeModal, setShowPayPerEnvelopeModal] = useState(false);
  const [payPerEnvelopeEnvelopeId, setPayPerEnvelopeEnvelopeId] = useState(null);
  const [recipientsWithoutFields, setRecipientsWithoutFields] = useState([]);
  const allowSendWithoutFieldsCheckRef = useRef(false);
  /** Stable row keys (`getDocumentCacheKey`) currently showing delete spinner; supports multiple concurrent deletes. */
  const [deletingDocumentRowKeys, setDeletingDocumentRowKeys] = useState([]);
  const deletingDocumentKeysInFlightRef = useRef(new Set());
  const documentOrderSaveTimeoutRef = useRef(null);
  const lastSyncedDocumentOrderSignatureRef = useRef('');
  const [draftEnvelopeId, setDraftEnvelopeId] = useState(null); // Store draft envelope ID (legacy, kept for compatibility)
  const [templateId, setTemplateId] = useState(null); // Store template ID for drafts
  const [pendingDraftFieldHydration, setPendingDraftFieldHydration] = useState(null);
  const [isDraftFieldsHydrating, setIsDraftFieldsHydrating] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  // While opening an existing draft, keep Step 1 Next disabled until draft docs are fetched + SVG-warmed.
  const [isDraftAssetsPreparing, setIsDraftAssetsPreparing] = useState(false);
  const [isSvgPrewarming, setIsSvgPrewarming] = useState(false);
  const [isNextTransitioning, setIsNextTransitioning] = useState(false);
  /** True only while Send / Save Template final action runs — not background autosave */
  const [isFinalActionSubmitting, setIsFinalActionSubmitting] = useState(false);
  /** Drives footer loading label: Save and Close → "Saving...", Send → "Sending..." */
  const [finalActionPendingKind, setFinalActionPendingKind] = useState(null);
  const savingLockRef = useRef(false); // Lock to prevent concurrent saves
  // Prevent autosave from retrying the exact same non-retryable document upload set.
  const blockedDocumentUploadSignatureRef = useRef('');
  const blockedDocumentUploadMessageRef = useRef('');
  /** True while saveDocuments is uploading local files (template batches or envelope try). */
  const documentUploadPipelineActiveRef = useRef(false);
  /** After reorder during upload, flush order sync once uploads finish (uses latest refs). */
  const pendingDeferredOrderSyncRef = useRef(false);
  const syncDocumentOrderToBackendRef = useRef(null);
  /** When true, save* functions ignore isSavingDraft so Send can run a full flush after setIsSavingDraft(true). */
  const orchestratedDraftSyncRef = useRef(false);
  const documentIdsRef = useRef(documentIds);
  const uploadedFilesRef = useRef(uploadedFiles);
  const documentFieldsRef = useRef(documentFields);
  const draftEnvelopeIdRef = useRef(draftEnvelopeId);
  // When a document is deleted, the backend typically deletes its fields too.
  // We keep a short-lived list of document_ids that are being deleted so `saveFields()`
  // doesn't spam DELETE calls for every field (leading to "too many responses").
  const pendingDocumentDeletesSkipFieldRef = useRef(new Set());
  /** Client doc ids removed from UI before server returned document_id — DELETE after upload maps an id. */
  const pendingDeleteAfterClientDocIdRef = useRef(new Set());
  /** Same row-removal as handleRemoveFile applyLocalDelete; used after deferred server DELETE during upload. */
  const purgeDocumentRowAtIndexRef = useRef(() => {});
  // Field IDs already deleted via direct UI action; skip one autosave diff-delete pass.
  const pendingExplicitFieldDeletesSkipRef = useRef(new Set());
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [documentUploadProgress, setDocumentUploadProgress] = useState({});

  /** Per client_document_id: pending | uploading | uploaded — avoids fragile row-index coupling during reorder. */
  const documentUploadRegistryRef = useRef(new Map());
  const [documentUploadRegistryEpoch, setDocumentUploadRegistryEpoch] = useState(0);
  const bumpDocumentUploadRegistry = useCallback(() => {
    setDocumentUploadRegistryEpoch((n) => n + 1);
  }, []);

  const setDocumentUploadRegistryEntry = useCallback(
    (cid, patch) => {
      const k = String(cid || '').trim();
      if (!k) return;
      const reg = documentUploadRegistryRef.current;
      const prev = reg.get(k) || { status: 'pending', documentId: null };
      const next = { ...prev, ...patch };
      if (prev.status === next.status && prev.documentId === next.documentId) return;
      reg.set(k, next);
      bumpDocumentUploadRegistry();
    },
    [bumpDocumentUploadRegistry]
  );

  const registerLocalDocumentUploadPending = useCallback(
    (file) => {
      if (!file || file.fromApi || !(file instanceof File)) return;
      const rawId = file?.documentId ?? file?.document_id ?? null;
      if (rawId != null && rawId !== '' && rawId !== 0) {
        const n = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId;
        if (Number.isFinite(n) && n > 0) {
          const cid = String(getFileClientDocumentId(file) || '').trim();
          if (cid) {
            documentUploadRegistryRef.current.set(cid, { status: 'uploaded', documentId: n });
            bumpDocumentUploadRegistry();
          }
          return;
        }
      }
      const cid = String(getFileClientDocumentId(file) || '').trim();
      if (!cid) return;
      const reg = documentUploadRegistryRef.current;
      const prev = reg.get(cid);
      if (prev?.status === 'uploaded') return;
      reg.set(cid, { status: 'pending', documentId: null });
      bumpDocumentUploadRegistry();
    },
    [bumpDocumentUploadRegistry]
  );

  const removeDocumentUploadRegistryEntry = useCallback(
    (cid) => {
      const k = String(cid || '').trim();
      if (!k) return;
      if (documentUploadRegistryRef.current.delete(k)) bumpDocumentUploadRegistry();
    },
    [bumpDocumentUploadRegistry]
  );

  /** Commit envelope rows and sync uploadedFilesRef / documentIdsRef immediately (upload + purge paths rely on this). */
  envelopeRowsCommitRef.current = (nextRows) => {
    const normalized = (Array.isArray(nextRows) ? nextRows : []).map((row) => ({
      rowId: row.rowId || newEnvelopeRowId(),
      file: row.file,
      serverDocumentId: normalizeRowServerDocumentId(row.serverDocumentId),
    }));
    envelopeDocumentRowsRef.current = normalized;
    uploadedFilesRef.current = normalized.map((r) => r.file);
    documentIdsRef.current = normalized.map((r) => r.serverDocumentId);
    setEnvelopeDocumentRows(normalized);
  };

  useEffect(() => {
    documentFieldsRef.current = documentFields;
  }, [documentFields]);
  useEffect(() => {
    svgPageHeightsRef.current = svgPageHeights;
  }, [svgPageHeights]);
  useEffect(() => () => {
    const { move, up } = marqueeHandlersRef.current;
    if (move) document.removeEventListener('mousemove', move);
    if (up) document.removeEventListener('mouseup', up);
    marqueeHandlersRef.current = { move: null, up: null };
    marqueeActiveRef.current = false;
  }, []);
  useEffect(() => {
    selectedFieldRef.current = selectedField;
  }, [selectedField]);
  useEffect(() => {
    draftEnvelopeIdRef.current = draftEnvelopeId;
  }, [draftEnvelopeId]);

  const refreshRecipientContacts = useCallback(async () => {
    const expectedOrgId =
      getOrganizationIdFromSubscription(userSubscription) ?? getActiveOrganizationId();

    try {
      const response = await getContactList();
      if (isContactListApiFailure(response)) {
        throw new Error(response?.message || 'Failed to load contacts');
      }

      const apiOrgId = extractOrganizationIdFromContactListResponse(response);
      if (apiOrgId) {
        setActiveOrganizationId(apiOrgId);
      }

      const activeOrgId = apiOrgId ?? expectedOrgId;
      if (!activeOrgId) {
        setSavedRecipientContacts([]);
        return;
      }

      if (expectedOrgId && apiOrgId && expectedOrgId !== apiOrgId) {
        console.warn('Contact list organization mismatch; clearing suggestions.');
        setSavedRecipientContacts([]);
        return;
      }

      const fromApi = extractContactsFromApiResponse(response);
      const stored = saveStoredRecipientContacts(fromApi, activeOrgId);
      setSavedRecipientContacts(stored);
    } catch (error) {
      console.warn('Could not refresh recipient contacts from API.', error);
      const orgId = expectedOrgId ?? getActiveOrganizationId();
      setSavedRecipientContacts(orgId ? loadStoredRecipientContacts(orgId) : []);
    }
  }, [userSubscription]);

  const didFetchContactsRef = useRef(false);
  useEffect(() => {
    if (didFetchContactsRef.current) return;
    didFetchContactsRef.current = true;
    refreshRecipientContacts();
  }, [refreshRecipientContacts]);

  const flushDeferredDocumentApiTasks = useCallback(async () => {
    if (!pendingDeferredOrderSyncRef.current) return;
    const syncFn = syncDocumentOrderToBackendRef.current;
    if (!syncFn) return;
    try {
      const ok = await syncFn(uploadedFilesRef.current || [], documentIdsRef.current || []);
      if (ok) pendingDeferredOrderSyncRef.current = false;
    } catch (orderErr) {
      console.error('Deferred document order sync failed:', orderErr);
    }
  }, []);

  const selectedFieldIdsSet = useMemo(() => new Set(selectedFields), [selectedFields]);
  const saveDraftRef = useRef(null);
  const lastSavedRecipientsRef = useRef([]); // Track last saved recipients to avoid duplicates
  const forceRecipientSaveOnNextEffectRef = useRef(false);
  const recipientIdMapRef = useRef({}); // Map recipient emails to recipient IDs: { email: recipient_id }
  const fieldIdMapRef = useRef({}); // Map field local IDs to API field IDs: { localFieldId: apiFieldId }
  const lastSavedFieldsRef = useRef([]); // Track last saved fields to detect deletions
  const fileInputRef = useRef(null);
  const svgContainerRef = useRef(null);
  const visiblePageRef = useRef(1);
  const selectedFieldRef = useRef(null);
  const dragRafRef = useRef(null);
  /** field id → .placed-field element; used to move/resize without per-frame React state */
  const fieldOverlayElRefs = useRef(new Map());
  const placedFieldRefCallbackCache = useRef(new Map());
  const getPlacedFieldRef = useCallback((fieldId) => {
    if (!placedFieldRefCallbackCache.current.has(fieldId)) {
      placedFieldRefCallbackCache.current.set(fieldId, (el) => {
        if (el) fieldOverlayElRefs.current.set(fieldId, el);
        else fieldOverlayElRefs.current.delete(fieldId);
      });
    }
    return placedFieldRefCallbackCache.current.get(fieldId);
  }, []);
  /** Latest drag positions (doc coords) committed on mouseup */
  const dragLivePositionsRef = useRef(new Map());
  const svgPageHeightsRef = useRef([]);
  /** Resize geometry during drag; committed on mouseup (field id → geom) */
  const resizeLiveGeometriesRef = useRef(new Map());
  const selectionBoxElRef = useRef(null);
  /** Mutated during marquee selection to avoid setState every mousemove */
  const selectionBoxDragRef = useRef({ startX: 0, startY: 0, endX: 0, endY: 0 });
  /** True while drawing marquee (sync); avoids relying on async isSelecting for document listeners */
  const marqueeActiveRef = useRef(false);
  const marqueeHandlersRef = useRef({ move: null, up: null });
  /** After a real marquee drag, skip one background click so it does not clear the new selection */
  const suppressNextPreviewBackgroundClickRef = useRef(false);
  /** After a real field drag, skip the next field click so multi-select does not collapse on mouseup. */
  const suppressNextFieldClickRef = useRef(false);
  const recipientDropdownRef = useRef(null);
  const contextMenuRef = useRef(null);
  const templateReadyRef = useRef(false);
  const templateAutoSaveTimeoutRef = useRef(null);
  const templateAutoSavingRef = useRef(false);
  const lastTemplateSaveFingerprintRef = useRef('');
  const isHydratingDraftRef = useRef(false);
  const pointerInteractionActiveRef = useRef(false);
  const pointerLastActivityAtRef = useRef(0);
  const pointerInteractionReleaseTimeoutRef = useRef(null);
  const draftHydrationTimeoutRef = useRef(null);
  const svgLoadCycleRef = useRef(0);
  // Cache PDF bytes by doc key so we don't re-download/re-read when entering step 2.
  const documentArrayBufferCacheRef = useRef(new Map());
  const documentArrayBufferPromiseRef = useRef(new Map());
  // Cache fully converted SVG pages by doc key so step 2 can render immediately.
  const documentSvgCacheRef = useRef(new Map());
  const documentSvgPromiseRef = useRef(new Map());
  const svgPrewarmPromiseRef = useRef(null);
  const svgPrewarmSignatureRef = useRef('');
  const finalizedSvgPrewarmSignatureRef = useRef('');
  const isSvgPrewarmActiveRef = useRef(false);
  const zipBatchApiChainRef = useRef(Promise.resolve());
  const zipChunkRequestPromiseRef = useRef(new Map());
  const documentHydrationPromiseRef = useRef(new Map());

  const ensureLocalUploadKey = useCallback((file, fallbackIndex = 0) => {
    if (!file || file.fromApi) return file;
    if (file.__localUploadKey) return file;
    const key = `local:${Date.now()}:${fallbackIndex}:${Math.random().toString(36).slice(2, 10)}`;
    try {
      Object.defineProperty(file, '__localUploadKey', {
        value: key,
        writable: false,
        enumerable: false,
      });
    } catch {
      file.__localUploadKey = key;
    }
    return file;
  }, []);

  const ensureClientDocumentId = useCallback((file, _fallbackIndex = 0) => {
    if (!file || file.fromApi) return file;
    if (file.__clientDocumentId) return file;
    const id = createBrowserClientDocumentId();
    try {
      Object.defineProperty(file, '__clientDocumentId', {
        value: id,
        writable: false,
        enumerable: false,
      });
    } catch {
      file.__clientDocumentId = id;
    }
    return file;
  }, []);

  const getDocumentCacheKey = useCallback((file, index = 0) => {
    const docId = file?.documentId ?? file?.document_id ?? null;
    if (file?.fromApi && docId != null) return `api:${docId}`;
    if (file?.__localUploadKey) return file.__localUploadKey;
    const name = file?.name || `doc-${index}`;
    const size = file?.size || 0;
    const lm = file?.lastModified || 0;
    return `local-fallback:${name}:${size}:${lm}:${index}`;
  }, []);

  const setUploadProgressEntries = useCallback((items, computeValue) => {
    setDocumentUploadProgress((prev) => {
      const next = { ...prev };
      items.forEach((item, itemIdx) => {
        const key = item?.key;
        if (!key) return;
        const value = typeof computeValue === 'function' ? computeValue(item, itemIdx, prev[key]) : computeValue;
        if (value == null) {
          delete next[key];
        } else {
          next[key] = value;
        }
      });
      return next;
    });
  }, []);

  const clearDocumentBufferCacheForFile = useCallback((file, index = 0) => {
    const key = getDocumentCacheKey(file, index);
    documentArrayBufferCacheRef.current.delete(key);
    documentArrayBufferPromiseRef.current.delete(key);
  }, [getDocumentCacheKey]);

  const clearDocumentSvgCacheForFile = useCallback((file, index = 0) => {
    const key = getDocumentCacheKey(file, index);
    documentSvgCacheRef.current.delete(key);
    documentSvgPromiseRef.current.delete(key);
  }, [getDocumentCacheKey]);

  const markPointerInteractionActive = useCallback(() => {
    pointerInteractionActiveRef.current = true;
    pointerLastActivityAtRef.current = Date.now();
    if (pointerInteractionReleaseTimeoutRef.current) {
      clearTimeout(pointerInteractionReleaseTimeoutRef.current);
      pointerInteractionReleaseTimeoutRef.current = null;
    }
  }, []);

  const markPointerInteractionIdle = useCallback((delayMs = 120) => {
    if (pointerInteractionReleaseTimeoutRef.current) {
      clearTimeout(pointerInteractionReleaseTimeoutRef.current);
    }
    pointerLastActivityAtRef.current = Date.now();
    pointerInteractionReleaseTimeoutRef.current = setTimeout(() => {
      pointerInteractionActiveRef.current = false;
      pointerLastActivityAtRef.current = Date.now();
      pointerInteractionReleaseTimeoutRef.current = null;
      if (
        !isTemplateMode &&
        draftEnvelopeIdRef.current &&
        documentFieldsRef.current.length > 0 &&
        saveDraftRef.current &&
        !isHydratingDraftRef.current
      ) {
        scheduleFieldAutosaveRef.current('pointer-idle-release');
      }
    }, delayMs);
  }, [isTemplateMode]);

  const shouldPauseFieldSaveDuringInteraction = useCallback(() => {
    if (pointerInteractionActiveRef.current) return true;
    if (draggedField || resizingField || isSelecting) return true;
    return false;
  }, [draggedField, resizingField, isSelecting]);

  const enqueueZipBatchRequest = useCallback(async (requestFn) => {
    const previous = zipBatchApiChainRef.current;
    let release;
    zipBatchApiChainRef.current = new Promise((resolve) => {
      release = resolve;
    });
    await previous;
    try {
      return await requestFn();
    } finally {
      release();
    }
  }, []);

  const normalizeDocumentId = useCallback((id) => {
    const n = typeof id === 'string' ? parseInt(id, 10) : id;
    return Number.isFinite(n) ? n : null;
  }, []);

  const inferDocumentIdFromFilename = useCallback((name) => {
    const raw = String(name || '').trim();
    if (!raw) return null;
    const matches = raw.match(/\d+/g);
    if (!matches || matches.length === 0) return null;
    const parsed = parseInt(matches[matches.length - 1], 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const extractZipBuffersFromBatch = useCallback((batchItem, requestedDocIdsSet, expectedDocIds = []) => (async () => {
    const zipUrl = String(batchItem?.zip_url || batchItem?.temporary_url || '').trim();
    if (!zipUrl) return new Map();
    logZipSvgDebug('📦 [ZIP] Starting batch download', {
      zipUrl,
      batchDocumentIds: batchItem?.document_ids || [],
      requestedCount: requestedDocIdsSet?.size || 0,
    });

    const batchDocIds = (
      Array.isArray(batchItem?.document_ids) ? batchItem.document_ids :
      Array.isArray(batchItem?.documents) ? batchItem.documents.map((d) => d?.document_id ?? d?.id) :
      []
    )
      .map((id) => normalizeDocumentId(id))
      .filter((id) => id != null);
    const fallbackDocIds = Array.isArray(expectedDocIds)
      ? expectedDocIds.map((id) => normalizeDocumentId(id)).filter((id) => id != null)
      : [];

    const normalizeZipEntryName = (value) =>
      String(value || '')
        .trim()
        .replace(/\\/g, '/')
        .toLowerCase();

    const filenameDocIdMap = new Map();
    if (Array.isArray(batchItem?.documents)) {
      batchItem.documents.forEach((doc) => {
        const docId = normalizeDocumentId(doc?.document_id ?? doc?.id);
        const fileNameRaw = String(doc?.file_name || doc?.filename || doc?.name || '').trim();
        if (docId != null && fileNameRaw) {
          const normalizedFullName = normalizeZipEntryName(fileNameRaw);
          const normalizedBaseName = normalizedFullName.split('/').pop() || normalizedFullName;
          filenameDocIdMap.set(normalizedFullName, docId);
          filenameDocIdMap.set(normalizedBaseName, docId);
        }
      });
    }
    const hasExplicitFilenameMapping = filenameDocIdMap.size > 0;

    const zipRes = await fetch(zipUrl);
    if (!zipRes.ok) {
      throw new Error(`Batch ZIP fetch failed with status ${zipRes.status}`);
    }
    logZipSvgDebug('⬇️ [ZIP] Downloaded ZIP response', {
      status: zipRes.status,
      contentLength: zipRes.headers.get('content-length'),
      contentType: zipRes.headers.get('content-type'),
    });

    const zipBytes = await zipRes.arrayBuffer();
    logZipSvgDebug('🧱 [ZIP] ZIP bytes received', { bytes: zipBytes.byteLength });
    const zip = await JSZip.loadAsync(zipBytes);
    const entries = Object.values(zip.files).filter((entry) => !entry.dir && /\.pdf$/i.test(entry.name));
    const extracted = new Map();
    logZipSvgDebug('📂 [ZIP] PDF entries discovered', {
      count: entries.length,
      entryNames: entries.map((entry) => entry.name),
    });

    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      const normalizedEntryFullName = normalizeZipEntryName(entry.name);
      const normalizedEntryBaseName = normalizedEntryFullName.split('/').pop() || normalizedEntryFullName;
      const explicitMappedDocId =
        filenameDocIdMap.get(normalizedEntryFullName) ??
        filenameDocIdMap.get(normalizedEntryBaseName) ??
        null;

      const candidateDocIds = (hasExplicitFilenameMapping
        ? [explicitMappedDocId]
        : [
            explicitMappedDocId,
            inferDocumentIdFromFilename(normalizedEntryBaseName),
            batchDocIds[i],
            fallbackDocIds[i],
          ])
        .map((id) => normalizeDocumentId(id))
        .filter((id) => id != null);

      // Pick first candidate that is actually part of this requested set and not already mapped.
      const docId = candidateDocIds.find((id) => requestedDocIdsSet.has(id) && !extracted.has(id)) ?? null;
      if (docId == null) {
        logZipSvgDebug('⚠️ [ZIP->PDF] Could not map ZIP entry to requested doc id', {
          entry: entry.name,
          candidates: candidateDocIds,
          requestedDocIds: Array.from(requestedDocIdsSet),
        });
        continue;
      }
      const buffer = await entry.async('arraybuffer');
      extracted.set(docId, buffer);
      logZipSvgDebug('📄 [ZIP->PDF] Extracted and mapped PDF', {
        entry: entry.name,
        mappedDocumentId: docId,
        pdfBytes: buffer.byteLength,
      });
    }

    logZipSvgDebug('✅ [ZIP] Batch extraction complete', {
      extractedDocumentIds: Array.from(extracted.keys()),
    });
    return extracted;
  })(), [inferDocumentIdFromFilename, normalizeDocumentId]);

  const hydrateDraftDocumentBuffersFromZip = useCallback(async (files) => {
    const apiItems = (Array.isArray(files) ? files : [])
      .map((file, idx) => {
        const docId = normalizeDocumentId(file?.documentId ?? file?.document_id);
        return docId != null ? { file, idx, docId } : null;
      })
      .filter(Boolean);

    if (apiItems.length === 0) return;

    const requestedDocIds = Array.from(new Set(apiItems.map((item) => item.docId)));
    const itemByDocId = new Map(apiItems.map((item) => [item.docId, item]));
    const toProgressItems = (docIds) =>
      docIds.map((docId) => ({
        key: `api:${docId}`,
        name: itemByDocId.get(docId)?.file?.name || `Document ${docId}`,
      }));

    setUploadProgressEntries(
      toProgressItems(requestedDocIds),
      (item, _idx, previous) => (previous?.status === 'ready'
        ? previous
        : { percent: 0, status: 'downloading', fileName: item.name })
    );

    logZipSvgDebug('🚀 [ZIP] Hydration started for API draft docs', {
      requestedDocIds,
      requestedCount: requestedDocIds.length,
    });
    const requestBatchSize = 10;
    const hydratedDocIds = new Set();
    const missingFromMeta = new Set();

    const processDocIdChunks = async (docIds, phase = 'initial') => {
      for (let offset = 0; offset < docIds.length; offset += requestBatchSize) {
        const idChunk = docIds.slice(offset, offset + requestBatchSize);
        const idChunkSet = new Set(idChunk);
        const chunkKey = idChunk.join(',');
        setUploadProgressEntries(
          toProgressItems(idChunk),
          (item, _idx, previous) => ({
            percent: Math.max(5, Number(previous?.percent) || 0),
            status: 'downloading',
            fileName: item.name,
          })
        );
        let response = null;
        try {
          logZipSvgDebug('📡 [ZIP] Requesting batch URL chunk', { idChunk, phase });
          const existingPromise = zipChunkRequestPromiseRef.current.get(chunkKey);
          if (existingPromise) {
            response = await existingPromise;
          } else {
            const requestPromise = enqueueZipBatchRequest(() => getDocumentFileBatchZipUrls(idChunk, 30))
              .finally(() => {
                zipChunkRequestPromiseRef.current.delete(chunkKey);
              });
            zipChunkRequestPromiseRef.current.set(chunkKey, requestPromise);
            response = await requestPromise;
          }
        } catch (error) {
          console.warn('⚠️ Failed to request document ZIP batch URL chunk:', idChunk, error);
          continue;
        }
        logZipSvgDebug('📥 [ZIP] Batch URL chunk response', {
          idChunk,
          phase,
          batchItemsCount: Array.isArray(response?.items) ? response.items.length : 0,
          meta: response?.meta || null,
        });

        const batches = Array.isArray(response?.items) ? response.items : [];
        for (let i = 0; i < batches.length; i += 1) {
          try {
            setUploadProgressEntries(
              toProgressItems(idChunk),
              (item, _idx, previous) => ({
                percent: Math.max(35, Number(previous?.percent) || 0),
                status: 'extracting',
                fileName: item.name,
              })
            );
            logZipSvgDebug('🗜️ [ZIP] Processing returned ZIP item', {
              chunk: idChunk,
              phase,
              batchIndex: i,
              batchDocumentIds: batches[i]?.document_ids || [],
            });
            const extracted = await extractZipBuffersFromBatch(batches[i], idChunkSet, idChunk);
            if (extracted.size > 0) {
              setUploadProgressEntries(
                toProgressItems(Array.from(extracted.keys())),
                (item) => ({ percent: 70, status: 'preparing_svg', fileName: item.name })
              );
            }
            extracted.forEach((buffer, docId) => {
              hydratedDocIds.add(docId);
              apiItems.forEach(({ file, idx, docId: fileDocId }) => {
                if (fileDocId !== docId) return;
                const key = getDocumentCacheKey(file, idx);
                documentArrayBufferCacheRef.current.set(key, buffer);
                logZipSvgDebug('💾 [PDF CACHE] Stored PDF buffer in cache', {
                  documentId: docId,
                  documentIndex: idx,
                  cacheKey: key,
                  bytes: buffer.byteLength,
                });
              });
            });
          } catch (error) {
            console.warn('⚠️ Failed to hydrate document ZIP batch:', error);
          }
        }

        const chunkMissing = Array.isArray(response?.meta?.missing_document_ids)
          ? response.meta.missing_document_ids.map((id) => normalizeDocumentId(id)).filter((id) => id != null)
          : [];
        chunkMissing.forEach((id) => missingFromMeta.add(id));
      }
    };

    await processDocIdChunks(requestedDocIds, 'initial');

    if (missingFromMeta.size > 0) {
      console.warn('⚠️ Some draft documents are missing for ZIP generation:', Array.from(missingFromMeta));
    }

    const unresolved = requestedDocIds.filter((id) => !hydratedDocIds.has(id));
    if (unresolved.length > 0) {
      console.warn('⚠️ Unable to resolve PDF bytes from ZIP batches for document ids:', unresolved);
    }
    logZipSvgDebug('🏁 [ZIP] Hydration finished', {
      hydratedDocumentIds: Array.from(hydratedDocIds),
      unresolvedDocumentIds: unresolved,
    });
  }, [enqueueZipBatchRequest, extractZipBuffersFromBatch, getDocumentCacheKey, normalizeDocumentId, setUploadProgressEntries]);

  const resolveDocumentArrayBuffer = useCallback(async (file, index = 0) => {
    const key = getDocumentCacheKey(file, index);
    const cached = documentArrayBufferCacheRef.current.get(key);
    if (cached) return cached;

    const pending = documentArrayBufferPromiseRef.current.get(key);
    if (pending) return pending;

    const promise = (async () => {
      const docId = file?.documentId ?? file?.document_id ?? null;
      const canReadLocal = typeof file?.arrayBuffer === 'function';

      if (!file?.fromApi && canReadLocal) {
        const localBuffer = await file.arrayBuffer();
        documentArrayBufferCacheRef.current.set(key, localBuffer);
        return localBuffer;
      }

      if (file?.fromApi) {
        if (docId == null) {
          throw new Error('Missing document id for API-backed draft file');
        }
        let hydrated = documentArrayBufferCacheRef.current.get(key);
        if (!hydrated) {
          const numericDocId = normalizeDocumentId(docId);
          const hydrationPromise = numericDocId != null
            ? documentHydrationPromiseRef.current.get(numericDocId)
            : null;
          if (hydrationPromise) {
            try {
              await hydrationPromise;
            } catch {
              // Continue; cached bytes check below is source of truth.
            }
            hydrated = documentArrayBufferCacheRef.current.get(key);
          }
        }
        if (!hydrated && svgPrewarmPromiseRef.current && !isSvgPrewarmActiveRef.current) {
          try {
            await svgPrewarmPromiseRef.current;
          } catch {
            // Keep the original error below if bytes are still unavailable.
          }
          hydrated = documentArrayBufferCacheRef.current.get(key);
        }
        if (!hydrated) {
          throw new Error(`Document ${docId} is not hydrated yet (waiting for ZIP batch prewarm)`);
        }
        return hydrated;
      }

      if (canReadLocal) {
        const localBuffer = await file.arrayBuffer();
        documentArrayBufferCacheRef.current.set(key, localBuffer);
        return localBuffer;
      }

      throw new Error('Unsupported file source for PDF loading (missing local bytes and document ID)');
    })().finally(() => {
      documentArrayBufferPromiseRef.current.delete(key);
    });

    documentArrayBufferPromiseRef.current.set(key, promise);
    return promise;
  }, [getDocumentCacheKey, normalizeDocumentId]);

  useEffect(() => {
    const loadTemplateMeta = async () => {
      const sourceTemplateId = isTemplateMode ? urlTemplateId : null;
      if (!sourceTemplateId) {
        templateReadyRef.current = true;
        return;
      }

      // Ensure autosave uses template_id immediately in edit mode.
      const numericTemplateId = Number(sourceTemplateId);
      setTemplateId(Number.isFinite(numericTemplateId) ? numericTemplateId : sourceTemplateId);

      try {
        const response = await getTemplate(sourceTemplateId);
        const template =
          response?.data?.template ||
          response?.template ||
          response?.data ||
          response;
        if (!template) return;

        if (template.template_id) {
          setTemplateId((prev) => prev || template.template_id);
        }
        const resolvedTemplateName = template.name || template.template_name;
        if (resolvedTemplateName) setTemplateName(resolvedTemplateName);
        if (template.subject) setSubject(template.subject);
        if (template.message) setMessage(template.message);
        if (template.reminder_frequency) setReminderFrequency(template.reminder_frequency);

        const templateDocs = Array.isArray(template.documents)
          ? template.documents
          : Array.isArray(template.template_documents)
            ? template.template_documents
            : Array.isArray(response?.documents)
              ? response.documents
              : [];
        const templateDocIds = Array.isArray(template.document_ids)
          ? template.document_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id))
          : [];
        const derivedDocIds = templateDocs
          .map((doc) => {
            const raw = doc.original_document_id ?? doc.document_id ?? doc.id;
            const n = typeof raw === 'string' ? parseInt(raw, 10) : raw;
            return Number.isFinite(n) ? n : null;
          })
          .filter((id) => id != null);
        const finalDocIds = derivedDocIds.length > 0 ? derivedDocIds : templateDocIds;
        if (finalDocIds.length > 0) {
          const placeholderFiles = finalDocIds.map((id, index) => {
            const doc = templateDocs[index];
            return {
              name: doc?.title || doc?.document_title || `Document ${index + 1}`,
              size: 0,
              fromApi: true,
              documentId: id,
              documentUrl: doc?.document_url || doc?.file_path || null,
            };
          });
          envelopeRowsCommitRef.current(mapFilesAndIdsToEnvelopeRows(placeholderFiles, finalDocIds));
        }

        const recipientsFromDocuments = templateDocs.flatMap((doc) =>
          Array.isArray(doc?.recipients) ? doc.recipients : []
        );
        const templateRecipients = Array.isArray(template.recipients)
          ? template.recipients
          : Array.isArray(template.template_recipients)
            ? template.template_recipients
            : Array.isArray(template.variable_signees)
              ? template.variable_signees
              : recipientsFromDocuments.length > 0
                ? recipientsFromDocuments
              : Array.isArray(response?.recipients)
                ? response.recipients
                : [];
        let mappedRecipients = [];
        if (templateRecipients.length > 0) {
          const recipientsWithAssignedColors = [];
          const normalizedRecipients = templateRecipients.map((r, index) => {
            const apiType = r.type || r.recipient_type;
            const role = apiType === 'signee' ? 'Needs to Sign' : apiType === 'review_recipient' ? 'Needs to Review' : 'Receives a Copy';
            const placeholder = r.signee_placeholder || r.placeholder || '';
            const resolvedEmail = r.email || r.resolved_email || '';
            const resolvedName = r.name || r.resolved_name || '';
            const rawId = r.id ?? r.recipient_id ?? r.envelope_recipient_id ?? r.template_recipient_id ?? null;
            const hasResolvedIdentity = Boolean(resolvedEmail && resolvedName);
            const isVariable = Boolean(placeholder) && !hasResolvedIdentity;
            const localKey = isVariable && placeholder
              ? `placeholder:${placeholder}`
              : resolvedEmail
                ? `email:${resolvedEmail}`
                : rawId != null
                  ? `id:${rawId}`
                  : `index:${index}`;
            const color = pickUniqueRecipientColor(r.signee_color, recipientsWithAssignedColors);
            const rawSignatureOrder = r.signature_order ?? r.signing_order ?? r.signatureOrder;
            const parsedSo =
              rawSignatureOrder != null && rawSignatureOrder !== ''
                ? Number(rawSignatureOrder)
                : NaN;
            const signature_order = Number.isFinite(parsedSo) ? parsedSo : undefined;
            const recipientRow = {
              id: rawId,
              name: resolvedName || placeholder || '',
              email: resolvedEmail,
              role,
              color,
              isVariable,
              placeholder,
              localKey,
              signature_order,
            };
            recipientsWithAssignedColors.push(recipientRow);
            return recipientRow;
          });

          const dedupedRecipients = [];
          const seen = new Set();
          normalizedRecipients.forEach((r) => {
            const key = String(r.localKey || '');
            if (!key || !seen.has(key)) {
              if (key) seen.add(key);
              dedupedRecipients.push(r);
            }
          });

          mappedRecipients = dedupedRecipients;
          mappedRecipients.forEach((r) => {
            if (r.email && r.id != null) {
              recipientIdMapRef.current[r.email] = r.id;
            }
          });
          const workflowTypeFromRecipients = templateRecipients.find((r) => r?.workflow_type)?.workflow_type;
          const isSequentialWorkflow = workflowTypeFromRecipients === 'sequential';
          const initialSeqInputs = mappedRecipients.map((row, idx) =>
            row.signature_order != null ? row.signature_order : idx + 1
          );
          const orderedRecipients = isSequentialWorkflow
            ? sortRecipientsBySigningOrder(mappedRecipients, initialSeqInputs)
            : { recipients: mappedRecipients, sequenceInputs: initialSeqInputs };
          setRecipients(orderedRecipients.recipients);
          setSequenceInputs(orderedRecipients.sequenceInputs);
          if (isSequentialWorkflow) {
            setSigningOrder(true);
          } else if (workflowTypeFromRecipients === 'parallel') {
            setSigningOrder(false);
          }
        }

        const fieldsFromDocuments = templateDocs.flatMap((doc) =>
          (Array.isArray(doc?.fields) ? doc.fields : []).map((f) => ({
            ...f,
            document_id: f?.document_id ?? doc?.document_id,
          }))
        );
        const templateFields = Array.isArray(template.fields)
          ? template.fields
          : Array.isArray(template.document_fields)
            ? template.document_fields
            : fieldsFromDocuments.length > 0
              ? fieldsFromDocuments
            : Array.isArray(response?.fields)
              ? response.fields
              : [];
        if (templateFields.length > 0) {
          const mappedFields = templateFields.map((field, index) => {
            const recipientId = field.recipient_id || field.envelope_recipient_id || field.recipient?.id;
            let recipientEmail = field.recipient_email || field.recipient?.email || '';
            const recipientPlaceholder = field.signee_placeholder || field.recipient?.signee_placeholder || field.recipient?.placeholder || '';
            const fieldNameTokens = String(field.field_name || '')
              .split(' - ')
              .map((part) => String(part).trim())
              .filter(Boolean);
            const tokenLooksLikeRecipient = (token) => (
              mappedRecipients.findIndex((r, ridx) => (
                (r.name && String(r.name).trim() === token) ||
                ((r.placeholder || getVariableSigneePlaceholder(r, ridx)) === token)
              )) >= 0
            );
            const ownerToken = (() => {
              const firstToken = fieldNameTokens[0] || '';
              const secondToken = fieldNameTokens[1] || '';
              // Backward compatible:
              // - old labels: "Recipient - Field type" => first token
              // - new labels: "Field type - Recipient" => second token
              if (secondToken && tokenLooksLikeRecipient(secondToken)) return secondToken;
              if (firstToken && tokenLooksLikeRecipient(firstToken)) return firstToken;
              return firstToken || secondToken || '';
            })();
            if (!recipientEmail && recipientId && templateRecipients.length > 0) {
              const foundRecipient = templateRecipients.find((r) => String(r.id || '') === String(recipientId));
              recipientEmail = foundRecipient?.email || '';
            }
            let rIndex = mappedRecipients.findIndex((r) =>
              (recipientEmail && r.email === recipientEmail) ||
              (recipientId && String(r.id || '') === String(recipientId)) ||
              (recipientPlaceholder && (r.placeholder || '') === recipientPlaceholder)
            );
            const tokenRecipientIndex = ownerToken
              ? mappedRecipients.findIndex((r, ridx) =>
                (r.name && String(r.name).trim() === ownerToken) ||
                ((r.placeholder || getVariableSigneePlaceholder(r, ridx)) === ownerToken)
              )
              : -1;
            // Some payloads have mismatched recipient_id but correct field_name owner token.
            if (tokenRecipientIndex >= 0 && tokenRecipientIndex !== rIndex) {
              rIndex = tokenRecipientIndex;
            } else if (rIndex < 0 && ownerToken) {
              rIndex = tokenRecipientIndex;
            }
            const matchedRecipient = rIndex >= 0 ? mappedRecipients[rIndex] : null;
            const recipientColor =
              field.recipient?.signee_color ||
              field.signee_color ||
              matchedRecipient?.color ||
                  pickUniqueRecipientColor(null, mappedRecipients);
            const recipientName =
              field.recipient?.name ||
              matchedRecipient?.name ||
              ownerToken ||
              recipientPlaceholder ||
              '';
            const localRecipientKey = matchedRecipient?.localKey || (
              recipientPlaceholder && (matchedRecipient?.isVariable || !recipientEmail)
                ? `placeholder:${recipientPlaceholder}`
                : ownerToken
                  ? (() => {
                    const byToken = mappedRecipients.find((r, ridx) =>
                      (r.name && String(r.name).trim() === ownerToken) ||
                      ((r.placeholder || getVariableSigneePlaceholder(r, ridx)) === ownerToken)
                    );
                    return byToken ? getRecipientSelectionKey(byToken) : '';
                  })()
                : recipientEmail
                  ? `email:${recipientEmail}`
                  : recipientId != null
                    ? `id:${recipientId}`
                    : ''
            );
            return {
              id: `template-field-${field.id || index}`,
              apiFieldId: field.id,
              documentId: field.document_id || null,
              apiDocumentId: field.document_id || null,
              documentIndex: finalDocIds.findIndex((id) => String(id) === String(field.document_id)),
              apiPageNumber: field.page_number,
              documentPageNumber: field.page_number,
              type: field.field_type,
              label: field.field_name || field.field_type,
              dataLabel: field.field_name || field.field_type,
              recipientEmail,
              recipientId: localRecipientKey,
              recipientPlaceholder,
              recipientColor,
              recipientName,
              x: field.x_position,
              y: field.y_position,
              width: field.width,
              height: field.height,
              page: field.page_number,
              required: field.is_required !== false,
              readOnly: field.read_only || false,
              value: mapApiFieldValueToLocal(field),
              valueHtml:
                (field.read_only || isInlineTextEditFieldType(field.field_type))
                && hasHtmlMarkup(field.field_value)
                  ? unwrapReadOnlyFieldValueWrapper(field.field_value)
                  : '',
              ...getFieldTypographyFromApiValue(field),
              ...(fieldTypeAllowsSingleOptionOnly(field.field_type)
                ? { options: parseChoiceFieldOptionsFromApi(field) }
                : {}),
            };
          });
          setDocumentFields(
            mappedFields
              .map(normalizeFieldOptionsForType)
              .map((f) =>
                (getReadOnlyFieldValueHtml(f)
                  ? fitReadOnlyFieldToContent(f)
                  : growReadOnlyFieldToFitContent(f))
              )
          );
        }

      } catch (error) {
        console.error('Error loading template metadata:', error);
      } finally {
        templateReadyRef.current = true;
      }
    };

    loadTemplateMeta();
  // Intentionally keyed only by template mode/id; helper callbacks are stable inputs for this load path.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTemplateMode, urlTemplateId]);

  // Shared zoom scale for both PDF layer and field overlay (single source of truth).
  const zoomRenderScale = useMemo(() => {
    // Recompute fit-to-width scale on window resize.
    const _resizeTick = windowWidth;
    void _resizeTick;
    if (currentStep !== 2) return 1;
    if (zoomLevel === 'fitToWidth') {
      const viewerContainer = svgContainerRef.current?.closest('.document-viewer');
      if (viewerContainer && svgMaxWidth > 0) {
        const containerWidth = viewerContainer.clientWidth;
        const scale = containerWidth / svgMaxWidth;
        return Number.isFinite(scale) && scale > 0 ? scale : 1;
      }
      return 1;
    }
    const scale = parseInt(zoomLevel, 10) / 100;
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
  }, [currentStep, zoomLevel, windowWidth, svgMaxWidth]);

  // Helper used by drag/resize/selection math; returns the same shared scale used by render.
  const getCurrentScale = useCallback(() => zoomRenderScale, [zoomRenderScale]);

  // Change signing order group (multiple recipients may share the same order).
  const handleSequenceChange = (currentIndex, value) => {
    if (!value) return;
    const targetOrder = parseInt(value, 10);
    if (Number.isNaN(targetOrder) || targetOrder < 1) return;

    const clampedOrder = Math.max(1, Math.min(recipients.length, targetOrder));
    const currentOrder = resolveRecipientSigningOrder(
      recipients[currentIndex],
      currentIndex,
      sequenceInputs
    );
    if (clampedOrder === currentOrder) {
      const { recipients: sorted, sequenceInputs: sortedSeq } = sortRecipientsBySigningOrder(
        recipients,
        sequenceInputs
      );
      setRecipients(sorted);
      setSequenceInputs(sortedSeq);
      return;
    }

    const { recipients: sorted, sequenceInputs: sortedSeq } = sortRecipientsBySigningOrder(
      recipients,
      sequenceInputs,
      { [currentIndex]: clampedOrder }
    );
    setRecipients(sorted);
    setSequenceInputs(sortedSeq);
    forceRecipientSaveOnNextEffectRef.current = true;
  };

  /** Revert empty signing order on blur; otherwise apply the new order. */
  const handleSequenceBlur = (currentIndex, value) => {
    const trimmed = String(value ?? '').trim();
    if (trimmed === '') {
      setSequenceInputs((prev) => {
        const next = [...prev];
        next[currentIndex] = resolveRecipientSigningOrder(
          recipients[currentIndex],
          currentIndex,
          prev
        );
        return next;
      });
      return;
    }
    handleSequenceChange(currentIndex, trimmed);
  };

  const uploadOptions = [
    { label: 'Desktop', icon: 'ri-computer-line' },
    { label: 'Use a template', icon: 'ri-file-list-3-line' },
    { label: 'Box', icon: 'ri-box-line' },
    { label: 'Dropbox', icon: 'ri-dropbox-line' },
    { label: 'Google Drive', icon: 'ri-google-drive-line' },
    { label: 'OneDrive', icon: 'ri-cloud-line' },
  ];

  const signingRoles = [
    { label: 'Needs to Sign', icon: 'ri-pen-nib-line' },
    { label: 'Needs to Review', icon: 'ri-eye-line' },
    { label: 'Receives a Copy', icon: 'ri-mail-line' },
  ];

  // Get color for recipient
  const recipientColorPoolRef = useRef(buildInitialRecipientColorPool(RECIPIENT_COLORS));
  const getRecipientColor = useCallback((index) => {
    const pool = recipientColorPoolRef.current;
    while (pool.length <= index) {
      pool.push(generateDistinctRecipientColor(pool));
    }
    return pool[index];
  }, []);

  // Only "Needs to Sign" recipients can have fields assigned; exclude "Needs to Review" and "Receives a Copy"
  const signeeRecipients = React.useMemo(
    () => recipients.filter(r => r.role === 'Needs to Sign'),
    [recipients]
  );

  // Convert hex color to rgba
  const hexToRgba = (hex, alpha = 1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Handle file selection
  const handleFileSelect = async (files) => {
    const fileArray = Array.from(files).map((f, idx) => ensureClientDocumentId(ensureLocalUploadKey(f, idx), idx));
    const pdfFiles = fileArray.filter((file) => {
      const extension = file.name.split('.').pop().toLowerCase();
      return extension === 'pdf';
    });

    if (pdfFiles.length !== fileArray.length) {
      setUploadError('Please upload only PDF files.');
      return;
    }

    if (replaceFileIndex == null) {
      pdfFiles.forEach(registerLocalDocumentUploadPending);
    }

    setUploadError('');

    const rowsNow = envelopeDocumentRowsRef.current || [];
    const filesNow = rowsNow.map((r) => r.file);

    const candidateFiles = replaceFileIndex !== null
      ? (() => {
          const next = [...filesNow];
          if (next[replaceFileIndex]) {
            next[replaceFileIndex] = ensureLocalUploadKey(pdfFiles[0], replaceFileIndex);
          }
          return next;
        })()
      : [...filesNow, ...pdfFiles];

    const limitsError = validateEnvelopeLimits({ files: candidateFiles, recipientsList: recipients, requirePageCounts: false });
    if (limitsError) {
      setUploadError(limitsError);
      return;
    }
    
    // If replacing a file, replace at specific index
    if (replaceFileIndex !== null) {
      const idx = replaceFileIndex;
      const prevRow = rowsNow[idx];
      const prevFile = prevRow?.file ?? filesNow[idx];
      if (!prevFile) {
        setReplaceFileIndex(null);
        return;
      }
      const oldDocIdRaw =
        prevRow?.serverDocumentId
        ?? documentIds[idx]
        ?? prevFile?.documentId
        ?? prevFile?.document_id
        ?? null;
      const oldDocId =
        oldDocIdRaw != null && oldDocIdRaw !== ''
          ? Number(oldDocIdRaw)
          : null;
      const oldDocIdValid = oldDocId != null && Number.isFinite(oldDocId);

      if (documentSaveTimeoutRef.current) {
        clearTimeout(documentSaveTimeoutRef.current);
        documentSaveTimeoutRef.current = null;
      }

      // Enforce strict replace sequence:
      // 1) delete old server document (if it exists)
      // 2) only then attach a brand-new row (new rowId + client id) at the same order slot
      if (oldDocIdValid) {
        const replaceRowKey = getDocumentCacheKey(prevFile, idx);
        deletingDocumentKeysInFlightRef.current.add(replaceRowKey);
        if (typeof React.startTransition === 'function') {
          React.startTransition(() =>
            setDeletingDocumentRowKeys((prev) =>
              prev.includes(replaceRowKey) ? prev : [...prev, replaceRowKey]
            )
          );
        } else {
          setDeletingDocumentRowKeys((prev) =>
            prev.includes(replaceRowKey) ? prev : [...prev, replaceRowKey]
          );
        }
        try {
          if (isTemplateMode && templateId) {
            await removeDocumentFromTemplate(templateId, oldDocId);
          }
          await deleteDocument(oldDocId);
          console.log('✅ Replaced document: removed previous from server, docId:', oldDocId);
        } catch (error) {
          console.error('❌ Failed to delete replaced document from server:', error);
          setUploadError(
            error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Could not remove the previous document from the server. Replacement was not uploaded.'
          );
          setReplaceFileIndex(null);
          return;
        } finally {
          deletingDocumentKeysInFlightRef.current.delete(replaceRowKey);
          setDeletingDocumentRowKeys((prev) => prev.filter((k) => k !== replaceRowKey));
        }
      }

      if (prevFile instanceof File && !prevFile?.fromApi) {
        const oldCid = String(getFileClientDocumentId(prevFile) || '').trim();
        if (oldCid) removeDocumentUploadRegistryEntry(oldCid);
      }

      setUploadProgressEntries([{ key: getDocumentCacheKey(prevFile, idx) }], null);

      clearDocumentBufferCacheForFile(prevFile, idx);
      clearDocumentSvgCacheForFile(prevFile, idx);

      const picked = pdfFiles[0];
      const buf = await picked.arrayBuffer();
      const freshFile = new File([buf], picked.name, {
        type: picked.type || 'application/pdf',
        lastModified: Date.now(),
      });
      const tagged = ensureClientDocumentId(ensureLocalUploadKey(freshFile, idx), idx);
      clearBackendDocumentIdStamp(tagged);
      registerLocalDocumentUploadPending(tagged);

      const newRow = createEnvelopeDocumentRow(tagged, null);
      const baseRows = envelopeDocumentRowsRef.current || [];
      const nextRows = baseRows.map((r, i) => (i === idx ? newRow : r));
      envelopeRowsCommitRef.current(nextRows);

      const replacementKey = getDocumentCacheKey(tagged, idx);
      setUploadProgressEntries(
        [{ key: replacementKey, name: tagged?.name || `Document ${idx + 1}` }],
        (item) => ({ percent: 0, status: 'pending', fileName: item.name })
      );
      const filesAfterCommit = envelopeDocumentRowsRef.current.map((r) => r.file);
      prewarmDocumentSvgs(filesAfterCommit);

      let startPage = 1;
      for (let i = 0; i < idx; i += 1) {
        startPage += documentPages[i] || 1;
      }
      const pagesInDoc = documentPages[idx] || 1;
      const endPage = startPage + pagesInDoc - 1;
      setDocumentFields((prev) =>
        prev.filter((f) => {
          const p = f.page || 1;
          return p < startPage || p > endPage;
        })
      );

      setMergedSvg('');
      setDocumentEditorSvgSegments([]);
      setSvgPageHeights([]);
      setDocumentPageSvgs({});
      setDocumentPages({});
      setPdfDocuments([]);

      setReplaceFileIndex(null);
    } else {
      // Otherwise, add new files
      const startIdx = (envelopeDocumentRowsRef.current || []).length;
      const tagged = pdfFiles.map((f, i) => ensureClientDocumentId(ensureLocalUploadKey(f, startIdx + i), startIdx + i));
      tagged.forEach(registerLocalDocumentUploadPending);
      const appended = tagged.map((f) => createEnvelopeDocumentRow(f, null));
      const nextRows = [...(envelopeDocumentRowsRef.current || []), ...appended];
      envelopeRowsCommitRef.current(nextRows);
      setUploadProgressEntries(
        tagged.map((file, i) => ({
          key: getDocumentCacheKey(file, startIdx + i),
          name: file?.name || `Document ${startIdx + i + 1}`,
        })),
        (item) => ({ percent: 0, status: 'pending', fileName: item.name })
      );
      const filesForPrewarm = envelopeDocumentRowsRef.current.map((r) => r.file);
      prewarmDocumentSvgs(filesForPrewarm);
      
      // If this is the first file and no draft exists, create draft immediately
      if (startIdx === 0 && !draftEnvelopeId && nextRows.length > 0) {
        // Trigger draft save after a short delay to ensure state is updated
        setTimeout(() => {
          saveDraft(true); // Skip debounce for initial save
        }, 500);
      }
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      void handleFileSelect(e.target.files);
      // Reset input so same file can be selected again
      e.target.value = '';
    }
  };

  // Map UI role to backend recipient type
  const roleToRecipientType = (role) => {
    if (role === 'Needs to Sign') return 'signee';
    if (role === 'Needs to Review') return 'review_recipient';
    if (role === 'Receives a Copy') return 'copy_recipient';
    return 'copy_recipient';
  };

  const hasHtmlMarkup = (value) => /<[^>]+>/.test(String(value || ''));
  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const getFieldValueForApi = useCallback((field) => {
    if (!field) return '';
    const typographyValue = buildReadOnlyFieldValueForApi(field);
    if (typographyValue !== null) return typographyValue;
    const isReadOnly = Boolean(field.readOnly ?? field.read_only);
    if (isReadOnly && fieldTypeAllowsSingleOptionOnly(field.type ?? field.field_type)) {
      return READONLY_CHOICE_FIELD_SELECTED_VALUE;
    }
    if (isReadOnly && field.valueHtml) return field.valueHtml;
    const fieldType = field.type ?? field.field_type;
    if (field.value != null && field.value !== '') {
      if (isDateFieldType(fieldType)) return normalizeToDateDDMMYYYY(field.value) || String(field.value);
      return String(field.value);
    }
    // Envelope rows from convertFieldsToEnvelopeFormat only carry `field_value` / `read_only` shape;
    // toSnapshot & bulk update reuse that object — read plain `field_value` here.
    if (field.field_value != null && field.field_value !== '') {
      if (isDateFieldType(fieldType)) return normalizeToDateDDMMYYYY(field.field_value) || field.field_value;
      return field.field_value;
    }
    return '';
  }, []);

  const getVariableSigneePlaceholder = useCallback((recipient, index) => {
    const raw = recipient?.placeholder || recipient?.signee_placeholder || recipient?.name || `signee_${index + 1}`;
    return String(raw).trim().replace(/\s+/g, '_');
  }, []);

  const isRecipientValidForCurrentMode = useCallback((recipient, index) => {
    if (!recipient) return false;
    const nameOk = Boolean(String(recipient.name || '').trim());
    const type = roleToRecipientType(recipient.role);
    if (isTemplateMode && type === 'signee' && recipient.isVariable) {
      return Boolean(getVariableSigneePlaceholder(recipient, index));
    }
    const emailTrim = String(recipient.email || '').trim();
    const emailOk = emailTrim.length > 0 && isValidEmailAddress(emailTrim);
    return nameOk && emailOk;
  }, [isTemplateMode, getVariableSigneePlaceholder]);

  const validateEnvelopeLimits = useCallback((options = {}) => {
    const files = Array.isArray(options.files) ? options.files : uploadedFiles;
    const recipientsList = Array.isArray(options.recipientsList) ? options.recipientsList : recipients;
    const pagesByIndex = options.documentPagesMap || documentPages;
    const requirePageCounts = options.requirePageCounts === true;

    if (files.length > MAX_DOCUMENTS_PER_ENVELOPE) {
      return `Envelope can include at most ${MAX_DOCUMENTS_PER_ENVELOPE} documents. You can upload 0 more document(s).`;
    }

    if (recipientsList.length > MAX_RECIPIENTS_PER_ENVELOPE) {
      return `Envelope can include at most ${MAX_RECIPIENTS_PER_ENVELOPE} recipients. You can add 0 more recipient(s).`;
    }

    let totalKnownBytes = 0;
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const size = Number(file?.size) || 0;
      if (size >= MAX_DOCUMENT_BYTES) {
        return `Document "${file?.name || i + 1}" exceeds the max file size (${formatMegabytes(MAX_DOCUMENT_BYTES)} limit).`;
      }
      if (size > 0) totalKnownBytes += size;
    }
    if (totalKnownBytes >= MAX_ENVELOPE_TOTAL_BYTES) {
      const overflowBytes = Math.max(0, totalKnownBytes - MAX_ENVELOPE_TOTAL_BYTES);
      return `Total envelope size exceeds limit (${formatMegabytes(MAX_ENVELOPE_TOTAL_BYTES)}). Remaining: ${formatMegabytes(0)}. Please remove at least ${formatMegabytes(overflowBytes)}.`;
    }

    let totalPages = 0;
    for (let i = 0; i < files.length; i += 1) {
      const pagesRaw = pagesByIndex?.[i];
      const pageCount = Number(pagesRaw);
      if (!Number.isFinite(pageCount) || pageCount <= 0) {
        if (requirePageCounts) {
          return 'Document page counts are still being calculated. Please wait until processing completes.';
        }
        continue;
      }
      if (pageCount >= MAX_PAGES_PER_DOCUMENT) {
        return `Document "${files[i]?.name || i + 1}" exceeds max pages (${MAX_PAGES_PER_DOCUMENT - 1} pages).`;
      }
      totalPages += pageCount;
      if (totalPages >= MAX_TOTAL_PAGES_PER_ENVELOPE) {
        return `Total pages exceed envelope limit (${MAX_TOTAL_PAGES_PER_ENVELOPE - 1} pages).`;
      }
    }

    return '';
  }, [uploadedFiles, recipients, documentPages]);

  const getRecipientSelectionKey = useCallback((recipient, index = -1) => {
    if (!recipient) return '';
    if (recipient.localKey) return String(recipient.localKey);
    const safeIndex = index >= 0 ? index : recipients.findIndex((r) => r === recipient);
    const map = recipientIdMapRef.current || {};
    const apiId = resolveEnvelopeRecipientId(recipient, map);
    if (apiId != null) return toRecipientIdSelectionKey(apiId);
    const placeholder = getVariableSigneePlaceholder(recipient, safeIndex >= 0 ? safeIndex : 0);
    if (recipient.isVariable && placeholder) return `placeholder:${placeholder}`;
    if (recipient.email) return `email:${recipient.email}`;
    return `index:${safeIndex >= 0 ? safeIndex : 0}`;
  }, [recipients, getVariableSigneePlaceholder]);

  const findRecipientBySelectionKey = useCallback((key) => {
    if (!key) return null;
    const [kind, ...rest] = String(key).split(':');
    const value = rest.join(':');
    if (kind === 'email') {
      return recipients.find((r) => String(r.email || '') === value) || null;
    }
    if (kind === 'placeholder') {
      return recipients.find((r, idx) => {
        const computed = getVariableSigneePlaceholder(r, idx);
        return String(r.placeholder || computed) === value;
      }) || null;
    }
    if (kind === 'id') {
      return recipients.find((r) => String(r.id ?? '') === value) || null;
    }
    if (kind === 'index') {
      const idx = Number(value);
      return Number.isInteger(idx) && idx >= 0 ? (recipients[idx] || null) : null;
    }
    // Backward compatibility: older fields stored raw email in recipientId.
    return recipients.find((r) => String(r.email || '') === String(key)) || null;
  }, [recipients, getVariableSigneePlaceholder]);

  const canonicalizeFieldRecipientKey = useCallback((field) => {
    if (!field) return '';
    let rawKey = '';
    if (field.recipientId != null && String(field.recipientId).trim() !== '') {
      const rid = String(field.recipientId);
      if (rid.includes(':')) rawKey = rid;
      else if (rid.includes('@')) rawKey = `email:${rid}`;
      else rawKey = `id:${rid}`;
    } else if (field.recipientEmail) {
      rawKey = `email:${String(field.recipientEmail).trim()}`;
    } else {
      return '';
    }
    const recipient = findRecipientBySelectionKey(rawKey);
    if (!recipient) return rawKey;
    const idx = recipients.findIndex((r) => r === recipient);
    return getRecipientSelectionKey(recipient, idx >= 0 ? idx : 0);
  }, [findRecipientBySelectionKey, getRecipientSelectionKey, recipients]);

  /** Numeric envelope recipient_id for API payloads and field mapping. */
  const resolveFieldApiRecipientId = useCallback(
    (field) => {
      if (!field) return null;
      const map = recipientIdMapRef.current || {};

      if (field.apiRecipientId != null && field.apiRecipientId !== '') {
        return field.apiRecipientId;
      }

      const fromKey = parseRecipientIdFromSelectionKey(field.recipientId);
      if (fromKey != null) return fromKey;

      const linked = field.recipientId
        ? findRecipientBySelectionKey(field.recipientId)
        : null;
      if (linked?.id != null) return linked.id;

      const email = String(field.recipientEmail || '').trim();
      if (email && map[email] != null) return map[email];

      return null;
    },
    [findRecipientBySelectionKey]
  );

  const applyRecipientIdMappingToFields = useCallback(
    (fieldsList) =>
      fieldsList.map((field) => {
        const apiId = resolveFieldApiRecipientId(field);
        if (apiId == null) return field;
        const idKey = toRecipientIdSelectionKey(apiId);
        if (
          String(field.apiRecipientId ?? '') === String(apiId) &&
          String(field.recipientId ?? '') === idKey
        ) {
          return field;
        }
        return {
          ...field,
          apiRecipientId: apiId,
          recipientId: idKey,
        };
      }),
    [resolveFieldApiRecipientId]
  );

  // Convert recipients to API format for envelope-create
  // Envelope API format: {email, name, type, signature_order, signee_color}
  // type: 'signee' | 'review_recipient' | 'copy_recipient'
  const convertRecipientsToEnvelopeFormat = useCallback(() => {
    if (isOnlySigner) return [];
    
    return recipients
      .map((recipient, origIdx) => ({ recipient, origIdx }))
      .filter(({ recipient, origIdx }) => isRecipientValidForCurrentMode(recipient, origIdx))
      .map(({ recipient, origIdx }) => {
        const recipientType = roleToRecipientType(recipient.role);
        const isVariableSignee = isTemplateMode && recipientType === 'signee' && recipient.isVariable;
        const placeholder = getVariableSigneePlaceholder(recipient, origIdx);
        const recipientData = {
          id: recipient.id || recipientIdMapRef.current[recipient.email] || undefined,
          email: isVariableSignee ? undefined : recipient.email,
          name: isVariableSignee ? placeholder : recipient.name,
          type: recipientType,
          ...buildRecipientSigningPayloadFields(recipient, origIdx, {
            signingOrder,
            sequenceInputs,
            getRecipientColor,
          }),
        };
        
        if (recipientType === 'signee' && isVariableSignee) {
            recipientData.signee_placeholder = placeholder;
        }
        
        return recipientData;
      });
  }, [recipients, isOnlySigner, signingOrder, sequenceInputs, getRecipientColor, isTemplateMode, getVariableSigneePlaceholder, isRecipientValidForCurrentMode]);

  // Convert recipients to API format for document-recipient-bulk-create (for separate recipient management)
  // recipient_type: 'signee' | 'review_recipient' | 'copy_recipient'
  // eslint-disable-next-line no-unused-vars
  const convertRecipientsToAPIFormat = useCallback(() => {
    if (isOnlySigner) return [];
    
    let signeeIndex = 0; // Track signee index for placeholder (signees only)
    
    return recipients
      .map((recipient, origIdx) => ({ recipient, origIdx }))
      .filter(({ recipient, origIdx }) => isRecipientValidForCurrentMode(recipient, origIdx))
      .map(({ recipient, origIdx }) => {
        const recipientType = roleToRecipientType(recipient.role);
        const isVariableSignee = isTemplateMode && recipientType === 'signee' && recipient.isVariable;
        const placeholder = getVariableSigneePlaceholder(recipient, origIdx);
        const recipientData = {
          recipient_type: recipientType,
          email: isVariableSignee ? undefined : recipient.email,
          name: isVariableSignee ? undefined : recipient.name,
          ...buildRecipientSigningPayloadFields(recipient, origIdx, {
            signingOrder,
            sequenceInputs,
            getRecipientColor,
          }),
          workflow_type: signingOrder ? 'sequential' : 'parallel',
        };
        
        if (recipientType === 'signee') {
          signeeIndex++;
          recipientData.signee_placeholder = isVariableSignee ? placeholder : `signee_${signeeIndex}`;
        }
        
        return recipientData;
      });
  }, [recipients, isOnlySigner, signingOrder, sequenceInputs, getRecipientColor, isTemplateMode, getVariableSigneePlaceholder, isRecipientValidForCurrentMode]);

  const getDocumentIndexForId = useCallback((documentId) => {
    const normalizedDocId = String(documentId ?? '').trim();
    if (!normalizedDocId) return -1;

    const ids =
      documentIdsRef.current && documentIdsRef.current.length > 0
        ? documentIdsRef.current
        : documentIds;
    const byDocumentIds = (ids || []).findIndex((id) => String(id ?? '').trim() === normalizedDocId);
    if (byDocumentIds >= 0) return byDocumentIds;

    return (uploadedFilesRef.current || []).findIndex((file) => {
      const fileDocId = file?.documentId ?? file?.document_id ?? file?.original_document_id ?? null;
      return String(fileDocId ?? '').trim() === normalizedDocId;
    });
  }, [documentIds]);

  const getDocumentIdForIndex = useCallback((docIndex) => {
    const index = Number(docIndex);
    if (!Number.isFinite(index) || index < 0) return null;
    const ids =
      documentIdsRef.current && documentIdsRef.current.length > 0
        ? documentIdsRef.current
        : documentIds;
    const fromIds = ids?.[index];
    if (fromIds !== undefined && fromIds !== null && fromIds !== '') return fromIds;
    const file = uploadedFilesRef.current?.[index];
    return file?.documentId ?? file?.document_id ?? file?.original_document_id ?? null;
  }, [documentIds]);

  const getFieldDocumentId = useCallback((field) => {
    return field?.documentId ?? field?.apiDocumentId ?? field?.document_id ?? null;
  }, []);

  const getDocumentPageCountByIndex = useCallback((docIndex) => {
    const fromState = Number(documentPages?.[docIndex]);
    const stateCount = Number.isFinite(fromState) && fromState > 0 ? Math.floor(fromState) : 0;
    const fromSvgLayout = (svgPageHeightsRef.current || [])
      .filter((page) => Number(page?.documentIndex) === Number(docIndex))
      .length;
    // Use the larger count: documentPages can lag at 1 before SVG hydrate finishes,
    // while svgPageHeights already reflects the real page count used for placement.
    const best = Math.max(stateCount, fromSvgLayout);
    return best > 0 ? best : 1;
  }, [documentPages]);

  const getPageInfoForDocumentPage = useCallback((documentId, documentPageNumber, heightsArg = null) => {
    const heights = Array.isArray(heightsArg) ? heightsArg : (svgPageHeightsRef.current || []);
    const docIndex = getDocumentIndexForId(documentId);
    const requestedPage = Number(documentPageNumber);
    const safeRequestedPage = Number.isFinite(requestedPage) && requestedPage > 0
      ? Math.floor(requestedPage)
      : 1;

    if (docIndex < 0) return null;

    const docPages = heights.filter((page) => Number(page?.documentIndex) === Number(docIndex));
    if (docPages.length > 0) {
      return docPages.find((page) => Number(page?.pageNumber) === safeRequestedPage) || docPages[docPages.length - 1] || null;
    }

    const pageCount = getDocumentPageCountByIndex(docIndex);
    const localPage = Math.min(Math.max(safeRequestedPage, 1), Math.max(1, pageCount));
    return {
      documentIndex: docIndex,
      pageNumber: localPage,
      y: 0,
      height: 0,
      offsetX: 0,
      width: 0,
    };
  }, [getDocumentIndexForId, getDocumentPageCountByIndex]);

  const getFieldPageInfo = useCallback((field, heightsArg = null) => {
    const heights = Array.isArray(heightsArg) ? heightsArg : (svgPageHeightsRef.current || []);
    const documentId = getFieldDocumentId(field);
    if (documentId !== null && documentId !== undefined && documentId !== '') {
      return getPageInfoForDocumentPage(documentId, field?.page || 1, heights);
    }
    return null;
  }, [getFieldDocumentId, getPageInfoForDocumentPage]);

  const resolvePlacementFromAbsolutePoint = useCallback((docX, docY, heightsArg = null) => {
    const heights = Array.isArray(heightsArg) ? heightsArg : (svgPageHeightsRef.current || []);
    const pageInfo = heights.find((page) => {
      const pageBottom = page.y + page.height;
      return docY >= page.y && docY < pageBottom;
    }) || null;

    const documentId = pageInfo ? getDocumentIdForIndex(pageInfo.documentIndex) : null;
    return {
      documentId,
      documentIndex: pageInfo?.documentIndex ?? 0,
      page: pageInfo?.pageNumber || 1,
      pageInfo,
      x: docX - (pageInfo?.offsetX || 0),
      y: pageInfo ? docY - pageInfo.y : docY,
    };
  }, [getDocumentIdForIndex]);

  const getApiFieldDocumentAndPage = useCallback((field) => {
    const documentId = getFieldDocumentId(field);
    const docIndex = getDocumentIndexForId(documentId);
    const requestedPage = Math.max(1, Number(field?.page) || Number(field?.documentPageNumber) || Number(field?.apiPageNumber) || 1);

    if (docIndex < 0) {
      return {
        documentId,
        pageNumber: requestedPage,
      };
    }

    const heights = svgPageHeightsRef.current || [];
    const pageExistsInLayout = heights.some(
      (page) => Number(page?.documentIndex) === Number(docIndex)
        && Number(page?.pageNumber) === requestedPage
    );
    if (pageExistsInLayout) {
      return { documentId, pageNumber: requestedPage };
    }

    const pageCount = getDocumentPageCountByIndex(docIndex);
    const localPage = Math.min(Math.max(requestedPage, 1), Math.max(1, pageCount));
    return { documentId, pageNumber: localPage };
  }, [getFieldDocumentId, getDocumentIndexForId, getDocumentPageCountByIndex]);

  const getFieldOverlayGeometry = useCallback((field, heightsArg = null) => {
    const pageInfo = getFieldPageInfo(field, heightsArg);
    return {
      pageInfo,
      fieldX: (pageInfo?.offsetX || 0) + (field?.x || 0),
      fieldY: (pageInfo?.y || 0) + (field?.y || 0),
      width: field?.width || 150,
      height: field?.height || 40,
    };
  }, [getFieldPageInfo]);

  const beginFieldResize = useCallback(
    (e, field, handle, fieldWidth, fieldHeight) => {
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
      if (e.type === 'touchstart' || e.pointerType === 'touch') {
        ignoreMouseFromTouchRef.current = true;
      }
      markPointerInteractionActive();
      const latestFields = documentFieldsRef.current || documentFields;
      resizeStartStateRef.current = latestFields;

      const isMultiResize =
        selectedFields.length > 1 && selectedFieldIdsSet.has(field.id);
      const fieldsToResize = isMultiResize ? [...selectedFields] : [field.id];
      resizingFieldsRef.current = fieldsToResize;

      const starts = new Map();
      const pageHeights = svgPageHeightsRef.current;
      fieldsToResize.forEach((fid) => {
        const f = latestFields.find((x) => x.id === fid);
        if (!f) return;
        const geom = getFieldOverlayGeometry(f, pageHeights);
        starts.set(fid, {
          x: f.x,
          y: f.y,
          width: f.width ?? geom.width,
          height: f.height ?? geom.height,
        });
      });
      resizeFieldStartsRef.current = starts;

      const primaryStart = starts.get(field.id) || {
        x: field.x,
        y: field.y,
        width: fieldWidth,
        height: fieldHeight,
      };
      const point = getClientPoint(e);
      resizeStartRef.current = {
        ...primaryStart,
        mouseX: point.clientX,
        mouseY: point.clientY,
      };
      setResizingField(field.id);
      setResizeHandle(handle);
    },
    [
      documentFields,
      selectedFields,
      selectedFieldIdsSet,
      getFieldOverlayGeometry,
      markPointerInteractionActive,
    ]
  );

  const clampFieldXToDocumentPageWidth = useCallback((field, pageInfoArg = null) => {
    const pageInfo = pageInfoArg || getFieldPageInfo(field);
    if (!pageInfo) return field;
    const width = field.width || 150;
    const maxX = Math.max(0, (pageInfo.width || 0) - width);
    return { ...field, x: Math.max(0, Math.min(field.x ?? 0, maxX)) };
  }, [getFieldPageInfo]);

  // Envelope API format: {document_id, recipient_id, field_type, page_number, x_position, y_position, width, height, is_required, read_only}
  const convertFieldsToEnvelopeFormat = useCallback(() => {
    const fieldsSnapshot =
      documentFieldsRef.current?.length > 0
        ? documentFieldsRef.current
        : documentFields;
    if (fieldsSnapshot.length === 0) {
      console.log('⚠️ No document fields to convert');
      return [];
    }
    
    const idsForFields =
      documentIdsRef.current && documentIdsRef.current.length > 0
        ? documentIdsRef.current
        : documentIds;
    if (idsForFields.length === 0) {
      console.warn('⚠️ No document IDs available for field conversion');
      return [];
    }
    
    // Check for recipientEmail or recipientId (for backward compatibility)
    const fieldsWithRecipient = fieldsSnapshot.filter(f => f.recipientEmail || f.recipientId);
    const fieldsWithoutRecipient = fieldsSnapshot.filter(f => !f.recipientEmail && !f.recipientId);
    
    if (fieldsWithoutRecipient.length > 0) {
      console.warn('⚠️ Some fields missing recipient email:', fieldsWithoutRecipient.length);
    }
    
    const convertedFields = fieldsWithRecipient.map((field) => {
      const recipientId = resolveFieldApiRecipientId(field);
      
      const { documentId, pageNumber } = getApiFieldDocumentAndPage(field);
      
      if (!documentId) {
        console.warn('⚠️ Could not determine document ID for field:', field);
        return null;
      }
      
      // Get field ID if available (for updates) — include field.field_id for loaded/API-shaped locals
      const fieldId = field.apiId || field.field_id || fieldIdMapRef.current[field.id];
      
      const convertedField = withChoiceFieldOptionsForApi({
        local_field_id: field.id,
        document_id: documentId,
        recipient_id: recipientId, // May be null if recipient not saved yet - API will resolve
        field_type: field.type,
        field_name: resolveFieldNameForApi(field),
        page_number: pageNumber,
        x_position: field.x,
        y_position: field.y,
        width: field.width,
        height: field.height,
        is_required: field.required !== false,
        read_only: field.readOnly || false,
        field_value: getFieldValueForApi(field),
      }, field, fieldsSnapshot);
      
      // Only include id or field_id if available (for updates)
      if (fieldId) {
        convertedField.id = fieldId;
        convertedField.field_id = fieldId; // Also support field_id alias
      }
      
      return convertedField;
    }).filter(f => f !== null); // Remove any null entries
    
    logFieldSaveDebug('✅ Converted fields to envelope format:', {
      totalFields: fieldsSnapshot.length,
      fieldsWithRecipient: fieldsWithRecipient.length,
      convertedCount: convertedFields.length,
      fieldsWithIds: convertedFields.filter(f => f.id || f.field_id).length,
      convertedFields: convertedFields,
      recipientIdMap: recipientIdMapRef.current,
      fieldIdMap: fieldIdMapRef.current
    });
    
    return convertedFields;
  }, [documentFields, documentIds, getApiFieldDocumentAndPage, getFieldValueForApi, resolveFieldApiRecipientId]);

  /** Full JSON for POST /envelope/{id}/pre-send-sync — no PDF binaries (documents = metadata only). */
  const buildPreSendSyncPayload = useCallback(() => {
    const envelopeId = draftEnvelopeIdRef.current || draftEnvelopeId;
    const files = uploadedFilesRef.current || uploadedFiles;
    const idsRaw = documentIdsRef.current?.length ? documentIdsRef.current : documentIds;
    const ids = Array.isArray(idsRaw) ? idsRaw : [];

    const documents = (files || []).map((file, index) => {
      const docId = ids[index];
      const pageCount = Number(documentPages?.[index]);
      const hasServerId = docId !== undefined && docId !== null && docId !== '' && docId !== 0;
      const title =
        file instanceof File
          ? (file.name || `Document ${index + 1}`)
          : (file?.name || file?.title || file?.file_name || `Document ${index + 1}`);
      const clientId =
        file instanceof File && !file?.fromApi
          ? String(getFileClientDocumentId(file) || '').trim() || undefined
          : String(
              file?.client_document_id
                || file?.client_document_uuid
                || file?.__clientDocumentId
                || ''
            ).trim() || undefined;

      return {
        document_index: index,
        document_order: index + 1,
        order: index + 1,
        document_id: hasServerId ? docId : undefined,
        title,
        page_count: Number.isFinite(pageCount) && pageCount > 0 ? Math.floor(pageCount) : undefined,
        client_document_id: clientId,
        from_api: Boolean(file?.fromApi),
      };
    });

    const recipientsPayload = isOnlySigner
      ? []
      : recipients
          .map((recipient, origIdx) => ({ recipient, origIdx }))
          .filter(({ recipient, origIdx }) => isRecipientValidForCurrentMode(recipient, origIdx))
          .map(({ recipient, origIdx }) => {
            const recipientType = roleToRecipientType(recipient.role);
            const isVariableSignee = isTemplateMode && recipientType === 'signee' && recipient.isVariable;
            const placeholder = getVariableSigneePlaceholder(recipient, origIdx);
            const emailKey = String(recipient.email || '').trim();
            const row = {
              id: recipient.id ?? (emailKey ? recipientIdMapRef.current[emailKey] : null) ?? undefined,
              email: isVariableSignee ? undefined : recipient.email,
              name: isVariableSignee ? undefined : recipient.name,
              role: recipient.role,
              recipient_type: recipientType,
              color: recipient.color || null,
              is_variable: Boolean(recipient.isVariable),
            };
            Object.assign(
              row,
              buildRecipientSigningPayloadFields(recipient, origIdx, {
                signingOrder,
                sequenceInputs,
                getRecipientColor,
              })
            );
            if (recipientType === 'signee' && isVariableSignee) {
              row.signee_placeholder = placeholder;
            }
            row.workflow_type = signingOrder ? 'sequential' : 'parallel';
            return row;
          });

    const fields = convertFieldsToEnvelopeFormat();

    return {
      envelope_id: envelopeId,
      name: templateName || subject || 'Untitled Envelope',
      is_only_signer: isOnlySigner,
      subject: subject || '',
      message: message || '',
      reminder_frequency: reminderFrequency || 'never',
      workflow_type: signingOrder ? 'sequential' : 'parallel',
      document_ids: ids.filter((id) => id !== undefined && id !== null && id !== '' && id !== 0),
      documents,
      recipients: recipientsPayload,
      fields,
    };
  }, [
    draftEnvelopeId,
    uploadedFiles,
    documentIds,
    documentPages,
    isOnlySigner,
    recipients,
    subject,
    message,
    reminderFrequency,
    signingOrder,
    sequenceInputs,
    templateName,
    isTemplateMode,
    isRecipientValidForCurrentMode,
    getVariableSigneePlaceholder,
    convertFieldsToEnvelopeFormat,
    getRecipientColor,
  ]);

  // Convert fields to API format for document-field-bulk-create (for separate field management)
  // eslint-disable-next-line no-unused-vars
  const convertFieldsToAPIFormat = useCallback(() => {
    if (!documentIds.length || documentFields.length === 0) return [];
    
    return documentFields
      .filter(f => f.recipientEmail || f.recipientId)
      .map((field) => {
        const recipient = findRecipientBySelectionKey(field.recipientId)
          || recipients.find((r) => r.email === field.recipientEmail)
          || null;
        const recipientIndex = recipient ? recipients.findIndex((r) => r === recipient) : -1;
        const placeholder = recipient ? getVariableSigneePlaceholder(recipient, recipientIndex >= 0 ? recipientIndex : 0) : '';
        const isVariable = Boolean(recipient?.isVariable && placeholder);

        const { documentId, pageNumber } = getApiFieldDocumentAndPage(field);
        const converted = withChoiceFieldOptionsForApi({
          document_id: documentId,
          recipient_id: isVariable ? (recipientIndex >= 0 ? recipientIndex : undefined) : undefined,
          signee_placeholder: isVariable ? placeholder : undefined,
          recipient_email: isVariable ? undefined : (field.recipientEmail || recipient?.email),
        field_name: resolveFieldNameForApi(field),
        field_type: field.type,
        x_position: field.x,
        y_position: field.y,
        width: field.width,
        height: field.height,
        is_required: field.required !== false,
          read_only: field.readOnly || false,
          page_number: pageNumber,
          field_value: getFieldValueForApi(field),
        }, field, documentFields);

        return converted;
      })
      .filter((f) => !!f.document_id && (!!f.signee_placeholder || !!f.recipient_email || f.recipient_id != null));
  }, [documentFields, documentIds, recipients, findRecipientBySelectionKey, getVariableSigneePlaceholder, getApiFieldDocumentAndPage, getFieldValueForApi]);

  // Save documents to envelope - sends files directly to envelope-create API
  // ONLY this function can CREATE an envelope (when files are uploaded)
  // If envelope_id exists, it's an update (only sends new documents)
  // If no envelope_id, creates new envelope with ALL available data (documents + recipients + fields + message)
  const saveDocuments = useCallback(async () => {
    const isValidDocumentId = (id) =>
      id !== undefined && id !== null && id !== '' && id !== 0;

    /** Row model server id, parallel ref slot, or id stamped on file — do not upload again. */
    const rowHasPersistedBackendDocumentId = (file, rowIndex) => {
      const fromRow = (envelopeDocumentRowsRef.current || [])[rowIndex]?.serverDocumentId ?? null;
      const fromArray = (documentIdsRef.current || [])[rowIndex];
      const fromFile =
        file?.documentId ??
        file?.document_id ??
        file?.original_document_id ??
        null;
      return (
        isValidDocumentId(fromRow)
        || isValidDocumentId(fromArray)
        || isValidDocumentId(fromFile)
      );
    };

    const stampBackendDocumentIdOnFile = (file, id) => {
      if (!file || !isValidDocumentId(id)) return;
      try {
        // eslint-disable-next-line no-param-reassign
        file.documentId = id;
        // eslint-disable-next-line no-param-reassign
        file.document_id = id;
      } catch {
        /* ignore */
      }
    };

    const applySequentialBatchUploadProgress = (batchItems, event) => {
      const totalBytes = batchItems.reduce((sum, item) => sum + Math.max(0, item?.size || 0), 0);
      if (totalBytes <= 0) {
        batchItems.forEach((item) => {
          setUploadProgressEntries(
            [{ key: item.key }],
            { percent: 0, status: 'uploading', fileName: item.name }
          );
        });
        return;
      }
      const eventTotal = Number(event?.total) > 0 ? Number(event.total) : totalBytes;
      const eventLoaded = Math.max(0, Number(event?.loaded) || 0);
      const uploadComplete = eventLoaded >= Math.max(1, eventTotal - 1);
      if (uploadComplete) {
        batchItems.forEach((item) => {
          setUploadProgressEntries(
            [{ key: item.key }],
            { percent: 95, status: 'processing', fileName: item.name }
          );
        });
        return;
      }
      const normalizedUploadedBytes = Math.min(
        totalBytes,
        Math.max(0, (eventLoaded / eventTotal) * totalBytes)
      );
      let remaining = normalizedUploadedBytes;
      batchItems.forEach((item) => {
        const size = Math.max(1, item?.size || 0);
        const uploadedForFile = Math.min(size, Math.max(0, remaining));
        remaining = Math.max(0, remaining - size);
        const rawPercent = Math.max(0, Math.min(100, Math.round((uploadedForFile / size) * 100)));
        const status = rawPercent >= 98 ? 'processing' : 'uploading';
        const percent = rawPercent >= 98 ? 95 : rawPercent;
        setUploadProgressEntries(
          [{ key: item.key }],
          { percent, status, fileName: item.name }
        );
      });
    };

    const PAGE_COUNT_MAX = 1999;
    const PAGE_COUNT_WAIT_TIMEOUT_MS = 45000;
    const PAGE_COUNT_POLL_MS = 120;
    const readExistingPageCount = (file, index) => {
      const key = getDocumentCacheKey(file, index);
      const fromSvgCache = Number(documentSvgCacheRef.current.get(key)?.numPages);
      if (Number.isFinite(fromSvgCache) && fromSvgCache > 0) {
        return Math.floor(fromSvgCache);
      }
      return null;
    };
    const waitForExistingPageCount = async (file, index) => {
      const label = file?.name || `Document ${index + 1}`;
      const startedAt = Date.now();
      while (Date.now() - startedAt < PAGE_COUNT_WAIT_TIMEOUT_MS) {
        const pageCount = readExistingPageCount(file, index);
        if (pageCount != null) {
          if (pageCount > PAGE_COUNT_MAX) {
            throw new Error(`"${label}" has ${pageCount} pages, but maximum allowed is ${PAGE_COUNT_MAX}.`);
          }
          return pageCount;
        }
        await new Promise((resolve) => setTimeout(resolve, PAGE_COUNT_POLL_MS));
      }
      throw new Error(`Page count is not ready for "${label}". Please wait for SVG processing to complete.`);
    };

    /** Drop removed/cancelled rows and fix indices before each upload (list can change while a batch is in flight). */
    const refreshPendingUploadItems = (items) => {
      const filesNow = uploadedFilesRef.current || [];
      return (Array.isArray(items) ? items : [])
        .map((item) => {
          if (!item) return null;
          const cid = String(item?.clientDocumentId || getFileClientDocumentId(item?.file) || '').trim();
          if (cid && pendingDeleteAfterClientDocIdRef.current.has(cid)) return null;
          let idx = -1;
          if (cid) {
            idx = filesNow.findIndex((f) => getFileClientDocumentId(f) === cid);
          }
          if (idx < 0) {
            idx = filesNow.findIndex((f, i) => getDocumentCacheKey(f, i) === item.key);
          }
          if (idx < 0) return null;
          const f = filesNow[idx];
          if (!(f instanceof File) || f?.fromApi) return null;
          if (rowHasPersistedBackendDocumentId(f, idx)) return null;
          ensureClientDocumentId(f, idx);
          return {
            index: idx,
            file: f,
            key: getDocumentCacheKey(f, idx),
            size: f?.size || 0,
            name: f.name || item.name || `Untitled Document ${idx + 1}`,
            clientDocumentId: getFileClientDocumentId(f),
          };
        })
        .filter(Boolean);
    };

    if (isTemplateMode) {
      // Template flow must not call envelope APIs. Only upload files to document API to get document_ids.
      const uploadedIds = new Array(uploadedFiles.length);
      const pendingLocal = [];
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        const fileKey = getDocumentCacheKey(file, i);
        const existingId = documentIds[i];
        if (existingId) {
          setUploadProgressEntries([{ key: fileKey }], null);
          uploadedIds[i] = existingId;
          continue;
        }
        if (file?.fromApi && file?.documentId) {
          setUploadProgressEntries([{ key: fileKey }], null);
          uploadedIds[i] = file.documentId;
          continue;
        }
        if (file instanceof File && !file?.fromApi && rowHasPersistedBackendDocumentId(file, i)) {
          const persisted = documentIds[i] ?? file.documentId ?? file.document_id;
          if (isValidDocumentId(persisted)) {
            setUploadProgressEntries([{ key: fileKey }], null);
            uploadedIds[i] = persisted;
          }
          continue;
        }
        if (file instanceof File && !file?.fromApi) {
          ensureClientDocumentId(file, i);
          registerLocalDocumentUploadPending(file);
          pendingLocal.push({
            index: i,
            file,
            key: fileKey,
            size: file?.size || 0,
            name: file.name || `Document ${i + 1}`,
            clientDocumentId: getFileClientDocumentId(file),
          });
        }
      }

      const hadTemplateUploads = pendingLocal.length > 0;
      if (hadTemplateUploads) {
        documentUploadPipelineActiveRef.current = true;
      }
      try {
      for (let b = 0; b < pendingLocal.length; b += DOCUMENT_UPLOAD_BATCH_SIZE) {
        const batch = pendingLocal.slice(b, b + DOCUMENT_UPLOAD_BATCH_SIZE);
        const waiting = pendingLocal.slice(b + DOCUMENT_UPLOAD_BATCH_SIZE);
        setUploadProgressEntries(
          waiting,
          (item, _idx, previous) => ({
            percent: previous?.status === 'complete' ? 100 : 0,
            status: 'pending',
            fileName: item.name,
          })
        );
        setUploadProgressEntries(batch, (item) => ({ percent: 0, status: 'uploading', fileName: item.name }));
        try {
          for (const item of batch) {
            const [live] = refreshPendingUploadItems([item]);
            const cidEarly = String(item.clientDocumentId || getFileClientDocumentId(item.file) || '').trim();
            if (!live) {
              setUploadProgressEntries([{ key: item.key }], null);
              if (cidEarly) setDocumentUploadRegistryEntry(cidEarly, { status: 'pending', documentId: null });
              continue;
            }
            const pageCount = await waitForExistingPageCount(live.file, live.index);
            const cidLive = String(live.clientDocumentId || getFileClientDocumentId(live.file) || '').trim();
            if (cidLive) setDocumentUploadRegistryEntry(cidLive, { status: 'uploading' });
            const res = await createDocument(live.file, live.name, '', {
              page_count: pageCount,
              order: live.index + 1,
              client_document_id: live.clientDocumentId || getFileClientDocumentId(live.file) || undefined,
              onUploadProgress: (event) => {
                const total = Number(event?.total) > 0 ? Number(event.total) : live.size || 1;
                const loaded = Math.max(0, Number(event?.loaded) || 0);
                const rawPercent = Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
                const status = rawPercent >= 98 ? 'processing' : 'uploading';
                const percent = rawPercent >= 98 ? 95 : rawPercent;
                setUploadProgressEntries(
                  [{ key: live.key }],
                  { percent, status, fileName: live.name }
                );
              },
            });
            const newId = res?.data?.document?.id || res?.data?.id || res?.document?.id || res?.id;
            if (!newId) {
              throw new Error(`Document upload succeeded but no document ID was returned for "${live.name}".`);
            }
            const listNow = uploadedFilesRef.current || [];
            const stillPresent = listNow.some((f, idx) => getDocumentCacheKey(f, idx) === live.key);
            const awaitingDelete = cidLive && pendingDeleteAfterClientDocIdRef.current.has(cidLive);
            if (!stillPresent || awaitingDelete) {
              if (awaitingDelete) pendingDeleteAfterClientDocIdRef.current.delete(cidLive);
              try {
                await deleteDocument(newId);
              } catch (purgeErr) {
                console.error('Failed to purge orphan template document:', purgeErr);
              }
              if (cidLive) removeDocumentUploadRegistryEntry(cidLive);
              if (awaitingDelete && stillPresent) {
                const removeIdx = (uploadedFilesRef.current || []).findIndex(
                  (f, idx) => getDocumentCacheKey(f, idx) === live.key
                );
                if (removeIdx >= 0) {
                  purgeDocumentRowAtIndexRef.current(removeIdx);
                }
                deletingDocumentKeysInFlightRef.current.delete(live.key);
                setDeletingDocumentRowKeys((prev) => prev.filter((k) => k !== live.key));
              }
            } else {
              let ri = resolveUploadRowIndexByClientId(uploadedFilesRef.current || [], live);
              if (ri < 0) {
                ri = (uploadedFilesRef.current || []).findIndex(
                  (f, idx) => getDocumentCacheKey(f, idx) === live.key
                );
              }
              if (ri >= 0) {
                while (uploadedIds.length <= ri) uploadedIds.push(null);
                uploadedIds[ri] = newId;
              }
              stampBackendDocumentIdOnFile(live.file, newId);
              if (cidLive) setDocumentUploadRegistryEntry(cidLive, { status: 'uploaded', documentId: newId });
            }
            setUploadProgressEntries(
              [{ key: live.key }],
              { percent: 100, status: 'complete', fileName: live.name }
            );
          }
        } catch (err) {
          batch.forEach((item) => {
            setUploadProgressEntries(
              [{ key: item.key }],
              { percent: 0, status: 'error', fileName: item.name }
            );
            const cid = String(item.clientDocumentId || getFileClientDocumentId(item.file) || '').trim();
            if (cid) setDocumentUploadRegistryEntry(cid, { status: 'pending', documentId: null });
            if (cid && pendingDeleteAfterClientDocIdRef.current.has(cid)) {
              pendingDeleteAfterClientDocIdRef.current.delete(cid);
              const removeIdx = (uploadedFilesRef.current || []).findIndex(
                (f, idx) => getDocumentCacheKey(f, idx) === item.key
              );
              if (removeIdx >= 0) {
                purgeDocumentRowAtIndexRef.current(removeIdx);
              }
              deletingDocumentKeysInFlightRef.current.delete(item.key);
              setDeletingDocumentRowKeys((prev) => prev.filter((k) => k !== item.key));
            }
          });
          console.error('Error bulk-uploading template documents:', err);
          const message =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            `Failed to upload documents (batch starting "${batch[0]?.name || 'unknown'}").`;
          setUploadError(message);
          throw new Error(message);
        } finally {
          batch.forEach((item) => {
            setTimeout(() => setUploadProgressEntries([{ key: item.key }], null), 800);
          });
        }
      }
      } catch (templateUploadErr) {
        throw templateUploadErr;
      } finally {
        if (hadTemplateUploads) {
          try {
            await flushDeferredDocumentApiTasks();
          } catch (flushErr) {
            console.error('Deferred document API flush failed:', flushErr);
          }
          documentUploadPipelineActiveRef.current = false;
        }
      }

      const rowsSnap = envelopeDocumentRowsRef.current || [];
      const nextIdsDense = rowsSnap.map((_, i) => uploadedIds[i] ?? null);
      if (nextIdsDense.some(isValidDocumentId)) {
        const prevDense = rowsSnap.map((r) => r.serverDocumentId ?? null);
        const sameLength = prevDense.length === nextIdsDense.length;
        const sameValues =
          sameLength &&
          prevDense.every((id, idx) => String(id ?? '') === String(nextIdsDense[idx] ?? ''));
        if (!sameValues) {
          const mergedRows = rowsSnap.map((row, i) => ({
            ...row,
            serverDocumentId: normalizeRowServerDocumentId(nextIdsDense[i]),
          }));
          mergedRows.forEach((row, i) => {
            stampBackendDocumentIdOnFile(row.file, mergedRows[i].serverDocumentId);
          });
          envelopeRowsCommitRef.current(mergedRows);
        }
      }
      return nextIdsDense;
    }

    if (uploadedFiles.length === 0) {
      return [...(documentIdsRef.current || [])];
    }
    if (!orchestratedDraftSyncRef.current && isSavingDraft) {
      console.log('⏸️ Skipping document save:', { isSavingDraft, hasFiles: uploadedFiles.length > 0 });
      return [...(documentIdsRef.current || [])];
    }
    
    // Check lock to prevent concurrent saves
    if (savingLockRef.current) {
      console.log('⏸️ Skipping document save - another save in progress');
      return [...(documentIdsRef.current || [])];
    }
    
    savingLockRef.current = true;
    setIsSavingDraft(true);
    documentUploadPipelineActiveRef.current = true;
    
    try {
      let envelopeIdForSave = draftEnvelopeIdRef.current || draftEnvelopeId || null;
      const workingDocumentIds = [...(documentIdsRef.current || [])];
      if (!envelopeIdForSave) {
        console.log('📮 Creating envelope metadata (without document files)...');
        const envelopeData = {
          name: templateName || subject || 'Untitled Envelope',
          subject: subject || 'Complete with Volksign:',
          message: message || '',
          reminder_frequency: reminderFrequency || 'daily',
          workflow_type: signingOrder ? 'sequential' : 'parallel',
        };
        const envelopeResponse = await createDraftEnvelope(envelopeData);
        const responseData = envelopeResponse?.data || {};
        if (responseData.envelope_id) {
          envelopeIdForSave = responseData.envelope_id;
          draftEnvelopeIdRef.current = envelopeIdForSave;
          setDraftEnvelopeId((prev) => prev || envelopeIdForSave);
        }
        if (responseData.template_id) {
          setTemplateId(responseData.template_id);
        }
      }

      /** Align local id list with ref, then list local File rows still missing an envelope document id. */
      const computePendingEnvelopeDocumentItems = () => {
        const filesSnapshot = uploadedFilesRef.current || [];
        const refIds = documentIdsRef.current || [];
        while (workingDocumentIds.length < filesSnapshot.length) workingDocumentIds.push(null);
        for (let i = 0; i < filesSnapshot.length; i += 1) {
          const rid = refIds[i];
          if (isValidDocumentId(rid)) {
            workingDocumentIds[i] = rid;
          }
        }
        const pendingItems = filesSnapshot
          .map((file, index) => {
            const isLocalUpload = file instanceof File && !file?.fromApi;
            if (!isLocalUpload || rowHasPersistedBackendDocumentId(file, index)) return null;
            ensureClientDocumentId(file, index);
            registerLocalDocumentUploadPending(file);
            const clientDocumentId = String(getFileClientDocumentId(file) || '').trim();
            if (clientDocumentId && pendingDeleteAfterClientDocIdRef.current.has(clientDocumentId)) {
              return null;
            }
            return {
              index,
              file,
              key: getDocumentCacheKey(file, index),
              size: file?.size || 0,
              name: file?.name || `Untitled Document ${index + 1}`,
              clientDocumentId: getFileClientDocumentId(file),
            };
          })
          .filter(Boolean);
        const pendingSignature = pendingItems
          .map((item) => item.key)
          .sort((a, b) => String(a).localeCompare(String(b)))
          .join('|');
        return { pendingItems, pendingSignature };
      };

      const runEnvelopeDocumentUploadBatch = async (uploadItemsIn, batchNumber, pendingTotalForLog) => {
        (uploadItemsIn || []).forEach((p) => {
          if (refreshPendingUploadItems([p]).length === 0) {
            setUploadProgressEntries([{ key: p.key }], null);
          }
        });
        let uploadItems = refreshPendingUploadItems(uploadItemsIn);
        if (uploadItems.length === 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log('⏭️ Skipping envelope document batch — all files removed or cancelled before upload ran', {
              batchNumber,
            });
          }
          return;
        }

          setUploadProgressEntries(
            uploadItems,
            (item) => ({ percent: 0, status: 'uploading', fileName: item.name })
          );

          console.log('📦 Uploading document batch:', {
            envelopeId: envelopeIdForSave,
            batchNumber,
            batchSize: uploadItems.length,
          pendingTotal: pendingTotalForLog,
            fileNames: uploadItems.map((item) => item.name),
          });

        let documentPageCounts = await Promise.all(
          uploadItems.map((item) => waitForExistingPageCount(item.file, item.index))
        );
        const postPageItems = refreshPendingUploadItems(uploadItems);
        uploadItems.forEach((old) => {
          if (!postPageItems.some((n) => n.key === old.key)) {
            setUploadProgressEntries([{ key: old.key }], null);
          }
        });
        if (postPageItems.length === 0) {
          console.log('⏭️ Skipping envelope document batch — files removed during page-count wait', {
            batchNumber,
          });
          return;
        }
        const survivorsChanged =
          postPageItems.length !== uploadItems.length ||
          postPageItems.some(
            (n, i) =>
              !uploadItems[i] || n.key !== uploadItems[i].key || n.index !== uploadItems[i].index
          );
        if (survivorsChanged) {
          documentPageCounts = await Promise.all(
            postPageItems.map((item) => waitForExistingPageCount(item.file, item.index))
          );
        }
        uploadItems = postPageItems;

          const documentFiles = uploadItems.map((item) => item.file);
          const documentTitles = uploadItems.map((item) => item.name);
          const documentOrders = uploadItems.map((item) => item.index + 1);
        const documentClientIds = uploadItems.map(
          (item) => item.clientDocumentId || getFileClientDocumentId(item.file)
          );
          const envelopeData = {
            envelope_id: envelopeIdForSave,
          };
        uploadItems.forEach((item) => {
          const cid = String(item.clientDocumentId || getFileClientDocumentId(item.file) || '').trim();
          if (cid) setDocumentUploadRegistryEntry(cid, { status: 'uploading' });
        });
          if (documentFiles.length === 1) {
            envelopeData.document_file = documentFiles[0];
            envelopeData.document_title = documentTitles[0];
            envelopeData.document_order = documentOrders[0];
            envelopeData.document_page_count = documentPageCounts[0];
          if (documentClientIds[0]) {
            envelopeData.document_client_id = documentClientIds[0];
          }
          } else {
            envelopeData.document_files = documentFiles;
            envelopeData.document_titles = documentTitles;
            envelopeData.document_orders = documentOrders;
            envelopeData.document_page_counts = documentPageCounts;
          if (documentClientIds.some((cid) => cid)) {
            envelopeData.document_client_ids = documentClientIds;
          }
          }

          try {
            const envelopePayload = await createDraftEnvelope(envelopeData, {
              onUploadProgress: (event) => applySequentialBatchUploadProgress(uploadItems, event),
            });
          const filesSnapshotNow = uploadedFilesRef.current || [];
            mergeWorkingDocumentIdsFromEnvelopeResponse(
              workingDocumentIds,
            filesSnapshotNow,
              uploadItems,
              envelopePayload
            );
            documentIdsRef.current = [...workingDocumentIds];
          while (documentIdsRef.current.length < (uploadedFilesRef.current || []).length) {
            documentIdsRef.current.push(null);
          }
          const deferredDeleteKeys = new Set();
          const filesAfterMerge = uploadedFilesRef.current || [];
          for (const item of uploadItems) {
            const cid = String(item.clientDocumentId || getFileClientDocumentId(item.file) || '').trim();
            const rowIdx = resolveUploadRowIndexByClientId(filesAfterMerge, item);
            const stillPresent = (uploadedFilesRef.current || []).some(
              (f, idx) => getDocumentCacheKey(f, idx) === item.key
            );
            const rawNew = rowIdx >= 0 ? workingDocumentIds[rowIdx] : null;
            const newId = typeof rawNew === 'string' ? parseInt(rawNew, 10) : rawNew;
            const idOk = Number.isFinite(newId) && newId > 0;
            if (!idOk) continue;
            const awaitingDelete = cid && pendingDeleteAfterClientDocIdRef.current.has(cid);
            if (!stillPresent || awaitingDelete) {
              if (awaitingDelete) pendingDeleteAfterClientDocIdRef.current.delete(cid);
              try {
                // eslint-disable-next-line no-await-in-loop
                await deleteDocument(newId);
              } catch (purgeErr) {
                console.error('Failed to purge orphan envelope document:', purgeErr);
              }
              if (cid) removeDocumentUploadRegistryEntry(cid);
              if (awaitingDelete && stillPresent) {
                deferredDeleteKeys.add(item.key);
              } else if (rowIdx >= 0) {
                workingDocumentIds[rowIdx] = null;
              }
            } else if (cid) {
              setDocumentUploadRegistryEntry(cid, { status: 'uploaded', documentId: newId });
            }
          }
          for (const key of [...deferredDeleteKeys].sort((a, b) => {
            const ai = (uploadedFilesRef.current || []).findIndex((f, idx) => getDocumentCacheKey(f, idx) === a);
            const bi = (uploadedFilesRef.current || []).findIndex((f, idx) => getDocumentCacheKey(f, idx) === b);
            return bi - ai;
          })) {
            const removeIdx = (uploadedFilesRef.current || []).findIndex(
              (f, idx) => getDocumentCacheKey(f, idx) === key
            );
            if (removeIdx >= 0) {
              purgeDocumentRowAtIndexRef.current(removeIdx);
            }
          }
          if (deferredDeleteKeys.size > 0) {
            workingDocumentIds.length = 0;
            workingDocumentIds.push(...(documentIdsRef.current || []));
            deferredDeleteKeys.forEach((k) => {
              deletingDocumentKeysInFlightRef.current.delete(k);
            });
            setDeletingDocumentRowKeys((prev) => prev.filter((k) => !deferredDeleteKeys.has(k)));
          }
          const prevRows = envelopeDocumentRowsRef.current || [];
              const currentFiles = uploadedFilesRef.current || [];
          const merged = prevRows.map((r) => ({ ...r }));
          while (merged.length < currentFiles.length) {
            merged.push(createEnvelopeDocumentRow(currentFiles[merged.length], null));
          }
              uploadItems.forEach((item) => {
                const currentIndex = currentFiles.findIndex((file, idx) => getDocumentCacheKey(file, idx) === item.key);
                if (currentIndex >= 0) {
              merged[currentIndex] = {
                ...merged[currentIndex],
                serverDocumentId: normalizeRowServerDocumentId(workingDocumentIds[currentIndex]),
              };
            }
          });
          envelopeRowsCommitRef.current(merged);
          const finalized = envelopeDocumentRowsRef.current || [];
          finalized.forEach((row, i) => {
            stampBackendDocumentIdOnFile(row.file, finalized[i].serverDocumentId);
          });
          uploadItems.forEach((item) => {
            const ri = resolveUploadRowIndexByClientId(uploadedFilesRef.current || [], item);
            const idForStamp =
              ri >= 0 ? workingDocumentIds[ri] ?? (documentIdsRef.current || [])[ri] : null;
            stampBackendDocumentIdOnFile(item.file, idForStamp);
            });
            uploadItems.forEach((item) => {
              setUploadProgressEntries(
                [{ key: item.key }],
                { percent: 100, status: 'complete', fileName: item.name }
              );
            });
          } catch (err) {
            const reason =
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              err?.message ||
              'Failed to upload document batch.';
            uploadItems.forEach((item) => {
              setUploadProgressEntries(
                [{ key: item.key }],
                { percent: 0, status: 'error', fileName: item.name }
              );
            const cid = String(item.clientDocumentId || getFileClientDocumentId(item.file) || '').trim();
            if (cid) setDocumentUploadRegistryEntry(cid, { status: 'pending', documentId: null });
            if (cid && pendingDeleteAfterClientDocIdRef.current.has(cid)) {
              pendingDeleteAfterClientDocIdRef.current.delete(cid);
              const removeIdx = (uploadedFilesRef.current || []).findIndex(
                (f, idx) => getDocumentCacheKey(f, idx) === item.key
              );
              if (removeIdx >= 0) {
                purgeDocumentRowAtIndexRef.current(removeIdx);
              }
              deletingDocumentKeysInFlightRef.current.delete(item.key);
              setDeletingDocumentRowKeys((prev) => prev.filter((k) => k !== item.key));
            }
            });
            const wrappedError = new Error(reason);
            wrappedError.originalError = err;
            throw wrappedError;
          }
        };

      const rowCount = Math.max(
        uploadedFilesRef.current?.length || 0,
        (documentIdsRef.current || []).length || 0
      );
      // One sync round runs all API batches for the current pending set. Extra rounds cover deferred
      // purges / row churn without an unbounded loop.
      const maxEnvelopeUploadSyncRounds = Math.min(
        24,
        Math.max(2, 2 + Math.ceil(rowCount / Math.max(1, DOCUMENT_UPLOAD_BATCH_SIZE)))
      );

      for (let syncRound = 0; syncRound < maxEnvelopeUploadSyncRounds; syncRound += 1) {
        const { pendingItems, pendingSignature } = computePendingEnvelopeDocumentItems();

        if (pendingItems.length === 0) {
          blockedDocumentUploadSignatureRef.current = '';
          blockedDocumentUploadMessageRef.current = '';
          break;
        }

        if (
          blockedDocumentUploadSignatureRef.current
          && blockedDocumentUploadSignatureRef.current === pendingSignature
        ) {
          const blockedMessage =
            blockedDocumentUploadMessageRef.current
            || 'Envelope document count limit exceeded.';
          setUploadError(blockedMessage);
          throw new Error(blockedMessage);
        }

        const uploadBatches = [];
        for (let i = 0; i < pendingItems.length; i += DOCUMENT_UPLOAD_BATCH_SIZE) {
          uploadBatches.push(pendingItems.slice(i, i + DOCUMENT_UPLOAD_BATCH_SIZE));
        }

        setUploadProgressEntries(
          pendingItems,
          (item, _idx, previous) => ({
            percent: previous?.status === 'complete' ? 100 : 0,
            status: previous?.status === 'complete' ? 'complete' : 'pending',
            fileName: item.name,
          })
        );

        let firstUploadError = null;
        for (let batchIndex = 0; batchIndex < uploadBatches.length; batchIndex += 1) {
          try {
            await runEnvelopeDocumentUploadBatch(
              uploadBatches[batchIndex],
              batchIndex + 1,
              pendingItems.length
            );
          } catch (error) {
            firstUploadError = error;
            break;
          }
        }

        if (firstUploadError) {
          const message = firstUploadError?.message || 'Failed to upload document batch.';
          if (isEnvelopeDocumentCountLimitError(firstUploadError)) {
            blockedDocumentUploadSignatureRef.current = pendingSignature;
            blockedDocumentUploadMessageRef.current = message;
          }
          setUploadError(message);
          throw new Error(message);
        }
      }

      const { pendingItems: stillPendingAfterSync } = computePendingEnvelopeDocumentItems();
      if (stillPendingAfterSync.length > 0) {
        const stuckMsg =
          'Some documents could not be linked to the envelope. Remove those files, add them again, then save.';
        setUploadError(stuckMsg);
        throw new Error(stuckMsg);
      }
      blockedDocumentUploadSignatureRef.current = '';
      blockedDocumentUploadMessageRef.current = '';
      setLastSavedAt(new Date());
      return [...(documentIdsRef.current || [])];
    } catch (error) {
      console.error('❌ Error saving envelope:', error);
      if (error.response?.data) {
        console.error('❌ Validation errors:', error.response.data);
      }
      const errorMessage = error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || 'Failed to save envelope changes.';
      setUploadError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      const completedItems = uploadedFilesRef.current.map((file, index) => ({
        key: getDocumentCacheKey(file, index),
      }));
      setTimeout(() => {
        setUploadProgressEntries(completedItems, (item, _idx, previous) => {
          if (!previous || (previous.status !== 'complete' && previous.status !== 'error')) return previous || null;
          return null;
        });
      }, 800);
      try {
        await flushDeferredDocumentApiTasks();
      } catch (flushErr) {
        console.error('Deferred document API flush failed:', flushErr);
      }
      documentUploadPipelineActiveRef.current = false;
      savingLockRef.current = false;
      setIsSavingDraft(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles, documentIds, isSavingDraft, draftEnvelopeId, templateName, subject, message, reminderFrequency, signingOrder, isTemplateMode, getDocumentCacheKey, setUploadProgressEntries, flushDeferredDocumentApiTasks, ensureClientDocumentId]);

  // Save recipients to envelope - ONLY updates existing envelopes (cannot create)
  // Envelope must be created first via saveDocuments (when files are uploaded)
  // Only sends recipients if there are new ones or if the list changed
  const saveRecipients = useCallback(async ({ force = false } = {}) => {
    if (isTemplateMode) return;
    if (!force && !orchestratedDraftSyncRef.current && isSavingDraft) {
      console.log('⏸️ Skipping recipient save - already saving');
      return;
    }
    
    const envelopeIdForSave = draftEnvelopeId || draftEnvelopeIdRef.current;
    // ONLY update if envelope exists - cannot create envelope here
    if (!envelopeIdForSave) {
      console.log('⏸️ Skipping recipient save - no envelope exists yet (will be included in first document save)');
      return;
    }
    
    // Skip if only signer mode (no recipients to save)
    if (isOnlySigner) {
      console.log('⏸️ Skipping recipient save - only signer mode');
      return;
    }
    
    // Convert recipients to envelope format (simpler format)
    const envelopeRecipients = convertRecipientsToEnvelopeFormat();
    
    if (envelopeRecipients.length === 0) {
      console.warn('⚠️ No recipients to save - skipping');
      return;
    }
    
    // Check if recipients actually changed (per-recipient id, order, color, role).
    const workflowType = signingOrder ? 'sequential' : 'parallel';
    const currentFingerprint = buildRecipientsSaveFingerprint(envelopeRecipients, workflowType);
    const lastFingerprint = buildRecipientsSaveFingerprint(
      lastSavedRecipientsRef.current,
      lastSavedRecipientsRef.current._workflowType || 'parallel'
    );
    
    console.log('🔍 Checking recipient changes:', {
      currentCount: envelopeRecipients.length,
      lastSavedCount: lastSavedRecipientsRef.current.length,
      workflowType,
      currentFingerprint: currentFingerprint.substring(0, 80) + '...',
      lastFingerprint: lastFingerprint.substring(0, 80) + '...'
    });
    
    if (!force && currentFingerprint === lastFingerprint && lastSavedRecipientsRef.current.length > 0) {
      console.log('⏸️ Recipients unchanged (including role and workflow) - skipping save');
      return;
    }
    
    // Separate recipients into new (no id) and existing (has id) for updates
    const newRecipients = envelopeRecipients.filter(r => !r.id);
    const existingRecipients = envelopeRecipients.filter(r => r.id);
    
    console.log('🔍 Recipient analysis:', {
      totalRecipients: envelopeRecipients.length,
      newRecipientsCount: newRecipients.length,
      existingRecipientsCount: existingRecipients.length,
      newRecipientEmails: newRecipients.map(r => r.email),
      existingRecipientIds: existingRecipients.map(r => ({ id: r.id, email: r.email }))
    });
    
    // Check lock to prevent concurrent saves
    if (savingLockRef.current) {
      console.log('⏸️ Skipping recipient save - another save in progress');
      return;
    }
    
    savingLockRef.current = true;
    setIsSavingDraft(true);
    
    try {
      // Send all recipients - new ones (no id) will be created, existing ones (with id) will be updated
      // This follows the new API architecture: include id for updates, omit for creates
      console.log('🔄 Updating envelope with recipients (new and existing)...', {
        newRecipientsCount: newRecipients.length,
        existingRecipientsCount: existingRecipients.length
      });
      
      // Prepare envelope data - send all recipients (API handles create vs update based on id presence)
      const envelopeData = {
        envelope_id: envelopeIdForSave,
        recipients: envelopeRecipients,  // Send all - API will create new ones and update existing ones based on id
        workflow_type: signingOrder ? 'sequential' : 'parallel',  // Always include workflow type with recipients
      };
      
      console.log('📤 Sending recipients to API (new and updates):', {
        envelope_id: envelopeData.envelope_id,
        recipientsCount: envelopeRecipients.length,
        newRecipients: newRecipients.map(r => ({ email: r.email, name: r.name })),
        existingRecipients: existingRecipients.map(r => ({ id: r.id, email: r.email, name: r.name })),
        recipientData: envelopeRecipients
      });
      
      const envelopeResponse = await createDraftEnvelope(envelopeData);
      console.log('✅ Recipients saved successfully:', envelopeResponse);
      
      // Only merge server `id` into local rows. Do not overwrite name, email, or role from the response —
      // otherwise a slow/stale response can undo edits the user made while the request was in flight.
      if (envelopeResponse?.data?.recipients && Array.isArray(envelopeResponse.data.recipients)) {
        const apiRecipients = envelopeResponse.data.recipients;
        setRecipients((prevRecipients) => {
          const next = prevRecipients.map((recipient) => {
            let apiR =
              recipient.id != null
                ? apiRecipients.find((ar) => String(ar.id) === String(recipient.id))
                : null;
            if (!apiR && recipient.email) {
              const e = String(recipient.email).trim();
              apiR = apiRecipients.find(
                (ar) =>
                  String(ar.email || ar.resolved_email || '').trim() === e
              );
            }
            if (apiR != null && apiR.id != null) {
              return { ...recipient, id: apiR.id };
            }
            return recipient;
          });
          recipientIdMapRef.current = {};
          next.forEach((r) => {
            const email = String(r.email || '').trim();
            if (email && r.id != null) {
              recipientIdMapRef.current[email] = r.id;
            }
          });
          return next;
        });
        setDocumentFields((prevFields) => {
          const mapped = applyRecipientIdMappingToFields(prevFields);
          documentFieldsRef.current = mapped;
          return mapped;
        });
      }
      
      // Update last saved recipients reference with all current recipients
      // Replace with current recipients to keep in sync, including workflow type
      const savedList = envelopeRecipients.map(r => ({
        recipient_id: r.recipient_id ?? r.id,
        id: r.recipient_id ?? r.id,
        email: r.email,
        name: r.name,
        type: r.type,
        signature_order: r.signature_order,
        signee_color: r.signee_color,
      }));
      savedList._workflowType = signingOrder ? 'sequential' : 'parallel';
      lastSavedRecipientsRef.current = savedList;
      
      console.log('✅ Updated saved recipients reference:', {
        totalSaved: lastSavedRecipientsRef.current.length,
        savedEmails: lastSavedRecipientsRef.current.map(r => r.email),
        recipientIdMap: recipientIdMapRef.current
      });
      
      setLastSavedAt(new Date());

      const contactRows = envelopeRecipients
        .filter((r) => String(r.email || '').trim() && String(r.name || '').trim())
        .map((r) => ({
          email: String(r.email).trim(),
          name: String(r.name).trim(),
        }));
      const orgId =
        getOrganizationIdFromSubscription(userSubscription) ?? getActiveOrganizationId();
      if (contactRows.length > 0 && orgId) {
        setSavedRecipientContacts(mergeContactsIntoStore(contactRows, orgId));
      }
    } catch (error) {
      console.error('❌ Error saving recipients:', error);
      if (error.response?.data) {
        console.error('❌ Validation errors:', error.response.data);
      }
      if (orchestratedDraftSyncRef.current) {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to save recipients.';
        setUploadError(msg);
        throw new Error(msg);
      }
    } finally {
      savingLockRef.current = false;
      setIsSavingDraft(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftEnvelopeId, recipients, isOnlySigner, signingOrder, sequenceInputs, isSavingDraft, convertRecipientsToEnvelopeFormat, isTemplateMode]);

  // Save fields to envelope - ONLY updates existing envelopes (cannot create)
  // Envelope must be created first via saveDocuments (when files are uploaded)
  const saveFields = useCallback(async (options = {}) => {
    const scope = options.scope || 'all';
    const runId = ++fieldSaveRunSeqRef.current;
    logFieldSaveDebug('🧪 [fields-save] ENTER', {
      runId,
      scope,
      source: lastFieldAutosaveSourceRef.current,
      draftEnvelopeId,
      draftEnvelopeIdRef: draftEnvelopeIdRef.current,
      fieldsCount: documentFields.length,
      docIdsCount: documentIds.length,
      isSavingDraft,
      savingLockActive: savingLockRef.current,
      autosaveToken: fieldAutosaveTokenRef.current,
    });
    if (isTemplateMode) return;
    if (!orchestratedDraftSyncRef.current && isSavingDraft) {
      console.log('⏸️ Skipping field save - already saving');
      return;
    }
    
    const envelopeIdForFields = draftEnvelopeId || draftEnvelopeIdRef.current;
    // ONLY update if envelope exists - cannot create envelope here
    if (!envelopeIdForFields) {
      console.log('⏸️ Skipping field save - no envelope exists yet (will be included in first document save)');
      return;
    }
    
    const documentIdsSource =
      orchestratedDraftSyncRef.current && documentIdsRef.current?.length
        ? documentIdsRef.current
        : documentIds;
    // Check if document IDs are available (required for field updates)
    const validDocumentIds = documentIdsSource.filter(id => id !== null && id !== undefined && id !== '');
    if (validDocumentIds.length === 0) {
      console.log('⏸️ Skipping field save - no document IDs available yet (documents must be saved first)');
      return;
    }
    
    // Hold lock before any await — otherwise two overlapping saveFields runs (e.g. documentFields +
    // documentIds microtasks) can both pass the old "lock after getEnvelope" gate and each bulk-create.
    if (savingLockRef.current) {
      console.log('⏸️ Skipping field save - another save in progress');
      return;
    }
    savingLockRef.current = true;

    const strictFieldSync = orchestratedDraftSyncRef.current;
    let shouldScheduleFollowupSave = false;

    try {
    // Log current state for debugging
    logFieldSaveDebug('🔍 Field save check:', {
      documentIdsCount: documentIds.length,
      validDocumentIdsCount: validDocumentIds.length,
      documentFieldsCount: documentFields.length,
      documentIds: documentIds,
      documentFields: documentFields.map(f => ({
        type: f.type,
        recipientEmail: f.recipientEmail,
        x: f.x,
        y: f.y,
        page: f.page
      }))
    });
    
    // Try to get recipient IDs from envelope if missing
    // Fetch envelope to get recipient IDs if our map is incomplete
    const fieldsNeedingRecipientIds = documentFields.filter(f => {
      const email = f.recipientEmail || f.recipientId;
      return email && !recipientIdMapRef.current[email];
    });
    
    if (fieldsNeedingRecipientIds.length > 0 && envelopeIdForFields) {
      try {
        console.log('🔍 Fetching envelope to get recipient IDs for fields...');
        const envelope = await getEnvelope(envelopeIdForFields);
        if (envelope?.recipients && Array.isArray(envelope.recipients)) {
          envelope.recipients.forEach(recipient => {
            if (recipient.email && recipient.id) {
              recipientIdMapRef.current[recipient.email] = recipient.id;
            }
          });
          console.log('✅ Updated recipient ID map from envelope:', recipientIdMapRef.current);
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch envelope for recipient IDs:', error);
      }
    }
    
    // Convert fields to envelope format
    const localFieldsSnapshot = documentFieldsRef.current || documentFields;
    const envelopeFields = convertFieldsToEnvelopeFormat();
    
    logFieldSaveDebug('🔍 Converted envelope fields:', {
      envelopeFieldsCount: envelopeFields.length,
      envelopeFields: envelopeFields,
      fieldsWithRecipientIds: envelopeFields.filter(f => f.recipient_id).length,
      fieldsWithoutRecipientIds: envelopeFields.filter(f => !f.recipient_id).length
    });
    
    if (envelopeFields.length === 0) {
      logFieldSaveDebug('⚠️ No current fields in payload; continuing to allow delete diff against lastSavedFieldsRef', {
        documentFieldsCount: documentFields.length,
        documentIdsCount: documentIds.length,
        fieldsWithRecipientEmail: documentFields.filter(f => f.recipientEmail || f.recipientId).length
      });
    }
    
    // Warn if some fields don't have recipient IDs
    const fieldsWithoutRecipientIds = envelopeFields.filter(f => !f.recipient_id);
    if (fieldsWithoutRecipientIds.length > 0) {
      console.warn('⚠️ Some fields missing recipient IDs - API may not be able to resolve them:', fieldsWithoutRecipientIds.length);
    }
    
      logFieldSaveDebug('🔄 Saving fields using dedicated field APIs...', {
        fieldsCount: envelopeFields.length,
        fields: envelopeFields,
        documentIds: validDocumentIds
      });
      const normalizeId = (v) => (v !== undefined && v !== null ? String(v) : '');
      const toSnapshot = (field) => {
        const id = field.id || field.field_id;
        return {
          id,
          sig: JSON.stringify({
            id: normalizeId(id),
            document_id: normalizeId(field.document_id),
            recipient_id: normalizeId(field.recipient_id),
            field_type: field.field_type || '',
            field_name: field.field_name || '',
            page_number: field.page_number ?? '',
            x_position: field.x_position ?? '',
            y_position: field.y_position ?? '',
            width: field.width ?? '',
            height: field.height ?? '',
            is_required: field.is_required !== false,
            read_only: field.read_only || false,
            field_value: getFieldValueForApi(field),
            ...(fieldTypeAllowsSingleOptionOnly(field.field_type)
              ? { field_options: field.field_options ?? getFieldChoiceOptionsForApi(field) }
              : {}),
          })
        };
      };
      const toNewFieldSig = (field) => JSON.stringify({
        document_id: normalizeId(field.document_id),
        recipient_id: normalizeId(field.recipient_id),
        field_type: field.field_type || '',
        field_name: field.field_name || '',
        page_number: field.page_number ?? '',
        x_position: field.x_position ?? '',
        y_position: field.y_position ?? '',
        width: field.width ?? '',
        height: field.height ?? '',
        is_required: field.is_required !== false,
        read_only: field.read_only || false,
        field_value: getFieldValueForApi(field),
        ...(fieldTypeAllowsSingleOptionOnly(field.field_type)
          ? { field_options: field.field_options ?? getFieldChoiceOptionsForApi(field) }
          : {}),
      });

      // Resolve id/field_id from local map first, so a field created moments ago is treated
      // as existing on subsequent saves even if the local object has not been hydrated yet.
      const envelopeFieldsResolved = envelopeFields.map((field) => {
        const mappedId =
          field.local_field_id != null ? fieldIdMapRef.current[field.local_field_id] : null;
        const resolvedId = field.id || field.field_id || mappedId;
        if (!resolvedId) return field;
        const numericId = typeof resolvedId === 'string' ? parseInt(resolvedId, 10) : resolvedId;
        if (Number.isNaN(numericId) || numericId <= 0) return field;
        return { ...field, id: numericId, field_id: numericId };
      });
      // Separate fields into new (no id) and existing (has id)
      const newFields = envelopeFieldsResolved.filter(f => !f.id && !f.field_id);
      const existingFields = envelopeFieldsResolved.filter(f => f.id || f.field_id);
      const previousNewFieldSigs = new Set(
        (lastSavedFieldsRef.current || [])
          .filter((s) => !s.id && s.sig)
          .map((s) => s.sig)
      );
      // Avoid re-creating unchanged new fields when backend hasn't returned/mapped IDs yet.
      // Also never re-create rows that are currently waiting for create response (pending by local_field_id).
      const pendingCreates = pendingFieldCreateRef.current;
      const pendingCreateMaxMs = FIELD_PENDING_CREATE_MAX_MS;
      const newFieldsToCreate = newFields.filter((f) => {
        const localKey = normalizeId(f.local_field_id);
        const currentSig = toNewFieldSig(f);
        if (localKey && pendingCreates.has(localKey)) {
          const meta = pendingCreates.get(localKey) || {};
          const ageMs = meta.requestedAt ? (Date.now() - meta.requestedAt) : 0;
          if (ageMs > pendingCreateMaxMs) {
            // Safety valve: release stale pending entries so user isn't blocked forever.
            pendingCreates.delete(localKey);
            return !previousNewFieldSigs.has(currentSig);
          }
          if (meta.sigSent && meta.sigSent !== currentSig) {
            // Field changed while create request is in-flight; queue one follow-up update.
            meta.dirtyAfterCreate = true;
            pendingCreates.set(localKey, meta);
            shouldScheduleFollowupSave = true;
          }
          return false;
        }
        return !previousNewFieldSigs.has(currentSig);
      });
      const existingSnapshots = new Map(
        existingFields
          .map(toSnapshot)
          .filter((s) => normalizeId(s.id))
          .map((s) => [normalizeId(s.id), s.sig])
      );
      const previousSnapshots = new Map(
        (lastSavedFieldsRef.current || [])
          .filter((s) => normalizeId(s.id))
          .map((s) => [normalizeId(s.id), s.sig || ''])
      );

      // Find deleted fields by comparing previous saved IDs vs current IDs (no API fan-out needed).
      const deletedFieldIds = Array.from(previousSnapshots.keys()).filter((id) => !existingSnapshots.has(id));
      const pendingDocIds = pendingDocumentDeletesSkipFieldRef.current;
      const pendingFieldIds = pendingExplicitFieldDeletesSkipRef.current;
      const skippedDocIds = new Set();

      // Update only changed existing fields (computed before delete/create so scope can gate work).
      const existingFieldsToUpdate = existingFields.filter((field) => {
        const id = normalizeId(field.id || field.field_id);
        const prevSig = previousSnapshots.get(id);
        const nextSig = existingSnapshots.get(id);
        return !prevSig || prevSig !== nextSig;
      });
      
      logFieldSaveDebug('🔍 Field analysis:', {
        newFieldsCount: newFields.length,
        newFieldsToCreateCount: newFieldsToCreate.length,
        existingFieldsCount: existingFields.length,
        changedExistingFieldsCount: existingFieldsToUpdate.length,
        deletedFieldsCount: deletedFieldIds.length
      });

      const runDeletes = scope === 'all' || scope === 'deletes-and-creates';
      const runCreates = scope === 'all' || scope === 'deletes-and-creates';
      const runUpdates = scope === 'all' || scope === 'updates';

      if (scope === 'deletes-and-creates' && deletedFieldIds.length === 0 && newFieldsToCreate.length === 0) {
        logFieldSaveDebug('⏸️ [fields-save] skips deletes-and-creates scope — nothing to persist');
        return;
      }
      if (scope === 'updates' && existingFieldsToUpdate.length === 0) {
        logFieldSaveDebug('⏸️ [fields-save] skips updates scope — no changed existing fields');
        return;
      }
      if (scope === 'updates' && pendingFieldCreateRef.current.size > 0) {
        logFieldSaveDebug('⏸️ [fields-save] deferring updates — field bulk-create still in flight (awaiting field_id)');
        return;
      }

      // After creates/deletes-only, keep prior signatures for existing server fields so debounced updates still see a diff.
      const preservedExistingSigs =
        scope === 'deletes-and-creates'
          ? new Map(
              (lastSavedFieldsRef.current || [])
                .filter((s) => normalizeId(s.id))
                .map((s) => [normalizeId(s.id), s.sig])
            )
          : null;

      const fieldDeleteBatch = [];
      if (runDeletes) for (const deletedId of deletedFieldIds) {
        const numericFieldId = parseInt(deletedId, 10);
        if (isNaN(numericFieldId) || numericFieldId <= 0) continue;
        const deletedIdKey = String(numericFieldId);
        if (pendingFieldIds.has(deletedIdKey)) {
          pendingFieldIds.delete(deletedIdKey);
          console.log(`⏭️ Skipping field DELETE ${numericFieldId} (already deleted directly from UI)`);
          continue;
        }
        
        // If the document itself is being deleted, the backend should cascade-delete its fields.
        // Prevent spamming DELETE /document-field-delete/{id} for every field.
        const prevSig = previousSnapshots.get(deletedId) || '';
        let snapDocId = null;
        try {
          snapDocId = JSON.parse(prevSig)?.document_id ?? null;
        } catch {
          // ignore parse errors
        }
        const snapDocIdKey = snapDocId !== null && snapDocId !== '' ? String(snapDocId) : null;
        if (snapDocIdKey && pendingDocIds.has(snapDocIdKey)) {
          skippedDocIds.add(snapDocIdKey);
          console.log(
            `⏭️ Skipping field DELETE ${numericFieldId} because document ${snapDocIdKey} is being deleted`
          );
          continue;
        }
        fieldDeleteBatch.push({ deletedId, numericFieldId });
      }

      if (runDeletes && fieldDeleteBatch.length > 1) {
        try {
          const ids = fieldDeleteBatch.map((r) => r.numericFieldId);
          console.log(`🗑️ Bulk deleting removed fields (${ids.length}) via document-field-bulk-delete`);
          await bulkDeleteDocumentFields(ids);
          if (preservedExistingSigs) {
            fieldDeleteBatch.forEach((r) => preservedExistingSigs.delete(r.deletedId));
          }
        } catch (error) {
          console.error('❌ Error bulk deleting fields:', error);
          if (strictFieldSync) {
            const msg =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Failed to bulk delete fields.';
            throw new Error(msg);
          }
        }
      } else if (runDeletes && fieldDeleteBatch.length === 1) {
        const { deletedId, numericFieldId } = fieldDeleteBatch[0];
        try {
          console.log(`🗑️ Deleting removed field ${numericFieldId}`);
          await deleteDocumentField(numericFieldId);
          if (preservedExistingSigs) preservedExistingSigs.delete(deletedId);
        } catch (error) {
          console.error(`❌ Error deleting field ${numericFieldId}:`, error);
          if (strictFieldSync) {
            const msg =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              `Failed to delete field ${numericFieldId}.`;
            throw new Error(msg);
          }
        }
      }

      // Clear processed skip-ids (short-lived)
      if (runDeletes && skippedDocIds.size > 0) {
        for (const docId of skippedDocIds) pendingDocIds.delete(docId);
      }

      // Existing fields: bulk update per document (transactional) when possible; single otherwise.
      const updatesByDocument = {};
      for (const field of existingFieldsToUpdate) {
        const rawId = field.id || field.field_id;
        const numericFieldId = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId;
        if (!numericFieldId || Number.isNaN(numericFieldId) || numericFieldId <= 0) {
          console.warn('⚠️ Skipping existing field update - missing/invalid field id:', field);
          continue;
        }
        if (!field.document_id) {
          console.warn(`⚠️ Skipping field ${numericFieldId} update - missing document_id`);
          continue;
        }
        const docKey = String(field.document_id);
        if (!updatesByDocument[docKey]) updatesByDocument[docKey] = [];
        updatesByDocument[docKey].push({ field, numericFieldId });
      }

      const persistFieldUpdates = async () => {
        if (!runUpdates) return;
        if (scope === 'updates' && pendingFieldCreateRef.current.size > 0) {
          logFieldSaveDebug('⏸️ [fields-save] persistFieldUpdates skipped — create still in flight');
          return;
        }
        for (const [docKey, rows] of Object.entries(updatesByDocument)) {
          const docId = docKey;
          if (!docId) continue;

          const buildRowPayload = ({ field, numericFieldId }) => withChoiceFieldOptionsForApi({
            id: numericFieldId,
            document_id: field.document_id,
            recipient_id: field.recipient_id,
            field_name: field.field_name || field.field_type,
            field_type: field.field_type,
            page_number: field.page_number,
            x_position: field.x_position,
            y_position: field.y_position,
            width: field.width,
            height: field.height,
            is_required: field.is_required !== false,
            read_only: field.read_only || false,
            field_value: getFieldValueForApi(field),
          }, field, localFieldsSnapshot);

          try {
            if (rows.length > 1) {
              const bulkFields = rows.map(buildRowPayload);
              console.log(
                `🔄 Bulk updating ${bulkFields.length} existing field(s) for document ${docId} via document-field-bulk-update`
              );
              await bulkUpdateDocumentFields(docId, bulkFields);
            } else {
              const { field, numericFieldId } = rows[0];
          const updateData = withChoiceFieldOptionsForApi({
            document_id: field.document_id,
            recipient_id: field.recipient_id,
            field_name: field.field_name || field.field_type,
            field_type: field.field_type,
            page_number: field.page_number,
            x_position: field.x_position,
            y_position: field.y_position,
            width: field.width,
            height: field.height,
            is_required: field.is_required !== false,
                read_only: field.read_only || false,
                field_value: getFieldValueForApi(field),
              }, field, localFieldsSnapshot);
              console.log(`🔄 Updating existing field ${numericFieldId} via document-field-update`);
              await updateDocumentField(numericFieldId, updateData);
            }
        } catch (error) {
            console.error(`❌ Error updating field(s) for document ${docId}:`, error);
            if (strictFieldSync) {
              const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                `Failed to update fields for document ${docId}.`;
              setUploadError(msg);
              throw new Error(msg);
            }
          }
        }
      };

      // Full draft flush (`scope === 'all'`): run bulk creates before updates so field_id exists on the server.
      const executeBulkFieldCreates = async () => {
      // Resolve server field id for bulk-create rows when the envelope payload lost id but the local
      // field still has apiId / fieldIdMap (avoids duplicate rows on document-field-bulk-create).
      const resolveBulkRowFieldIds = (envField) => {
        let raw = envField.id || envField.field_id || null;
        if (!raw) {
          let localField = null;
          // Prefer direct local linkage when present.
          if (envField.local_field_id != null) {
            localField = documentFields.find((lf) => String(lf.id) === String(envField.local_field_id)) || null;
          }
          // Fallback to positional match for older rows without local_field_id.
          if (!localField) {
            localField = documentFields.find((lf) => {
              const { documentId: docIdForLocal, pageNumber: pageNumberForLocal } = getApiFieldDocumentAndPage(lf);
              if (String(docIdForLocal) !== String(envField.document_id)) return false;
              if ((pageNumberForLocal || 1) !== (envField.page_number || 1)) return false;
              if ((lf.type || '') !== (envField.field_type || '')) return false;
              if (Math.abs((lf.x ?? 0) - (envField.x_position ?? 0)) >= 1) return false;
              if (Math.abs((lf.y ?? 0) - (envField.y_position ?? 0)) >= 1) return false;
              return true;
            });
          }
          if (localField) {
            raw = localField.apiId || localField.field_id || fieldIdMapRef.current[localField.id] || null;
          }
        }
        if (raw == null || raw === '') return {};
        const n = typeof raw === 'string' ? parseInt(raw, 10) : raw;
        if (Number.isNaN(n) || n <= 0) return {};
        return { id: n, field_id: n };
      };
      
      // Create new fields - group by document_id for bulk create
      const fieldsByDocument = {};
      newFieldsToCreate.forEach(field => {
        if (!field.document_id) return;
        if (!fieldsByDocument[field.document_id]) {
          fieldsByDocument[field.document_id] = [];
        }
        const ids = resolveBulkRowFieldIds(field);
        fieldsByDocument[field.document_id].push({
          ...ids,
          local_field_id: field.local_field_id,
          ...withChoiceFieldOptionsForApi({
          document_id: field.document_id,
          recipient_id: field.recipient_id,
          field_name: field.field_name || field.field_type,
          field_type: field.field_type,
          page_number: field.page_number,
          x_position: field.x_position,
          y_position: field.y_position,
          width: field.width,
          height: field.height,
          is_required: field.is_required,
          read_only: field.read_only,
          field_value: getFieldValueForApi(field),
          }, field, localFieldsSnapshot),
        });
      });
      
      // Bulk create fields for each document
      // Note: Always uses bulk create API, even for single fields (API supports this)
      for (const [docId, fields] of Object.entries(fieldsByDocument)) {
        if (fields.length === 0) continue;
        
        try {
          const requestRows = fields.map(({ local_field_id, ...row }) => row);
          const requestedLocalIds = fields
            .map((f) => normalizeId(f.local_field_id))
            .filter(Boolean);
          requestedLocalIds.forEach((localId) => {
            const row = fields.find((f) => normalizeId(f.local_field_id) === localId);
            pendingFieldCreateRef.current.set(localId, {
              sigSent: row ? toNewFieldSig(row) : '',
              dirtyAfterCreate: false,
              requestedAt: Date.now(),
            });
          });
          console.log(`📝 Bulk creating ${requestRows.length} field(s) for document ${docId} using bulk API`);
          const response = await bulkCreateFields(parseInt(docId), requestRows);
          
          // Update field ID map from response (supports different payload shapes).
          const responseRows = [
            ...(Array.isArray(response?.fields) ? response.fields : []),
            ...(Array.isArray(response?.created_fields) ? response.created_fields : []),
            ...(Array.isArray(response?.updated_fields) ? response.updated_fields : []),
            ...(Array.isArray(response?.data?.fields) ? response.data.fields : []),
            ...(Array.isArray(response?.data?.created_fields) ? response.data.created_fields : []),
            ...(Array.isArray(response?.data?.updated_fields) ? response.data.updated_fields : []),
          ];
          const rowSig = (r) => JSON.stringify({
            document_id: normalizeId(r.document_id ?? docId),
            recipient_id: normalizeId(r.recipient_id),
            field_type: r.field_type || '',
            field_name: r.field_name || '',
            page_number: r.page_number ?? '',
            x_position: r.x_position ?? '',
            y_position: r.y_position ?? '',
            width: r.width ?? '',
            height: r.height ?? '',
            is_required: r.is_required !== false,
            read_only: r.read_only || false,
            field_value: getFieldValueForApi(r),
            ...(fieldTypeAllowsSingleOptionOnly(r.field_type)
              ? { field_options: r.field_options ?? getFieldChoiceOptionsForApi(r) }
              : {}),
          });
          const localIdsBySig = new Map();
          fields.forEach((row) => {
            const localId = normalizeId(row.local_field_id);
            if (!localId) return;
            const sig = rowSig(row);
            const bucket = localIdsBySig.get(sig) || [];
            bucket.push(localId);
            localIdsBySig.set(sig, bucket);
          });
          const unresolvedLocalIds = new Set(requestedLocalIds);
          const unmatchedApiIds = [];

          responseRows.forEach((apiField) => {
            const apiId = apiField?.id ?? apiField?.field_id;
            if (!apiId) return;
            let mappedLocalId = null;
            if (apiField.local_field_id != null) {
              mappedLocalId = normalizeId(apiField.local_field_id);
            }
            if (!mappedLocalId) {
              const sig = rowSig(apiField);
              const bucket = localIdsBySig.get(sig) || [];
              mappedLocalId = bucket.shift() || null;
              if (bucket.length > 0) localIdsBySig.set(sig, bucket);
              else localIdsBySig.delete(sig);
            }
            if (mappedLocalId) {
              fieldIdMapRef.current[mappedLocalId] = apiId;
              unresolvedLocalIds.delete(mappedLocalId);
              console.log(`✅ Mapped field ${mappedLocalId} to API field ID ${apiId}`);
            } else {
              unmatchedApiIds.push(apiId);
            }
          });

          // Fallback: if backend returns created IDs without enough per-row payload to signature-match,
          // map remaining IDs to remaining local rows by request order.
          if (unmatchedApiIds.length > 0 && unresolvedLocalIds.size > 0) {
            let apiIdx = 0;
            for (const localId of requestedLocalIds) {
              if (apiIdx >= unmatchedApiIds.length) break;
              if (!unresolvedLocalIds.has(localId)) continue;
              const apiId = unmatchedApiIds[apiIdx];
              fieldIdMapRef.current[localId] = apiId;
              unresolvedLocalIds.delete(localId);
              apiIdx += 1;
              console.log(`✅ Fallback-mapped field ${localId} to API field ID ${apiId}`);
            }
          }

          // Resolve pending-create lifecycle for rows in this request.
          for (const row of fields) {
            const localId = normalizeId(row.local_field_id);
            if (!localId) continue;
            const meta = pendingFieldCreateRef.current.get(localId);
            const resolvedApiId = fieldIdMapRef.current[localId];
            if (resolvedApiId) {
              if (meta?.dirtyAfterCreate) {
                shouldScheduleFollowupSave = true;
              }
              pendingFieldCreateRef.current.delete(localId);
            } else if (meta?.dirtyAfterCreate) {
              // Keep pending if unresolved; we'll retry once mapping catches up.
              shouldScheduleFollowupSave = true;
            } else {
              // Keep pending until ID resolves or entry expires (avoids duplicate re-create).
            }
          }
        } catch (error) {
          // Unblock pending rows on failure so a later autosave can retry.
          for (const row of fields) {
            const localId = normalizeId(row.local_field_id);
            if (localId) pendingFieldCreateRef.current.delete(localId);
          }
          console.error(`❌ Error creating fields for document ${docId}:`, error);
          if (strictFieldSync) {
            const msg =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              `Failed to create fields on document ${docId}.`;
            throw new Error(msg);
          }
        }
      }
      };

      if (scope === 'all') {
        if (runCreates) {
          await executeBulkFieldCreates();
        }
        await persistFieldUpdates();
      } else {
        await persistFieldUpdates();
        if (runCreates) {
          await executeBulkFieldCreates();
        }
      }

      // Update last saved fields reference using freshest resolved IDs (including newly mapped ones)
      // so the next autosave run does not treat just-created fields as "changed existing".
      const resolvedSnapshots = envelopeFields.map((f) => {
        const resolvedId =
          f.id ||
          f.field_id ||
          (f.local_field_id != null ? fieldIdMapRef.current[f.local_field_id] : null);
        if (resolvedId) {
          const n = typeof resolvedId === 'string' ? parseInt(resolvedId, 10) : resolvedId;
          if (!Number.isNaN(n) && n > 0) {
            return toSnapshot({ ...f, id: n, field_id: n });
          }
        }
        return { id: null, sig: toNewFieldSig(f) };
      });
      if (scope === 'deletes-and-creates' && preservedExistingSigs) {
        lastSavedFieldsRef.current = envelopeFields.map((f) => {
          const resolvedId =
            f.id ||
            f.field_id ||
            (f.local_field_id != null ? fieldIdMapRef.current[f.local_field_id] : null);
          if (resolvedId) {
            const n = typeof resolvedId === 'string' ? parseInt(resolvedId, 10) : resolvedId;
            if (!Number.isNaN(n) && n > 0) {
              const idKey = normalizeId(n);
              const preserved = preservedExistingSigs.get(idKey);
              if (preserved) return { id: n, sig: preserved };
              return toSnapshot({ ...f, id: n, field_id: n });
            }
          }
          return { id: null, sig: toNewFieldSig(f) };
        });
      } else {
        lastSavedFieldsRef.current = resolvedSnapshots;
      }
      
      logFieldSaveDebug('✅ Fields saved successfully using dedicated APIs');
      logFieldSaveDebug('✅ Updated field ID map:', fieldIdMapRef.current);
      
      setLastSavedAt(new Date());
      logFieldSaveDebug('🧪 [fields-save] SUCCESS', { runId });
    } catch (error) {
      logFieldSaveDebug('🧪 [fields-save] ERROR', { runId, message: error?.message || String(error) });
      console.error('❌ Error saving fields:', error);
      if (error.response?.data) {
        console.error('❌ Validation errors:', error.response.data);
      }
      if (orchestratedDraftSyncRef.current) {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to save fields.';
        setUploadError(msg);
        throw error instanceof Error ? error : new Error(msg);
      }
    } finally {
      logFieldSaveDebug('🧪 [fields-save] EXIT', { runId, lockBeforeRelease: savingLockRef.current });
      savingLockRef.current = false;
      if (shouldScheduleFollowupSave) {
        logFieldSaveDebug('🧪 [fields-save] scheduling follow-up save for dirty fields updated during pending-create');
        scheduleFieldAutosaveRef.current('post-create-followup');
      }
      // Don't reset isSavingDraft since we didn't set it for field operations
      // setIsSavingDraft(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftEnvelopeId, documentIds, documentFields, isSavingDraft, convertFieldsToEnvelopeFormat, isTemplateMode]);

  // Save message metadata to envelope - can create envelope if it doesn't exist
  // Only sends message metadata (lighter payload for updates)
  // Save message metadata - ONLY updates existing envelopes (cannot create)
  // Envelope must be created first via saveDocuments (when files are uploaded)
  const saveMessage = useCallback(async () => {
    if (isTemplateMode) return;
    if (!orchestratedDraftSyncRef.current && isSavingDraft) {
      console.log('⏸️ Skipping message save - already saving');
      return;
    }
    
    const envelopeIdForMessage = draftEnvelopeId || draftEnvelopeIdRef.current;
    // ONLY update if envelope exists - cannot create envelope here
    if (!envelopeIdForMessage) {
      console.log('⏸️ Skipping message save - no envelope exists yet (will be included in first document save)');
      return;
    }
    
    // Check lock to prevent concurrent saves
    if (savingLockRef.current) {
      console.log('⏸️ Skipping message save - another save in progress');
      return;
    }
    
    savingLockRef.current = true;
    setIsSavingDraft(true);
    
    try {
      console.log('🔄 Updating envelope with message metadata...');
      
      // Prepare envelope data - only send message metadata (update only)
      const envelopeData = {
        envelope_id: envelopeIdForMessage,
      };
      
      // Only add fields that have values
      if (subject) envelopeData.subject = subject;
      if (message) envelopeData.message = message;
      if (reminderFrequency) envelopeData.reminder_frequency = reminderFrequency;
      envelopeData.workflow_type = signingOrder ? 'sequential' : 'parallel';
      
      // Don't send if no data (workflow_type is always included, so check others)
      if (!envelopeData.subject && !envelopeData.message && !envelopeData.reminder_frequency && !envelopeData.workflow_type) {
        console.warn('⚠️ No message data to save - skipping');
        savingLockRef.current = false;
        setIsSavingDraft(false);
        return;
      }
      
      console.log('📤 Updating envelope with message metadata:', {
        envelope_id: envelopeData.envelope_id,
        subject: envelopeData.subject
      });
      
      const envelopeResponse = await createDraftEnvelope(envelopeData);
      console.log('✅ Message metadata saved successfully:', envelopeResponse);
      
      setLastSavedAt(new Date());
    } catch (error) {
      console.error('❌ Error saving message metadata:', error);
      if (error.response?.data) {
        console.error('❌ Validation errors:', error.response.data);
      }
      if (orchestratedDraftSyncRef.current) {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to save message details.';
        setUploadError(msg);
        throw new Error(msg);
      }
    } finally {
      savingLockRef.current = false;
      setIsSavingDraft(false);
    }
  }, [draftEnvelopeId, subject, message, reminderFrequency, signingOrder, isSavingDraft, isTemplateMode]);

  // Legacy saveDraft function for backward compatibility (used by send)
  const saveDraft = useCallback(async () => {
    if (isTemplateMode) {
      // In template mode, only ensure documents are uploaded and IDs are available.
      return saveDocuments();
    }
    // Save all components: documents, recipients, fields, and message metadata
    const savedDocumentIds = await saveDocuments();
    await saveRecipients();
    await saveFields();
    await saveMessage();
    return { savedDocumentIds };
  }, [saveDocuments, saveRecipients, saveFields, saveMessage, isTemplateMode]);

  const waitForSaveLockIdle = useCallback(async (maxMs = 30000) => {
    const start = Date.now();
    while (savingLockRef.current && Date.now() - start < maxMs) {
      await new Promise((r) => setTimeout(r, 50));
    }
    if (savingLockRef.current) {
      throw new Error('Another save is still running. Please wait a moment and try Send again.');
    }
  }, []);

  const verifyEnvelopeReadyToSend = useCallback(() => {
    const files = uploadedFilesRef.current || [];
    const ids = documentIdsRef.current || [];
    const limitsError = validateEnvelopeLimits({
      files,
      recipientsList: recipients,
      documentPagesMap: documentPages,
      requirePageCounts: true,
    });
    if (limitsError) {
      throw new Error(limitsError);
    }
    if (files.length === 0) {
      throw new Error('No documents to send.');
    }
    if (ids.length < files.length) {
      throw new Error('Not all documents were saved to the server. Please try Send again.');
    }
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      const id = ids[i];
      const needsId = f instanceof File && !f.fromApi;
      if (needsId && (id === undefined || id === null || id === '')) {
        throw new Error(`Document "${f?.name || i + 1}" is still saving. Please try Send again.`);
      }
    }
    const fields = documentFieldsRef.current || [];
    const unsynced = fields.filter((f) => {
      const mappedId = f?.id ? fieldIdMapRef.current[f.id] : null;
      return !(f?.apiId || f?.field_id || mappedId);
    });
    if (unsynced.length > 0) {
      throw new Error(
        `${unsynced.length} field(s) are not saved to the server yet. Fix any errors or wait, then try Send again.`
      );
    }
    if (!draftEnvelopeIdRef.current) {
      throw new Error('Envelope was not created. Please re-upload your documents.');
    }
  }, [documentPages, recipients, validateEnvelopeLimits]);

  /**
   * Before send: finish in-flight autosaves, upload any pending PDFs, sync fields.
   * Recipients/message/metadata go through pre-send-sync only — not envelope-create.
   */
  const prepareEnvelopeForSend = useCallback(async () => {
    if (isTemplateMode) {
      return saveDraft();
    }
    orchestratedDraftSyncRef.current = true;
    try {
      await waitForSaveLockIdle();
      await saveDocuments();
      await saveFields({ scope: 'all' });
      await waitForSaveLockIdle();
      await flushDeferredDocumentApiTasks();
      return { savedDocumentIds: [...(documentIdsRef.current || [])] };
    } finally {
      orchestratedDraftSyncRef.current = false;
    }
  }, [
    isTemplateMode,
    saveDraft,
    saveDocuments,
    saveFields,
    waitForSaveLockIdle,
    flushDeferredDocumentApiTasks,
  ]);

  /** Call pre-send-sync until success; only the exact missing-documents response triggers upload + another pre-send. */
  const runPreSendSyncUntilReady = useCallback(
    async (envelopeId) => {
      const maxRounds = 8;
      let lastResponse = null;
      for (let round = 0; round < maxRounds; round += 1) {
        const payload = buildPreSendSyncPayload();
        lastResponse = await preSendEnvelopeSync(envelopeId, payload);

        if (!lastResponse || typeof lastResponse !== 'object') {
          throw new Error('Unexpected response from pre-send sync. Please try again.');
        }

        const layer =
          lastResponse.data != null && typeof lastResponse.data === 'object'
            ? lastResponse.data
            : lastResponse;

        if (layer.success === false) {
          if (isPreSendSyncMissingDocumentsOnlyFailure(lastResponse)) {
            const missing = extractPreSendSyncMissingDocuments(lastResponse);
            console.log(
              '📄 Pre-send sync: missing documents per server; uploading then retrying…',
              { round, missing, missing_document_ids: layer.missing_documents }
            );

            orchestratedDraftSyncRef.current = true;
            try {
              await saveDocuments();
            } finally {
              orchestratedDraftSyncRef.current = false;
            }
            await waitForSaveLockIdle();
            await new Promise((r) => setTimeout(r, 280));
            continue;
          }

          // Any other failure: show API message to the customer — do not call pre-send-sync again.
          throw new Error(formatPreSendSyncCustomerMessage(lastResponse));
        }

        return lastResponse;
      }

      throw new Error(
        'Could not sync the envelope before sending: documents are still reported as missing after upload attempts.'
      );
    },
    [
      buildPreSendSyncPayload,
      saveDocuments,
      waitForSaveLockIdle,
    ]
  );

  const saveTemplateDraft = useCallback(async (statusOverride = 'draft', options = {}) => {
    if (!isTemplateMode) return null;
    const force = Boolean(options.force);

    // Autosave can be in flight; user-initiated saves must wait, not no-op.
    if (force) {
      const start = Date.now();
      while (templateAutoSavingRef.current && Date.now() - start < 30000) {
        await new Promise((r) => setTimeout(r, 50));
      }
    } else if (statusOverride === 'draft' && templateAutoSavingRef.current) {
      return templateId || null;
    }

    const savedDocumentIds = await saveDocuments();
    const validDocumentIds = (Array.isArray(savedDocumentIds) && savedDocumentIds.length > 0
      ? savedDocumentIds
      : (documentIds || [])
    ).filter((id) => id != null);

    if (!templateId && validDocumentIds.length === 0) {
      // Don't create empty templates before user uploads docs.
      return null;
    }

    const payload = {
      template_id: templateId || undefined,
      name: templateName || subject || 'Untitled Template',
      subject,
      message,
      reminder_frequency: reminderFrequency,
      status: statusOverride,
      document_ids: validDocumentIds,
      workflow_type: signingOrder ? 'sequential' : 'parallel',
      recipients: convertRecipientsToAPIFormat(),
      fields: convertFieldsToAPIFormat(),
    };

    const fingerprint = JSON.stringify(payload);
    if (!force && statusOverride === 'draft' && fingerprint === lastTemplateSaveFingerprintRef.current) {
      return templateId || null;
    }

    try {
      templateAutoSavingRef.current = true;
      const created = await createTemplate(payload);
      const createdTemplateId =
        created?.data?.data?.template_id ||
        created?.data?.template_id ||
        created?.data?.data?.id ||
        created?.template_id ||
        created?.data?.id ||
        null;
      if (createdTemplateId) setTemplateId(createdTemplateId);
      lastTemplateSaveFingerprintRef.current = fingerprint;
      return createdTemplateId || templateId || null;
    } finally {
      templateAutoSavingRef.current = false;
    }
  }, [
    isTemplateMode,
    saveDocuments,
    documentIds,
    templateName,
    subject,
    message,
    reminderFrequency,
    signingOrder,
    convertRecipientsToAPIFormat,
    convertFieldsToAPIFormat,
    templateId,
  ]);

  const handleSaveAndClose = useCallback(async () => {
    if (isTemplateMode) {
      try {
        setFinalActionPendingKind('save');
        setIsSavingDraft(true);
        setIsFinalActionSubmitting(true);
        const savedTemplateId = await saveTemplateDraft('draft', { force: true });
        exitEditor('/templates', {
          action: 'saved',
          mode: 'template',
          navigateTo: 'templates',
          templateId: savedTemplateId || templateId,
        });
      } catch (error) {
        console.error('Error saving template:', error);
        const errorMessage = error.response?.data?.message
          || error.response?.data?.error
          || error?.message
          || 'Could not save the template. Please try again.';
        setUploadError(errorMessage);
        if (!isEmbedMode) {
          alert(errorMessage);
        }
      } finally {
        setIsSavingDraft(false);
        setIsFinalActionSubmitting(false);
        setFinalActionPendingKind(null);
      }
      return;
    }

    if (uploadedFiles.length === 0) {
      alert('Please upload at least one document.');
      return;
    }

    try {
      setFinalActionPendingKind('save');
      setIsSavingDraft(true);
      setIsFinalActionSubmitting(true);
      await prepareEnvelopeForSend();
      const envelopeIdToSync = draftEnvelopeIdRef.current || draftEnvelopeId;
      if (!envelopeIdToSync) {
        throw new Error('Envelope was not created. Please re-upload your documents.');
      }
      await runPreSendSyncUntilReady(envelopeIdToSync);
      exitEditor('/envelopes/drafts', {
        action: 'saved',
        mode: 'envelope',
        navigateTo: 'envelopes',
        envelopeId: envelopeIdToSync,
      });
    } catch (error) {
      console.error('Error saving envelope:', error);
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error?.message
        || 'Could not save the envelope. Please try again.';
      setUploadError(errorMessage);
      if (!isEmbedMode) {
        alert(errorMessage);
      }
    } finally {
      setIsSavingDraft(false);
      setIsFinalActionSubmitting(false);
      setFinalActionPendingKind(null);
    }
  }, [
    isTemplateMode,
    uploadedFiles.length,
    prepareEnvelopeForSend,
    draftEnvelopeId,
    runPreSendSyncUntilReady,
    saveTemplateDraft,
    exitEditor,
    templateId,
    isEmbedMode,
  ]);

  const handleDiscard = useCallback(() => {
    setShowDiscardConfirmModal(true);
  }, []);

  const confirmDiscard = useCallback(async () => {
    setShowDiscardConfirmModal(false);

    const discardedTemplateId = templateId;
    const discardedEnvelopeId = draftEnvelopeIdRef.current || draftEnvelopeId;

    try {
      setIsSavingDraft(true);
      setIsFinalActionSubmitting(true);

      if (isTemplateMode) {
        if (discardedTemplateId) {
          await deleteTemplate(discardedTemplateId);
        }
      } else if (discardedEnvelopeId) {
        await deleteEnvelope(discardedEnvelopeId);
      }

      localStorage.removeItem('current_draft_document_id');
      localStorage.removeItem('current_draft_envelope_id');
      exitEditor(isTemplateMode ? '/templates' : '/envelopes/drafts', {
        action: 'discarded',
        mode: isTemplateMode ? 'template' : 'envelope',
        navigateTo: isTemplateMode ? 'templates' : 'envelopes',
        templateId: isTemplateMode ? discardedTemplateId : undefined,
        envelopeId: isTemplateMode ? undefined : discardedEnvelopeId,
      });
    } catch (error) {
      console.error('Error discarding draft:', error);
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error?.message
        || 'Could not discard the draft. Please try again.';
      setUploadError(errorMessage);
      if (!isEmbedMode) {
        alert(errorMessage);
      }
    } finally {
      setIsSavingDraft(false);
      setIsFinalActionSubmitting(false);
    }
  }, [isTemplateMode, templateId, draftEnvelopeId, exitEditor, isEmbedMode]);

  const finishSuccessfulSend = useCallback(() => {
    localStorage.removeItem('current_draft_document_id');
    localStorage.removeItem('current_draft_envelope_id');
    setTemplateId(null);
    if (!isEmbedMode) {
      alert('Envelope sent successfully! Recipients will receive email notifications.');
    }
    exitEditor('/envelopes/drafts', {
      action: 'sent',
      mode: 'envelope',
      navigateTo: 'envelopes',
      envelopeId: draftEnvelopeIdRef.current || draftEnvelopeId,
    });
  }, [isEmbedMode, exitEditor, draftEnvelopeId]);

  const executeEnvelopeSend = useCallback(async (envelopeIdToSend) => {
    await runPreSendSyncUntilReady(envelopeIdToSend);
    await sendEnvelope(envelopeIdToSend);
    finishSuccessfulSend();
  }, [runPreSendSyncUntilReady, finishSuccessfulSend]);

  const handlePayPerEnvelopeComplete = useCallback(async () => {
    const envelopeId = payPerEnvelopeEnvelopeId;
    setShowPayPerEnvelopeModal(false);
    if (!envelopeId) return;
    try {
      setFinalActionPendingKind('send');
      setIsSavingDraft(true);
      setIsFinalActionSubmitting(true);
      await executeEnvelopeSend(envelopeId);
    } catch (error) {
      console.error('Error sending envelope after payment:', error);
      if (isQuotaExceededError(error)) {
        const quotaRaw = syncSubscriptionFromQuotaError(error);
        if (quotaRaw) dispatch(syncEnvelopeQuotaFromApi(quotaRaw));
        const ppe = getPayPerEnvelopeFromError(error);
        if (ppe?.enabled) {
          setPayPerEnvelopeEnvelopeId(envelopeId);
          setShowPayPerEnvelopeModal(true);
          setUploadError(getQuotaExceededMessage(error));
          return;
        }
        setQuotaModalMessage(getQuotaExceededMessage(error));
        setShowQuotaModal(true);
        setUploadError(getQuotaExceededMessage(error));
        return;
      }
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error?.message
        || 'Error sending envelope. Please try again.';
      setUploadError(errorMessage);
      alert(errorMessage);
    } finally {
      setIsSavingDraft(false);
      setIsFinalActionSubmitting(false);
      setFinalActionPendingKind(null);
    }
  }, [
    payPerEnvelopeEnvelopeId,
    executeEnvelopeSend,
    dispatch,
  ]);

  const openQuotaBlockedUi = useCallback((subscription, message, envelopeId = null, error = null) => {
    const ppeFromError = error ? getPayPerEnvelopeFromError(error) : null;
    const payEnabled = ppeFromError?.enabled === true || isPayPerEnvelopeEnabled(subscription);
    if (payEnabled && envelopeId) {
      setQuotaModalMessage(message);
      setPayPerEnvelopeEnvelopeId(envelopeId);
      setShowPayPerEnvelopeModal(true);
      setUploadError(message);
      return;
    }
    setQuotaModalMessage(message);
    setShowQuotaModal(true);
    setUploadError(message);
  }, []);

  const handleViewPlansFromEditor = useCallback(() => {
    if (isEmbedMode) {
      exitEditor('/plans', {
        action: 'upgrade_required',
        mode: isTemplateMode ? 'template' : 'envelope',
        navigateTo: 'plans',
        envelopeId: draftEnvelopeIdRef.current || draftEnvelopeId || undefined,
        templateId: isTemplateMode ? templateId : undefined,
      });
      return;
    }
    navigate('/plans');
  }, [isEmbedMode, exitEditor, isTemplateMode, draftEnvelopeId, templateId, navigate]);

  // Final send function
  const handleFinalSend = useCallback(async () => {
    // Validate required fields first
    if (uploadedFiles.length === 0) {
      alert('Please upload at least one document.');
      return;
    }

    if (!subject || subject.trim() === '') {
      alert('Please enter a subject for the envelope.');
      return;
    }

    if (!isOnlySigner && recipients.length === 0) {
      alert('Please add at least one recipient.');
      return;
    }

    if (!isOnlySigner && recipients.some((r, idx) => !isRecipientValidForCurrentMode(r, idx))) {
      const invalidEmail = recipients.some((r, idx) => {
        if (isTemplateMode && roleToRecipientType(r.role) === 'signee' && r.isVariable) return false;
        const e = String(r.email || '').trim();
        return e.length > 0 && !isValidEmailAddress(e);
      });
      alert(invalidEmail
        ? 'One or more email addresses are invalid. Please enter a valid address for each recipient.'
        : (isTemplateMode
          ? 'Please ensure all recipients have valid data (name/email or placeholder for variable signees).'
          : 'Please ensure all recipients have a valid name and email.'));
      setRecipientFieldErrorsVisible(true);
      return;
    }

    if (documentFields.length === 0) {
      alert('Please place at least one field on the document.');
      return;
    }

    if (!allowSendWithoutFieldsCheckRef.current) {
      const signeeRecipientsWithoutFields = signeeRecipients.filter((recipient) => {
        const recipientIndex = recipients.findIndex((r) => r === recipient);
        const selectionKey = getRecipientSelectionKey(recipient, recipientIndex);
        return !documentFields.some((field) =>
          fieldBelongsToEnvelopeRecipient(field, recipient, selectionKey)
        );
      });

      if (signeeRecipientsWithoutFields.length > 0) {
        setRecipientsWithoutFields(signeeRecipientsWithoutFields);
        setShowRecipientWithoutFieldsModal(true);
        return;
      }
    }

    allowSendWithoutFieldsCheckRef.current = false;

    const readOnlyFieldsMissingValue = documentFields.filter((f) => {
      if (!Boolean(f.readOnly ?? f.read_only)) return false;
      const v = getFieldValueForApi(f);
      return String(v ?? '').trim() === '';
    });
    if (readOnlyFieldsMissingValue.length > 0) {
      const names = readOnlyFieldsMissingValue
        .map((f) => String(f.label || f.type || 'Field').trim() || 'Field')
        .join(', ');
      alert(
        `Read-only fields must have a value before sending.\n\nPlease add a value for: ${names}\n\n(Turn on “Read only” in field properties and fill “Field value”, or double‑click the field on the document.)`
      );
      return;
    }

    try {
      setFinalActionPendingKind('send');
      setIsSavingDraft(true);
      setIsFinalActionSubmitting(true);

      await prepareEnvelopeForSend();
      verifyEnvelopeReadyToSend();

      const envelopeIdToSend = draftEnvelopeIdRef.current || draftEnvelopeId;
      console.log('📧 Preparing to send envelope:', envelopeIdToSend);

      if (isEnvelopeSendQuotaExceeded(userSubscription)) {
        openQuotaBlockedUi(
          userSubscription,
          getEnvelopeQuotaExceededMessage(userSubscription),
          envelopeIdToSend
        );
        return;
      }

      await executeEnvelopeSend(envelopeIdToSend);
    } catch (error) {
      console.error('Error sending envelope:', error);
      if (isQuotaExceededError(error)) {
        const quotaRaw = syncSubscriptionFromQuotaError(error);
        if (quotaRaw) dispatch(syncEnvelopeQuotaFromApi(quotaRaw));
        const envelopeIdToSend = draftEnvelopeIdRef.current || draftEnvelopeId;
        openQuotaBlockedUi(
          userSubscription,
          getQuotaExceededMessage(error),
          envelopeIdToSend,
          error
        );
        return;
      }
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error?.message ||
                          'Error sending envelope. Please try again.';
      setUploadError(errorMessage);
      alert(errorMessage);
    } finally {
      setIsSavingDraft(false);
      setIsFinalActionSubmitting(false);
      setFinalActionPendingKind(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    templateId,
    draftEnvelopeId,
    recipients,
    subject,
    message,
    templateName,
    reminderFrequency,
    isOnlySigner,
    documentFields,
    prepareEnvelopeForSend,
    verifyEnvelopeReadyToSend,
    isRecipientValidForCurrentMode,
    executeEnvelopeSend,
    openQuotaBlockedUi,
    dispatch,
    signeeRecipients,
    getRecipientSelectionKey,
    userSubscription,
  ]);

  const handleFinalSaveTemplate = useCallback(async () => {
    if (uploadedFiles.length === 0) {
      alert('Please upload at least one document.');
      return;
    }

    if (!subject || subject.trim() === '') {
      alert('Please enter a subject for the template.');
      return;
    }

    if (!isOnlySigner && recipients.length === 0) {
      alert('Please add at least one recipient.');
      return;
    }

    if (!isOnlySigner && recipients.some((r, idx) => !isRecipientValidForCurrentMode(r, idx))) {
      const invalidEmail = recipients.some((r, idx) => {
        if (roleToRecipientType(r.role) === 'signee' && r.isVariable) return false;
        const e = String(r.email || '').trim();
        return e.length > 0 && !isValidEmailAddress(e);
      });
      alert(invalidEmail
        ? 'One or more email addresses are invalid. Please enter a valid address for each recipient.'
        : 'Please ensure all recipients have valid data (name/email or placeholder for variable signees).');
      setRecipientFieldErrorsVisible(true);
      return;
    }

    try {
      setFinalActionPendingKind('save');
      setIsSavingDraft(true);
      setIsFinalActionSubmitting(true);
      const savedTemplateId = await saveTemplateDraft('complete', { force: true });

      localStorage.removeItem('current_draft_document_id');
      localStorage.removeItem('current_draft_envelope_id');

      if (!isEmbedMode) {
        alert('Template saved successfully!');
      }
      exitEditor('/templates', {
        action: 'saved',
        mode: 'template',
        navigateTo: 'templates',
        templateId: savedTemplateId || templateId,
      });
    } catch (error) {
      console.error('Error saving template:', error);
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || 'Error saving template. Please try again.';
      if (!isEmbedMode) {
        alert(errorMessage);
      }
    } finally {
      setIsSavingDraft(false);
      setIsFinalActionSubmitting(false);
      setFinalActionPendingKind(null);
    }
  }, [
    uploadedFiles,
    subject,
    saveTemplateDraft,
    isOnlySigner,
    recipients,
    isRecipientValidForCurrentMode,
    isEmbedMode,
    exitEditor,
    templateId,
  ]);

  // Template mode autosave (backend-backed, debounced)
  useEffect(() => {
    if (!isTemplateMode || !templateReadyRef.current) return undefined;

    if (templateAutoSaveTimeoutRef.current) {
      clearTimeout(templateAutoSaveTimeoutRef.current);
    }

    templateAutoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        await saveTemplateDraft('draft');
        setLastSavedAt(new Date());
      } catch (error) {
        console.error('Template autosave failed:', error);
      }
    }, 1500);

    return () => {
      if (templateAutoSaveTimeoutRef.current) {
        clearTimeout(templateAutoSaveTimeoutRef.current);
      }
    };
  }, [
    isTemplateMode,
    uploadedFiles,
    documentIds,
    recipients,
    documentFields,
    subject,
    message,
    reminderFrequency,
    signingOrder,
    saveTemplateDraft,
  ]);

  const handleFinalAction = isTemplateMode ? handleFinalSaveTemplate : handleFinalSend;

  // Load draft document on mount (using envelopeId from URL params)
  useEffect(() => {
    const loadDraft = async () => {
      // Prevent autosave effects from firing while restoring API state.
      isHydratingDraftRef.current = true;
      setIsDraftAssetsPreparing(false);
      if (draftHydrationTimeoutRef.current) {
        clearTimeout(draftHydrationTimeoutRef.current);
      }
      try {
        // If there is no envelopeId in the URL, this is a NEW envelope.
        // Clear any stale draft IDs and skip loading.
        if (!urlEnvelopeId) {
          localStorage.removeItem('current_draft_envelope_id');
          localStorage.removeItem('current_draft_document_id');
          return;
        }

        // Editing an existing envelope: load it by ID from URL
        const envelopeIdToLoad = String(urlEnvelopeId || '').trim();

        if (envelopeIdToLoad) {
          // Load from envelope using URL parameter or localStorage (backward compatibility)
          const envelopeId = envelopeIdToLoad;
          console.log('📋 Loading envelope from URL/localStorage:', envelopeId);

          // getDraftEnvelope/getEnvelope may return different shapes:
          // - { id, subject, recipients, fields, ... }
          // - { data: { ...envelope } }
          // - { data: { envelope: { ... } } }
          let rawEnvelope = null;
          try {
            rawEnvelope = await getDraftEnvelope(envelopeId);
            console.log('📦 Raw draft-envelope response:', rawEnvelope);
          } catch (draftErr) {
            console.warn('⚠️ getDraftEnvelope failed, falling back to getEnvelope:', draftErr);
            rawEnvelope = await getEnvelope(envelopeId);
            console.log('📦 Raw envelope response (fallback):', rawEnvelope);
          }

          const draft =
            (rawEnvelope && rawEnvelope.envelope) ||
            (rawEnvelope && rawEnvelope.data && (rawEnvelope.data.envelope || rawEnvelope.data)) ||
            rawEnvelope;

          const statusLower = (draft?.status || '').toLowerCase();
          const isEditableEnvelope = !statusLower || statusLower === 'draft';

          if (draft && isEditableEnvelope) {
            // Prefer envelope ID from API, fall back to the one we requested
            const finalEnvelopeId = draft.id || draft.envelope_id || envelopeId;
            setDraftEnvelopeId(finalEnvelopeId);
            localStorage.setItem('current_draft_envelope_id', String(finalEnvelopeId));
            
            // Load document data if available
            // IMPORTANT: Always explicitly set uploadedFiles and documentIds from the API response.
            // This ensures deleted documents don't persist from stale/cached state.
            let docIds = [];
            let restoredFiles = [];

            if (draft.documents && Array.isArray(draft.documents) && draft.documents.length > 0) {
              // Handle multiple documents
              docIds = draft.documents
                .map(doc => {
                  const raw =
                    doc.original_document_id ??
                    doc.document_id ??
                    doc.id;
                  const n = typeof raw === 'string' ? parseInt(raw, 10) : raw;
                  return Number.isFinite(n) ? n : null;
                })
                .filter(Boolean);

              if (docIds.length > 0) {
                // Hydrate uploadedFiles with placeholder entries so the UI shows existing documents.
                // These are not real File objects; they are just for display and alignment with documentIds.
                restoredFiles = draft.documents.map(doc => ({
                  name: doc.title || 'Document',
                  size: 0,
                  fromApi: true,
                  documentId: (doc.original_document_id ?? doc.document_id ?? doc.id),
                  documentUrl: doc.document_url,
                }));
              }
            } else if (draft.document || draft.document_id) {
              // Handle single document (legacy)
              const rawDocId =
                draft.document?.original_document_id ??
                draft.document_id ??
                draft.document?.document_id ??
                draft.document?.id;
              const docId = typeof rawDocId === 'string' ? parseInt(rawDocId, 10) : rawDocId;
              if (docId) {
                docIds = [docId];
                restoredFiles = [
                  {
                    name:
                      (draft.document && (draft.document.title || draft.document.document_title)) ||
                      'Document',
                    size: 0,
                    fromApi: true,
                    documentId: docId,
                    documentUrl: draft.document?.document_url,
                  },
                ];
              }
            }

            // Always set from API response — clears stale data if API returns no documents
            envelopeRowsCommitRef.current(mapFilesAndIdsToEnvelopeRows(restoredFiles, docIds));
            if (restoredFiles.length > 0) {
              // Scenario 2: editing a saved draft -> block Step 1 "Next" until docs + SVGs are ready.
              setIsDraftAssetsPreparing(true);
              try {
                await prewarmDocumentSvgs(restoredFiles);
              } catch (prewarmError) {
                console.warn('⚠️ Draft SVG prewarm finished with errors:', prewarmError);
              }
              setIsDraftAssetsPreparing(false);
            }
            if (docIds.length > 0) {
              localStorage.setItem('current_draft_document_id', docIds[0].toString());
            } else {
              localStorage.removeItem('current_draft_document_id');
            }
            
            // Load subject, message, reminder_frequency
            if (draft.subject) setSubject(draft.subject);
            if (draft.message) setMessage(draft.message);
            if (draft.reminder_frequency) setReminderFrequency(draft.reminder_frequency);
            
              // Load recipients if available
            let loadedRecipients = [];
            if (draft.recipients && draft.recipients.length > 0) {
              loadedRecipients = draft.recipients.map((r, index) => {
                // Store recipient ID in the object for easier access
                const recipientId = r.id ?? r.recipient_id ?? r.envelope_recipient_id ?? null;
                if (r.email && recipientId) {
                  recipientIdMapRef.current[r.email] = recipientId;
                }
                
                const apiType = r.type || r.recipient_type;
                const role = apiType === 'signee' ? 'Needs to Sign' : apiType === 'review_recipient' ? 'Needs to Review' : 'Receives a Copy';
                const placeholder = r.signee_placeholder || '';
                const resolvedEmail = r.email || r.resolved_email || '';
                const resolvedName = r.name || r.resolved_name || '';
                const hasResolvedIdentity = Boolean(resolvedEmail && resolvedName);
                const localKey = Boolean(placeholder) && !hasResolvedIdentity
                  ? `placeholder:${placeholder}`
                  : resolvedEmail
                    ? `email:${resolvedEmail}`
                    : recipientId != null
                      ? `id:${recipientId}`
                      : `index:${index}`;
                const rawSigOrder =
                  r.signature_order ?? r.signing_order ?? r.signatureOrder;
                const signature_order =
                  rawSigOrder != null && rawSigOrder !== '' && !Number.isNaN(Number(rawSigOrder))
                    ? Number(rawSigOrder)
                    : undefined;
                return {
                  id: recipientId,
                  name: resolvedName || '',
                  email: resolvedEmail,
                  role,
                  color: r.signee_color || getRecipientColor(index),
                  isVariable: Boolean(placeholder) && !hasResolvedIdentity,
                  placeholder,
                  localKey,
                  signature_order,
                };
              });
              const isSequentialDraft = draft.workflow_type === 'sequential';
              const draftSeqInputs = loadedRecipients.map((row, idx) => row.signature_order ?? idx + 1);
              const orderedDraftRecipients = isSequentialDraft
                ? sortRecipientsBySigningOrder(loadedRecipients, draftSeqInputs)
                : { recipients: loadedRecipients, sequenceInputs: draftSeqInputs };
              setRecipients(
                orderedDraftRecipients.recipients.length > 0
                  ? orderedDraftRecipients.recipients
                  : [{ name: '', email: '', role: 'Needs to Sign', color: '#6366f1', isVariable: false, placeholder: '' }]
              );
              setSequenceInputs(orderedDraftRecipients.sequenceInputs);
              
              // Check if sequential workflow
              if (isSequentialDraft) {
                setSigningOrder(true);
              }
            }
            
            // Load message if available
            if (draft.subject) setSubject(draft.subject);
            if (draft.message) setMessage(draft.message);
            
            // Defer heavy field hydration until Step 2, so page switch stays immediate.
            let allFields = [];
            if (draft.fields && draft.fields.length > 0) {
              allFields = draft.fields;
            }
            console.log('✅ Found fields in envelope response:', allFields.length);
            if (allFields.length === 0) {
              setDocumentFields([]);
              lastSavedFieldsRef.current = [];
              setPendingDraftFieldHydration(null);
            } else {
              setPendingDraftFieldHydration({ allFields, loadedRecipients });
            }
          } else if (draft) {
            console.warn(`⚠️ Envelope ${envelopeId} is not editable in CreateEnvelope. Status: ${draft.status}`);
            if (statusLower === 'revoked') {
              if (isEmbedMode) {
                notifyEmbedParent({
                  action: 'not_editable',
                  mode: 'envelope',
                  navigateTo: 'envelopes',
                  envelopeId,
                  status: 'revoked',
                });
                setEmbedExitPending(true);
              } else {
                navigate(`/envelopes/${envelopeId}/progress`, { replace: true });
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading draft:', error);
        const idForCopy = urlEnvelopeId ? String(urlEnvelopeId).trim() : '';
        if (isRevokedEnvelopeBlockedError(error) && idForCopy) {
          const shouldCopy = window.confirm(
            'This envelope was revoked. Create a new draft copy to continue editing?'
          );
          if (shouldCopy) {
            try {
              const copyResponse = await copyEnvelope(idForCopy);
              const copiedId = getCopiedEnvelopeId(copyResponse);
              if (copiedId) {
                navigate(
                  isEmbedMode ? `/embed/create-envelope/${copiedId}` : `/create-envelope/${copiedId}`,
                  { replace: true }
                );
                return;
              }
            } catch (copyErr) {
              console.error('Error copying revoked envelope:', copyErr);
            }
          }
        }
        localStorage.removeItem('current_draft_document_id');
        localStorage.removeItem('current_draft_envelope_id');
      } finally {
        // Wait one tick so all state updates from hydration settle before enabling autosave.
        draftHydrationTimeoutRef.current = setTimeout(() => {
          isHydratingDraftRef.current = false;
        }, 500);
        setIsDraftAssetsPreparing(false);
      }
    };

    loadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlEnvelopeId]); // Run when URL envelopeId changes

  // Hydrate draft fields after navigating to Step 2.
  useEffect(() => {
    if (currentStep !== 2 || !pendingDraftFieldHydration) return undefined;
    if (uploadedFiles.length > 0 && svgPageHeights.length === 0) return undefined;
    const payload = pendingDraftFieldHydration;
    let cancelled = false;
    isHydratingDraftRef.current = true;
    setIsDraftFieldsHydrating(true);
    let chunkTimerId = null;
    const scheduleChunk = (fn) => {
      chunkTimerId = window.setTimeout(fn, 0);
    };
    scheduleChunk(() => {
      try {
        const allFields = Array.isArray(payload?.allFields) ? payload.allFields : [];
        const loadedRecipients = Array.isArray(payload?.loadedRecipients) ? payload.loadedRecipients : [];
        if (allFields.length === 0) {
          lastSavedFieldsRef.current = [];
          setPendingDraftFieldHydration(null);
          setIsDraftFieldsHydrating(false);
          isHydratingDraftRef.current = false;
          return;
        }

        const recipientsByEmail = new Map();
        const recipientsById = new Map();
        const recipientsByPlaceholder = new Map();
        const recipientsByToken = new Map();
        loadedRecipients.forEach((recipient, ridx) => {
          if (recipient?.email) recipientsByEmail.set(String(recipient.email), { recipient, ridx });
          if (recipient?.id != null) recipientsById.set(String(recipient.id), { recipient, ridx });
          const placeholderToken = recipient?.placeholder || getVariableSigneePlaceholder(recipient, ridx);
          if (placeholderToken) recipientsByPlaceholder.set(String(placeholderToken), { recipient, ridx });
          if (recipient?.name) recipientsByToken.set(String(recipient.name).trim(), { recipient, ridx });
          if (placeholderToken) recipientsByToken.set(String(placeholderToken), { recipient, ridx });
        });

        const loadedFields = [];
        const mapFieldToLocal = (field, index) => {
        const localFieldId = Date.now() + index + Math.random();
        const apiFieldId = field.id || field.field_id;
        if (apiFieldId) fieldIdMapRef.current[localFieldId] = apiFieldId;

        let recipientEmail = field.recipient_email || field.recipient?.email;
        const recipientId = field.recipient_id;
        const recipientPlaceholder = field.signee_placeholder || field.recipient?.signee_placeholder || field.recipient?.placeholder || '';
        const fieldNameTokens = String(field.field_name || '')
          .split(' - ')
          .map((part) => String(part).trim())
          .filter(Boolean);
        const ownerToken = (() => {
          const firstToken = fieldNameTokens[0] || '';
          const secondToken = fieldNameTokens[1] || '';
          // Backward compatible:
          // - old labels: "Recipient - Field type" => first token
          // - new labels: "Field type - Recipient" => second token
          if (secondToken && recipientsByToken.has(String(secondToken))) return secondToken;
          if (firstToken && recipientsByToken.has(String(firstToken))) return firstToken;
          return firstToken || secondToken || '';
        })();

        let matched = null;
        if (recipientEmail && recipientsByEmail.has(String(recipientEmail))) matched = recipientsByEmail.get(String(recipientEmail));
        if (!matched && recipientId != null && recipientsById.has(String(recipientId))) matched = recipientsById.get(String(recipientId));
        if (!matched && recipientPlaceholder && recipientsByPlaceholder.has(String(recipientPlaceholder))) matched = recipientsByPlaceholder.get(String(recipientPlaceholder));
        if (!matched && ownerToken && recipientsByToken.has(String(ownerToken))) matched = recipientsByToken.get(String(ownerToken));
        if (recipientId && !recipientEmail && matched?.recipient?.email) recipientEmail = matched.recipient.email;

        const matchedRecipient = matched?.recipient || null;
        const matchedRecipientIndex = matched?.ridx ?? -1;
        const recipientColor =
          field.recipient?.signee_color ||
          field.signee_color ||
          matchedRecipient?.color ||
          getRecipientColor(matchedRecipientIndex >= 0 ? matchedRecipientIndex : index);
        const recipientName = matchedRecipient?.name || matchedRecipient?.email || ownerToken || recipientPlaceholder || '';
        const localRecipientKey =
          recipientId != null
            ? toRecipientIdSelectionKey(recipientId)
            : matchedRecipient?.localKey || (
              recipientPlaceholder && (matchedRecipient?.isVariable || !recipientEmail)
                ? `placeholder:${recipientPlaceholder}`
                : recipientEmail
                  ? `email:${recipientEmail}`
                  : ''
            );

        const apiDocumentId = field.document_id || null;
        const rawDocumentPageNumber = Number(field.page_number);
        const requestedDocumentPageNumber = Number.isFinite(rawDocumentPageNumber) && rawDocumentPageNumber > 0
          ? Math.floor(rawDocumentPageNumber)
          : 1;
        const apiDocumentIndex = getDocumentIndexForId(apiDocumentId);
        const apiDocumentPageCount = apiDocumentIndex >= 0
          ? getDocumentPageCountByIndex(apiDocumentIndex)
          : 1;
        const documentPageNumber = Math.min(
          Math.max(requestedDocumentPageNumber, 1),
          Math.max(1, apiDocumentPageCount)
        );
          return {
          id: localFieldId,
          apiId: apiFieldId,
          documentId: apiDocumentId,
          apiDocumentId,
          documentIndex: apiDocumentIndex >= 0 ? apiDocumentIndex : undefined,
          apiPageNumber: requestedDocumentPageNumber,
          documentPageNumber,
          apiRecipientId: recipientId,
          type: field.field_type,
          label: field.field_name || field.field_type,
          dataLabel: field.field_name || field.field_type,
          recipientEmail,
          recipientId: localRecipientKey,
          recipientPlaceholder,
          recipientColor,
          recipientName,
          x: field.x_position,
          y: field.y_position,
          width: field.width,
          height: field.height,
          page: documentPageNumber,
          required: field.is_required !== false,
          readOnly: field.read_only || false,
          value: mapApiFieldValueToLocal(field),
          valueHtml:
            (field.read_only || isInlineTextEditFieldType(field.field_type))
            && hasHtmlMarkup(field.field_value)
              ? unwrapReadOnlyFieldValueWrapper(field.field_value)
              : '',
          ...getFieldTypographyFromApiValue(field),
          ...(fieldTypeAllowsSingleOptionOnly(field.field_type)
            ? { options: parseChoiceFieldOptionsFromApi(field) }
            : {}),
        };
        };

        const chunkSize = 40;
        let cursor = 0;
        const processChunk = () => {
          if (cancelled) return;
          const end = Math.min(allFields.length, cursor + chunkSize);
          for (let i = cursor; i < end; i += 1) {
            loadedFields.push(mapFieldToLocal(allFields[i], i));
          }
          cursor = end;
          if (cursor < allFields.length) {
            scheduleChunk(processChunk);
            return;
          }

          if (cancelled) return;
          setDocumentFields(
            loadedFields
              .map(normalizeFieldOptionsForType)
              .map((f) =>
                (getReadOnlyFieldValueHtml(f)
                  ? fitReadOnlyFieldToContent(f)
                  : growReadOnlyFieldToFitContent(f))
              )
          );
          lastSavedFieldsRef.current = loadedFields
            .filter((f) => f.apiId)
            .map((f) => {
              const { documentId: docId, pageNumber } = getApiFieldDocumentAndPage(f);
              const id = f.apiId;
              return {
                id,
                sig: JSON.stringify({
                  id: String(id),
                  document_id: String(docId ?? ''),
                  recipient_id: String(f.apiRecipientId ?? ''),
                  field_type: f.type || '',
                  field_name: resolveFieldNameForApi(f),
                  page_number: pageNumber ?? '',
                  x_position: f.x ?? '',
                  y_position: f.y ?? '',
                  width: f.width ?? '',
                  height: f.height ?? '',
                  is_required: f.required !== false,
                  read_only: f.readOnly || false,
                  field_value: getFieldValueForApi(f),
                  ...(fieldTypeAllowsSingleOptionOnly(f.type)
                    ? { field_options: getFieldChoiceOptionsForApi(f) }
                    : {}),
                }),
              };
            });
          console.log('✅ Loaded', loadedFields.length, 'fields');
          setPendingDraftFieldHydration(null);
          setIsDraftFieldsHydrating(false);
          isHydratingDraftRef.current = false;
        };
        processChunk();
      } finally {
        // Keep loading state until final chunk commits or cancellation.
      }
    });
    return () => {
      cancelled = true;
      if (chunkTimerId) window.clearTimeout(chunkTimerId);
      setIsDraftFieldsHydrating(false);
      isHydratingDraftRef.current = false;
    };
  }, [
    currentStep,
    pendingDraftFieldHydration,
    uploadedFiles.length,
    svgPageHeights.length,
    getVariableSigneePlaceholder,
    getRecipientColor,
    getDocumentIndexForId,
    getDocumentPageCountByIndex,
    getApiFieldDocumentAndPage,
    getFieldValueForApi,
  ]);

  // Store the latest save functions in refs to avoid dependency issues
  useEffect(() => {
    saveDraftRef.current = { saveDocuments, saveRecipients, saveFields, saveMessage };
  }, [saveDocuments, saveRecipients, saveFields, saveMessage]);

  // Separate debounce timeouts for each save type to prevent interference
  const documentSaveTimeoutRef = useRef(null);
  const recipientSaveTimeoutRef = useRef(null);
  const fieldSaveTimeoutRef = useRef(null);
  const fieldImmediateSaveTimeoutRef = useRef(null);
  const messageSaveTimeoutRef = useRef(null);
  /** Updated when scheduling debounced field *updates*; used to ensure 2s idle before saveFields({ scope: 'updates' }) */
  const lastFieldEditTimeRef = useRef(0);
  /** Monotonic token to invalidate stale autosave timers. */
  const fieldAutosaveTokenRef = useRef(0);
  /** local_field_id -> { sigSent, dirtyAfterCreate } while create API is in-flight */
  const pendingFieldCreateRef = useRef(new Map());
  /** Incremental ids to correlate timer->save runs in logs. */
  const fieldAutosaveScheduleSeqRef = useRef(0);
  const fieldSaveRunSeqRef = useRef(0);
  const lastFieldAutosaveSourceRef = useRef('unknown');
  const scheduleFieldAutosaveRef = useRef(() => {});
  const scheduleImmediateFieldCreateDeleteSaveRef = useRef(() => {});
  const hadDocumentIdsRef = useRef(false);
  const wasSigningOrderDisabledRef = useRef(recipients.length < 2);

  // Auto-enable signing order when it first becomes available (2+ recipients).
  useEffect(() => {
    const isDisabled = recipients.length < 2;
    const justBecameEnabled = wasSigningOrderDisabledRef.current && !isDisabled;
    if (justBecameEnabled) {
      setSigningOrder(true);
      // Do not reset sequenceInputs here: draft/template hydration can jump 1 → N recipients
      // and would overwrite API signature_order values. handleAddRecipient already assigns 1..n.
    }
    wasSigningOrderDisabledRef.current = isDisabled;
  }, [recipients]);

  // Auto-save documents when files are uploaded/changed
  // Only saveDocuments should send files - this is the only one that handles documents
  // This ensures ALL files are sent even if uploads happen quickly
  useEffect(() => {
    if (isTemplateMode) return undefined;
    const pendingLocalUploads = uploadedFiles
      .map((file, index) => {
        if (!file || file.fromApi) return null;
        const docId = documentIds[index];
        const missingDocId = !(docId !== undefined && docId !== null && docId !== '' && docId !== 0);
        if (!missingDocId) return null;
        return {
          file,
          index,
          key: getDocumentCacheKey(file, index),
        };
      })
      .filter(Boolean);
    const pendingLocalUploadSignature = pendingLocalUploads
      .map((item) => item.key)
      .sort((a, b) => String(a).localeCompare(String(b)))
      .join('|');

    if (pendingLocalUploads.length === 0) {
      blockedDocumentUploadSignatureRef.current = '';
      blockedDocumentUploadMessageRef.current = '';
      return undefined;
    }

    if (blockedDocumentUploadSignatureRef.current) {
      if (blockedDocumentUploadSignatureRef.current === pendingLocalUploadSignature) {
        console.log('⏸️ Skipping document autosave - blocked by non-retryable limit error', {
          pendingCount: pendingLocalUploads.length,
          blockedMessage: blockedDocumentUploadMessageRef.current,
        });
        return undefined;
      }
      // Pending set changed (e.g. user removed/replaced files) -> allow new attempts.
      blockedDocumentUploadSignatureRef.current = '';
      blockedDocumentUploadMessageRef.current = '';
    }

    if (pendingLocalUploads.length > 0 && !isSavingDraft && !savingLockRef.current && saveDraftRef.current) {
      // Clear any pending document save
      if (documentSaveTimeoutRef.current) {
        clearTimeout(documentSaveTimeoutRef.current);
      }
      
      // Keep this short so uploads start quickly; saveDocuments handles batching/queueing.
      const waitMs = 150;
      console.log('📄 Files changed, scheduling document save (debounced)...', {
        fileCount: uploadedFiles.length,
        pendingCount: pendingLocalUploads.length,
        fileNames: pendingLocalUploads.map((item) => item.file?.name).filter(Boolean),
        waitMs,
      });
      
      // Store current file count to ensure we send all files
      const currentFileCount = uploadedFiles.length;
      // eslint-disable-next-line no-unused-vars
      const currentDocumentIdsCount = documentIds.length;
      
      documentSaveTimeoutRef.current = setTimeout(() => {
        // Check if more files were added during the debounce period
        // If so, we need to ensure all files without IDs are sent
        if (!savingLockRef.current && !isSavingDraft) {
          console.log('▶️ Executing document save', {
            currentFileCount,
            actualFileCount: uploadedFiles.length,
            documentIdsCount: documentIds.length
          });
          
          // Force save all files that don't have IDs yet
          // This ensures rapid uploads don't miss files
          Promise.resolve(saveDraftRef.current.saveDocuments()).catch((error) => {
            const message = error?.message || 'Failed to save documents.';
            // Prevent infinite autosave retry loops for the exact same pending file set.
            blockedDocumentUploadSignatureRef.current = pendingLocalUploadSignature;
            blockedDocumentUploadMessageRef.current = message;
            setUploadError(message);
          });
        } else {
          console.log('⏸️ Skipping document save - lock or saving state active');
        }
      }, waitMs);
    }
    
    return () => {
      if (documentSaveTimeoutRef.current) {
        clearTimeout(documentSaveTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles, documentIds, isSavingDraft, isTemplateMode, draftEnvelopeId, getDocumentCacheKey]);

  // Auto-save recipients when they change (only if envelope exists and has valid recipients)
  // If documents are currently saving, keep retrying until the lock is released so
  // recipient edits made during upload are flushed automatically.
  useEffect(() => {
    if (isTemplateMode) return undefined;
    // Only save if envelope exists, not only signer, and has valid recipients
    const hasValidRecipients = recipients.length > 0 && recipients.some((r, idx) => isRecipientValidForCurrentMode(r, idx));
    
    if (draftEnvelopeId && !isOnlySigner && hasValidRecipients && !isHydratingDraftRef.current && saveDraftRef.current) {
      // Clear any pending recipient save
      if (recipientSaveTimeoutRef.current) {
        clearTimeout(recipientSaveTimeoutRef.current);
      }
      
      // Debounce recipient saves
      const forceSave = forceRecipientSaveOnNextEffectRef.current;
      if (forceSave) {
        forceRecipientSaveOnNextEffectRef.current = false;
      }
      const debounceMs = forceSave ? 400 : 2000;
      console.log(
        `👥 Recipients changed, scheduling recipient save (debounced ${debounceMs}ms${forceSave ? ', forced' : ''})...`
      );
      const runRecipientSave = () => {
        if (!savingLockRef.current) {
          console.log('▶️ Executing recipient save');
          saveDraftRef.current.saveRecipients({ force: forceSave });
        } else {
          // Document save is still in-flight; retry shortly instead of dropping this change.
          recipientSaveTimeoutRef.current = setTimeout(runRecipientSave, 350);
        }
      };
      recipientSaveTimeoutRef.current = setTimeout(runRecipientSave, debounceMs);
    }
    
    return () => {
      if (recipientSaveTimeoutRef.current) {
        clearTimeout(recipientSaveTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipients, isOnlySigner, signingOrder, sequenceInputs, draftEnvelopeId, isRecipientValidForCurrentMode, isTemplateMode]);

  scheduleImmediateFieldCreateDeleteSaveRef.current = (source = 'unknown') => {
    if (fieldImmediateSaveTimeoutRef.current) {
      clearTimeout(fieldImmediateSaveTimeoutRef.current);
    }
    fieldImmediateSaveTimeoutRef.current = setTimeout(() => {
      if (shouldPauseFieldSaveDuringInteraction()) {
        scheduleImmediateFieldCreateDeleteSaveRef.current(source);
        return;
      }
      if (
        saveDraftRef.current &&
        !savingLockRef.current &&
        !isHydratingDraftRef.current
      ) {
        lastFieldAutosaveSourceRef.current = source;
        saveDraftRef.current.saveFields({ scope: 'deletes-and-creates' });
      }
    }, 120);
  };

  scheduleFieldAutosaveRef.current = (source = 'unknown') => {
    fieldAutosaveTokenRef.current += 1;
    const token = fieldAutosaveTokenRef.current;
    const scheduleSeq = ++fieldAutosaveScheduleSeqRef.current;
    lastFieldAutosaveSourceRef.current = source;
    const now = Date.now();
    lastFieldEditTimeRef.current = now;
    logFieldAutosaveDebug('⏱️ [fields-autosave] schedule requested', {
      at: new Date(now).toISOString(),
      scheduleSeq,
      source,
      token,
      debounceMs: FIELD_AUTOSAVE_DEBOUNCE_MS,
      hasPendingTimer: !!fieldSaveTimeoutRef.current,
      fieldsCount: documentFieldsRef.current?.length || 0,
      savingLockActive: savingLockRef.current,
      hasSaveDraftRef: !!saveDraftRef.current,
    });
    if (fieldSaveTimeoutRef.current) {
      logFieldAutosaveDebug('⏱️ [fields-autosave] clearing previous pending timer');
      clearTimeout(fieldSaveTimeoutRef.current);
    }
    const tick = () => {
      if (token !== fieldAutosaveTokenRef.current) {
        logFieldAutosaveDebug('⏱️ [fields-autosave] stale timer ignored', {
          scheduleSeq,
          source,
          token,
          current: fieldAutosaveTokenRef.current,
        });
        return;
      }
      const tickNow = Date.now();
      const idleMs = tickNow - lastFieldEditTimeRef.current;
      logFieldAutosaveDebug('⏱️ [fields-autosave] timer check', {
        at: new Date(tickNow).toISOString(),
        scheduleSeq,
        source,
        token,
        idleMs,
        requiredIdleMs: FIELD_AUTOSAVE_DEBOUNCE_MS,
        savingLockActive: savingLockRef.current,
        hasSaveDraftRef: !!saveDraftRef.current,
        fieldsCount: documentFieldsRef.current?.length || 0,
      });
      if (idleMs < FIELD_AUTOSAVE_DEBOUNCE_MS) {
        const waitMs = Math.max(1, FIELD_AUTOSAVE_DEBOUNCE_MS - idleMs);
        logFieldAutosaveDebug('⏱️ [fields-autosave] not idle long enough, waiting more', { waitMs });
        fieldSaveTimeoutRef.current = setTimeout(tick, waitMs);
        return;
      }
      fieldSaveTimeoutRef.current = null;
      if (shouldPauseFieldSaveDuringInteraction()) {
        fieldSaveTimeoutRef.current = setTimeout(tick, 150);
        return;
      }
      if (savingLockRef.current) {
        logFieldAutosaveDebug('⏱️ [fields-autosave] save lock active, retrying shortly', { scheduleSeq, source, token });
        fieldSaveTimeoutRef.current = setTimeout(tick, 250);
        return;
      }
      if (pendingFieldCreateRef.current.size > 0) {
        const pruneNow = Date.now();
        let releasedCount = 0;
        for (const [localId, meta] of pendingFieldCreateRef.current.entries()) {
          const ageMs = meta?.requestedAt ? pruneNow - meta.requestedAt : FIELD_PENDING_CREATE_MAX_MS + 1;
          if (ageMs > FIELD_PENDING_CREATE_MAX_MS) {
            pendingFieldCreateRef.current.delete(localId);
            releasedCount += 1;
          }
        }
        if (releasedCount > 0) {
          console.warn('⚠️ [fields-autosave] released stale pending create entries', { releasedCount });
        }
      }
      if (pendingFieldCreateRef.current.size > 0) {
        logFieldAutosaveDebug('⏱️ [fields-autosave] bulk field create in flight — holding debounced updates until field_id resolves', {
          scheduleSeq,
          source,
          token,
          pendingCount: pendingFieldCreateRef.current.size,
        });
        fieldSaveTimeoutRef.current = setTimeout(tick, 250);
        return;
      }
      if (saveDraftRef.current) {
        logFieldAutosaveDebug('▶️ [fields-autosave] firing saveFields({ scope: updates }) after idle window');
        saveDraftRef.current.saveFields({ scope: 'updates' });
      } else {
        logFieldAutosaveDebug('⏸️ [fields-autosave] skip fire: lock active or missing saveDraftRef');
      }
    };
    logFieldAutosaveDebug('⏱️ [fields-autosave] arming timer', { waitMs: FIELD_AUTOSAVE_DEBOUNCE_MS });
    fieldSaveTimeoutRef.current = setTimeout(tick, FIELD_AUTOSAVE_DEBOUNCE_MS);
  };

  // Auto-save fields when they change (only if envelope exists and has fields)
  useEffect(() => {
    if (isTemplateMode) return undefined;
    // Check for recipientEmail or recipientId (for backward compatibility)
    const hasFieldsWithRecipient = documentFields.some(f => f.recipientEmail || f.recipientId);
    const hasDocumentIds = documentIds.length > 0 && documentIds.some(id => id !== null && id !== undefined && id !== '');
    
    logFieldAutosaveDebug('📝 Fields change detected:', {
      draftEnvelopeId: !!draftEnvelopeId,
      documentFieldsCount: documentFields.length,
      hasFieldsWithRecipient,
      hasDocumentIds,
      isSavingDraft,
      savingLockActive: savingLockRef.current,
      hasSaveDraftRef: !!saveDraftRef.current
    });
    
    if (draftEnvelopeId && documentFields.length > 0 && hasFieldsWithRecipient && hasDocumentIds && !isSavingDraft && !isHydratingDraftRef.current && saveDraftRef.current) {
      logFieldAutosaveDebug('📝 Fields changed: immediate creates/deletes + debounced updates (2s idle)...', {
        fieldsCount: documentFields.length,
        fieldsWithRecipient: documentFields.filter(f => f.recipientEmail || f.recipientId).length
      });
      // Run immediate create/delete persistence (pointer-gated inside scheduler).
      if (!shouldPauseFieldSaveDuringInteraction()) {
      scheduleImmediateFieldCreateDeleteSaveRef.current('documentFields-immediate-creates-deletes');
      }
      // Always arm debounced updates; tick() waits until pointer/drag idle before calling saveFields.
      scheduleFieldAutosaveRef.current('documentFields-useEffect');
    } else {
      logFieldAutosaveDebug('⏸️ Field save conditions not met:', {
        hasEnvelope: !!draftEnvelopeId,
        hasFields: documentFields.length > 0,
        hasFieldsWithRecipient,
        hasDocumentIds,
        canSave: !isSavingDraft && !savingLockRef.current && !!saveDraftRef.current
      });
    }
    
    return () => {
      if (fieldSaveTimeoutRef.current) {
        logFieldAutosaveDebug('⏱️ [fields-autosave] cleanup cleared pending timer from effect');
        clearTimeout(fieldSaveTimeoutRef.current);
      }
      fieldAutosaveTokenRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentFields, draftEnvelopeId, documentIds, isTemplateMode]);

  // When document IDs become available, use the same scheduler (avoids a second faster timer racing the main debounce)
  useEffect(() => {
    if (isTemplateMode) return undefined;
    const hasFieldsWithRecipient = documentFields.some(f => f.recipientEmail || f.recipientId);
    const hasDocumentIds = documentIds.length > 0 && documentIds.some(id => id !== null && id !== undefined && id !== '');

    const hadDocumentIds = hadDocumentIdsRef.current;
    hadDocumentIdsRef.current = hasDocumentIds;
    if (!hasDocumentIds || hadDocumentIds) {
      return undefined;
    }

    if (draftEnvelopeId && hasFieldsWithRecipient && documentFields.length > 0 && !isSavingDraft && !isHydratingDraftRef.current && saveDraftRef.current) {
      logFieldAutosaveDebug('📝 Document IDs available — immediate creates/deletes + debounced updates:', {
        documentIds: documentIds,
        fieldsCount: documentFields.length,
      });
      if (!shouldPauseFieldSaveDuringInteraction()) {
      scheduleImmediateFieldCreateDeleteSaveRef.current('documentIds-available-immediate-creates-deletes');
      }
      scheduleFieldAutosaveRef.current('documentIds-available');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentIds.length, draftEnvelopeId, isTemplateMode, shouldPauseFieldSaveDuringInteraction]);

  // Auto-save message metadata when they change (only if envelope exists)
  useEffect(() => {
    if (isTemplateMode) return undefined;
    if (draftEnvelopeId && !isSavingDraft && !savingLockRef.current && !isHydratingDraftRef.current && saveDraftRef.current) {
      // Only save if there's actual content
      if (subject || message || reminderFrequency) {
        // Clear any pending message save
        if (messageSaveTimeoutRef.current) {
          clearTimeout(messageSaveTimeoutRef.current);
        }
        
        // Debounce message saves
        console.log('💬 Message metadata changed, scheduling message save (debounced 2s)...');
        messageSaveTimeoutRef.current = setTimeout(() => {
          if (!savingLockRef.current && !isSavingDraft) {
            console.log('▶️ Executing message save');
            saveDraftRef.current.saveMessage();
          } else {
            console.log('⏸️ Skipping message save - lock or saving state active');
          }
        }, 2000);
      }
    }
    
    return () => {
      if (messageSaveTimeoutRef.current) {
        clearTimeout(messageSaveTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, message, reminderFrequency, draftEnvelopeId, isTemplateMode]);
  
  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      if (documentSaveTimeoutRef.current) clearTimeout(documentSaveTimeoutRef.current);
      if (documentOrderSaveTimeoutRef.current) clearTimeout(documentOrderSaveTimeoutRef.current);
      if (recipientSaveTimeoutRef.current) clearTimeout(recipientSaveTimeoutRef.current);
      if (fieldSaveTimeoutRef.current) clearTimeout(fieldSaveTimeoutRef.current);
      if (fieldImmediateSaveTimeoutRef.current) clearTimeout(fieldImmediateSaveTimeoutRef.current);
      if (messageSaveTimeoutRef.current) clearTimeout(messageSaveTimeoutRef.current);
      if (draftHydrationTimeoutRef.current) clearTimeout(draftHydrationTimeoutRef.current);
      if (pointerInteractionReleaseTimeoutRef.current) clearTimeout(pointerInteractionReleaseTimeoutRef.current);
      // Bootstrap modals can leave body/backdrop behind after route change, blocking clicks.
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
      document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    };
  }, []);

  // Store document ID in localStorage when it changes (for draft resumption)
  useEffect(() => {
    const firstId = documentIds.find(id => id != null);
    if (firstId != null) {
      localStorage.setItem('current_draft_document_id', firstId.toString());
    }
  }, [documentIds]);


  // Save state to history for undo/redo
  const saveToHistory = (fieldsToSave) => {
    const fieldsCopy = cloneFieldsSnapshot(fieldsToSave);
    setHistory((prev) => {
      const newUndo = [...prev.undo, fieldsCopy];
      // Keep only last 5 operations
      if (newUndo.length > maxHistorySize) {
        newUndo.shift();
      }
      // Clear redo stack when new action is performed
      return { undo: newUndo, redo: [] };
    });
  };

  /** Place a standard field at a client (viewport) point on the document preview. */
  const placeFieldAtClientPoint = useCallback(
    (clientX, clientY, fieldType, grabOffsetX = 0, grabOffsetY = 0) => {
      if (!fieldType || isHiddenFieldType(fieldType)) return false;

      const recipient =
        (selectedRecipient &&
        signeeRecipients.some(
          (r) => getRecipientSelectionKey(r) === getRecipientSelectionKey(selectedRecipient)
        )
          ? selectedRecipient
          : null) || (signeeRecipients.length > 0 ? signeeRecipients[0] : null);
      if (!recipient) {
        alert('Please add at least one recipient with role "Needs to Sign" to assign fields.');
        return false;
      }

      const previewContainer = svgContainerRef.current;
      if (!previewContainer) return false;

      const previewRect = previewContainer.getBoundingClientRect();
      const mouseX = clientX - previewRect.left;
      const mouseY = clientY - previewRect.top;
      const currentScale = getCurrentScale();
      const docX = mouseX / currentScale;
      const docY = mouseY / currentScale;

      const placement = resolvePlacementFromAbsolutePoint(docX, docY, svgPageHeights);
      if (!placement.pageInfo || !placement.documentId) return false;

      const targetPage = placement.page;
      const targetDocumentId = placement.documentId;
      const pageOffsetX = placement.pageInfo?.offsetX || 0;
      const defaultWidth = 150;
      const defaultHeight = 40;
      const grabOffsetInDocX = grabOffsetX / currentScale;
      const grabOffsetInDocY = grabOffsetY / currentScale;
      const pageRelativeX = docX - pageOffsetX - grabOffsetInDocX;
      const pageRelativeY = placement.y - grabOffsetInDocY;

      const recipientIdx = recipients.findIndex(
        (r) => getRecipientSelectionKey(r) === getRecipientSelectionKey(recipient)
      );
      const apiRecipientId = resolveEnvelopeRecipientId(
        recipient,
        recipientIdMapRef.current || {}
      );
      const recipientKey = getRecipientSelectionKey(recipient, recipientIdx);
      const recipientName =
        recipient.name ||
        recipient.email ||
        getVariableSigneePlaceholder(recipient, recipientIdx >= 0 ? recipientIdx : 0);
      const typeLabels = {
        signature: 'Signature',
        initial: 'Initial',
        dateSigned: 'Date Signed',
        date: 'Date',
        name: 'Name',
        email: 'Email',
        company: 'Company',
        title: 'Title',
        text: 'Text',
        number: 'Number',
        checkbox: 'Checkbox',
        dropdown: 'Dropdown',
        radio: 'Radio',
        stamp: 'Stamp',
      };
      const autoLabel = `${
        typeLabels[fieldType] || fieldType.charAt(0).toUpperCase() + fieldType.slice(1)
      } - ${recipientName}`;

      const prevFields = documentFieldsRef.current || documentFields;
      const draftField = normalizeFieldOptionsForType({
        id: Date.now(),
        type: fieldType,
        label: autoLabel,
        dataLabel: autoLabel,
        recipientId: recipientKey,
        recipientEmail: recipient.email || '',
        ...(apiRecipientId != null ? { apiRecipientId } : {}),
        recipientPlaceholder: recipient.isVariable
          ? getVariableSigneePlaceholder(recipient, recipientIdx >= 0 ? recipientIdx : 0)
          : '',
        recipientName,
        recipientColor: recipient.color || getRecipientColor(recipientIdx >= 0 ? recipientIdx : 0),
        x: pageRelativeX,
        y: pageRelativeY,
        documentId: targetDocumentId,
        apiDocumentId: targetDocumentId,
        documentIndex: placement.documentIndex,
        page: targetPage,
        width: defaultWidth,
        height: defaultHeight,
        required: true,
        ...(fieldTypeAllowsSingleOptionOnly(fieldType) ? { options: [''] } : {}),
      });
      const newField = clampFieldXToDocumentPageWidth(draftField, placement.pageInfo);
      saveToHistory(prevFields);
      const nextFields = [...prevFields, newField];
      documentFieldsRef.current = nextFields;
      setDocumentFields(nextFields);
      return true;
    },
    [
      selectedRecipient,
      signeeRecipients,
      recipients,
      svgPageHeights,
      documentFields,
      getCurrentScale,
      resolvePlacementFromAbsolutePoint,
      clampFieldXToDocumentPageWidth,
      getRecipientColor,
      getVariableSigneePlaceholder,
      getRecipientSelectionKey,
    ]
  );

  // Helper function to update field with history tracking
  const updateFieldWithHistory = (updatedField) => {
    const prevFields = documentFieldsRef.current || documentFields;
    saveToHistory(prevFields); // Save state before change
    const nextFields = prevFields.map((f) =>
      f.id === updatedField.id ? updatedField : f
    );
    documentFieldsRef.current = nextFields;
    setDocumentFields(nextFields);
    setSelectedField(updatedField);
  };

  const handlePatchRecipient = useCallback(
    (index, patch) => {
      const oldRecipient = recipients[index];
      if (!oldRecipient) return;

      const newRecipient = { ...oldRecipient, ...patch };
      const oldKey = getRecipientSelectionKey(oldRecipient, index);
      const newKey = getRecipientSelectionKey(newRecipient, index);
      const oldDisplay = getRecipientDisplayNameForFieldLabel(
        oldRecipient,
        index,
        isTemplateMode,
        getVariableSigneePlaceholder
      );
      const newDisplay = getRecipientDisplayNameForFieldLabel(
        newRecipient,
        index,
        isTemplateMode,
        getVariableSigneePlaceholder
      );
      const oldEmail = String(oldRecipient.email || '').trim();
      const newEmail = String(newRecipient.email || '').trim();
      const displayChanged = oldDisplay !== newDisplay;
      const emailChanged = oldEmail !== newEmail;

      setRecipients((prev) =>
        prev.map((r, i) => (i === index ? newRecipient : r))
      );

      if (!displayChanged && !emailChanged) return;

      const resolvedRecipientApiId =
        newRecipient.id ??
        oldRecipient.id ??
        (oldEmail ? recipientIdMapRef.current[oldEmail] : null) ??
        null;

      if (emailChanged && resolvedRecipientApiId != null) {
        if (oldEmail && recipientIdMapRef.current[oldEmail] != null) {
          delete recipientIdMapRef.current[oldEmail];
        }
        const newEmailTrim = String(newRecipient.email || '').trim();
        if (newEmailTrim) {
          recipientIdMapRef.current[newEmailTrim] = resolvedRecipientApiId;
        }
      }

      const prevFields = documentFieldsRef.current || [];
      let anySynced = false;
      const variablePlaceholder = newRecipient.isVariable
        ? getVariableSigneePlaceholder(newRecipient, index)
        : '';

      const nextFields = prevFields.map((field) => {
        if (!fieldMatchesRecipientForSync(field, oldRecipient, oldKey)) return field;

        const syncLabel = shouldSyncFieldLabelForRecipient(field, oldDisplay, oldEmail);
        const newLabel = buildAutoFieldLabel(field.type, newDisplay);
        anySynced = true;

        const fieldApiRecipientId =
          field.apiRecipientId ?? resolvedRecipientApiId ?? null;
        const fieldRecipientKey =
          fieldApiRecipientId != null
            ? toRecipientIdSelectionKey(fieldApiRecipientId)
            : newKey;

        return {
          ...field,
          recipientId: fieldRecipientKey,
          recipientEmail: newRecipient.email || '',
          recipientPlaceholder: newRecipient.isVariable ? variablePlaceholder : '',
          recipientName: newDisplay,
          ...(fieldApiRecipientId != null ? { apiRecipientId: fieldApiRecipientId } : {}),
          ...(syncLabel ? { label: newLabel, dataLabel: newLabel } : {}),
        };
      });

      if (!anySynced) return;

      saveToHistory(prevFields);
      documentFieldsRef.current = nextFields;
      setDocumentFields(nextFields);

      const sel = selectedFieldRef.current;
      if (sel) {
        const nf = nextFields.find((f) => f.id === sel.id);
        if (
          nf &&
          (nf.label !== sel.label ||
            nf.dataLabel !== sel.dataLabel ||
            nf.recipientName !== sel.recipientName ||
            nf.recipientEmail !== sel.recipientEmail ||
            nf.recipientId !== sel.recipientId)
        ) {
          queueMicrotask(() => setSelectedField(nf));
        }
      }
    },
    [recipients, getRecipientSelectionKey, getVariableSigneePlaceholder, isTemplateMode]
  );

  const openInlineReadOnlyValueEditor = (field) => {
    const fresh =
      (documentFieldsRef.current || []).find((f) => f.id === field?.id) || field;
    if (!fieldSupportsInlineTextEdit(fresh)) return;

    setSelectedFields([fresh.id]);
    setSelectedField(fresh);
    setExpandedSections({
      formatting: false,
      dataLabel: false,
      tooltip: false,
      location: false,
      options: false,
    });
    const plainSeed =
      getReadOnlyFieldTextForMeasure(fresh) || getReadOnlyFieldCanvasDisplayValue(fresh) || '';
    const draftHtml = prepareReadOnlyValueHtml(
      getReadOnlyFieldValueHtml(fresh)
        || (plainSeed ? escapeHtml(plainSeed).replace(/\r?\n/g, '<br/>') : '')
    );
    const fitted = fitReadOnlyFieldToContent(fresh, draftHtml, {});
    if ((fitted.height || 40) !== (fresh.height || 40)) {
      const nextFields = (documentFieldsRef.current || documentFields).map((f) =>
        (f.id === fresh.id ? fitted : f)
      );
      documentFieldsRef.current = nextFields;
      setDocumentFields(nextFields);
      setSelectedField(fitted);
    }
    setInlineReadOnlyEditor({
      fieldId: fitted.id,
      draftHtml,
      fontFamily: fresh.fontFamily || 'Arial',
      fontSize: String(fresh.fontSize || '12'),
      fontColor: fresh.fontColor || '#000000',
      textAlign: fresh.textAlign || 'left',
    });
  };

  const applyEditorFieldLevelTypography = useCallback((fieldId, typographyPatch) => {
    if (!fieldId || !typographyPatch) return;
    const rawHtml = prepareReadOnlyValueHtml(typographyPatch.html || '');
    let updated = null;

    setDocumentFields((prevFields) => {
      const sourceField =
        prevFields.find((f) => f.id === fieldId)
        || (selectedField?.id === fieldId ? selectedField : null);
      if (!sourceField) return prevFields;

      const valueHtml = rawHtml;
      const stylePatch = {
        fontFamily: typographyPatch.fontFamily ?? sourceField.fontFamily ?? 'Arial',
        fontSize: String(typographyPatch.fontSize ?? sourceField.fontSize ?? '12'),
        fontColor: (() => {
          const raw = typographyPatch.fontColor ?? sourceField.fontColor ?? '#000000';
          if (raw === false || raw == null || raw === '' || raw === true) return '#000000';
          return String(raw);
        })(),
        textAlign: typographyPatch.textAlign ?? sourceField.textAlign ?? 'left',
      };
      const merged = {
        ...sourceField,
        value: stripHtmlForMeasure(valueHtml),
        valueHtml,
        ...stylePatch,
      };
      const neededHeight = computeReadOnlyFieldNeededHeight(merged, {
        textOverride: valueHtml,
        styleOverride: stylePatch,
      });
      updated = { ...merged, height: neededHeight };
      const nextFields = prevFields.map((f) => (f.id === fieldId ? updated : f));
      documentFieldsRef.current = nextFields;
      return nextFields;
    });
    if (!updated) return;

    setSelectedField((prev) => (prev?.id === fieldId ? updated : prev));
    setInlineReadOnlyEditor((prev) => (
      prev && prev.fieldId === fieldId
        ? {
            ...prev,
            draftHtml: updated.valueHtml || '',
            fontFamily: updated.fontFamily || prev.fontFamily || 'Arial',
            fontSize: String(updated.fontSize || prev.fontSize || '12'),
            fontColor: updated.fontColor || prev.fontColor || '#000000',
            textAlign: updated.textAlign || prev.textAlign || 'left',
          }
        : prev
    ));
  }, [selectedField]);

  const syncInlineEditorHtmlToField = useCallback((fieldId, nextHtml) => {
    if (!fieldId) return;
    const rawHtml = prepareReadOnlyValueHtml(nextHtml || '');
    let updated = null;

    setDocumentFields((prevFields) => {
      const sourceField =
        prevFields.find((f) => f.id === fieldId)
        || (selectedField?.id === fieldId ? selectedField : null);
      if (!sourceField) return prevFields;

      const valueHtml = rawHtml;
      const merged = {
        ...sourceField,
        value: stripHtmlForMeasure(valueHtml),
        valueHtml,
      };
      const neededHeight = computeReadOnlyFieldNeededHeight(merged, {
        textOverride: valueHtml,
      });
      const currentHeight = sourceField.height || 40;
      updated = { ...merged, height: Math.max(currentHeight, neededHeight) };
      const nextFields = prevFields.map((f) => (f.id === fieldId ? updated : f));
      documentFieldsRef.current = nextFields;
      return nextFields;
    });
    if (!updated) return;

    setSelectedField((prev) => (prev?.id === fieldId ? updated : prev));
    setInlineReadOnlyEditor((prev) => (
      prev && prev.fieldId === fieldId
        ? { ...prev, draftHtml: updated.valueHtml || '' }
        : prev
    ));
  }, [selectedField]);

  const applyInlineEditorFormatAndSyncField = useCallback((field, formatKey, formatValue) => {
    if (!field || !inlineReadOnlyEditor || inlineReadOnlyEditor.fieldId !== field.id) return false;
    if (readOnlyRichEditorRef.current?.applyFieldLevelFormat?.(formatKey, formatValue)) {
      return true;
    }
    const applied = readOnlyRichEditorRef.current?.applyFormat?.(formatKey, formatValue);
    if (!applied) return false;
    syncInlineEditorHtmlToField(
      field.id,
      readOnlyRichEditorRef.current?.getHtml?.() ?? inlineReadOnlyEditor.draftHtml ?? ''
    );
    return true;
  }, [inlineReadOnlyEditor, syncInlineEditorHtmlToField]);

  const commitInlineReadOnlyValueEditor = () => {
    if (!inlineReadOnlyEditor?.fieldId) return;

    const fieldId = inlineReadOnlyEditor.fieldId;
    const editorSnapshot = inlineReadOnlyEditor;
    const existing =
      (documentFieldsRef.current || documentFields).find((f) => f.id === fieldId)
      || null;
    const rawHtml = prepareReadOnlyValueHtml(
      readOnlyRichEditorRef.current?.getHtml?.()
        ?? editorSnapshot.draftHtml
        ?? ''
    );
    const valueHtml = rawHtml;
    const stylePatch = {
      fontFamily: editorSnapshot.fontFamily || existing?.fontFamily || 'Arial',
      fontSize: String(editorSnapshot.fontSize || existing?.fontSize || '12'),
      fontColor: editorSnapshot.fontColor || existing?.fontColor || '#000000',
      textAlign: editorSnapshot.textAlign || existing?.textAlign || 'left',
    };

    const prevHtml = getReadOnlyFieldValueHtml(existing) || '';
    if (String(prevHtml) === String(valueHtml)) {
      setInlineReadOnlyEditor(null);
      requestAnimationFrame(() => {
        purgeOrphanQuillToolbars(fieldOverlayElRefs.current.get(fieldId));
      });
      return;
    }

    const merged = {
      ...(existing || {}),
      id: fieldId,
      value: stripHtmlForMeasure(valueHtml),
      valueHtml,
      ...stylePatch,
    };
    const neededHeight = computeReadOnlyFieldNeededHeight(merged, {
      textOverride: valueHtml,
      styleOverride: stylePatch,
    });
    const committedField = { ...merged, height: neededHeight };

    saveToHistory(documentFieldsRef.current || documentFields);
    const nextFields = (documentFieldsRef.current || documentFields).map((f) =>
      (f.id === fieldId ? committedField : f)
    );
    documentFieldsRef.current = nextFields;
    setDocumentFields(nextFields);
    setSelectedField(committedField);
    setInlineReadOnlyEditor(null);
    requestAnimationFrame(() => {
      purgeOrphanQuillToolbars(fieldOverlayElRefs.current.get(fieldId));
    });
  };

  const cancelInlineReadOnlyValueEditor = () => {
    const fieldId = inlineReadOnlyEditor?.fieldId;
    setInlineReadOnlyEditor(null);
    if (fieldId) {
      requestAnimationFrame(() => {
        purgeOrphanQuillToolbars(fieldOverlayElRefs.current.get(fieldId));
      });
    }
  };

  // Grow read-only field height while editing when text overflows (never shrink).
  useEffect(() => {
    if (!inlineReadOnlyEditor?.fieldId) return undefined;
    const fieldId = inlineReadOnlyEditor.fieldId;
    const field = (documentFieldsRef.current || documentFields).find((f) => f.id === fieldId);
    if (!field || !fieldSupportsInlineTextEdit(field)) return undefined;

    const fitted = fitReadOnlyFieldToContent(
      field,
      inlineReadOnlyEditor.draftHtml ?? '',
      {
        fontFamily: inlineReadOnlyEditor.fontFamily,
        fontSize: inlineReadOnlyEditor.fontSize,
        fontColor: inlineReadOnlyEditor.fontColor,
        textAlign: inlineReadOnlyEditor.textAlign,
      }
    );
    if ((fitted.height || 40) === (field.height || 40)) return undefined;

    const nextFields = (documentFieldsRef.current || documentFields).map((f) =>
      (f.id === fieldId ? fitted : f)
    );
    documentFieldsRef.current = nextFields;
    setDocumentFields(nextFields);
    setSelectedField((prev) => (prev?.id === fieldId ? fitted : prev));
    return undefined;
  }, [
    inlineReadOnlyEditor,
    documentFields,
  ]);

  // Close inline read-only editor when clicking anywhere outside it.
  useEffect(() => {
    if (!inlineReadOnlyEditor) return undefined;

    const handleOutsidePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Keep open only when interacting inside inline editor UI.
      const activeRoot = document.querySelector(
        `[data-readonly-quill-root="${inlineReadOnlyEditor.fieldId}"]`
      );
      if (activeRoot?.contains(target)) {
        return;
      }

      // Clicking outside should keep the value and only hide controls/editor UI.
      commitInlineReadOnlyValueEditor();
    };

    document.addEventListener('mousedown', handleOutsidePointerDown, true);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineReadOnlyEditor]);

  // Handle undo
  const handleUndo = () => {
    if (history.undo.length === 0) return;
    
    const newUndo = [...history.undo];
    const previousState = newUndo.pop();
    const currentState = cloneFieldsSnapshot(documentFields);
    if (!previousState) return;

    // If undo removes field(s) from canvas, remove those saved fields from backend too.
    const previousIds = new Set(previousState.map((f) => String(f.id)));
    const removedFields = currentState.filter((f) => !previousIds.has(String(f.id)));
    if (removedFields.length > 0) {
      removedFields.forEach((field) => {
        const rawApiId = field.apiId || field.field_id || fieldIdMapRef.current[field.id];
        const numericFieldId = typeof rawApiId === 'string' ? parseInt(rawApiId, 10) : rawApiId;
        if (!numericFieldId || Number.isNaN(numericFieldId) || numericFieldId <= 0) return;
        deleteDocumentField(numericFieldId)
          .then(() => {
            if (fieldIdMapRef.current[field.id]) {
              delete fieldIdMapRef.current[field.id];
            }
          })
          .catch((error) => {
            console.error(`❌ Undo delete failed for field ${numericFieldId}:`, error);
          });
      });
    }

        setDocumentFields(previousState);
        // Clear selected field if it no longer exists
        const fieldExists = previousState.some(f => f.id === selectedField?.id);
        if (!fieldExists) {
          setSelectedField(null);
        } else if (selectedField) {
          const restoredField = previousState.find(f => f.id === selectedField.id);
          if (restoredField) {
            setSelectedField(restoredField);
          }
        }

    setHistory({
          undo: newUndo,
      redo: [currentState, ...history.redo].slice(0, maxHistorySize),
    });
  };

  // Handle redo
  const handleRedo = () => {
    if (history.redo.length === 0) return;
    
    setHistory((prev) => {
      const newRedo = [...prev.redo];
      const nextState = newRedo.shift(); // Get first redo state (deep clone)
      const currentState = cloneFieldsSnapshot(documentFields); // Current state goes to undo
      
      if (nextState) {
        setDocumentFields(nextState);
        // Clear selected field if it no longer exists
        const fieldExists = nextState.some(f => f.id === selectedField?.id);
        if (!fieldExists) {
          setSelectedField(null);
        } else if (selectedField) {
          // Update selected field to match the restored state
          const restoredField = nextState.find(f => f.id === selectedField.id);
          if (restoredField) {
            setSelectedField(restoredField);
          }
        }
        return {
          undo: [...prev.undo, currentState].slice(-maxHistorySize), // Keep max 5 in undo
          redo: newRedo
        };
      }
      return prev;
    });
  };

  // Handle copy field
  const handleCopyField = () => {
    // Copy should create NEW server fields on paste, so strip server-side identifiers.
    const stripServerFieldIdentity = (field) => {
      const next = cloneFieldsSnapshot([field])[0] || { ...field };
      delete next.apiId;
      delete next.field_id;
      delete next.id;
      return next;
    };

    // If multiple fields are selected, copy all of them
    if (selectedFields.length > 1) {
      const fieldsToCopy = documentFields.filter(f => selectedFields.includes(f.id));
      const fieldsCopy = fieldsToCopy.map((f) => stripServerFieldIdentity(f));
      setCopiedFields(fieldsCopy);
      setCopiedField(null); // Clear single field copy
    } else if (selectedFields.length === 1 && !selectedField) {
      // Fallback: single selected id exists but selectedField object is temporarily stale.
      const single = documentFields.find((f) => String(f.id) === String(selectedFields[0]));
      if (single) {
        setCopiedField(stripServerFieldIdentity(single));
        setCopiedFields([]);
      }
    } else if (selectedField) {
      // Single field copy (backward compatibility)
      const fieldCopy = stripServerFieldIdentity(selectedField);
    setCopiedField(fieldCopy);
      setCopiedFields([]); // Clear multi-field copy
    }
  };

  // Handle paste field(s)
  // pasteToLocation=true  → place at exact mouse pointer coordinates (context menu "Paste to Location")
  // pasteToLocation=false → keep original x/y but paste on the currently visible page
  const handlePasteField = (pasteToLocation = false) => {
    const targetVisiblePage = visiblePageRef.current || 1;
    const targetVisiblePageInfo = (svgPageHeightsRef.current || [])[Math.max(0, targetVisiblePage - 1)] || null;
    const targetVisibleDocumentId = targetVisiblePageInfo
      ? getDocumentIdForIndex(targetVisiblePageInfo.documentIndex)
      : null;
    if (!targetVisibleDocumentId || !targetVisiblePageInfo) return;
    console.groupCollapsed(
      `[field-paste] ${pasteToLocation ? 'paste-to-location' : 'direct-paste'} zoom=${String(zoomLevel)} visiblePage=${targetVisiblePage}`
    );
    console.log('[field-paste] inputs', {
      pasteToLocation,
      zoomLevel,
      zoomRenderScale,
      visiblePageRef: visiblePageRef.current,
      copiedFieldCount: copiedFields.length,
      hasSingleCopiedField: Boolean(copiedField),
      contextMenu: {
        docX: contextMenu.docX ?? null,
        docY: contextMenu.docY ?? null,
        docPage: contextMenu.docPage ?? null,
      },
    });

    if (copiedFields.length > 0) {
      let baseOffsetX = 0;
      let baseOffsetY = 0;
      let targetPage = targetVisiblePageInfo?.pageNumber || targetVisiblePage;
      let targetDocumentId = targetVisibleDocumentId;
      let targetPageInfo = targetVisiblePageInfo;

      if (pasteToLocation && contextMenu.docX != null) {
        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        for (const field of copiedFields) {
          if (field.x < minX) minX = field.x;
          if (field.y < minY) minY = field.y;
        }
        if (!Number.isFinite(minX)) minX = 0;
        if (!Number.isFinite(minY)) minY = 0;
        baseOffsetX = contextMenu.docX - minX;
        baseOffsetY = contextMenu.docY - minY;
        targetPage = contextMenu.docPage;
        targetDocumentId = contextMenu.docDocumentId ?? targetDocumentId;
        targetPageInfo = getPageInfoForDocumentPage(targetDocumentId, targetPage, svgPageHeights);
        if (!targetDocumentId || !targetPageInfo) return;
      }

      const newFields = copiedFields.map((field, index) => {
        const draft = {
          ...field,
          id: Date.now() + index,
          x: field.x + baseOffsetX,
          y: field.y + baseOffsetY,
          documentId: targetDocumentId,
          apiDocumentId: targetDocumentId,
          documentIndex: targetPageInfo?.documentIndex,
          page: targetPage,
          recipientEmail: field.recipientEmail || (
            typeof field.recipientId === 'string' && field.recipientId.startsWith('email:')
              ? field.recipientId.slice(6)
              : ''
          ),
        };
        return clampFieldXToDocumentPageWidth(draft, targetPageInfo);
      });
      console.log('[field-paste] multi result', {
        targetPage,
        baseOffsetX,
        baseOffsetY,
        result: newFields.map((field) => ({
          id: field.id,
          page: field.page,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
        })),
      });
      saveToHistory(documentFields);
      setDocumentFields([...documentFields, ...newFields]);
      setSelectedFields(newFields.map(f => f.id));
      setSelectedField(newFields[0]);
      setContextMenu({ show: false, x: 0, y: 0, fieldId: null });
    } else if (copiedField) {
      let newX = copiedField.x;
      let newY = copiedField.y;
      let newPage = targetVisiblePageInfo?.pageNumber || targetVisiblePage;
      let newDocumentId = targetVisibleDocumentId;
      let newPageInfo = targetVisiblePageInfo;

      if (pasteToLocation && contextMenu.docX != null) {
        newX = contextMenu.docX;
        newY = contextMenu.docY;
        newPage = contextMenu.docPage;
        newDocumentId = contextMenu.docDocumentId ?? newDocumentId;
        newPageInfo = getPageInfoForDocumentPage(newDocumentId, newPage, svgPageHeights);
        if (!newDocumentId || !newPageInfo) return;
      }

      const draftField = {
      ...copiedField,
        id: Date.now(),
        x: newX,
        y: newY,
        documentId: newDocumentId,
        apiDocumentId: newDocumentId,
        documentIndex: newPageInfo?.documentIndex,
        page: newPage,
        recipientEmail: copiedField.recipientEmail || (
          typeof copiedField.recipientId === 'string' && copiedField.recipientId.startsWith('email:')
            ? copiedField.recipientId.slice(6)
            : ''
        ),
      };
      const newField = normalizeFieldOptionsForType(
        clampFieldXToDocumentPageWidth(draftField, newPageInfo)
      );
      console.log('[field-paste] single result', {
        source: copiedField,
        draftField,
        newField,
      });

      saveToHistory(documentFields);
      setDocumentFields([...documentFields, newField]);
      setSelectedFields([newField.id]);
      setSelectedField(newField);
      setContextMenu({ show: false, x: 0, y: 0, fieldId: null });
    }
    console.groupEnd();
  };

  // Delete one or more fields from API and UI.
  const handleDeleteFields = async (fieldIdsInput = []) => {
    const uniqueFieldIds = Array.from(new Set((fieldIdsInput || []).filter((id) => id !== undefined && id !== null)));
    if (uniqueFieldIds.length === 0) return;

    const fieldsToDelete = documentFields.filter((f) => uniqueFieldIds.includes(f.id));
    if (fieldsToDelete.length === 0) {
      console.warn('⚠️ No matching fields found for delete request:', uniqueFieldIds);
      return;
    }
    
    const apiDeleteRows = [];
    fieldsToDelete.forEach((field) => {
      const localFieldId = field.id;
      const rawApiId = field.apiId || field.field_id || fieldIdMapRef.current[localFieldId];
      const numericFieldId = typeof rawApiId === 'string' ? parseInt(rawApiId, 10) : rawApiId;
      if (!numericFieldId || Number.isNaN(numericFieldId) || numericFieldId <= 0) return;
      apiDeleteRows.push({ localFieldId, numericFieldId });
    });

    if (draftEnvelopeId && apiDeleteRows.length > 1) {
      try {
        const bulkIds = apiDeleteRows.map((r) => r.numericFieldId);
        console.log(`🗑️ Bulk deleting ${bulkIds.length} field(s) from API`, {
          localFieldIds: apiDeleteRows.map((r) => r.localFieldId),
          apiFieldIds: bulkIds,
        });
        await bulkDeleteDocumentFields(bulkIds);
        apiDeleteRows.forEach(({ localFieldId, numericFieldId }) => {
          pendingExplicitFieldDeletesSkipRef.current.add(String(numericFieldId));
          if (fieldIdMapRef.current[localFieldId]) delete fieldIdMapRef.current[localFieldId];
        });
      } catch (error) {
        console.error('❌ Error bulk deleting fields from API:', error);
        if (error.response?.data) console.error('❌ Error details:', error.response.data);
        // Continue with UI deletion even if API call fails.
      }
    } else if (draftEnvelopeId && apiDeleteRows.length === 1) {
      const { localFieldId, numericFieldId } = apiDeleteRows[0];
        try {
          console.log(`🗑️ Deleting field ${numericFieldId} from API`, {
          localFieldId,
            apiFieldId: numericFieldId,
          });
          await deleteDocumentField(numericFieldId);
        pendingExplicitFieldDeletesSkipRef.current.add(String(numericFieldId));
        if (fieldIdMapRef.current[localFieldId]) delete fieldIdMapRef.current[localFieldId];
        } catch (error) {
          console.error(`❌ Error deleting field ${numericFieldId} from API:`, error);
        if (error.response?.data) console.error('❌ Error details:', error.response.data);
          }
    }
    
    // Remove from UI in one state update.
    const deleteSet = new Set(fieldsToDelete.map((f) => f.id));
    saveToHistory(documentFields);
    setDocumentFields((prevFields) => prevFields.filter((f) => !deleteSet.has(f.id)));
    if (selectedField && deleteSet.has(selectedField.id)) {
      setSelectedField(null);
    }
    setSelectedFields((prev) => prev.filter((id) => !deleteSet.has(id)));
  };

  // Single-field wrapper for existing call sites.
  const handleDeleteField = async (fieldId) => {
    await handleDeleteFields([fieldId]);
  };

  // Resolve which field IDs a cut/delete should affect.
  // Right-click on one of the selected fields → operate on the whole selection.
  // Right-click on an unselected field → operate on that field only.
  const resolveMultiSelectActionIds = (fieldId) => {
    if (fieldId != null && selectedFields.length > 1 && selectedFields.includes(fieldId)) {
      return [...selectedFields];
    }
    if (fieldId != null) return [fieldId];
    if (selectedFields.length > 0) return [...selectedFields];
    if (selectedField?.id != null) return [selectedField.id];
    return [];
  };

  // Cut one or more fields: copy to clipboard then delete from document.
  const handleCutFields = (fieldIdsInput = []) => {
    const stripServerFieldIdentity = (field) => {
      const next = cloneFieldsSnapshot([field])[0] || { ...field };
      delete next.apiId;
      delete next.field_id;
      delete next.id;
      return next;
    };

    const uniqueIds = Array.from(
      new Set((Array.isArray(fieldIdsInput) ? fieldIdsInput : [fieldIdsInput]).filter((id) => id != null))
    );
    if (uniqueIds.length === 0) return;

    const fieldsToCut = documentFields.filter((f) => uniqueIds.includes(f.id));
    if (fieldsToCut.length === 0) return;

    if (fieldsToCut.length > 1) {
      setCopiedFields(fieldsToCut.map(stripServerFieldIdentity));
      setCopiedField(null);
    } else {
      setCopiedField(stripServerFieldIdentity(fieldsToCut[0]));
      setCopiedFields([]);
    }

    handleDeleteFields(uniqueIds);
    setContextMenu({ show: false, x: 0, y: 0, fieldId: null });
  };

  // Single-field / multi-aware cut: if the target is part of a multi-selection, cut all selected.
  const handleCutField = (fieldId) => {
    handleCutFields(resolveMultiSelectActionIds(fieldId));
  };

  // Handle context menu
  const handleContextMenu = (e, fieldId = null) => {
    e.preventDefault(); // Prevent default browser context menu

    // Convert mouse position to unscaled document coordinates
    let docX = null;
    let docY = null;
    let docPage = 1;
    let docDocumentId = null;
    const container = svgContainerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      const scale = getCurrentScale();
      // Include current scroll offsets before converting to doc-space.
      const rawX = (e.clientX - rect.left) / scale;
      const rawY = (e.clientY - rect.top) / scale;

      const placement = resolvePlacementFromAbsolutePoint(rawX, rawY, svgPageHeights);
      if (placement.pageInfo) {
        docPage = placement.page;
        docDocumentId = placement.documentId;
        docX = placement.x;
        docY = placement.y;
      }
      if (docX === null) {
        docX = rawX;
        docY = rawY;
      }
      console.log('[field-context-menu]', {
        zoomLevel,
        zoomRenderScale,
        scale,
        clientX: e.clientX,
        clientY: e.clientY,
        rectLeft: rect.left,
        rectTop: rect.top,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
        rawX,
        rawY,
        docX,
        docY,
        docPage,
        docDocumentId,
      });
    }

    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      fieldId: fieldId,
      docX,
      docY,
      docPage,
      docDocumentId,
    });
    // Select the field if not already selected (only if fieldId is provided)
    if (fieldId) {
      const field = documentFields.find(f => f.id === fieldId);
      if (field && (!selectedField || selectedField.id !== fieldId)) {
        setSelectedField(field);
      }
    } else {
      // If right-clicking on document (not a field), deselect any selected field
      setSelectedField(null);
    }
  };

  // Handle preview — same document-space layout as the field editor (segments + scaled stage).
  const handlePreview = () => {
    const segments = documentEditorSvgSegments;
    const pageHeights = svgPageHeightsRef.current?.length ? svgPageHeightsRef.current : svgPageHeights;
    const fieldsSnapshot = (documentFieldsRef.current?.length ? documentFieldsRef.current : documentFields)
      .filter((field) => !isHiddenFieldType(field.type))
      .map((field) => {
        const { fieldX, fieldY, width, height, pageInfo } = getFieldOverlayGeometry(field, pageHeights);
        if (!pageInfo) return null;

        const snapshot = {
          x: fieldX,
          y: fieldY,
          width,
          height,
          type: field.type,
          label: field.label || field.type || 'Field',
          color: field.recipientColor || '#6366f1',
          required: field.required !== false,
          fontFamily: field.fontFamily || 'Arial',
          fontSize: Number(field.fontSize) || 12,
          fontColor: field.fontColor || '#1f2937',
          fontWeight: field.fontWeight || 'normal',
          fontStyle: field.fontStyle || 'normal',
          textDecoration: field.textDecoration || 'none',
          textAlign: field.textAlign || 'left',
        };

        if (fieldTypeAllowsSingleOptionOnly(field.type)) {
          return {
            ...snapshot,
            mode: 'choice',
            checked: isChoiceFieldDisplayChecked(field),
            optionLabel: getFieldChoiceOptionLabel(field) || '',
          };
        }

        if (field.type === 'signature' || field.type === 'initial') {
          const raw = String(field.value || '').trim();
          return {
            ...snapshot,
            mode: 'signature',
            signatureSrc: raw.startsWith('data:image') ? raw : '',
            label: field.label || (field.type === 'initial' ? 'Initials' : 'Signature'),
          };
        }

        const richHtml = getReadOnlyFieldValueHtml(field);
        if (richHtml && hasHtmlMarkup(richHtml)) {
          return {
            ...snapshot,
            mode: 'rich',
            html: prepareReadOnlyValueHtml(unwrapReadOnlyFieldValueWrapper(richHtml)),
          };
        }

        const plain =
          getReadOnlyFieldCanvasDisplayValue(field)
          || String(field.value || '').trim();
        if (plain && (readOnlyFieldShowsValueOnly(field) || field.readOnly)) {
          return { ...snapshot, mode: 'text', text: plain };
        }

        return { ...snapshot, mode: 'placeholder' };
      })
      .filter(Boolean);

    if (!segments?.length || !svgMaxWidth || !svgTotalHeight) {
      alert('Please wait for the document to finish loading before previewing.');
      return;
    }

    const previewData = {
      svgSegments: segments,
      svgMaxWidth,
      svgTotalHeight,
      fields: fieldsSnapshot,
    };

    const encodedData = encodeURIComponent(JSON.stringify(previewData));
    
    // Open preview in new window
    const previewWindow = window.open('', '_blank', 'width=1200,height=800');
    if (previewWindow) {
      previewWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Document Preview</title>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: Arial, sans-serif;
                background-color: #e8e8e8;
                overflow: hidden;
              }
              .preview-container {
                display: flex;
                flex-direction: column;
                height: 100vh;
                width: 100vw;
              }
              .preview-header {
                background-color: #ffffff;
                border-bottom: 1px solid #e0e0e0;
                padding: 0.75rem 1.25rem;
                display: flex;
                justify-content: space-between;
                align-items: center;
                flex-shrink: 0;
              }
              .preview-title {
                font-size: 1.125rem;
                font-weight: 600;
                color: #212529;
              }
              .device-controls {
                display: flex;
                gap: 0.25rem;
                align-items: center;
                background: #f1f3f5;
                border-radius: 0.5rem;
                padding: 0.25rem;
              }
              .device-btn {
                padding: 0.4rem 0.9rem;
                border: none;
                background-color: transparent;
                border-radius: 0.375rem;
                cursor: pointer;
                font-size: 0.8125rem;
                font-weight: 500;
                color: #495057;
                transition: all 0.2s;
                display: flex;
                align-items: center;
                gap: 0.375rem;
              }
              .device-btn:hover {
                background-color: #e9ecef;
              }
              .device-btn.active {
                background-color: #ffffff;
                color: #6366f1;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              }
              .device-btn svg {
                width: 16px;
                height: 16px;
                fill: currentColor;
              }
              .preview-content {
                flex: 1;
                overflow: auto;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                padding: 2rem;
                background-color: #e8e8e8;
              }
              /* Tablet / Mobile: show a device frame */
              .preview-content.tablet-view,
              .preview-content.mobile-view {
                align-items: flex-start;
                padding-top: 2rem;
              }
              .preview-wrapper {
                background-color: #ffffff;
                transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
                margin: 0 auto;
                position: relative;
                overflow: hidden;
              }
              .preview-wrapper.desktop {
                width: 100%;
                max-width: 100%;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              }
              .preview-wrapper.tablet {
                width: 768px;
                max-width: 768px;
                border-radius: 16px;
                box-shadow: 0 0 0 8px #222, 0 8px 40px rgba(0,0,0,0.25);
              }
              .preview-wrapper.mobile {
                width: 375px;
                max-width: 375px;
                border-radius: 24px;
                box-shadow: 0 0 0 6px #222, 0 8px 40px rgba(0,0,0,0.25);
              }
              .document-preview {
                width: 100%;
                position: relative;
                display: block;
                overflow: hidden;
              }
              .document-stage {
                position: relative;
                transform-origin: top left;
              }
              .document-stage svg {
                display: block;
                width: 100%;
                height: 100%;
              }
              .preview-segment-layer {
                position: absolute;
                left: 0;
                width: 100%;
                overflow: hidden;
              }
              .preview-fields-layer {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
              }
              .preview-field {
                position: absolute;
                box-sizing: border-box;
                border: 1.5px solid;
                border-radius: 4px;
                overflow: hidden;
                pointer-events: none;
              }
              .preview-field--placeholder {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 4px;
                padding: 0 4px;
                font-size: 12px;
                white-space: nowrap;
                text-overflow: ellipsis;
              }
              .preview-field--text,
              .preview-field--rich {
                padding: 2px 4px;
                white-space: pre-wrap;
                word-break: break-word;
                line-height: 1.2;
                overflow: visible;
              }
              .preview-field--rich p,
              .preview-field--rich .readonly-block {
                margin: 0;
                padding: 0;
                line-height: 1.2;
              }
              .preview-field--choice {
                display: flex;
                align-items: center;
                gap: 4px;
                padding: 0 4px;
                font-size: 12px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .preview-field--signature {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 2px;
              }
              .preview-field--signature img {
                width: 100%;
                height: 100%;
                object-fit: contain;
              }
              .preview-field--signature .sig-placeholder {
                font-size: 11px;
                font-style: italic;
                opacity: 0.75;
              }
            </style>
          </head>
          <body>
            <div class="preview-container">
              <div class="preview-header">
                <h1 class="preview-title">Document Preview</h1>
                <div class="device-controls">
                  <button class="device-btn active" data-device="desktop">
                    <svg viewBox="0 0 24 24"><path d="M21 2H3a1 1 0 00-1 1v14a1 1 0 001 1h7v2H8a1 1 0 000 2h8a1 1 0 000-2h-2v-2h7a1 1 0 001-1V3a1 1 0 00-1-1zm-1 14H4V4h16v12z"/></svg>
                    Desktop
                  </button>
                  <button class="device-btn" data-device="tablet">
                    <svg viewBox="0 0 24 24"><path d="M19 1H5a2 2 0 00-2 2v18a2 2 0 002 2h14a2 2 0 002-2V3a2 2 0 00-2-2zm0 18H5V4h14v15zm-7 2a1 1 0 110-2 1 1 0 010 2z"/></svg>
                    Tablet
                  </button>
                  <button class="device-btn" data-device="mobile">
                    <svg viewBox="0 0 24 24"><path d="M17 1H7a2 2 0 00-2 2v18a2 2 0 002 2h10a2 2 0 002-2V3a2 2 0 00-2-2zm0 18H7V4h10v15zm-5 2a1 1 0 110-2 1 1 0 010 2z"/></svg>
                    Mobile
                  </button>
                </div>
              </div>
              <div class="preview-content" id="previewContent">
                <div class="preview-wrapper desktop" id="previewWrapper">
                  <div class="document-preview" id="documentPreview"></div>
                </div>
              </div>
            </div>
            <script>
              const previewData = JSON.parse(decodeURIComponent('${encodedData}'));
              const previewWrapper = document.getElementById('previewWrapper');
              const previewContent = document.getElementById('previewContent');
              const documentPreview = document.getElementById('documentPreview');

              function hexToRgb(hex) {
                var h = String(hex || '#6366f1');
                if (h.indexOf('rgb') === 0) return '99, 102, 241';
                if (!h || h.length < 7) return '99, 102, 241';
                return parseInt(h.slice(1, 3), 16) + ', ' + parseInt(h.slice(3, 5), 16) + ', ' + parseInt(h.slice(5, 7), 16);
              }

              function renderPreview() {
                documentPreview.innerHTML = '';
                var stage = document.createElement('div');
                stage.className = 'document-stage';
                stage.id = 'documentStage';
                stage.style.width = previewData.svgMaxWidth + 'px';
                stage.style.height = previewData.svgTotalHeight + 'px';

                (previewData.svgSegments || []).forEach(function(seg) {
                  if (!seg || !seg.innerMarkup) return;
                  var layer = document.createElement('div');
                  layer.className = 'preview-segment-layer';
                  layer.style.top = seg.top + 'px';
                  layer.style.height = seg.height + 'px';
                  layer.innerHTML = '<svg width="' + previewData.svgMaxWidth + '" height="' + seg.height + '" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ' + previewData.svgMaxWidth + ' ' + seg.height + '">' + seg.innerMarkup + '</svg>';
                  stage.appendChild(layer);
                });

                var fieldsLayer = document.createElement('div');
                fieldsLayer.className = 'preview-fields-layer';

                (previewData.fields || []).forEach(function(field) {
                  var el = document.createElement('div');
                  el.className = 'preview-field preview-field--' + field.mode;
                  el.style.left = field.x + 'px';
                  el.style.top = field.y + 'px';
                  el.style.width = field.width + 'px';
                  el.style.height = field.height + 'px';
                  el.style.borderColor = field.color;
                  el.style.color = field.color;
                  el.style.backgroundColor = field.required
                    ? 'rgba(' + hexToRgb(field.color) + ', 0.1)'
                    : 'transparent';

                  if (field.mode === 'rich') {
                    el.style.fontFamily = field.fontFamily;
                    el.style.fontSize = field.fontSize + 'px';
                    el.style.color = field.fontColor;
                    el.style.fontWeight = field.fontWeight;
                    el.style.fontStyle = field.fontStyle;
                    el.style.textDecoration = field.textDecoration;
                    el.style.textAlign = field.textAlign;
                    el.innerHTML = field.html || '';
                  } else if (field.mode === 'text') {
                    el.style.fontFamily = field.fontFamily;
                    el.style.fontSize = field.fontSize + 'px';
                    el.style.color = field.fontColor;
                    el.style.fontWeight = field.fontWeight;
                    el.style.fontStyle = field.fontStyle;
                    el.style.textDecoration = field.textDecoration;
                    el.style.textAlign = field.textAlign;
                    el.textContent = field.text || '';
                  } else if (field.mode === 'choice') {
                    el.textContent = (field.checked ? '\\u2611' : '\\u2610') + (field.optionLabel ? ' ' + field.optionLabel : '');
                  } else if (field.mode === 'signature') {
                    if (field.signatureSrc) {
                      var img = document.createElement('img');
                      img.src = field.signatureSrc;
                      img.alt = field.label;
                      el.appendChild(img);
                    } else {
                      var ph = document.createElement('span');
                      ph.className = 'sig-placeholder';
                      ph.textContent = field.label;
                      el.appendChild(ph);
                    }
                  } else {
                    el.textContent = field.label;
                  }

                  fieldsLayer.appendChild(el);
                });

                stage.appendChild(fieldsLayer);
                documentPreview.appendChild(stage);
                layoutPreviewStage();
              }

              function layoutPreviewStage() {
                var stage = document.getElementById('documentStage');
                if (!stage || !previewData.svgMaxWidth) return;
                var scale = previewWrapper.clientWidth / previewData.svgMaxWidth;
                if (!Number.isFinite(scale) || scale <= 0) scale = 1;
                stage.style.transform = 'scale(' + scale + ')';
                documentPreview.style.height = Math.ceil(previewData.svgTotalHeight * scale) + 'px';
              }

              renderPreview();

              var resizeTimeout;
              window.addEventListener('resize', function() {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(layoutPreviewStage, 60);
              });

              document.querySelectorAll('.device-btn').forEach(function(btn) {
                btn.addEventListener('click', function() {
                  document.querySelectorAll('.device-btn').forEach(function(b) { b.classList.remove('active'); });
                  btn.classList.add('active');
                  var device = btn.dataset.device;
                  previewWrapper.className = 'preview-wrapper ' + device;
                  previewContent.className = 'preview-content' + (device !== 'desktop' ? ' ' + device + '-view' : '');
                  setTimeout(layoutPreviewStage, 50);
                  setTimeout(layoutPreviewStage, 400);
                });
              });
            </script>
          </body>
        </html>
      `);
      previewWindow.document.close();
    }
  };

  // Handle drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      void handleFileSelect(e.dataTransfer.files);
    }
  };

  // Remove file
  const handleRemoveFile = async (index, options = {}) => {
    const { waitForApi = false } = options;

    let deferredLocalRemove = false;

    // Capture IDs BEFORE clearing state
    const file = uploadedFiles[index];
    if (!file) return false;
    const rowKey = getDocumentCacheKey(file, index);
    const docId = file?.documentId ?? file?.document_id ?? documentIds[index];

    // 1. Cancel any pending auto-save timeout so it doesn't re-upload this file
    if (documentSaveTimeoutRef.current) {
      clearTimeout(documentSaveTimeoutRef.current);
      documentSaveTimeoutRef.current = null;
    }

    const applyLocalDelete = () => {
      purgeDocumentRowAtIndexRef.current(index);
    };

    const numericDocId =
      docId != null && docId !== '' && docId !== 0 ? Number(docId) : NaN;
    // Persisted rows can have a server document_id before draftEnvelopeId is set (e.g. URL load);
    // still await DELETE so the row stays until the API returns.
    const requiresApiDelete =
      Number.isFinite(numericDocId) && numericDocId > 0;

    const executePersistedDocumentDelete = async (documentId) => {
      const id =
        documentId != null && documentId !== '' && documentId !== 0
          ? Number(documentId)
          : NaN;
      const idForApi = Number.isFinite(id) && id > 0 ? id : documentId;
      if (isTemplateMode && templateId) {
        await removeDocumentFromTemplate(templateId, idForApi);
      }
      await deleteDocument(idForApi);
    };

    if (waitForApi) {
      if (deletingDocumentKeysInFlightRef.current.has(rowKey)) return false;

      /** Local PDF rows: drive delete timing from client_document_id registry (pending/uploading/uploaded). */
      let localUploadDeleteKind = 'immediate';
      if (!requiresApiDelete && file instanceof File && !file?.fromApi) {
        ensureClientDocumentId(file, index);
        const pendingCidProbe = String(getFileClientDocumentId(file) || '').trim();
        const regProbe = pendingCidProbe ? documentUploadRegistryRef.current.get(pendingCidProbe) : null;
        const lifecycleProbe = regProbe?.status;
        const prog = documentUploadProgress[rowKey];
        const st = prog?.status;
        const rowHasActiveTransfer = [
          'uploading',
          'processing',
          'downloading',
          'extracting',
          'preparing_svg',
        ].includes(st);
        const regRawId = regProbe?.documentId;
        const regParsedId =
          typeof regRawId === 'string' ? parseInt(regRawId, 10) : regRawId;
        const registryServerIdOk =
          Number.isFinite(regParsedId) && regParsedId > 0 ? regParsedId : null;

        if (lifecycleProbe === 'uploaded' && registryServerIdOk) {
          localUploadDeleteKind = 'server';
        } else if (lifecycleProbe === 'uploading' || rowHasActiveTransfer) {
          localUploadDeleteKind = 'defer';
      } else {
          localUploadDeleteKind = 'immediate';
        }
      }

      const willDeferRemove = localUploadDeleteKind === 'defer';
      const showDeleteSpinner = requiresApiDelete || localUploadDeleteKind === 'server' || willDeferRemove;
      if (showDeleteSpinner) {
        deletingDocumentKeysInFlightRef.current.add(rowKey);
      setTimeout(() => {
        if (typeof React.startTransition === 'function') {
            React.startTransition(() =>
              setDeletingDocumentRowKeys((prev) => (prev.includes(rowKey) ? prev : [...prev, rowKey]))
            );
        } else {
            setDeletingDocumentRowKeys((prev) => (prev.includes(rowKey) ? prev : [...prev, rowKey]));
        }
      }, 0);
      }
      try {
        // Yield once so click handler returns quickly and spinner can paint when we show it.
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (requiresApiDelete) {
          pendingDocumentDeletesSkipFieldRef.current.add(String(docId));
          await executePersistedDocumentDelete(docId);
          console.log('✅ Document deleted from server, docId:', docId);
          applyLocalDelete();
        } else {
          if (file instanceof File && !file?.fromApi) {
            ensureClientDocumentId(file, index);
          }
          const pendingCid = String(getFileClientDocumentId(file) || '').trim();
          const reg = pendingCid ? documentUploadRegistryRef.current.get(pendingCid) : null;
          const regRawId = reg?.documentId;
          const regParsedId = typeof regRawId === 'string' ? parseInt(regRawId, 10) : regRawId;
          const registryServerIdOk =
            Number.isFinite(regParsedId) && regParsedId > 0 ? regParsedId : null;

          if (localUploadDeleteKind === 'server' && registryServerIdOk) {
            pendingDocumentDeletesSkipFieldRef.current.add(String(registryServerIdOk));
            await executePersistedDocumentDelete(registryServerIdOk);
            if (pendingCid) removeDocumentUploadRegistryEntry(pendingCid);
        applyLocalDelete();
          } else if (localUploadDeleteKind === 'defer') {
            if (!pendingCid) {
              throw new Error('Missing client document id for delete.');
            }
            const DELETE_ID_WAIT_MS = 120000;
            const POLL_MS = 200;
            const started = Date.now();
            let serverId = null;
            while (Date.now() - started < DELETE_ID_WAIT_MS) {
              const filesNow = uploadedFilesRef.current || [];
              const rowIdx = filesNow.findIndex(
                (f) => String(getFileClientDocumentId(f) || '').trim() === pendingCid
              );
              if (rowIdx < 0) {
                pendingDeleteAfterClientDocIdRef.current.delete(pendingCid);
                setUploadProgressEntries([{ key: rowKey }], null);
                return true;
              }
              const rawId =
                (documentIdsRef.current || [])[rowIdx] ??
                filesNow[rowIdx]?.documentId ??
                filesNow[rowIdx]?.document_id;
              const n = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId;
              if (Number.isFinite(n) && n > 0) {
                serverId = n;
                break;
              }
              const regEntry = documentUploadRegistryRef.current.get(pendingCid);
              const regId = regEntry?.documentId;
              const regN = typeof regId === 'string' ? parseInt(regId, 10) : regId;
              if (Number.isFinite(regN) && regN > 0) {
                serverId = regN;
                break;
              }
              // eslint-disable-next-line no-await-in-loop
              await new Promise((r) => setTimeout(r, POLL_MS));
            }
            if (!serverId) {
              throw new Error(
                'Delete timed out: the document did not finish uploading in time.'
              );
            }
            pendingDocumentDeletesSkipFieldRef.current.add(String(serverId));
            await executePersistedDocumentDelete(serverId);
            pendingDeleteAfterClientDocIdRef.current.delete(pendingCid);
            removeDocumentUploadRegistryEntry(pendingCid);
            const removeIdx = (uploadedFilesRef.current || []).findIndex(
              (f) => String(getFileClientDocumentId(f) || '').trim() === pendingCid
            );
            if (removeIdx >= 0) {
              purgeDocumentRowAtIndexRef.current(removeIdx);
            }
            setUploadProgressEntries([{ key: rowKey }], null);
            deferredLocalRemove = false;
          } else {
            if (pendingCid) pendingDeleteAfterClientDocIdRef.current.delete(pendingCid);
            if (pendingCid) removeDocumentUploadRegistryEntry(pendingCid);
            setUploadProgressEntries([{ key: rowKey }], null);
            applyLocalDelete();
          }
        }
        return true;
      } catch (error) {
        if (docId) {
          pendingDocumentDeletesSkipFieldRef.current.delete(String(docId));
        }
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to delete document. Please try again.';
        console.error('❌ Failed to delete document from server:', error);
        setUploadError(msg);
        deletingDocumentKeysInFlightRef.current.delete(rowKey);
        setDeletingDocumentRowKeys((prev) => prev.filter((k) => k !== rowKey));
        return false;
      } finally {
        if (!deferredLocalRemove) {
          deletingDocumentKeysInFlightRef.current.delete(rowKey);
          setDeletingDocumentRowKeys((prev) => prev.filter((k) => k !== rowKey));
        }
      }
    }

    // Non-blocking path (legacy callers): server document must be removed on the API before the row is purged.
    if (!waitForApi) {
    if (requiresApiDelete) {
      pendingDocumentDeletesSkipFieldRef.current.add(String(docId));
        void executePersistedDocumentDelete(docId)
          .then(() => {
            const idx = (uploadedFilesRef.current || []).findIndex(
              (f, i) => getDocumentCacheKey(f, i) === rowKey
            );
            if (idx >= 0) purgeDocumentRowAtIndexRef.current(idx);
            pendingDocumentDeletesSkipFieldRef.current.delete(String(docId));
            console.log('✅ Document deleted from server, docId:', docId);
          })
          .catch((error) => {
            console.error('❌ Failed to delete document from server:', error);
            pendingDocumentDeletesSkipFieldRef.current.delete(String(docId));
          });
        return true;
      }
      if (file instanceof File && !file?.fromApi) {
        ensureClientDocumentId(file, index);
        const cid = String(getFileClientDocumentId(file) || '').trim();
        const reg = cid ? documentUploadRegistryRef.current.get(cid) : null;
        const lifecycle = reg?.status;
        const prog = documentUploadProgress[rowKey];
        const st = prog?.status;
        const rowHasActiveTransfer = [
          'uploading',
          'processing',
          'downloading',
          'extracting',
          'preparing_svg',
        ].includes(st);
        if (lifecycle === 'uploading' || lifecycle === 'uploaded' || rowHasActiveTransfer) {
          return handleRemoveFile(index, { waitForApi: true });
        }
        if (cid) pendingDeleteAfterClientDocIdRef.current.delete(cid);
        if (cid) removeDocumentUploadRegistryEntry(cid);
        applyLocalDelete();
    return true;
      }
      if (!requiresApiDelete) {
        const pendingCid = getFileClientDocumentId(file);
        if (pendingCid) pendingDeleteAfterClientDocIdRef.current.add(pendingCid);
      }
      applyLocalDelete();
      return true;
    }
  };

  // Helper: get a Blob URL for a file (handles both local File objects and API placeholders)
  const getFileBlobUrl = async (file, fileIndex = 0) => {
    if (file.fromApi) {
      const arrayBuffer = await resolveDocumentArrayBuffer(file, fileIndex);
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    }
    // Local File / Blob — createObjectURL works directly
    return URL.createObjectURL(file);
  };

  // Handle document menu actions
  const handleDocumentAction = async (action, fileIndex) => {
    switch (action) {
      case 'replace':
        // Store index to replace after file selection
        setReplaceFileIndex(fileIndex);
        // Trigger file input to replace the document
        fileInputRef.current?.click();
        break;
      
      case 'download':
        // Download the file
        const fileToDownload = uploadedFiles[fileIndex];
        if (fileToDownload) {
          try {
            const url = await getFileBlobUrl(fileToDownload, fileIndex);
            if (url) {
          const a = document.createElement('a');
          a.href = url;
              a.download = fileToDownload.name || 'document.pdf';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
            }
          } catch (error) {
            console.error('❌ Failed to download document:', error);
          }
        }
        break;
      
      case 'rename':
        // Prompt for new name
        {
        const rowsRename = [...(envelopeDocumentRowsRef.current || [])];
        const currentRow = rowsRename[fileIndex];
        const currentFile = currentRow?.file;
        if (currentRow && currentFile) {
          const newName = prompt('Enter new file name:', currentFile.name);
          if (newName && newName.trim() !== '') {
            if (currentFile.fromApi) {
              rowsRename[fileIndex] = {
                ...currentRow,
                file: { ...currentFile, name: newName.trim() },
              };
            } else {
            const renamedFile = new File([currentFile], newName.trim(), {
              type: currentFile.type,
              lastModified: currentFile.lastModified,
            });
              rowsRename[fileIndex] = { ...currentRow, file: renamedFile };
            }
            envelopeRowsCommitRef.current(rowsRename);
          }
          }
        }
        break;
      
      case 'delete':
      {
        const shouldWaitForApi = true;
        await handleRemoveFile(fileIndex, { waitForApi: shouldWaitForApi });
        break;
      }
      
      case 'view':
        // Open file in new tab/window
        const fileToView = uploadedFiles[fileIndex];
        if (fileToView) {
          try {
            const url = await getFileBlobUrl(fileToView, fileIndex);
            if (url) {
          window.open(url, '_blank');
          // Clean up the URL after a delay
              setTimeout(() => URL.revokeObjectURL(url), 5000);
            }
          } catch (error) {
            console.error('❌ Failed to open document:', error);
          }
        }
        break;
      
      default:
        break;
    }
  };

  const syncDocumentOrderToBackend = useCallback(async (orderedFiles, orderedIds) => {
    if (isTemplateMode) return false;
    const envelopeIdForSave = draftEnvelopeIdRef.current || draftEnvelopeId;
    if (!envelopeIdForSave) return false;

    const normalizedRows = (Array.isArray(orderedFiles) ? orderedFiles : []).map((file, index) => {
      const rawId = orderedIds?.[index] ?? file?.documentId ?? file?.document_id ?? null;
      const docId = rawId != null && rawId !== '' && rawId !== 0 ? rawId : null;
      return { file, index, docId };
    });

    if (normalizedRows.length === 0 || normalizedRows.some((row) => row.docId == null)) {
      // Reorder was local-only or uploads are still pending IDs; sync later after IDs are available.
      return false;
    }

    const orderedDocumentIds = normalizedRows.map((row) => row.docId);
    const orderSignature = orderedDocumentIds.map((id) => String(id)).join('|');
    if (!orderSignature || orderSignature === lastSyncedDocumentOrderSignatureRef.current) {
      return true;
    }

    const pageCountsByDocumentId = {};
    for (const row of normalizedRows) {
      const fromState = Number(documentPages?.[row.index]);
      let resolvedCount = Number.isFinite(fromState) && fromState > 0 ? Math.floor(fromState) : null;
      if (resolvedCount == null) {
        const key = getDocumentCacheKey(row.file, row.index);
        const fromCache = Number(documentSvgCacheRef.current.get(key)?.numPages);
        if (Number.isFinite(fromCache) && fromCache > 0) {
          resolvedCount = Math.floor(fromCache);
        }
      }
      if (resolvedCount == null) {
        // Wait until counts are ready before sending document_ids payload.
        return false;
      }
      pageCountsByDocumentId[String(row.docId)] = resolvedCount;
    }

    try {
      await createDraftEnvelope({
        envelope_id: envelopeIdForSave,
        document_ids: orderedDocumentIds,
        document_page_counts_by_document_id: pageCountsByDocumentId,
      });
      lastSyncedDocumentOrderSignatureRef.current = orderSignature;
      setLastSavedAt(new Date());
      return true;
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to update document order.';
      setUploadError(msg);
      return false;
    }
  }, [isTemplateMode, draftEnvelopeId, documentPages, getDocumentCacheKey]);

  useEffect(() => {
    syncDocumentOrderToBackendRef.current = syncDocumentOrderToBackend;
  }, [syncDocumentOrderToBackend]);

  const scheduleDocumentOrderSync = useCallback((orderedFiles, orderedIds) => {
    if (documentOrderSaveTimeoutRef.current) {
      clearTimeout(documentOrderSaveTimeoutRef.current);
    }
    documentOrderSaveTimeoutRef.current = setTimeout(() => {
      if (documentUploadPipelineActiveRef.current) {
        pendingDeferredOrderSyncRef.current = true;
        return;
      }
      void (async () => {
        const ok = await syncDocumentOrderToBackend(orderedFiles, orderedIds);
        if (ok === true) {
          pendingDeferredOrderSyncRef.current = false;
        } else {
          pendingDeferredOrderSyncRef.current = true;
        }
      })();
    }, 350);
  }, [syncDocumentOrderToBackend]);

  // Reorder documents via drag-and-drop
  const handleReorderDocuments = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    const reorderArray = (arr) => {
      const result = [...arr];
      const [moved] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, moved);
      return result;
    };

    const reorderObjectByIndex = (obj) => {
      const keys = Object.keys(obj).map(Number).sort((a, b) => a - b);
      const values = keys.map(k => obj[k]);
      const [moved] = values.splice(fromIndex, 1);
      values.splice(toIndex, 0, moved);
      const result = {};
      values.forEach((v, i) => { if (v !== undefined) result[i] = v; });
      return result;
    };

    const reorderedRows = reorderEnvelopeDocumentRows(envelopeDocumentRowsRef.current || [], fromIndex, toIndex);
    envelopeRowsCommitRef.current(reorderedRows);
    const reorderedFiles = reorderedRows.map((r) => r.file);
    const reorderedIds = reorderedRows.map((r) => r.serverDocumentId ?? null);
    setDocumentPages(prev => reorderObjectByIndex(prev));
    const reorderedBundles = reorderArray(documentSvgBundles || []);
    setDocumentSvgBundles(reorderedBundles);
    if (currentStep === 2) {
      applySvgStateFromBundles(reorderedBundles);
    }
    setDocumentPageSvgs(prev => reorderObjectByIndex(prev));
    setPdfDocuments(prev => reorderArray(prev));
    scheduleDocumentOrderSync(reorderedFiles, reorderedIds);
    const needsDeferredOrderSync =
      documentUploadPipelineActiveRef.current ||
      reorderedFiles.some((f, i) => {
        if (!f || f.fromApi) return false;
        const raw = reorderedIds[i] ?? f?.documentId ?? f?.document_id ?? null;
        return raw == null || raw === '' || raw === 0;
      });
    if (needsDeferredOrderSync) {
      pendingDeferredOrderSyncRef.current = true;
    }
  };

  // Re-try deferred document order API when ids/page counts catch up (e.g. after uploads finish).
  useEffect(() => {
    if (isTemplateMode) return undefined;
    if (!pendingDeferredOrderSyncRef.current) return undefined;
    const t = setTimeout(() => {
      void flushDeferredDocumentApiTasks();
    }, 400);
    return () => clearTimeout(t);
  }, [documentPages, documentIds, uploadedFiles, isTemplateMode, flushDeferredDocumentApiTasks]);

  // Trigger file input
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Add new recipient
  const handleAddRecipient = () => {
    const nextRecipientsDraft = [...recipients, { name: '', email: '', role: 'Needs to Sign', color: '#6366f1', isVariable: false, placeholder: '' }];
    const limitsError = validateEnvelopeLimits({ recipientsList: nextRecipientsDraft, requirePageCounts: false });
    if (limitsError) {
      setUploadError(limitsError);
      return;
    }
    const color = getNextUnusedRecipientColor(recipients, recipientColorPoolRef.current);
      if (!recipientColorPoolRef.current.includes(color)) {
        recipientColorPoolRef.current.push(color);
      }
    const defaultOrder = signingOrder
      ? getNextRecipientSigningOrder(recipients, sequenceInputs)
      : recipients.length + 1;
      const nextRecipients = [
      ...recipients,
      {
        name: '',
        email: '',
        role: 'Needs to Sign',
        color,
        isVariable: false,
        placeholder: '',
        ...(signingOrder ? { signature_order: defaultOrder } : {}),
      },
    ];
    const nextSeqInputs = [...sequenceInputs, defaultOrder];
    if (signingOrder) {
      const { recipients: sorted, sequenceInputs: sortedSeq } = sortRecipientsBySigningOrder(
        nextRecipients,
        nextSeqInputs
      );
      setRecipients(sorted);
      setSequenceInputs(sortedSeq);
      return;
    }
    setRecipients(nextRecipients);
      setSequenceInputs(nextRecipients.map((_, idx) => idx + 1));
  };

  const handleRemoveRecipient = async (removeIndex) => {
    if (recipients.length <= 1) {
      alert('At least one recipient is required. Add another recipient before removing this one.');
      return;
    }
    if (removingRecipientIndex !== null) {
      return;
    }
    
    const recipientToRemove = recipients[removeIndex];
    const recipientEmail = recipientToRemove?.email;
    const removeSelectionKey = getRecipientSelectionKey(recipientToRemove, removeIndex);
    const recipientId =
      recipientToRemove?.id ||
      (recipientEmail ? recipientIdMapRef.current[recipientEmail] : null);
    const needsApiDelete = Boolean(draftEnvelopeId && recipientId);

    const removeFieldsForRecipient = () => {
      const prevFields = documentFieldsRef.current || documentFields;
      const nextFields = prevFields.filter(
        (f) => !fieldBelongsToEnvelopeRecipient(f, recipientToRemove, removeSelectionKey)
      );
      if (nextFields.length === prevFields.length) return 0;

      saveToHistory(prevFields);
      documentFieldsRef.current = nextFields;
      setDocumentFields(nextFields);

      const removedIds = new Set(
        prevFields
          .filter((f) => fieldBelongsToEnvelopeRecipient(f, recipientToRemove, removeSelectionKey))
          .map((f) => f.id)
      );
      if (selectedField && removedIds.has(selectedField.id)) {
        setSelectedField(null);
      }
      if (selectedFields.length > 0) {
        const nextSelected = selectedFields.filter((id) => !removedIds.has(id));
        setSelectedFields(nextSelected);
      }
      return prevFields.length - nextFields.length;
    };
    
    if (needsApiDelete) {
      setRemovingRecipientIndex(removeIndex);
    }

    try {
    // If recipient has been saved to API (has ID), use dedicated delete API
      if (needsApiDelete) {
      try {
        console.log('🗑️ Deleting recipient from envelope:', { recipientId, email: recipientEmail });
        await deleteEnvelopeRecipient(recipientId);
        
        // Remove from recipient ID map
        if (recipientEmail) {
          delete recipientIdMapRef.current[recipientEmail];
        }

        const removedFieldCount = removeFieldsForRecipient();
        if (removedFieldCount > 0) {
          console.log(`🗑️ Removed ${removedFieldCount} field(s) for deleted recipient`);
        }
        
        // Update last saved recipients reference
        lastSavedRecipientsRef.current = lastSavedRecipientsRef.current.filter(
          (r) =>
            String(r.recipient_id ?? r.id ?? '') !== String(recipientId) &&
            String(r.email || '') !== String(recipientEmail || '')
        );
        
        console.log('✅ Recipient deleted successfully');
      } catch (error) {
        console.error('❌ Error deleting recipient:', error);
        alert('Error deleting recipient. Please try again.');
          return;
      }
    } else {
      removeFieldsForRecipient();
    }
    
    // Remove from local state
    const newRecipients = recipients.filter((_, i) => i !== removeIndex);
    const newSeqInputs = sequenceInputs.filter((_, i) => i !== removeIndex);
    if (signingOrder) {
      const { recipients: sorted, sequenceInputs: sortedSeq } = sortRecipientsBySigningOrder(
        newRecipients,
        newSeqInputs
      );
      setRecipients(sorted);
      setSequenceInputs(sortedSeq);
    } else {
    setRecipients(newRecipients);
      setSequenceInputs(newSeqInputs);
    }
    } finally {
      if (needsApiDelete) {
        setRemovingRecipientIndex(null);
      }
    }
  };

  // Handle Dropbox upload
  const handleDropboxUpload = async () => {
    const appKey = process.env.REACT_APP_DROPBOX_APP_KEY;
    if (!appKey) {
      alert('Dropbox integration requires REACT_APP_DROPBOX_APP_KEY in .env file');
      return;
    }

    try {
      await loadDropboxScript(appKey);
      
      if (window.Dropbox) {
        window.Dropbox.choose({
          success: (files) => {
            const filePromises = files.map((file) => {
              return fetch(file.link)
                .then((response) => response.blob())
                .then((blob) => {
                  const fileName = file.name || `dropbox-file-${Date.now()}.pdf`;
                  return new File([blob], fileName, { type: 'application/pdf' });
                });
            });
            
            Promise.all(filePromises).then((fileObjects) => {
              void handleFileSelect(fileObjects);
            }).catch((error) => {
              console.error('Error downloading Dropbox files:', error);
              setUploadError('Failed to download files from Dropbox');
            });
          },
          cancel: () => {
            console.log('Dropbox picker cancelled');
          },
          linkType: 'direct',
          multiselect: true,
          extensions: ['.pdf'],
          folderselect: false,
        });
      }
    } catch (error) {
      console.error('Dropbox error:', error);
      setUploadError('Failed to load Dropbox picker. Please check your API key.');
    }
  };

  // Handle Google Drive upload
  const handleGoogleDriveUpload = async () => {
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    if (!apiKey) {
      alert('Google Drive integration requires REACT_APP_GOOGLE_API_KEY in .env file');
      return;
    }

    try {
      await loadGooglePicker();
      
      // Note: Google Picker requires OAuth authentication
      // For production, implement OAuth flow on backend
      if (window.gapi && window.gapi.picker) {
        const view = new window.gapi.picker.DocsView(window.gapi.picker.ViewId.DOCS);
        view.setMimeTypes('application/pdf');
        view.setSelectModeEnabled(true);

        const picker = new window.gapi.picker.PickerBuilder()
          .setDeveloperKey(apiKey)
          .addView(view)
          .setCallback((data) => {
            if (data[window.gapi.picker.Response.ACTION] === window.gapi.picker.Action.PICKED) {
              const docs = data[window.gapi.picker.Response.DOCUMENTS];
              // Note: To download files, you need OAuth token
              // For now, show a message that backend integration is needed
              alert(`Selected ${docs.length} file(s) from Google Drive. Backend integration required to download files.`);
              console.log('Selected Google Drive files:', docs);
            }
          })
          .build();
        
        picker.setVisible(true);
      }
    } catch (error) {
      console.error('Google Drive error:', error);
      setUploadError('Failed to load Google Drive picker. Please check your API key.');
    }
  };

  // Handle OneDrive upload
  const handleOneDriveUpload = async () => {
    const clientId = process.env.REACT_APP_ONEDRIVE_CLIENT_ID;
    if (!clientId) {
      alert('OneDrive integration requires REACT_APP_ONEDRIVE_CLIENT_ID in .env file');
      return;
    }

    try {
      await loadOneDriveScript();
      
      if (window.OneDrive) {
        window.OneDrive.open({
          clientId: clientId,
          action: 'query',
          multiSelect: true,
          advanced: {
            queryParameters: '$filter=file%20eq%20pdf',
            redirectUri: window.location.origin,
          },
          success: (files) => {
            if (files && files.value) {
              const filePromises = files.value.map((file) => {
                const downloadUrl = file['@microsoft.graph.downloadUrl'] || file.downloadUrl;
                if (!downloadUrl) {
                  console.error('No download URL for file:', file);
                  return null;
                }
                return fetch(downloadUrl)
                  .then((response) => response.blob())
                  .then((blob) => {
                    return new File([blob], file.name || `onedrive-file-${Date.now()}.pdf`, { type: 'application/pdf' });
                  });
              }).filter(Boolean);
              
              Promise.all(filePromises).then((fileObjects) => {
                void handleFileSelect(fileObjects);
              }).catch((error) => {
                console.error('Error downloading OneDrive files:', error);
                setUploadError('Failed to download files from OneDrive');
              });
            }
          },
          cancel: () => {
            console.log('OneDrive picker cancelled');
          },
          error: (error) => {
            console.error('OneDrive picker error:', error);
            setUploadError('Error accessing OneDrive. Please check your credentials.');
          },
        });
      }
    } catch (error) {
      console.error('OneDrive error:', error);
      setUploadError('Failed to load OneDrive picker. Please check your client ID.');
    }
  };

  // Handle Box upload
  const handleBoxUpload = async () => {
    const clientId = process.env.REACT_APP_BOX_CLIENT_ID;
    if (!clientId) {
      alert('Box integration requires REACT_APP_BOX_CLIENT_ID in .env file');
      return;
    }

    try {
      await loadBoxScript();
      
      if (window.Box) {
        const boxPicker = window.Box.Picker({
          clientId: clientId,
          linkType: 'direct',
          multiselect: true,
          extensions: ['pdf'],
        });

        boxPicker.addListener(window.Box.EventType.CHOOSE, (items) => {
          const filePromises = items.map((item) => {
            return fetch(item.url)
              .then((response) => response.blob())
              .then((blob) => {
                return new File([blob], item.name || `box-file-${Date.now()}.pdf`, { type: 'application/pdf' });
              });
          });
          
          Promise.all(filePromises).then((fileObjects) => {
            void handleFileSelect(fileObjects);
          }).catch((error) => {
            console.error('Error downloading Box files:', error);
            setUploadError('Failed to download files from Box');
          });
        });

        boxPicker.show();
      }
    } catch (error) {
      console.error('Box error:', error);
      setUploadError('Failed to load Box picker. Please check your client ID.');
    }
  };

  // Handle template upload
  // eslint-disable-next-line no-unused-vars
  const handleTemplateUpload = () => {
    // Navigate to templates or show template selector
    console.log('Template upload - to be implemented');
    alert('Template selection will be implemented');
  };

  // Initialize selected recipient on step change
  useEffect(() => {
    if (currentStep === 2 && recipients.length > 0 && !selectedRecipient) {
      setSelectedRecipient(recipients[0]);
    }
  }, [currentStep, recipients, selectedRecipient]);

  // Close context menu on outside pointer down / scroll / Escape (capture phase = reliable on SVG/canvas).
  useEffect(() => {
    if (!contextMenu.show) return undefined;

    const close = () => {
        setContextMenu({ show: false, x: 0, y: 0, fieldId: null });
    };

    const isInsideMenu = (target) => {
      if (!(target instanceof Node)) return false;
      return Boolean(contextMenuRef.current?.contains(target));
    };

    const handlePointerDown = (e) => {
      if (isInsideMenu(e.target)) return;
      close();
    };

    const handleScroll = () => {
      close();
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };

    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('touchstart', handlePointerDown, true);
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('touchstart', handlePointerDown, true);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [contextMenu.show]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore global shortcuts only while typing/editing actual text fields.
      const target = e.target;
      const isTextInput =
        target instanceof HTMLInputElement &&
        !['button', 'checkbox', 'radio', 'range', 'file', 'color', 'submit', 'reset'].includes(
          (target.type || '').toLowerCase()
        );
      const isTypingTarget =
        isTextInput ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable);
      if (isTypingTarget) {
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        const key = (e.key || '').toLowerCase();
        if (key === 'z') {
          // Undo
          e.preventDefault();
          handleUndo();
        } else if (key === 'y') {
          // Redo
          e.preventDefault();
          handleRedo();
        } else if (key === 'x' && (selectedField || selectedFields.length > 0)) {
          e.preventDefault();
          const ids = selectedFields.length > 0
            ? selectedFields
            : (selectedField ? [selectedField.id] : []);
          handleCutFields(ids);
        } else if (key === 'c' && (selectedField || selectedFields.length > 0)) {
          e.preventDefault();
          handleCopyField();
        } else if (key === 'v' && (copiedField || copiedFields.length > 0)) {
          e.preventDefault();
          handlePasteField();
        } else if (key === 'a') {
          // Select all fields
          e.preventDefault();
          const allFieldIds = documentFields.map(f => f.id);
          setSelectedFields(allFieldIds);
          if (allFieldIds.length > 0) {
            setSelectedField(documentFields[0]);
          }
        }
      } else if (
        (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
        (selectedField || selectedFields.length > 0)
      ) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        const targetIds = selectedFields.length > 0
          ? selectedFields
          : (selectedField ? [selectedField.id] : []);

        if (targetIds.length === 0) return;
        console.log('🧪 [fields-keyboard-move]', {
          key: e.key,
          repeat: e.repeat,
          step,
          dx,
          dy,
          targetCount: targetIds.length,
          selectedFieldId: selectedField?.id || null,
          selectedFieldsCount: selectedFields.length,
        });

        const moveFieldByDelta = (field) => {
          // Convert to absolute doc coordinates first
          const currentGeometry = getFieldOverlayGeometry(field, svgPageHeights);
          const absX = currentGeometry.fieldX;
          const absY = currentGeometry.fieldY;

          const nextAbsX = absX + dx;
          const nextAbsY = absY + dy;

          const placement = resolvePlacementFromAbsolutePoint(nextAbsX, nextAbsY, svgPageHeights);
          if (!placement.pageInfo || !placement.documentId) return field;
          return {
            ...field,
            x: placement.x,
            y: placement.y,
            documentId: placement.documentId,
            apiDocumentId: placement.documentId,
            documentIndex: placement.documentIndex,
            page: placement.page,
          };
        };

        saveToHistory(documentFields);
        setDocumentFields((prevFields) => prevFields.map((f) => (
          targetIds.includes(f.id) ? moveFieldByDelta(f) : f
        )));
        if (selectedField) {
          const active = documentFields.find((f) => f.id === selectedField.id);
          if (active) {
            setSelectedField(moveFieldByDelta(active));
          }
        }
      } else if (e.key === 'Delete' && (selectedField || selectedFields.length > 0)) {
        e.preventDefault();
        if (selectedFields.length > 1) {
          // Delete multiple fields in one API request (when possible)
          handleDeleteFields(selectedFields);
          setSelectedFields([]);
          setSelectedField(null);
        } else if (selectedField) {
        handleDeleteField(selectedField.id);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedField, selectedFields, copiedField, copiedFields, documentFields, contextMenu, history, svgPageHeights]);

  // Close recipient dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (recipientDropdownRef.current && !recipientDropdownRef.current.contains(event.target)) {
        setShowRecipientDropdown(false);
      }
    };

    if (showRecipientDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showRecipientDropdown]);

  // Handle field dragging
  useEffect(() => {
    if (!draggedField) return;

    const handleMouseMoveCore = (e) => {
      const latestFields = documentFieldsRef.current || [];
      const field = latestFields.find(f => f.id === draggedField);
      if (!field) return;
      
      const draggedFields = draggedFieldsRef.current || [];
      const dragOffset = dragOffsetRef.current || { x: 0, y: 0 };
      // Check if we're dragging multiple fields
      const isMultiDrag = draggedFields.length > 1;

      const viewerContainer = svgContainerRef.current?.closest('.document-viewer');
      if (!viewerContainer || !svgContainerRef.current) return;

      // Get the document-preview container (the one with scroll)
      const previewContainer = svgContainerRef.current;
      const containerRect = previewContainer.getBoundingClientRect();
      // eslint-disable-next-line no-unused-vars
      const scrollTop = previewContainer.scrollTop;

      const point = getClientPoint(e);

      // Auto-scroll when near edges
      const edgeThreshold = 50; // pixels from edge to trigger scroll
      const scrollSpeed = 10; // pixels per scroll
      const mouseY = point.clientY;
      const containerTop = containerRect.top;
      const containerBottom = containerRect.bottom;

      // Clear existing auto-scroll
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }

      // Check if mouse is near top or bottom edge
      if (mouseY < containerTop + edgeThreshold) {
        // Near top edge - scroll up
        autoScrollIntervalRef.current = setInterval(() => {
          if (previewContainer.scrollTop > 0) {
            previewContainer.scrollTop = Math.max(0, previewContainer.scrollTop - scrollSpeed);
          } else {
            if (autoScrollIntervalRef.current) {
              clearInterval(autoScrollIntervalRef.current);
              autoScrollIntervalRef.current = null;
            }
          }
        }, 16); // ~60fps
      } else if (mouseY > containerBottom - edgeThreshold) {
        // Near bottom edge - scroll down
        autoScrollIntervalRef.current = setInterval(() => {
          const maxScroll = previewContainer.scrollHeight - previewContainer.clientHeight;
          if (previewContainer.scrollTop < maxScroll) {
            previewContainer.scrollTop = Math.min(maxScroll, previewContainer.scrollTop + scrollSpeed);
          } else {
            if (autoScrollIntervalRef.current) {
              clearInterval(autoScrollIntervalRef.current);
              autoScrollIntervalRef.current = null;
            }
          }
        }, 16); // ~60fps
      }

      // Calculate mouse position relative to the document container (accounting for current scroll)
      const mouseX = point.clientX - containerRect.left;
      const mouseYRelative = point.clientY - containerRect.top + previewContainer.scrollTop;

      // Get current scale factor to convert from screen coordinates to document coordinates
      const currentScale = getCurrentScale();
      
      // Convert screen coordinates to original document coordinates
      const docMouseX = mouseX / currentScale;
      const docMouseY = mouseYRelative / currentScale;

      // Calculate new position by subtracting the drag offset (which is also in document coordinates)
      const newX = docMouseX - dragOffset.x;
      const newY = docMouseY - dragOffset.y;

      const placement = resolvePlacementFromAbsolutePoint(newX, newY, svgPageHeights);
      if (!placement.pageInfo || !placement.documentId) return;
      const targetPage = placement.page;
      const targetDocumentId = placement.documentId;
      const pageRelativeY = placement.y;
      const pageRelativeX = placement.x;

      // Calculate the delta (change) from the original position
      // Get original position from dragStartState (state before drag started)
      const dragStartState = dragStartStateRef.current;
      const originalField = dragStartState?.find(f => f.id === draggedField) || field;
      const originalGeometry = getFieldOverlayGeometry(originalField, svgPageHeights);
      const originalFieldDocX = originalGeometry.fieldX;
      const originalFieldDocY = originalGeometry.fieldY;
      
      const deltaX = newX - originalFieldDocX;
      const deltaY = newY - originalFieldDocY;
      
      const pageHeights = svgPageHeightsRef.current;

      if (isMultiDrag && dragStartState) {
        dragLivePositionsRef.current.clear();
        draggedFields.forEach((fid) => {
          const base = latestFields.find((f) => f.id === fid);
          if (!base) return;
          const originalF = dragStartState.find((orig) => orig.id === fid) || base;

          const originalFGeometry = getFieldOverlayGeometry(originalF, svgPageHeights);
          const fOriginalDocX = originalFGeometry.fieldX;
          const fOriginalDocY = originalFGeometry.fieldY;

          const fNewDocX = fOriginalDocX + deltaX;
          const fNewDocY = fOriginalDocY + deltaY;

          const fPlacement = resolvePlacementFromAbsolutePoint(fNewDocX, fNewDocY, svgPageHeights);
          if (!fPlacement.pageInfo || !fPlacement.documentId) return;
          const updated = {
            ...base,
            x: fPlacement.x,
            y: fPlacement.y,
            documentId: fPlacement.documentId,
            apiDocumentId: fPlacement.documentId,
            documentIndex: fPlacement.documentIndex,
            page: fPlacement.page,
          };
          dragLivePositionsRef.current.set(fid, {
            x: fPlacement.x,
            y: fPlacement.y,
            documentId: fPlacement.documentId,
            apiDocumentId: fPlacement.documentId,
            documentIndex: fPlacement.documentIndex,
            page: fPlacement.page,
          });
          applyPlacedFieldDomGeometry(
            fieldOverlayElRefs.current.get(fid),
            { ...updated, __pageInfo: fPlacement.pageInfo },
            pageHeights
          );
        });
      } else {
        const updatedField = {
          ...field,
          x: pageRelativeX,
          y: pageRelativeY,
          documentId: targetDocumentId,
          apiDocumentId: targetDocumentId,
          documentIndex: placement.documentIndex,
          page: targetPage,
        };
        dragLivePositionsRef.current.clear();
        dragLivePositionsRef.current.set(draggedField, {
          x: pageRelativeX,
          y: pageRelativeY,
          documentId: targetDocumentId,
          apiDocumentId: targetDocumentId,
          documentIndex: placement.documentIndex,
          page: targetPage,
        });
        applyPlacedFieldDomGeometry(
          fieldOverlayElRefs.current.get(draggedField),
          { ...updatedField, __pageInfo: placement.pageInfo },
          pageHeights
        );
      }
    };

    const handleMouseMove = (e) => {
      if (e.cancelable && e.type.startsWith('touch')) e.preventDefault();
      if (dragRafRef.current) return;
      dragRafRef.current = window.requestAnimationFrame(() => {
        dragRafRef.current = null;
        handleMouseMoveCore(e);
      });
    };

    const handleMouseUp = () => {
      markPointerInteractionIdle();
      if (ignoreMouseFromTouchRef.current) {
        window.setTimeout(() => {
          ignoreMouseFromTouchRef.current = false;
        }, 400);
      }
      const dragStartState = dragStartStateRef.current;
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }

      const liveSnapshot =
        dragLivePositionsRef.current.size > 0 ? new Map(dragLivePositionsRef.current) : null;
      dragLivePositionsRef.current.clear();
      let didMoveDuringDrag = false;

      if (draggedField && dragStartState) {
        const fieldBeforeDrag = dragStartState.find((f) => f.id === draggedField);
        let fieldAfterDrag = (documentFieldsRef.current || []).find((f) => f.id === draggedField);
        if (liveSnapshot && liveSnapshot.has(draggedField)) {
          const u = liveSnapshot.get(draggedField);
          fieldAfterDrag = fieldAfterDrag ? { ...fieldAfterDrag, ...u } : null;
        }
        if (
          fieldBeforeDrag &&
          fieldAfterDrag &&
          (fieldBeforeDrag.x !== fieldAfterDrag.x ||
            fieldBeforeDrag.y !== fieldAfterDrag.y ||
            fieldBeforeDrag.page !== fieldAfterDrag.page ||
            getFieldDocumentId(fieldBeforeDrag) !== getFieldDocumentId(fieldAfterDrag))
        ) {
          didMoveDuringDrag = true;
          saveToHistory(dragStartState);
        }
      }
      if (!didMoveDuringDrag && liveSnapshot && dragStartState) {
        for (const [fid, pos] of liveSnapshot.entries()) {
          const original = dragStartState.find((f) => f.id === fid);
          if (!original) continue;
          if (
            original.x !== pos.x ||
            original.y !== pos.y ||
            original.page !== pos.page ||
            getFieldDocumentId(original) !== pos.documentId
          ) {
            didMoveDuringDrag = true;
            break;
          }
        }
      }

      if (liveSnapshot && liveSnapshot.size > 0) {
        setDocumentFields((prev) => {
          const next = prev.map((f) => {
            const u = liveSnapshot.get(f.id);
            return u ? { ...f, x: u.x, y: u.y, documentId: u.documentId, apiDocumentId: u.apiDocumentId, documentIndex: u.documentIndex, page: u.page } : f;
          });
          const sel = selectedFieldRef.current;
          if (sel && liveSnapshot.has(sel.id)) {
            const nf = next.find((f) => f.id === sel.id);
            if (nf) queueMicrotask(() => setSelectedField(nf));
          }
          return next;
        });
      }
      if (didMoveDuringDrag) {
        // Mouseup after drag can emit a click; suppress one field/background click to preserve selection.
        suppressNextFieldClickRef.current = true;
        suppressNextPreviewBackgroundClickRef.current = true;
      }

      if (dragRafRef.current) {
        window.cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
      setDraggedField(null);
      draggedFieldsRef.current = [];
      dragStartStateRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove, { passive: false });
    document.addEventListener('touchend', handleMouseUp);
    document.addEventListener('touchcancel', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('touchcancel', handleMouseUp);
      // Clean up auto-scroll interval
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      if (dragRafRef.current) {
        window.cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
    };
    // getCurrentScale reads the DOM; omit from deps to avoid re-binding listeners every parent render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggedField, svgPageHeights, zoomLevel, svgMaxWidth, svgTotalHeight]);

  // Handle field resizing (DOM-only during move; single state commit on mouseup — avoids full tree re-renders)
  useEffect(() => {
    if (!resizingField || !resizeHandle) return;

    const rid = resizingField;
    const handle = resizeHandle;
    resizeLiveGeometriesRef.current.clear();

    const handleMouseMove = (e) => {
      if (e.cancelable && e.type.startsWith('touch')) e.preventDefault();
      const latestFields = documentFieldsRef.current || [];
      const resizeStart = resizeStartRef.current;
      if (!resizeStart) return;

      const point = getClientPoint(e);
      const deltaX = point.clientX - resizeStart.mouseX;
      const deltaY = point.clientY - resizeStart.mouseY;
      const currentScale = getCurrentScale();
      const docDeltaX = deltaX / currentScale;
      const docDeltaY = deltaY / currentScale;

      const primaryGeomRaw = computeFieldResizeGeometry(
        resizeStart,
        handle,
        docDeltaX,
        docDeltaY
      );
      const primaryField = latestFields.find((f) => f.id === rid);
      if (!primaryField) return;

      const primaryGeom = clampReadOnlyFieldResizeGeometry(
        primaryField,
        primaryGeomRaw,
        handle,
        resizeStart
      );
      const { scaleX, scaleY } = getResizeScaleFactors(handle, resizeStart, primaryGeom);

      const fieldsToResize = resizingFieldsRef.current?.length
        ? resizingFieldsRef.current
        : [rid];
      const fieldStarts = resizeFieldStartsRef.current;
      const isMultiResize = fieldsToResize.length > 1;
      const pageHeights = svgPageHeightsRef.current;

      resizeLiveGeometriesRef.current.clear();
      fieldsToResize.forEach((fid) => {
        const baseField = latestFields.find((f) => f.id === fid);
        if (!baseField) return;

        const fStart = fieldStarts.get(fid) || resizeStart;
        const geomRaw =
          isMultiResize && fid !== rid
            ? applyProportionalResizeGeometry(fStart, handle, scaleX, scaleY)
            : primaryGeom;
        const geom = clampReadOnlyFieldResizeGeometry(
          baseField,
          geomRaw,
          handle,
          fStart
        );

        resizeLiveGeometriesRef.current.set(fid, geom);
        const visualField = { ...baseField, ...geom };
        const pageInfo = getFieldPageInfo(visualField, pageHeights);
        applyPlacedFieldDomGeometry(
          fieldOverlayElRefs.current.get(fid),
          { ...visualField, __pageInfo: pageInfo },
          pageHeights
        );
      });
    };

    const handleMouseUp = () => {
      markPointerInteractionIdle();
      if (ignoreMouseFromTouchRef.current) {
        window.setTimeout(() => {
          ignoreMouseFromTouchRef.current = false;
        }, 400);
      }
      const liveSnapshot =
        resizeLiveGeometriesRef.current.size > 0
          ? new Map(resizeLiveGeometriesRef.current)
          : null;
      resizeLiveGeometriesRef.current.clear();

      const resizeStartState = resizeStartStateRef.current;
      let didResize = false;
      if (liveSnapshot && resizeStartState) {
        for (const [fid, live] of liveSnapshot.entries()) {
          const fieldBeforeResize = resizeStartState.find((f) => f.id === fid);
          let fieldAfterResize = documentFieldsRef.current.find((f) => f.id === fid);
          if (live && fieldAfterResize) {
            fieldAfterResize = { ...fieldAfterResize, ...live };
          }
          if (
            fieldBeforeResize &&
            fieldAfterResize &&
            (fieldBeforeResize.width !== fieldAfterResize.width ||
              fieldBeforeResize.height !== fieldAfterResize.height ||
              fieldBeforeResize.x !== fieldAfterResize.x ||
              fieldBeforeResize.y !== fieldAfterResize.y)
          ) {
            didResize = true;
            break;
          }
        }
        if (didResize) {
          saveToHistory(resizeStartState);
        }
      }

      if (liveSnapshot && liveSnapshot.size > 0) {
        setDocumentFields((prev) => {
          const next = prev.map((f) => {
            const live = liveSnapshot.get(f.id);
            return live ? { ...f, ...live } : f;
          });
          const sel = selectedFieldRef.current;
          if (sel && liveSnapshot.has(sel.id)) {
            const nf = next.find((f) => f.id === sel.id);
            if (nf) queueMicrotask(() => setSelectedField(nf));
          }
          return next;
        });
        suppressNextFieldClickRef.current = true;
        suppressNextPreviewBackgroundClickRef.current = true;
      }

      setResizingField(null);
      setResizeHandle(null);
      resizingFieldsRef.current = [];
      resizeFieldStartsRef.current = new Map();
      resizeStartStateRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove, { passive: false });
    document.addEventListener('touchend', handleMouseUp);
    document.addEventListener('touchcancel', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('touchcancel', handleMouseUp);
    };
    // getCurrentScale reads the DOM; omit from deps to avoid re-binding listeners every parent render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizingField, resizeHandle, zoomLevel, svgMaxWidth, svgTotalHeight]);

  // Reset page when switching documents
  useEffect(() => {
    setCurrentPage(1);
    visiblePageRef.current = 1;
  }, [currentDocumentIndex]);

  // Track currently visible page from scroll position
  useEffect(() => {
    const viewer = svgContainerRef.current?.closest('.document-viewer');
    if (!viewer || !svgPageHeights.length) return;
    const onScroll = () => {
      const previousVisiblePage = visiblePageRef.current;
      const scale = getCurrentScale();
      const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
      // Convert viewport midpoint from scaled pixels back to document-space.
      const scrollMidDoc = (viewer.scrollTop + viewer.clientHeight / 2) / safeScale;

      for (let i = 0; i < svgPageHeights.length; i += 1) {
        const pageInfo = svgPageHeights[i];
        const pageBottom = pageInfo.y + pageInfo.height;
        if (scrollMidDoc >= pageInfo.y && scrollMidDoc < pageBottom) {
          visiblePageRef.current = i + 1;
          setCurrentPage(i + 1);
          if (visiblePageRef.current !== previousVisiblePage) {
            logVisiblePageDebug('[field-visible-page]', {
              reason: 'range-match',
              zoomLevel,
              zoomRenderScale,
              scale,
              scrollTop: viewer.scrollTop,
              clientHeight: viewer.clientHeight,
              scrollMidDoc,
              previousVisiblePage,
              nextVisiblePage: visiblePageRef.current,
              matchedPage: {
                index: i,
                y: pageInfo.y,
                height: pageInfo.height,
                bottom: pageBottom,
              },
            });
          }
          return;
        }
      }

      // Fallback when viewport center is above first page or below last page.
      if (scrollMidDoc < svgPageHeights[0].y) {
        visiblePageRef.current = 1;
        setCurrentPage(1);
      } else {
        visiblePageRef.current = svgPageHeights.length;
        setCurrentPage(svgPageHeights.length);
      }
      if (visiblePageRef.current !== previousVisiblePage) {
        logVisiblePageDebug('[field-visible-page]', {
          reason: 'fallback',
          zoomLevel,
          zoomRenderScale,
          scale,
          scrollTop: viewer.scrollTop,
          clientHeight: viewer.clientHeight,
          scrollMidDoc,
          previousVisiblePage,
          nextVisiblePage: visiblePageRef.current,
          firstPageY: svgPageHeights[0]?.y ?? null,
          lastPageY: svgPageHeights[svgPageHeights.length - 1]?.y ?? null,
          lastPageHeight: svgPageHeights[svgPageHeights.length - 1]?.height ?? null,
        });
      }
    };
    onScroll();
    viewer.addEventListener('scroll', onScroll, { passive: true });
    return () => viewer.removeEventListener('scroll', onScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgPageHeights, zoomRenderScale]);

  // Adjust currentDocumentIndex if it's out of bounds (e.g., after deletion)
  useEffect(() => {
    if (currentDocumentIndex >= uploadedFiles.length && uploadedFiles.length > 0) {
      setCurrentDocumentIndex(uploadedFiles.length - 1);
    }
  }, [uploadedFiles.length, currentDocumentIndex]);

  // Convert PDF page to SVG (via canvas with high DPI for crisp rendering)
  const convertPageToSvg = useCallback(async (page, viewport) => {
    // Use 2x or 3x scale for high DPI rendering to ensure crisp text
    // Higher scale = better quality but larger file size
    const renderScale = 2; // 2x resolution for crisp rendering
    
    // Create a high-resolution canvas
    const canvas = document.createElement('canvas');
    const scaledWidth = viewport.width * renderScale;
    const scaledHeight = viewport.height * renderScale;
    canvas.width = scaledWidth;
    canvas.height = scaledHeight;
    const context = canvas.getContext('2d');
    
    // Create a scaled viewport for rendering at higher resolution
    const scaledViewport = page.getViewport({ scale: viewport.scale * renderScale });
    
    // Render PDF page to canvas at high resolution
    await page.render({
      canvasContext: context,
      viewport: scaledViewport
    }).promise;
    
    // Convert canvas to data URL with maximum quality (PNG for lossless quality)
    const imageDataUrl = canvas.toDataURL('image/png', 1.0);
    
    // Create SVG with embedded image at original display size but with high-res source
    // Use smooth scaling for crisp text rendering
    const svgString = `<svg width="${viewport.width}" height="${viewport.height}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <image x="0" y="0" width="${viewport.width}" height="${viewport.height}" xlink:href="${imageDataUrl}" preserveAspectRatio="xMidYMid meet" style="image-rendering: -webkit-optimize-contrast; image-rendering: auto;"/>
    </svg>`;
    
    return svgString;
  }, []);

  const applySvgStateFromBundles = useCallback((bundles) => {
    const safeBundles = Array.isArray(bundles) ? bundles.filter(Boolean) : [];
    if (safeBundles.length === 0) {
      setDocumentPages({});
      setDocumentPageSvgs({});
      setPdfDocuments([]);
      setMergedSvg('');
      setDocumentEditorSvgSegments([]);
      setSvgPageHeights([]);
      setSvgMaxWidth(0);
      setSvgTotalHeight(0);
      return;
    }

        const initialExpanded = {};
        const initialPages = {};
        const loadedDocs = [];
        const allSvgPages = [];
    const docPageSvgs = {};

    safeBundles.forEach((bundle, docIndex) => {
          initialExpanded[docIndex] = true;
      initialPages[docIndex] = bundle.numPages || 1;
      loadedDocs.push(null);
      docPageSvgs[docIndex] = bundle.pageSvgs || {};
      (bundle.pages || []).forEach((p) => {
                allSvgPages.push({
          ...p,
                  documentIndex: docIndex,
        });
      });
    });

    const maxWidth = Math.max(...allSvgPages.map((p) => p.viewport.width), 800);
        let cumulativeY = 0;
        const pageHeightMap = [];
    const pageBreakHeight = 20;

        // Layout-only pass: field coordinates and segment tops (avoid one giant merged SVG string — it can exceed JS max string length).
        allSvgPages.forEach((pageData, index) => {
          const width = pageData.viewport.width;
          const height = pageData.viewport.height;
          const offsetX = 0;

          if (index > 0) {
            cumulativeY += pageBreakHeight;
          }

          pageHeightMap.push({
            y: cumulativeY,
        height,
        offsetX,
        width,
            documentIndex: pageData.documentIndex,
        pageNumber: pageData.pageNumber,
          });
          cumulativeY += height;
        });

        const totalHeight = cumulativeY;
        const mergedSvgString = `<svg width="${maxWidth}" height="${totalHeight}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${maxWidth} ${totalHeight}"></svg>`;

    // One merged SVG per uploaded document (stacked at global Y offsets — field math unchanged).
    const firstGlobalPageIndexByDoc = [];
    let pageCursor = 0;
    safeBundles.forEach((bundle, docIndex) => {
      firstGlobalPageIndexByDoc[docIndex] = pageCursor;
      pageCursor += (bundle.pages || []).length;
    });

    const pagesByDoc = Array.from({ length: safeBundles.length }, () => []);
    allSvgPages.forEach((p) => {
      pagesByDoc[p.documentIndex].push(p);
    });

    const segments = [];
    let globalPageNum = 1;
    pagesByDoc.forEach((docPages, docIndex) => {
      if (!docPages.length) return;
      const docMergedParts = [];
      let docCumulativeY = 0;
      docPages.forEach((pageData, localIndex) => {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(pageData.svg, 'image/svg+xml');
        const svgElement = svgDoc.documentElement;
        const width = pageData.viewport.width;
        const height = pageData.viewport.height;
        const offsetX = 0;
        const svgContent = svgElement.innerHTML;

        if (localIndex > 0) {
          const pageBreak = `<g transform="translate(0, ${docCumulativeY})">
          <line x1="0" y1="0" x2="${maxWidth}" y2="0" stroke="#e0e0e0" stroke-width="2" stroke-dasharray="5,5"/>
          <rect x="0" y="0" width="${maxWidth}" height="${pageBreakHeight}" fill="#f8f9fa" opacity="0.5"/>
        </g>`;
          docMergedParts.push(pageBreak);
          docCumulativeY += pageBreakHeight;
        }

        const pageGroup = `<g data-vs-page="${globalPageNum}" data-vs-doc="${pageData.documentIndex}" data-vs-doc-page="${pageData.pageNumber}" transform="translate(${offsetX}, ${docCumulativeY})">
        <rect x="0" y="0" width="${width}" height="${height}" fill="#ffffff" stroke="#d0d0d0" stroke-width="1"/>
        ${svgContent}
      </g>`;
        docMergedParts.push(pageGroup);
        docCumulativeY += height;
        globalPageNum += 1;
      });

      const docSvgHeight = docCumulativeY;
      const firstG = firstGlobalPageIndexByDoc[docIndex];
      const topY = pageHeightMap[firstG]?.y ?? 0;

      let innerMarkup = '';
      try {
        innerMarkup = joinSvgMarkupParts(docMergedParts);
      } catch (e) {
        console.error('createEnvelope: document segment SVG too large', docIndex, e);
      }

      segments.push({
        documentIndex: docIndex,
        top: topY,
        height: docSvgHeight,
        innerMarkup,
      });
    });

        setExpandedDocuments(initialExpanded);
        setDocumentPages(initialPages);
        setPdfDocuments(loadedDocs);
        setMergedSvg(mergedSvgString);
    setDocumentEditorSvgSegments(segments);
        setSvgPageHeights(pageHeightMap);
        setSvgMaxWidth(maxWidth);
        setSvgTotalHeight(totalHeight);
    setDocumentPageSvgs(docPageSvgs);
  }, []);

  purgeDocumentRowAtIndexRef.current = (removeIndex) => {
    if (!Number.isFinite(removeIndex) || removeIndex < 0) return;
    const rowsBefore = [...(envelopeDocumentRowsRef.current || [])];
    if (removeIndex >= rowsBefore.length) return;
    const purgedRow = rowsBefore[removeIndex];
    const rowFile = purgedRow.file;
    const rowDocId =
      purgedRow.serverDocumentId ?? rowFile?.documentId ?? rowFile?.document_id ?? null;

    if (documentSaveTimeoutRef.current) {
      clearTimeout(documentSaveTimeoutRef.current);
      documentSaveTimeoutRef.current = null;
    }

    clearDocumentBufferCacheForFile(rowFile, removeIndex);
    clearDocumentSvgCacheForFile(rowFile, removeIndex);

    const rowCid = String(getFileClientDocumentId(rowFile) || '').trim();
    if (rowCid) removeDocumentUploadRegistryEntry(rowCid);

    const nextRows = rowsBefore.filter((_, i) => i !== removeIndex);
    envelopeRowsCommitRef.current(nextRows);
    const nextFiles = nextRows.map((r) => r.file);

    setDocumentFields((prev) => {
      const deletedDocKey = rowDocId != null && rowDocId !== '' ? String(rowDocId) : null;
      return prev.filter((field) => {
        const fieldDocId = getFieldDocumentId(field);
        const fieldDocKey = fieldDocId != null && fieldDocId !== '' ? String(fieldDocId) : null;
        const sameDocumentId = deletedDocKey && fieldDocKey && fieldDocKey === deletedDocKey;
        return !sameDocumentId;
      });
    });

    const nextBundles = (documentSvgBundles || []).filter((_, i) => i !== removeIndex);
    setDocumentSvgBundles(nextBundles);
    if (currentStep === 2) {
      applySvgStateFromBundles(nextBundles);
    } else {
      setMergedSvg('');
      setDocumentEditorSvgSegments([]);
      setSvgPageHeights([]);
      setDocumentPageSvgs({});
      setDocumentPages({});
      setPdfDocuments([]);
    }

    if (nextFiles.length <= 0) {
      localStorage.removeItem('current_draft_document_id');
    }

    const progressKey = getDocumentCacheKey(rowFile, removeIndex);
    setUploadProgressEntries([{ key: progressKey }], null);
  };

  const ensureDocumentSvgData = useCallback(async (file, docIndex = 0) => {
    const key = getDocumentCacheKey(file, docIndex);
    const cached = documentSvgCacheRef.current.get(key);
    if (cached) return cached;

    const pending = documentSvgPromiseRef.current.get(key);
    if (pending) return pending;

    const promise = (async () => {
      const arrayBuffer = await resolveDocumentArrayBuffer(file, docIndex);
      const pdf = await pdfjsLib.getDocument({
        data: arrayBuffer,
        verbosity: pdfjsLib.VerbosityLevel?.ERRORS,
      }).promise;
      const numPages = pdf.numPages;
      logZipSvgDebug('🧾 [SVG] Starting SVG generation for document', {
        documentIndex: docIndex,
        pages: numPages,
      });
      const pages = [];
      const pageSvgs = {};

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.0 });
        try {
          const svgString = await convertPageToSvg(page, viewport);
          pages.push({
            documentIndex: docIndex,
            pageNumber: pageNum,
            svg: svgString,
            viewport,
            totalPages: numPages,
          });
          pageSvgs[pageNum] = svgString;
          logZipSvgDebug('🖼️ [SVG] Generated page SVG', {
            documentIndex: docIndex,
            pageNumber: pageNum,
            svgLength: svgString?.length || 0,
          });
        } catch (error) {
          const placeholderSvg = `<svg width="${viewport.width}" height="${viewport.height}" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#f0f0f0"/><text x="50%" y="50%" text-anchor="middle" fill="#999">Error loading page</text></svg>`;
          pages.push({
            documentIndex: docIndex,
            pageNumber: pageNum,
            svg: placeholderSvg,
            viewport,
            totalPages: numPages,
          });
          pageSvgs[pageNum] = placeholderSvg;
          console.error(`Error converting page ${docIndex}-${pageNum} to SVG:`, error);
        }
      }

      const payload = { numPages, pages, pageSvgs };
      documentSvgCacheRef.current.set(key, payload);
      logZipSvgDebug('✅ [SVG] Document SVG generation complete', {
        documentIndex: docIndex,
        pagesGenerated: pages.length,
      });
      return payload;
    })().finally(() => {
      documentSvgPromiseRef.current.delete(key);
    });

    documentSvgPromiseRef.current.set(key, promise);
    return promise;
  }, [convertPageToSvg, getDocumentCacheKey, resolveDocumentArrayBuffer]);

  const prewarmDocumentSvgs = useCallback(async (files) => {
    const list = Array.isArray(files) ? files : [];
    if (list.length === 0) return;
    const signature = list
      .map((file, idx) => getDocumentCacheKey(file, idx))
      .join('|');

    if (svgPrewarmPromiseRef.current && svgPrewarmSignatureRef.current === signature) {
      return svgPrewarmPromiseRef.current;
    }

    const runPromise = (async () => {
      isSvgPrewarmActiveRef.current = true;
      const apiItems = [];
      const localItems = [];
      const failedSvgItems = new Map();

      list.forEach((file, idx) => {
        const key = getDocumentCacheKey(file, idx);
        const alreadyHasSvg = documentSvgCacheRef.current.has(key);
        if (alreadyHasSvg) {
          const name = file?.name || `Document ${idx + 1}`;
          if (file?.fromApi) {
            setUploadProgressEntries(
              [{ key, name }],
              (item, _itemIdx, previous) => (previous?.status === 'error'
                ? previous
                : { percent: 100, status: 'ready', fileName: item.name })
            );
          }
          return;
        }
        if (file?.fromApi) {
          const docId = normalizeDocumentId(file?.documentId ?? file?.document_id);
          apiItems.push({ file, idx, docId });
        } else {
          localItems.push({ file, idx });
        }
      });

      if (apiItems.length === 0 && localItems.length === 0) {
        return;
      }

      // Draft API flow:
      // API requests are serialized by enqueueZipBatchRequest (one at a time),
      // while per-chunk download/extract/svg workers can overlap.
      const requestBatchSize = 10;
      const maxChunkWorkers = 2;
      const activeChunkWorkers = new Set();
      for (let offset = 0; offset < apiItems.length; offset += requestBatchSize) {
        const chunk = apiItems.slice(offset, offset + requestBatchSize);
        const chunkDocIds = chunk
          .map((item) => normalizeDocumentId(item?.docId))
          .filter((id) => id != null);
        const chunkHydrationPromise = hydrateDraftDocumentBuffersFromZip(chunk.map((item) => item.file));
        const runChunk = (async () => {
          chunkDocIds.forEach((docId) => {
            documentHydrationPromiseRef.current.set(docId, chunkHydrationPromise);
          });
          logZipSvgDebug('🔁 [PIPELINE] Processing API chunk', {
            startOffset: offset,
            size: chunk.length,
            documentIds: chunk.map((item) => item.docId),
          });
          await chunkHydrationPromise;
          logZipSvgDebug('✅ [PIPELINE] ZIP hydration done for chunk', {
            documentIds: chunk.map((item) => item.docId),
          });

          for (let i = 0; i < chunk.length; i += 1) {
            const { file, idx } = chunk[i];
            const progressKey = getDocumentCacheKey(file, idx);
            const progressName = file?.name || `Document ${idx + 1}`;
            try {
              setUploadProgressEntries(
                [{ key: progressKey, name: progressName }],
                { percent: 80, status: 'preparing_svg', fileName: progressName }
              );
              const bundle = await ensureDocumentSvgData(file, idx);
              const pageCount = bundle?.numPages;
              if (Number.isFinite(pageCount) && pageCount > 0) {
                setDocumentPages((prev) => {
                  if (prev[idx] === pageCount) return prev;
                  return { ...prev, [idx]: pageCount };
                });
              }
              setUploadProgressEntries(
                [{ key: progressKey, name: progressName }],
                { percent: 100, status: 'ready', fileName: progressName }
              );
              logZipSvgDebug('✅ [PIPELINE] SVG ready for document', {
                documentIndex: idx,
                pages: pageCount,
              });
            } catch (error) {
              setUploadProgressEntries(
                [{ key: progressKey, name: progressName }],
                { percent: 80, status: 'preparing_svg', fileName: progressName }
              );
              failedSvgItems.set(idx, { file, idx, key: progressKey, name: progressName });
              console.warn(`⚠️ SVG pre-convert failed for document ${idx}:`, error);
            }
          }
        })();

        activeChunkWorkers.add(runChunk);
        runChunk.finally(() => {
          chunkDocIds.forEach((docId) => {
            const current = documentHydrationPromiseRef.current.get(docId);
            if (current === chunkHydrationPromise) {
              documentHydrationPromiseRef.current.delete(docId);
            }
          });
          activeChunkWorkers.delete(runChunk);
        });

        if (activeChunkWorkers.size >= maxChunkWorkers) {
          await Promise.race(activeChunkWorkers);
        }
      }

      if (activeChunkWorkers.size > 0) {
        await Promise.all(Array.from(activeChunkWorkers));
      }

      // Local files can keep a small parallelism.
      const localConcurrency = 2;
      let localCursor = 0;
      const localWorkers = Array.from({ length: Math.min(localConcurrency, localItems.length) }, () => (async () => {
        while (localCursor < localItems.length) {
          const item = localItems[localCursor];
          localCursor += 1;
          try {
            const bundle = await ensureDocumentSvgData(item.file, item.idx);
            const pageCount = bundle?.numPages;
            if (Number.isFinite(pageCount) && pageCount > 0) {
              setDocumentPages((prev) => {
                if (prev[item.idx] === pageCount) return prev;
                return { ...prev, [item.idx]: pageCount };
              });
            }
          } catch (error) {
            failedSvgItems.set(item.idx, {
              file: item.file,
              idx: item.idx,
              key: getDocumentCacheKey(item.file, item.idx),
              name: item.file?.name || `Document ${item.idx + 1}`,
            });
            console.warn(`⚠️ SVG pre-convert failed for document ${item.idx}:`, error);
          }
        }
      })());
      await Promise.all(localWorkers);

      if (failedSvgItems.size > 0) {
        const retryItems = Array.from(failedSvgItems.values());
        logZipSvgDebug('🔁 [PIPELINE] Retrying failed SVG documents', {
          count: retryItems.length,
          indexes: retryItems.map((item) => item.idx),
        });

        for (let i = 0; i < retryItems.length; i += 1) {
          const retryItem = retryItems[i];
          try {
            // Retry phase is conversion-only.
            // Do NOT issue new ZIP/API requests here; use already hydrated PDF bytes.
            const bundle = await ensureDocumentSvgData(retryItem.file, retryItem.idx);
            const pageCount = bundle?.numPages;
            if (Number.isFinite(pageCount) && pageCount > 0) {
              setDocumentPages((prev) => {
                if (prev[retryItem.idx] === pageCount) return prev;
                return { ...prev, [retryItem.idx]: pageCount };
              });
            }
            if (retryItem?.file?.fromApi) {
              setUploadProgressEntries(
                [{ key: retryItem.key, name: retryItem.name }],
                { percent: 100, status: 'ready', fileName: retryItem.name }
              );
            }
            failedSvgItems.delete(retryItem.idx);
          } catch (error) {
            console.warn(`⚠️ Final SVG retry failed for document ${retryItem.idx}:`, error);
          }
        }
      }

      // Cleanup stale progress bars: keep "ready" only for actually cached SVGs.
      // Do not clear local-file upload rows (uploading/pending/processing/complete); SVG prewarm
      // finishing must not wipe server-upload progress or users see "Waiting to upload" mid-transfer.
      const finalizeProgressItems = list.map((file, idx) => {
        const key = getDocumentCacheKey(file, idx);
        return {
          key,
          name: file?.name || `Document ${idx + 1}`,
          cached: documentSvgCacheRef.current.has(key),
          isApiDocument: Boolean(file?.fromApi),
        };
      });
      setUploadProgressEntries(
        finalizeProgressItems,
        (item, _itemIdx, previous) => {
          if (item.cached && item.isApiDocument) {
            return { percent: 100, status: 'ready', fileName: item.name };
          }
          if (!item.isApiDocument) {
            return previous ?? null;
          }
          return null;
        }
      );
    })();

    svgPrewarmSignatureRef.current = signature;
    setIsSvgPrewarming(true);
    svgPrewarmPromiseRef.current = runPromise;
    try {
      await runPromise;
    } finally {
      isSvgPrewarmActiveRef.current = false;
      if (svgPrewarmPromiseRef.current === runPromise) {
        svgPrewarmPromiseRef.current = null;
        svgPrewarmSignatureRef.current = '';
        setIsSvgPrewarming(false);
      }
    }
  }, [ensureDocumentSvgData, getDocumentCacheKey, hydrateDraftDocumentBuffersFromZip, normalizeDocumentId, setUploadProgressEntries]);

  const hasPendingDocumentUploads = useMemo(() => {
    void documentUploadRegistryEpoch;
    if (uploadedFiles.length === 0) return false;

    const isMissingServerDocumentId = (id) =>
      !(id !== undefined && id !== null && id !== '' && id !== 0);

    const reg = documentUploadRegistryRef.current;
    return uploadedFiles.some((file, index) => {
      if (!file || file.fromApi) return false;
      if (!isMissingServerDocumentId(documentIds[index])) return false;
      const cid = String(getFileClientDocumentId(file) || '').trim();
      const rs = cid ? reg.get(cid)?.status : null;
      if (rs === 'uploading' || rs === 'pending') return true;
      if (!cid) {
      const key = getDocumentCacheKey(file, index);
      const status = documentUploadProgress?.[key]?.status;
      return status === 'uploading' || status === 'pending' || status === 'processing';
      }
      if (!rs) return true;
      return false;
    });
  }, [uploadedFiles, documentIds, documentUploadProgress, getDocumentCacheKey, documentUploadRegistryEpoch]);

  // True only while this row is actively transferring/processing — not while merely queued (status "pending").
  const isDocumentRowUploadInFlight = useCallback(
    (index) => {
      void documentUploadRegistryEpoch;
    const file = uploadedFiles[index];
    if (!file || file.fromApi) return false;
      const docId = documentIds[index];
      const missingServerId = !(docId !== undefined && docId !== null && docId !== '' && docId !== 0);
      if (!missingServerId) return false;
      const cid = String(getFileClientDocumentId(file) || '').trim();
      if (cid && documentUploadRegistryRef.current.get(cid)?.status === 'uploading') return true;
    const key = getDocumentCacheKey(file, index);
      const st = documentUploadProgress?.[key]?.status;
      return ['uploading', 'processing', 'downloading', 'extracting', 'preparing_svg'].includes(st);
    },
    [uploadedFiles, documentIds, documentUploadProgress, getDocumentCacheKey, documentUploadRegistryEpoch]
  );

  // Do not memoize against uploadedFiles only: SVG cache fills via ref mutation,
  // so this must re-evaluate on any render to avoid stale "pending" state.
  const hasPendingSvgPrep = uploadedFiles.some((file, index) => {
    const key = getDocumentCacheKey(file, index);
    return !documentSvgCacheRef.current.has(key);
  });

  const getDocumentThumbnailSvg = useCallback((file, index) => {
    const key = getDocumentCacheKey(file, index);
    return documentSvgCacheRef.current.get(key)?.pageSvgs?.[1] || null;
  }, [getDocumentCacheKey]);

  const isStep2ProcessingDocuments =
    isDraftAssetsPreparing || hasPendingDocumentUploads || hasPendingSvgPrep || isSvgPrewarming;
  // Keep "Next" clickable after successful uploads even if SVG prewarm is still finishing;
  // click handler already awaits pending SVG work before step transition.
  const isNextStepBlocked = isDraftAssetsPreparing || hasPendingDocumentUploads || isNextTransitioning;

  // Keep Step-1 page labels in sync with cache/progressive prewarm results.
  useEffect(() => {
    if (!uploadedFiles.length) {
      setDocumentPages({});
      return;
    }
    setDocumentPages((prev) => {
      const next = {};
      uploadedFiles.forEach((file, idx) => {
        const key = getDocumentCacheKey(file, idx);
        const cached = documentSvgCacheRef.current.get(key);
        const cachedPages = cached?.numPages;
        if (Number.isFinite(cachedPages) && cachedPages > 0) {
          next[idx] = cachedPages;
        } else if (Number.isFinite(prev[idx]) && prev[idx] > 0) {
          next[idx] = prev[idx];
        }
      });
      const same = Object.keys(next).length === Object.keys(prev).length &&
        Object.keys(next).every((k) => next[k] === prev[k]);
      return same ? prev : next;
    });
  }, [uploadedFiles, getDocumentCacheKey]);

  // Pre-convert docs to SVG in background immediately after upload/download restore.
  useEffect(() => {
    if (!uploadedFiles.length) return undefined;
    const hasMissingSvg = uploadedFiles.some((file, idx) => {
      const key = getDocumentCacheKey(file, idx);
      return !documentSvgCacheRef.current.has(key);
    });
    if (!hasMissingSvg) return undefined;
    prewarmDocumentSvgs(uploadedFiles);
    return undefined;
  }, [uploadedFiles, prewarmDocumentSvgs, getDocumentCacheKey]);

  // Finalize prewarm as soon as transfer/hydration settles so Step 2 opens "fully warm".
  useEffect(() => {
    if (!uploadedFiles.length) {
      finalizedSvgPrewarmSignatureRef.current = '';
      return undefined;
    }
    const hasPendingUploadsLocal = uploadedFiles.some((file, index) => {
      if (!file || file.fromApi) return false;
      const docId = documentIds[index];
      const isMissingServerDocumentId = !(docId !== undefined && docId !== null && docId !== '' && docId !== 0);
      if (!isMissingServerDocumentId) return false;
      const key = getDocumentCacheKey(file, index);
      const status = documentUploadProgress?.[key]?.status;
      return status === 'uploading' || status === 'pending' || status === 'processing';
    });
    if (isDraftAssetsPreparing || hasPendingUploadsLocal) return undefined;

    const hasPendingSvgPrepLocal = uploadedFiles.some((file, index) => {
      const key = getDocumentCacheKey(file, index);
      return !documentSvgCacheRef.current.has(key);
    });

    const signature = uploadedFiles
      .map((file, idx) => getDocumentCacheKey(file, idx))
      .join('|');

    if (!hasPendingSvgPrepLocal) {
      finalizedSvgPrewarmSignatureRef.current = signature;
      return undefined;
    }
    if (finalizedSvgPrewarmSignatureRef.current === signature && isSvgPrewarming) {
      return undefined;
    }

    let cancelled = false;
    const run = async () => {
      try {
        await prewarmDocumentSvgs(uploadedFiles);
        if (!cancelled) {
          finalizedSvgPrewarmSignatureRef.current = signature;
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('⚠️ Finalize SVG prewarm pass failed:', error);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    uploadedFiles,
    documentIds,
    documentUploadProgress,
    isSvgPrewarming,
    isDraftAssetsPreparing,
    prewarmDocumentSvgs,
    getDocumentCacheKey,
  ]);

  // Load/reuse per-document SVG bundles when entering field step.
  useEffect(() => {
    if (uploadedFiles.length > 0 && currentStep === 2) {
      const loadAndConvertPDFs = async () => {
        const cycleId = Date.now();
        svgLoadCycleRef.current = cycleId;

        const hasApiDraftFiles = uploadedFiles.some((file, docIndex) => {
          if (!file?.fromApi) return false;
          const key = getDocumentCacheKey(file, docIndex);
          return !documentSvgCacheRef.current.has(key);
        });
        if (hasApiDraftFiles) {
          try {
            if (svgPrewarmPromiseRef.current) {
              await svgPrewarmPromiseRef.current;
            }
          } catch (error) {
            console.warn('⚠️ Step 2 prewarm wait failed:', error);
          }
          if (svgLoadCycleRef.current !== cycleId) return;
        }

        // 1) Render immediately from already converted cache (no blocking on all docs).
        const immediateBundles = uploadedFiles.map((file, docIndex) => {
          const key = getDocumentCacheKey(file, docIndex);
          return (
            documentSvgCacheRef.current.get(key) ||
            {
              numPages: 1,
              pages: [],
              pageSvgs: {},
            }
          );
        });
        setDocumentSvgBundles(immediateBundles);
        applySvgStateFromBundles(immediateBundles);

        // 2) Fill missing bundles in background and refresh progressively (don't wait for all docs).
        const missingIndexes = uploadedFiles
          .map((file, docIndex) => {
            const key = getDocumentCacheKey(file, docIndex);
            return documentSvgCacheRef.current.has(key) ? -1 : docIndex;
          })
          .filter((idx) => idx >= 0);

        const inFlightCount = uploadedFiles.reduce((acc, file, docIndex) => {
          const key = getDocumentCacheKey(file, docIndex);
          return acc + (documentSvgPromiseRef.current.has(key) ? 1 : 0);
        }, 0);
        if (SVG_LOAD_STATUS_DEBUG) {
          console.log('📄 SVG load status', {
            total: uploadedFiles.length,
            cached: uploadedFiles.length - missingIndexes.length,
            missing: missingIndexes.length,
            inFlight: inFlightCount,
          });
        }

        if (missingIndexes.length === 0) return;

        const hydratedBundles = [...immediateBundles];
        const concurrency = 3;
        let cursor = 0;
        let pendingCommit = false;
        let pendingHydratedSinceCommit = 0;
        const commitHydratedBundles = () => {
          if (svgLoadCycleRef.current !== cycleId) return;
          const next = [...hydratedBundles];
          setDocumentSvgBundles(next);
          applySvgStateFromBundles(next);
        };
        const scheduleHydratedCommit = () => {
          if (pendingCommit) return;
          pendingCommit = true;
          window.requestAnimationFrame(() => {
            pendingCommit = false;
            commitHydratedBundles();
          });
        };
        const workers = Array.from({ length: Math.min(concurrency, missingIndexes.length) }, () => (async () => {
          while (cursor < missingIndexes.length) {
            const docIndex = missingIndexes[cursor];
            cursor += 1;
            const file = uploadedFiles[docIndex];
            try {
              hydratedBundles[docIndex] = await ensureDocumentSvgData(file, docIndex);
              if (svgLoadCycleRef.current !== cycleId) return;
              pendingHydratedSinceCommit += 1;
              if (pendingHydratedSinceCommit >= 4) {
                pendingHydratedSinceCommit = 0;
                scheduleHydratedCommit();
              }
            } catch (error) {
              console.error(`Error loading SVG bundle for PDF ${docIndex}:`, error);
            }
          }
        })());
        await Promise.all(workers);
        if (svgLoadCycleRef.current === cycleId) {
          commitHydratedBundles();
        }
      };

      loadAndConvertPDFs();
    } else {
      // Clear SVG when not in step 2
      setMergedSvg('');
      setDocumentEditorSvgSegments([]);
      setSvgPageHeights([]);
      setSvgMaxWidth(0);
      setSvgTotalHeight(0);
    }
  }, [uploadedFiles, currentStep, ensureDocumentSvgData, applySvgStateFromBundles, getDocumentCacheKey]);

  // Recalculate zoom on window resize for fit modes
  
  // "Fit to Page" has been removed from the UI; normalize any legacy state to fit-to-width.
  useEffect(() => {
    if (zoomLevel === 'fitToPage') {
      setZoomLevel('fitToWidth');
    }
  }, [zoomLevel]);
  
  useEffect(() => {
    if (zoomLevel === 'fitToWidth') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [zoomLevel]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsMobileFieldLayout(mq.matches);
    sync();
    if (mq.addEventListener) {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  useEffect(() => {
    if (!isMobileFieldLayout) {
      setFieldsDrawerOpen(false);
      setPendingPlaceField(null);
      setPropertiesDrawerOpen(false);
    }
  }, [isMobileFieldLayout]);

  useEffect(() => {
    if (currentStep !== 2) {
      setFieldsDrawerOpen(false);
      setPendingPlaceField(null);
      setPropertiesDrawerOpen(false);
    }
  }, [currentStep]);

  useEffect(() => {
    if (!selectedField) {
      setPropertiesDrawerOpen(false);
    }
  }, [selectedField]);

  // Recalculate fit-to-width after entering field placement / mobile layout changes
  useEffect(() => {
    if (currentStep !== 2) return undefined;
    const syncWidth = () => setWindowWidth(window.innerWidth);
    syncWidth();
    const raf = window.requestAnimationFrame(syncWidth);
    return () => window.cancelAnimationFrame(raf);
  }, [currentStep, isMobileFieldLayout]);

  const deferredSelectedField = useDeferredValue(selectedField);
  const fieldsSelectionKey = useMemo(
    () => [...selectedFields].map(String).sort((a, b) => a.localeCompare(b)).join('|'),
    [selectedFields]
  );
  const deferredFieldsSelectionKey = useDeferredValue(fieldsSelectionKey);
  const selectionPropertiesInSync =
    deferredSelectedField?.id === selectedField?.id &&
    deferredFieldsSelectionKey === fieldsSelectionKey;
  const runSelectionUpdate = useCallback((updater) => {
    if (typeof React.startTransition === 'function') {
      React.startTransition(updater);
      return;
    }
    updater();
  }, []);

  const selectedFieldIdsForRecipient = useMemo(() => {
    if (selectedFields.length > 0) return selectedFields;
    if (selectedField?.id) return [selectedField.id];
    return [];
  }, [selectedFields, selectedField?.id]);

  const propertiesPanelRecipientKey = useMemo(() => {
    const fields = documentFields.filter((f) => selectedFieldIdsForRecipient.includes(f.id));
    if (!fields.length) return '';
    const keys = fields.map(canonicalizeFieldRecipientKey);
    const assigned = keys.filter(Boolean);
    if (!assigned.length) return '';
    const first = assigned[0];
    return assigned.every((k) => k === first) ? first : '__mixed__';
  }, [documentFields, selectedFieldIdsForRecipient, canonicalizeFieldRecipientKey]);

  const FONT_FORMAT_FIELD_TYPES = useMemo(
    () => new Set([
      'dateSigned', 'date', 'name', 'email', 'company', 'title',
      'text', 'number', 'checkbox', 'dropdown', 'radio',
    ]),
    []
  );

  const selectedFieldsForProperties = useMemo(() => {
    const ids = selectedFieldIdsForRecipient;
    return documentFields.filter((f) => ids.includes(f.id));
  }, [documentFields, selectedFieldIdsForRecipient]);

  const selectionHasFontFormatting = useMemo(
    () => selectedFieldsForProperties.some((f) => FONT_FORMAT_FIELD_TYPES.has(f.type)),
    [selectedFieldsForProperties, FONT_FORMAT_FIELD_TYPES]
  );

  const getSharedSelectedFieldValue = useCallback((key, fallback = '') => {
    const fields = selectedFieldsForProperties;
    if (!fields.length) return fallback;
    const values = fields.map((f) => {
      const raw = f[key];
      return raw === undefined || raw === null || raw === '' ? fallback : raw;
    });
    const first = values[0];
    return values.every((v) => String(v) === String(first)) ? first : '__mixed__';
  }, [selectedFieldsForProperties]);

  const applyFormattingToSelectedFields = useCallback((patch, options = {}) => {
    const { types = null } = options;
    const targetIds = selectedFieldIdsForRecipient;
    if (!targetIds.length) return;

    const prevFields = documentFieldsRef.current || documentFields;
    saveToHistory(prevFields);

    const nextFields = prevFields.map((f) => {
      if (!targetIds.includes(f.id)) return f;
      if (types && !types.has(f.type)) return f;

      let updated = { ...f, ...patch };
      if (
        patch.fontSize != null
        && fieldUsesContentHeightFitting(updated)
        && readOnlyFieldHasDisplayContent(updated)
      ) {
        updated = fitReadOnlyFieldToContent(updated, undefined, { fontSize: patch.fontSize });
      }
      return updated;
    });

    documentFieldsRef.current = nextFields;
    setDocumentFields(nextFields);

    if (selectedField && targetIds.includes(selectedField.id)) {
      const nextSelected = nextFields.find((f) => f.id === selectedField.id);
      if (nextSelected) setSelectedField(nextSelected);
    }
  }, [
    selectedFieldIdsForRecipient,
    documentFields,
    selectedField,
  ]);

  // Multi-select: open Formatting so the shared typography controls are visible.
  useEffect(() => {
    if (selectedFields.length > 1) {
      setExpandedSections((prev) => (
        prev.formatting ? prev : { ...prev, formatting: true }
      ));
    }
  }, [selectedFields.length]);

  const assignRecipientToSelectedFields = useCallback((selectedRecipientKey) => {
    if (!selectedRecipientKey || selectedRecipientKey === '__mixed__') return;

    const targetRecipient = signeeRecipients.find(
      (recipient, idx) => getRecipientSelectionKey(recipient, idx) === selectedRecipientKey
    ) || findRecipientBySelectionKey(selectedRecipientKey);
    if (!targetRecipient) return;

    const targetIds = selectedFieldIdsForRecipient;
    if (!targetIds.length) return;

    const recipientIndex = recipients.findIndex(
      (r, idx) => getRecipientSelectionKey(r, idx) === selectedRecipientKey
    );
    const placeholder = getVariableSigneePlaceholder(
      targetRecipient,
      recipientIndex >= 0 ? recipientIndex : 0
    );
    const recipientName = targetRecipient.name || targetRecipient.email || placeholder;
    const recipientColor = targetRecipient.color || getRecipientColor(recipientIndex >= 0 ? recipientIndex : 0);
    const numericRecipientId = resolveEnvelopeRecipientId(
      targetRecipient,
      recipientIdMapRef.current || {}
    );
    const mappedRecipientKey =
      numericRecipientId != null
        ? toRecipientIdSelectionKey(numericRecipientId)
        : selectedRecipientKey;

    const getFieldTypeLabel = (type) => {
      const typeLabels = {
        signature: 'Signature',
        initial: 'Initial',
        dateSigned: 'Date Signed',
        date: 'Date',
        name: 'Name',
        email: 'Email',
        company: 'Company',
        title: 'Title',
        text: 'Text',
        number: 'Number',
        checkbox: 'Checkbox',
        dropdown: 'Dropdown',
        radio: 'Radio',
        stamp: 'Stamp',
      };
      return typeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1);
    };

    saveToHistory(documentFieldsRef.current || documentFields);
    setDocumentFields((prevFields) => {
      const nextFields = prevFields.map((f) => {
        if (!targetIds.includes(f.id)) return f;
        const newLabel = `${getFieldTypeLabel(f.type)} - ${recipientName}`;
        return {
          ...f,
          recipientId: mappedRecipientKey,
          recipientEmail: targetRecipient.email || '',
          recipientPlaceholder: targetRecipient.isVariable ? placeholder : '',
          apiRecipientId: numericRecipientId,
          recipientName,
          recipientColor,
          label: newLabel,
          dataLabel: newLabel,
        };
      });
      documentFieldsRef.current = nextFields;

      const primaryId =
        selectedField?.id && targetIds.includes(selectedField.id)
          ? selectedField.id
          : targetIds[0];
      const updatedPrimary = nextFields.find((f) => f.id === primaryId);
      if (updatedPrimary) {
        setSelectedField(updatedPrimary);
      }

      return nextFields;
    });

    setSelectedRecipient(targetRecipient);
  }, [
    documentFields,
    findRecipientBySelectionKey,
    getRecipientColor,
    getRecipientSelectionKey,
    getVariableSigneePlaceholder,
    recipients,
    selectedField?.id,
    selectedFieldIdsForRecipient,
    signeeRecipients,
  ]);

  useEffect(() => {
    if (!propertiesPanelRecipientKey || propertiesPanelRecipientKey === '__mixed__') return;
    const recipient = signeeRecipients.find(
      (r, idx) => getRecipientSelectionKey(r, idx) === propertiesPanelRecipientKey
    ) || findRecipientBySelectionKey(propertiesPanelRecipientKey);
    if (!recipient) return;
    setSelectedRecipient((prev) => {
      const prevIdx = prev ? recipients.findIndex((r) => r === prev) : -1;
      const prevKey = prev ? getRecipientSelectionKey(prev, prevIdx >= 0 ? prevIdx : 0) : '';
      const nextIdx = recipients.findIndex((r) => r === recipient);
      const nextKey = getRecipientSelectionKey(recipient, nextIdx >= 0 ? nextIdx : 0);
      return prevKey === nextKey ? prev : recipient;
    });
  }, [
    propertiesPanelRecipientKey,
    signeeRecipients,
    findRecipientBySelectionKey,
    getRecipientSelectionKey,
    recipients,
  ]);

  useEffect(() => {
    // Keep Step 1 loading state visible until we actually leave Step 1.
    if (currentStep !== 1 && isNextTransitioning) {
      setIsNextTransitioning(false);
    }
  }, [currentStep, isNextTransitioning]);

  // Toggle document expansion
  const toggleDocumentExpansion = (index) => {
    setExpandedDocuments(prev => ({
      ...prev,
      [index]: prev[index] !== false ? false : true
    }));
  };

  // Check if document is expanded (defaults to true)
  const isDocumentExpanded = (index) => {
    return expandedDocuments[index] !== false;
  };

  return (
    <>
    {embedExitPending && (
      <div
        className="d-flex flex-column justify-content-center align-items-center min-vh-100 p-4"
        style={{ position: 'fixed', inset: 0, zIndex: 20000, background: '#fff' }}
      >
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Closing editor...</span>
        </div>
        <p className="text-muted mb-0 text-center">Returning to the app…</p>
      </div>
    )}
    {currentStep === 1 ? (
    <div className="create-envelope-page">
      {/* Header */}
      <div className="page-header-custom">
        <div className="d-flex align-items-center page-header-title-wrap">
            <h5 className="page-title-custom mb-0">
            {subject?.trim() || 'Complete with Volksign:'}
            </h5>
        </div>
        <div className="d-flex align-items-center gap-2 page-header-actions">
          <Button variant="link" className="btn-icon" type="button">
            <i className="ri-question-line"></i>
          </Button>
          <Button variant="outline-secondary" size="sm" className="page-header-btn-advanced">
            ADVANCED OPTIONS
          </Button>
          {isOrgAdmin && (
            <Button variant="primary" size="sm" className="page-header-btn-plans" onClick={handleViewPlansFromEditor}>
              VIEW PLANS
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="page-body-custom">
        {/* Add Documents Section */}
        <div className="section-wrapper">
          <div
            className="section-header"
            onClick={() => setDocumentsExpanded(!documentsExpanded)}
          >
            <h6 className="section-title mb-0">
              Add documents
              {uploadedFiles.length > 0 ? ` (${uploadedFiles.length})` : ' (0)'}
            </h6>
            <i
              className={`ri-arrow-${documentsExpanded ? 'up' : 'down'}-s-line`}
            ></i>
          </div>

          {documentsExpanded && (
              <div className="section-content">
              <DocumentsSection
                fileInputRef={fileInputRef}
                uploadedFiles={uploadedFiles}
                documentIds={documentIds}
                documentUploadProgress={documentUploadProgress}
                uploadError={uploadError}
                setUploadError={setUploadError}
                isDragging={isDragging}
                documentPages={documentPages}
                documentPageSvgs={documentPageSvgs}
                getDocumentThumbnailSvg={getDocumentThumbnailSvg}
                handleFileInputChange={handleFileInputChange}
                handleDocumentAction={handleDocumentAction}
                handleUploadClick={handleUploadClick}
                handleDragEnter={handleDragEnter}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                handleDropboxUpload={handleDropboxUpload}
                handleGoogleDriveUpload={handleGoogleDriveUpload}
                handleOneDriveUpload={handleOneDriveUpload}
                handleBoxUpload={handleBoxUpload}
                uploadOptions={uploadOptions}
                replaceFileIndex={replaceFileIndex}
                setReplaceFileIndex={setReplaceFileIndex}
                showCloudUpload={false}
                onReorderDocuments={handleReorderDocuments}
                idPrefix=""
                deletingDocumentRowKeys={deletingDocumentRowKeys}
                isDocumentRowUploadInFlight={isDocumentRowUploadInFlight}
              />
            </div>
          )}
        </div>

        {/* Add Recipients Section */}
        <div className="section-wrapper">
          <div
            className="section-header"
            onClick={() => setRecipientsExpanded(!recipientsExpanded)}
          >
            <h6 className="section-title mb-0">Add recipients</h6>
            <i
              className={`ri-arrow-${recipientsExpanded ? 'up' : 'down'}-s-line`}
            ></i>
          </div>

          {recipientsExpanded && (
            <div className="section-content">
              <RecipientsSection
                recipients={recipients}
                setRecipients={setRecipients}
                onPatchRecipient={handlePatchRecipient}
                isOnlySigner={isOnlySigner}
                setIsOnlySigner={setIsOnlySigner}
                signingOrder={signingOrder}
                setSigningOrder={setSigningOrder}
                sequenceInputs={sequenceInputs}
                setSequenceInputs={setSequenceInputs}
                handleSequenceChange={handleSequenceChange}
                handleSequenceBlur={handleSequenceBlur}
                handleAddRecipient={handleAddRecipient}
                handleRemoveRecipient={handleRemoveRecipient}
                getRecipientColor={getRecipientColor}
                signingRoles={signingRoles}
                isTemplateMode={isTemplateMode}
                showRecipientFieldErrors={recipientFieldErrorsVisible}
                removingRecipientIndex={removingRecipientIndex}
                idPrefix=""
                savedContacts={savedRecipientContacts}
              />
            </div>
          )}
        </div>

        {/* Add Message Section */}
        <div className="section-wrapper">
          <div
            className="section-header"
            onClick={() => setMessageExpanded(!messageExpanded)}
          >
            <h6 className="section-title mb-0">Add message</h6>
            <i
              className={`ri-arrow-${messageExpanded ? 'up' : 'down'}-s-line`}
            ></i>
          </div>

          {messageExpanded && (
            <div className="section-content">
              <MessageSection
                subject={subject}
                setSubject={setSubject}
                message={message}
                setMessage={setMessage}
                reminderFrequency={reminderFrequency}
                setReminderFrequency={setReminderFrequency}
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer with Next Button */}
      <div className="page-footer-custom">
        <Button 
          variant="primary" 
          size="lg"
          disabled={isNextStepBlocked}
          onClick={async () => {
            if (isNextStepBlocked) return;
            setIsNextTransitioning(true);
            try {
              // Let the loading state paint before running validation and step switch.
              await new Promise((resolve) => window.requestAnimationFrame(resolve));
              await new Promise((resolve) => window.setTimeout(resolve, 0));
              await new Promise((resolve) => window.requestAnimationFrame(resolve));

              // Validate before proceeding
              if (uploadedFiles.length === 0) {
                setUploadError('Please upload at least one document');
                setIsNextTransitioning(false);
                return;
              }
              const limitsError = validateEnvelopeLimits({ requirePageCounts: true });
              if (limitsError) {
                setUploadError(limitsError);
                setIsNextTransitioning(false);
                return;
              }
              if (!isOnlySigner && recipients.some((r, idx) => !isRecipientValidForCurrentMode(r, idx))) {
                setRecipientFieldErrorsVisible(true);
                const invalidEmail = recipients.some((r, idx) => {
                  if (isTemplateMode && roleToRecipientType(r.role) === 'signee' && r.isVariable) return false;
                  const e = String(r.email || '').trim();
                  return e.length > 0 && !isValidEmailAddress(e);
                });
                setUploadError(
                  invalidEmail
                    ? 'One or more email addresses are invalid. Please correct them before continuing.'
                    : (isTemplateMode ? 'Please complete all recipient details/placeholders' : 'Please fill in all recipient details')
                );
                setIsNextTransitioning(false);
                return;
              }
              setRecipientFieldErrorsVisible(false);
              if (hasPendingSvgPrep) {
                await prewarmDocumentSvgs(uploadedFiles);
                if (svgPrewarmPromiseRef.current) {
                  await svgPrewarmPromiseRef.current;
                }
              }
              React.startTransition(() => {
                setCurrentStep(2);
              });
            } catch (_error) {
              setIsNextTransitioning(false);
            }
          }}
        >
          {isNextTransitioning ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Loading...
            </>
          ) : 'Next'}
        </Button>
      </div>
    </div>
  ) : (
    // Step 2: Field Placement View
    <div
      className={`field-placement-page${zoomLevel !== 'fitToWidth' ? ' is-zoomed' : ''}${
        isMobileFieldLayout ? ' is-mobile-field-layout' : ''
      }${fieldsDrawerOpen ? ' fields-drawer-open' : ''}${
        propertiesDrawerOpen && isMobileFieldLayout ? ' properties-drawer-open' : ''
      }${pendingPlaceField ? ' is-placing-field' : ''}`}
    >
      {isStep2ProcessingDocuments && (
        <div
          className="field-processing-banner"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '8px 0 12px',
            padding: '10px 12px',
            borderRadius: '8px',
            background: '#fff7e6',
            border: '1px solid #ffd591',
            color: '#8c5a00',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>
            {isDraftAssetsPreparing
              ? 'Loading documents. Please wait...'
              : 'Documents are still uploading/processing. You can continue; preview will update automatically.'}
          </span>
        </div>
      )}
      {isDraftFieldsHydrating && (
        <div
          className="field-processing-banner"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '8px 0 12px',
            padding: '10px 12px',
            borderRadius: '8px',
            background: '#eef6ff',
            border: '1px solid #bcdcff',
            color: '#0b4f8a',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>Loading field placements...</span>
        </div>
      )}
      {/* Top Header */}
      <div
        className="field-placement-header"
        style={{ position: 'relative', zIndex: 3000 }}
      >
        <div className="header-left">
          <h5 className="document-title mb-0">
            {subject?.trim() || 'Complete with Volksign:'}
          </h5>
        </div>
        <div className="header-center">
          <Button 
            variant="link" 
            className="btn-icon" 
            title="Undo"
            disabled={history.undo.length === 0}
            onClick={handleUndo}
          >
            <i className="ri-arrow-go-back-line"></i>
          </Button>
          <Button 
            variant="link" 
            className="btn-icon" 
            title="Redo"
            disabled={history.redo.length === 0}
            onClick={handleRedo}
          >
            <i className="ri-arrow-go-forward-line"></i>
          </Button>
          <Button 
            variant="link" 
            className="btn-icon" 
            title={selectedFields.length > 0 ? `Copy ${selectedFields.length} Field${selectedFields.length > 1 ? 's' : ''}` : "Copy Field"}
            disabled={!selectedField && selectedFields.length === 0}
            onClick={handleCopyField}
          >
            <i className="ri-file-copy-line"></i>
            {selectedFields.length > 0 && (
              <span className="badge bg-primary ms-1" style={{ fontSize: '10px' }}>
                {selectedFields.length}
              </span>
            )}
          </Button>
          <Button 
            variant="link" 
            className="btn-icon" 
            title={(copiedFields.length > 0 ? copiedFields.length : (copiedField ? 1 : 0)) > 0
              ? `Paste ${copiedFields.length > 0 ? copiedFields.length : 1} Field${(copiedFields.length > 0 ? copiedFields.length : 1) > 1 ? 's' : ''}`
              : "Paste Field"}
            disabled={!copiedField && copiedFields.length === 0}
            onClick={handlePasteField}
          >
            <i className="ri-clipboard-line"></i>
            {(copiedFields.length > 0 || copiedField) && (
              <span className="badge bg-primary ms-1" style={{ fontSize: '10px' }}>
                {copiedFields.length > 0 ? copiedFields.length : 1}
              </span>
            )}
          </Button>
          <div className="zoom-controls">
            <div
              className="text-muted small d-flex align-items-center justify-content-center field-page-indicator"
              title="Current visible page"
            >
              Page {Math.max(1, currentPage)} / {Math.max(1, svgPageHeights.length)}
            </div>
            <Form.Select
              value={zoomLevel}
              onChange={(e) => setZoomLevel(e.target.value)}
              className="zoom-select"
            >
              <option value="fitToWidth">Fit to Width</option>
              <option value="50">50%</option>
              <option value="75">75%</option>
              <option value="100">100%</option>
              <option value="125">125%</option>
              <option value="150">150%</option>
              <option value="200">200%</option>
              <option value="250">250%</option>
              <option value="300">300%</option>
            </Form.Select>
          </div>
        </div>
        <div className="header-right">
          {isMobileFieldLayout && (
            <Button
              variant="primary"
              size="sm"
              className="mobile-add-field-btn"
              onClick={() => {
                setFieldsDrawerOpen((open) => !open);
              }}
            >
              <i className="ri-add-line me-1"></i>
              Add field
            </Button>
          )}
          {isMobileFieldLayout && selectedField && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="mobile-field-props-btn"
              onClick={() => setPropertiesDrawerOpen(true)}
            >
              Properties
            </Button>
          )}
                            <Dropdown style={{ position: 'relative', zIndex: 3500 }}>
            <Dropdown.Toggle 
              variant="link"
              className="dropdown-toggle-default-arrow"
            >
              Actions
                              </Dropdown.Toggle>
                              <Dropdown.Menu
                                renderOnMount
                                style={{ zIndex: 4000 }}
                              >
                                  <Dropdown.Item
                                    as="button"
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleSaveAndClose();
                                    }}
                                    disabled={isSavingDraft || isFinalActionSubmitting}
              >
                Save and Close
                                  </Dropdown.Item>
              <Dropdown.Item
                as="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDiscard();
                }}
                disabled={isSavingDraft || isFinalActionSubmitting}
              >
                Discard
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item 
                as="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowEditMessageModal(true);
                }}
              >
                Edit Message
              </Dropdown.Item>
              <Dropdown.Item 
                as="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowEditRecipientsModal(true);
                }}
              >
                Edit Recipients
              </Dropdown.Item>
              <Dropdown.Item 
                as="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowEditDocumentsModal(true);
                }}
              >
                Edit Documents
              </Dropdown.Item>
              <Dropdown.Item 
                onClick={() => {
                  // Handle Advanced Options
                  console.log('Advanced Options');
                  // TODO: Implement advanced options functionality
                }}
              >
                Advanced Options
              </Dropdown.Item>
              {isMobileFieldLayout && !isEmbedMode && (
                <Dropdown.Item
                  onClick={(e) => {
                    e.preventDefault();
                    handlePreview();
                  }}
                >
                  Preview
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
          {!isEmbedMode && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="field-header-preview-btn"
              onClick={handlePreview}
            >
              PREVIEW
            </Button>
          )}
          {isOrgAdmin && (
            <Button
              variant="primary"
              size="sm"
              className="field-header-plans-btn"
              onClick={handleViewPlansFromEditor}
            >
              VIEW PLANS
            </Button>
          )}
          {draftEnvelopeId && documentIds.length > 0 && lastSavedAt && (
            <span className="text-muted ms-2 d-flex align-items-center field-header-draft-status" style={{ fontSize: '0.75rem' }}>
              <i className="ri-save-line me-1"></i>
              Draft saved {new Date(lastSavedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="field-placement-body">
        {isMobileFieldLayout && fieldsDrawerOpen && (
          <button
            type="button"
            className="fields-drawer-backdrop"
            aria-label="Close fields panel"
            onClick={() => setFieldsDrawerOpen(false)}
          />
        )}
        {isMobileFieldLayout && pendingPlaceField && (
          <div className="mobile-place-field-banner">
            <span>
              Tap the document to place <strong>{pendingPlaceField.label}</strong>
            </span>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => setPendingPlaceField(null)}
            >
              Cancel
            </Button>
          </div>
        )}
        {/* Left Sidebar - Fields Panel */}
        <div className={`fields-sidebar${fieldsDrawerOpen ? ' is-open' : ''}`}>
          {isMobileFieldLayout && (
            <div className="fields-drawer-header">
              <h6 className="mb-0">Add field</h6>
              <Button
                variant="link"
                className="btn-icon p-0"
                aria-label="Close"
                onClick={() => setFieldsDrawerOpen(false)}
              >
                <i className="ri-close-line"></i>
              </Button>
            </div>
          )}
          <div className="sidebar-section">
            <div className="recipient-select-wrapper mb-3" ref={recipientDropdownRef}>
              <div className="recipient-select-with-indicator position-relative">
                <span 
                  className="recipient-color-indicator-select me-2"
                  style={{ 
                    backgroundColor: (selectedRecipient && signeeRecipients.some(r => getRecipientSelectionKey(r) === getRecipientSelectionKey(selectedRecipient)))
                      ? (selectedRecipient.color || getRecipientColor(signeeRecipients.findIndex(r => getRecipientSelectionKey(r) === getRecipientSelectionKey(selectedRecipient))))
                      : (signeeRecipients.length > 0 ? (signeeRecipients[0].color || getRecipientColor(0)) : '#6366f1')
                  }}
                ></span>
                <button
                  type="button"
                  className="recipient-select-button"
                  onClick={() => setShowRecipientDropdown(!showRecipientDropdown)}
                >
                  {(selectedRecipient && signeeRecipients.some(r => getRecipientSelectionKey(r) === getRecipientSelectionKey(selectedRecipient)))
                    ? (selectedRecipient.name || selectedRecipient.email || selectedRecipient.placeholder)
                    : (signeeRecipients.length > 0 ? (signeeRecipients[0].name || signeeRecipients[0].email || signeeRecipients[0].placeholder) : 'Select Recipient')
                  }
                </button>
                {showRecipientDropdown && (
                  <div className="recipient-dropdown-menu">
                    {signeeRecipients.map((recipient, index) => (
                      <div
                        key={index}
                        className={`recipient-dropdown-item ${getRecipientSelectionKey(selectedRecipient) === getRecipientSelectionKey(recipient) ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedRecipient(recipient);
                          setShowRecipientDropdown(false);
                        }}
                      >
                        <span 
                          className="recipient-color-indicator me-2"
                          style={{ backgroundColor: recipient.color || getRecipientColor(index) }}
                        ></span>
                        {recipient.name || recipient.email || recipient.placeholder}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="search-fields mb-3">
              <div className="input-group">
                <Form.Control
                  type="text"
                  placeholder="Search Fields"
                  value={searchFields}
                  onChange={(e) => setSearchFields(e.target.value)}
                />
                {searchFields && (
                  <Button
                    variant="link"
                    className="input-group-text"
                    onClick={() => setSearchFields('')}
                  >
                    <i className="ri-close-line"></i>
                  </Button>
                )}
              </div>
            </div>

            <div className="standard-fields">
              <h6 className="fields-title">Standard Fields</h6>
              <div className="fields-list">
                {[
                  { label: 'Signature', icon: 'ri-pen-nib-line', type: 'signature' },
                  { label: 'Initial', icon: 'ri-pen-nib-line', type: 'initial' },
                  { label: 'Date Signed', icon: 'ri-calendar-line', type: 'dateSigned' },
                  { label: 'Date', icon: 'ri-calendar-2-line', type: 'date' },
                  { label: 'Name', icon: 'ri-user-line', type: 'name' },
                  { label: 'Email', icon: 'ri-mail-line', type: 'email' },
                  { label: 'Company', icon: 'ri-building-line', type: 'company' },
                  { label: 'Title', icon: 'ri-file-line', type: 'title' },
                  { label: 'Text', icon: 'ri-text', type: 'text' },
                  { label: 'Number', icon: 'ri-hashtag', type: 'number' },
                  { label: 'Checkbox', icon: 'ri-checkbox-line', type: 'checkbox' },
                ]
                  .filter((field) => !isHiddenFieldType(field.type))
                  .filter(field => 
                    !searchFields || 
                    field.label.toLowerCase().includes(searchFields.toLowerCase())
                  )
                  .map((field, index) => (
                    <div
                      key={index}
                      className={`field-item${
                        pendingPlaceField?.type === field.type ? ' is-pending-place' : ''
                      }`}
                      draggable={!isMobileFieldLayout}
                      onClick={() => {
                        if (!isMobileFieldLayout) return;
                        setPendingPlaceField({ type: field.type, label: field.label });
                        setFieldsDrawerOpen(false);
                      }}
                      onDragStart={(e) => {
                        if (isMobileFieldLayout) {
                          e.preventDefault();
                          return;
                        }
                        e.dataTransfer.setData('fieldType', field.type);
                        e.dataTransfer.setData('fieldLabel', field.label);
                        // Store where inside the drag element the user grabbed
                        const rect = e.currentTarget.getBoundingClientRect();
                        e.dataTransfer.setData('dragOffsetX', String(e.clientX - rect.left));
                        e.dataTransfer.setData('dragOffsetY', String(e.clientY - rect.top));
                      }}
                    >
                      <i className={field.icon}></i>
                      <span>{field.label}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Document Viewer */}
        <div className="document-viewer">
          <div 
            className="document-canvas"
            onDrop={(e) => {
              e.preventDefault();
              const fieldType = e.dataTransfer.getData('fieldType');
              if (fieldType && isHiddenFieldType(fieldType)) return;
              if (fieldType) {
                const grabOffsetX = parseFloat(e.dataTransfer.getData('dragOffsetX')) || 0;
                const grabOffsetY = parseFloat(e.dataTransfer.getData('dragOffsetY')) || 0;
                placeFieldAtClientPoint(
                  e.clientX,
                  e.clientY,
                  fieldType,
                  grabOffsetX,
                  grabOffsetY
                );
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onClick={(e) => {
              if (!pendingPlaceField) return;
              if (e.target.closest('.placed-field')) return;
              const placed = placeFieldAtClientPoint(
                e.clientX,
                e.clientY,
                pendingPlaceField.type,
                (150 * getCurrentScale()) / 2,
                (40 * getCurrentScale()) / 2
              );
              if (placed) {
                setPendingPlaceField(null);
              }
            }}
          >
            {uploadedFiles.length > 0 && (
              <div 
                className="document-preview" 
                ref={svgContainerRef}
                style={
                  isMobileFieldLayout && svgMaxWidth > 0
                    ? {
                        width: `${svgMaxWidth * zoomRenderScale}px`,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                      }
                    : undefined
                }
                onContextMenu={(e) => {
                  // Only show context menu if clicking on document background (not on a field)
                  if (
                    e.target === e.currentTarget ||
                    e.target.closest('.merged-svg-container') ||
                    e.target.closest('.field-editor-overlay-scale-root') ||
                    e.target.tagName === 'svg'
                  ) {
                    handleContextMenu(e, null);
                  }
                }}
                onMouseDown={(e) => {
                  // Start selection box if clicking on document background (not on a field)
                  if (
                    e.target === e.currentTarget ||
                    e.target.closest('.merged-svg-container') ||
                    e.target.closest('.field-editor-overlay-scale-root')
                  ) {
                    // Mobile tap-to-place: skip marquee so the click can place the field
                    if (pendingPlaceField) {
                      return;
                    }
                    markPointerInteractionActive();
                    // Only start selection if not clicking on a field and not using Ctrl/Cmd
                    if (!e.target.closest('.placed-field') && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                      e.preventDefault();
                      const previewContainer = svgContainerRef.current;
                      if (!previewContainer) return;
                      const containerRect = previewContainer.getBoundingClientRect();
                      const scrollTop = previewContainer.scrollTop;
                      const scrollLeft = previewContainer.scrollLeft;

                      const sx = e.clientX - containerRect.left + scrollLeft;
                      const sy = e.clientY - containerRect.top + scrollTop;
                      selectionBoxDragRef.current = { startX: sx, startY: sy, endX: sx, endY: sy };

                      const prevH = marqueeHandlersRef.current;
                      if (prevH.move) document.removeEventListener('mousemove', prevH.move);
                      if (prevH.up) document.removeEventListener('mouseup', prevH.up);

                      marqueeActiveRef.current = true;
                      setSelectedFields([]);
                      setSelectedField(null);
                      setIsSelecting(true);

                      const syncMarqueeDom = () => {
                        const el = selectionBoxElRef.current;
                        const b = selectionBoxDragRef.current;
                        if (!el) return;
                        el.style.left = `${Math.min(b.startX, b.endX)}px`;
                        el.style.top = `${Math.min(b.startY, b.endY)}px`;
                        el.style.width = `${Math.abs(b.endX - b.startX)}px`;
                        el.style.height = `${Math.abs(b.endY - b.startY)}px`;
                      };

                      const onMarqueeMove = (ev) => {
                        if (!marqueeActiveRef.current) return;
                        const pc = svgContainerRef.current;
                        if (!pc) return;
                        const cr = pc.getBoundingClientRect();
                        const b = selectionBoxDragRef.current;
                        b.endX = ev.clientX - cr.left + pc.scrollLeft;
                        b.endY = ev.clientY - cr.top + pc.scrollTop;
                        syncMarqueeDom();
                      };

                      const onMarqueeUp = () => {
                        if (!marqueeActiveRef.current) return;
                        marqueeActiveRef.current = false;
                        markPointerInteractionIdle();
                        document.removeEventListener('mousemove', onMarqueeMove);
                        document.removeEventListener('mouseup', onMarqueeUp);
                        marqueeHandlersRef.current = { move: null, up: null };

                        const b = selectionBoxDragRef.current;
                        const boxLeft = Math.min(b.startX, b.endX);
                        const boxRight = Math.max(b.startX, b.endX);
                        const boxTop = Math.min(b.startY, b.endY);
                        const boxBottom = Math.max(b.startY, b.endY);
                        const dx = Math.abs(b.endX - b.startX);
                        const dy = Math.abs(b.endY - b.startY);
                        suppressNextPreviewBackgroundClickRef.current = dx * dy > 25;

                        const selScale = getCurrentScale();
                        const fields = documentFieldsRef.current || [];
                        const heights = svgPageHeightsRef.current || [];
                        const fieldsInBox = fields.filter((field) => {
                          const { fieldX, fieldY } = getFieldOverlayGeometry(field, heights);

                          const fieldScreenX = fieldX * selScale;
                          const fieldScreenY = fieldY * selScale;
                          const fieldScreenWidth = (field.width || 150) * selScale;
                          const fieldScreenHeight = (field.height || 40) * selScale;

                          return !(
                            fieldScreenX + fieldScreenWidth < boxLeft ||
                            fieldScreenX > boxRight ||
                            fieldScreenY + fieldScreenHeight < boxTop ||
                            fieldScreenY > boxBottom
                          );
                        });

                        runSelectionUpdate(() => {
                          if (fieldsInBox.length > 0) {
                            const freshFields = documentFieldsRef.current || documentFields;
                            const boxIds = fieldsInBox.map((f) => f.id);
                            setSelectedFields(boxIds);
                            setSelectedField(
                              freshFields.find((f) => f.id === boxIds[boxIds.length - 1]) || fieldsInBox[0]
                            );
                          } else {
                            setSelectedFields([]);
                            setSelectedField(null);
                          }
                        });

                        setIsSelecting(false);
                      };

                      marqueeHandlersRef.current = { move: onMarqueeMove, up: onMarqueeUp };
                      document.addEventListener('mousemove', onMarqueeMove, { passive: true });
                      document.addEventListener('mouseup', onMarqueeUp);
                    } else if (!e.target.closest('.placed-field')) {
                      // Click on background without modifiers - clear selection
                      markPointerInteractionIdle();
                      runSelectionUpdate(() => {
                        setSelectedFields([]);
                        setSelectedField(null);
                      });
                    }
                  }
                }}
                onClick={(e) => {
                  if (suppressNextPreviewBackgroundClickRef.current) {
                    suppressNextPreviewBackgroundClickRef.current = false;
                    return;
                  }
                  // Deselect if clicking on document background (not on a field) without modifiers
                  if ((e.target === e.currentTarget || e.target.closest('.merged-svg-container') || e.target.closest('.field-editor-overlay-scale-root')) && 
                      !e.target.closest('.placed-field') && 
                      !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                    runSelectionUpdate(() => {
                      setSelectedFields([]);
                      setSelectedField(null);
                    });
                  }
                }}
              >
                {documentEditorSvgSegments.length === 0 ? (
                  <div className="text-center p-5">
                    <p>Loading PDF pages...</p>
                  </div>
                ) : (
                  <FieldEditorSvgLayers
                    segments={documentEditorSvgSegments}
                    svgMaxWidth={svgMaxWidth}
                    svgTotalHeight={svgTotalHeight}
                    zoomScale={zoomRenderScale}
                  />
                )}
                {/* Selection box visual */}
                {isSelecting && (
                  <div
                    ref={(el) => {
                      selectionBoxElRef.current = el;
                      if (el) {
                        const r = selectionBoxDragRef.current;
                        el.style.left = `${Math.min(r.startX, r.endX)}px`;
                        el.style.top = `${Math.min(r.startY, r.endY)}px`;
                        el.style.width = `${Math.abs(r.endX - r.startX)}px`;
                        el.style.height = `${Math.abs(r.endY - r.startY)}px`;
                      }
                    }}
                    className="selection-box"
                    style={{
                      position: 'absolute',
                      border: '2px dashed #6366f1',
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      pointerEvents: 'none',
                      zIndex: 1000,
                    }}
                  />
                )}
                <div
                  className="field-editor-overlay-scale-root"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: `${svgMaxWidth}px`,
                    height: `${svgTotalHeight}px`,
                    transform: `scale(${zoomRenderScale})`,
                    transformOrigin: 'top left',
                    zIndex: 900,
                  }}
                >
                {documentFields.map((field) => {
                  if (isHiddenFieldType(field.type)) return null;
                  const {
                    pageInfo,
                    fieldX,
                    fieldY,
                    width: fieldWidth,
                    height: fieldHeight,
                  } = getFieldOverlayGeometry(field, svgPageHeights);
                  if (!pageInfo) return null;

                  return (
                    <div
                      key={field.id}
                      ref={getPlacedFieldRef(field.id)}
                      className={[
                        'placed-field',
                        selectedField?.id === field.id || selectedFieldIdsSet.has(field.id) ? 'selected' : '',
                        inlineReadOnlyEditor?.fieldId === field.id ? 'is-readonly-editing' : '',
                      ].filter(Boolean).join(' ')}
                      style={{
                        left: `${fieldX}px`,
                        top: `${fieldY}px`,
                        width: `${fieldWidth}px`,
                        height: `${fieldHeight}px`,
                        zIndex: inlineReadOnlyEditor?.fieldId === field.id ? 5000 : undefined,
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        openInlineReadOnlyValueEditor(field);
                      }}
                      onClick={(e) => {
                        if (e.target.classList.contains('resize-handle')) return;
                        if (suppressNextFieldClickRef.current) {
                          suppressNextFieldClickRef.current = false;
                        e.stopPropagation();
                          return;
                        }
                        e.stopPropagation();
                        
                        // Handle multi-select with Ctrl/Cmd or Shift
                        if (e.ctrlKey || e.metaKey) {
                          const freshFields = documentFieldsRef.current || documentFields;
                          // Toggle field selection
                          if (selectedFieldIdsSet.has(field.id)) {
                            const newSelected = selectedFields.filter(id => id !== field.id);
                            runSelectionUpdate(() => {
                              setSelectedFields(newSelected);
                              setSelectedField(
                                newSelected.length > 0
                                  ? freshFields.find((f) => f.id === newSelected[newSelected.length - 1]) || null
                                  : null
                              );
                            });
                          } else {
                            const newSelected = [...selectedFields, field.id];
                            runSelectionUpdate(() => {
                              setSelectedFields(newSelected);
                              setSelectedField(freshFields.find((f) => f.id === field.id) || field);
                            });
                          }
                        } else if (e.shiftKey && selectedFields.length > 0) {
                          const freshFields = documentFieldsRef.current || documentFields;
                          // Range select: select all fields between first selected and clicked field
                          const firstSelectedIndex = documentFields.findIndex(f => f.id === selectedFields[0]);
                          const clickedIndex = documentFields.findIndex(f => f.id === field.id);
                          if (firstSelectedIndex !== -1 && clickedIndex !== -1) {
                            const start = Math.min(firstSelectedIndex, clickedIndex);
                            const end = Math.max(firstSelectedIndex, clickedIndex);
                            const rangeFields = documentFields.slice(start, end + 1);
                            runSelectionUpdate(() => {
                              setSelectedFields(rangeFields.map(f => f.id));
                              setSelectedField(freshFields.find((f) => f.id === field.id) || field);
                            });
                          }
                        } else {
                          const freshFields = documentFieldsRef.current || documentFields;
                          // Single select
                          runSelectionUpdate(() => {
                            setSelectedFields([field.id]);
                            setSelectedField(freshFields.find((f) => f.id === field.id) || field);
                          });
                        // Reset expanded sections when selecting a new field
                        setExpandedSections({
                          formatting: false,
                          dataLabel: false,
                          tooltip: false,
                          location: false,
                          options: false
                        });
                        }
                      }}
                      onContextMenu={(e) => {
                        e.stopPropagation(); // Prevent document context menu from showing
                        handleContextMenu(e, field.id);
                      }}
                      onTouchStart={(e) => {
                        if (e.target.classList.contains('resize-handle')) return;
                        if (inlineReadOnlyEditor?.fieldId === field.id) return;
                        if (e.cancelable) e.preventDefault();
                        ignoreMouseFromTouchRef.current = true;
                        markPointerInteractionActive();

                        const isMultiSelect = selectedFields.length > 1 && selectedFieldIdsSet.has(field.id);
                        const fieldsToDrag = isMultiSelect ? selectedFields : [field.id];

                        dragStartStateRef.current = documentFieldsRef.current || documentFields;
                        setDraggedField(field.id);
                        draggedFieldsRef.current = isMultiSelect ? fieldsToDrag : [field.id];

                        const previewContainer = svgContainerRef.current;
                        if (!previewContainer) return;

                        const containerRect = previewContainer.getBoundingClientRect();
                        const scrollTop = previewContainer.scrollTop;
                        const point = getClientPoint(e);
                        const mouseX = point.clientX - containerRect.left;
                        const mouseY = point.clientY - containerRect.top + scrollTop;
                        const currentScale = getCurrentScale();
                        const docMouseX = mouseX / currentScale;
                        const docMouseY = mouseY / currentScale;
                        const { fieldX: fieldDocX, fieldY: fieldDocY } = getFieldOverlayGeometry(field, svgPageHeights);

                        dragOffsetRef.current = {
                          x: docMouseX - fieldDocX,
                          y: docMouseY - fieldDocY
                        };

                        if (!selectedFieldIdsSet.has(field.id)) {
                          runSelectionUpdate(() => {
                            setSelectedFields([field.id]);
                            setSelectedField(field);
                          });
                        }
                      }}
                      onMouseDown={(e) => {
                        if (ignoreMouseFromTouchRef.current) return;
                        if (e.target.classList.contains('resize-handle')) return;
                        if (inlineReadOnlyEditor?.fieldId === field.id) return;
                        markPointerInteractionActive();
                        
                        // Check if this field is part of a multi-selection
                        const isMultiSelect = selectedFields.length > 1 && selectedFieldIdsSet.has(field.id);
                        const fieldsToDrag = isMultiSelect ? selectedFields : [field.id];
                        
                        // Save baseline reference before drag starts; deep copy happens later on commit.
                        dragStartStateRef.current = documentFieldsRef.current || documentFields;
                        setDraggedField(field.id); // Store the primary dragged field
                        // Store all fields being dragged for multi-select
                        if (isMultiSelect) {
                          draggedFieldsRef.current = fieldsToDrag;
                        } else {
                          draggedFieldsRef.current = [field.id];
                        }
                        
                        // Calculate drag offset relative to the document container
                        const previewContainer = svgContainerRef.current;
                        if (!previewContainer) return;
                        
                        const containerRect = previewContainer.getBoundingClientRect();
                        const scrollTop = previewContainer.scrollTop;
                        const point = getClientPoint(e);
                        
                        // Calculate mouse position relative to container (with scroll)
                        const mouseX = point.clientX - containerRect.left;
                        const mouseY = point.clientY - containerRect.top + scrollTop;
                        
                        // Get current scale factor to convert from screen coordinates to document coordinates
                        const currentScale = getCurrentScale();
                        
                        // Convert screen coordinates to original document coordinates
                        const docMouseX = mouseX / currentScale;
                        const docMouseY = mouseY / currentScale;
                        
                        const { fieldX: fieldDocX, fieldY: fieldDocY } = getFieldOverlayGeometry(field, svgPageHeights);
                        
                        // Calculate offset from mouse to field's position in document coordinates
                        dragOffsetRef.current = {
                          x: docMouseX - fieldDocX,
                          y: docMouseY - fieldDocY
                        };
                      }}
                    >
                      <div 
                        className={[
                          'field-badge',
                          readOnlyFieldShowsValueOnly(field) ? 'field-badge--readonly-value' : '',
                        ].filter(Boolean).join(' ')}
                        style={{
                          backgroundColor: field.required !== false 
                            ? (field.recipientColor ? hexToRgba(field.recipientColor, 0.1) : 'rgba(99, 102, 241, 0.1)')
                            : 'transparent',
                          borderColor: field.recipientColor || '#6366f1',
                          color: field.recipientColor || '#6366f1',
                          width: '100%',
                          height: '100%',
                          pointerEvents: readOnlyFieldShowsValueOnly(field) ? 'none' : undefined,
                        }}
                      >
                        {fieldTypeAllowsSingleOptionOnly(field.type) ? (
                          <span
                            className="field-choice-preview d-flex align-items-center gap-1"
                            style={{
                              width: '100%',
                              overflow: 'hidden',
                              fontFamily: field.fontFamily || 'inherit',
                              fontSize: `${field.fontSize || 12}px`,
                              color: field.fontColor || 'inherit',
                              fontWeight: field.fontWeight || 'normal',
                              fontStyle: field.fontStyle || 'normal',
                              textDecoration: field.textDecoration || 'none',
                            }}
                          >
                            <span aria-hidden="true" style={{ flexShrink: 0, lineHeight: 1 }}>
                              {isChoiceFieldDisplayChecked(field) ? '\u2611' : '\u2610'}
                            </span>
                            {getFieldChoiceOptionLabel(field) ? (
                              <span
                                style={{
                                  overflow: field.readOnly ? 'visible' : 'hidden',
                                  textOverflow: field.readOnly ? 'clip' : 'ellipsis',
                                  whiteSpace: field.readOnly ? 'pre-wrap' : 'nowrap',
                                  lineHeight: READONLY_FIELD_TEXT_LINE_HEIGHT,
                                }}
                              >
                                {getFieldChoiceOptionLabel(field)}
                              </span>
                            ) : null}
                          </span>
                        ) : readOnlyFieldShowsValueOnly(field) && inlineReadOnlyEditor?.fieldId !== field.id ? (
                          <div
                            className="field-readonly-value-display"
                            style={{
                              ...(getReadOnlyFieldValueHtml(field)
                                ? {}
                                : { textAlign: field.textAlign || 'left' }),
                              whiteSpace: 'pre-wrap',
                              overflow: 'visible',
                              width: '100%',
                              height: '100%',
                              display: 'block',
                              pointerEvents: 'none',
                              fontFamily: field.fontFamily || 'inherit',
                              fontSize: `${field.fontSize || 12}px`,
                              color: field.fontColor || 'inherit',
                              fontWeight: field.fontWeight || 'normal',
                              fontStyle: field.fontStyle || 'normal',
                              textDecoration: field.textDecoration || 'none',
                              lineHeight: READONLY_FIELD_TEXT_LINE_HEIGHT,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: (() => {
                                const richHtml = getReadOnlyFieldValueHtml(field);
                                if (richHtml) {
                                  return prepareReadOnlyValueHtml(richHtml);
                                }
                                return escapeHtml(
                                  getReadOnlyFieldCanvasDisplayValue(field)
                                ).replace(/\r?\n/g, '<br/>');
                              })(),
                            }}
                          />
                        ) : (
                          <>
                            <i className={`ri-${field.type === 'signature' ? 'pen-nib' : field.type === 'initial' ? 'pen-nib' : field.type === 'dateSigned' || field.type === 'date' ? 'calendar' : 'user'}-line`}></i>
                          <span
                            style={{
                              textAlign: field.textAlign || 'center',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              width: '100%',
                              fontFamily: field.fontFamily || 'inherit',
                              fontSize: `${field.fontSize || 12}px`,
                              color: field.fontColor || 'inherit',
                              fontWeight: field.fontWeight || 'normal',
                              fontStyle: field.fontStyle || 'normal',
                              textDecoration: field.textDecoration || 'none',
                            }}
                          >
                            {field.label}
                          </span>
                          </>
                        )}
                      </div>

                      {inlineReadOnlyEditor?.fieldId === field.id && (
                        <ReadOnlyInlineRichEditor
                          key={`quill-${field.id}`}
                          fieldId={field.id}
                          ref={readOnlyRichEditorRef}
                          initialHtml={inlineReadOnlyEditor.draftHtml || ''}
                          borderColor={field.recipientColor || '#6366f1'}
                          baseStyle={{
                            fontFamily: field.fontFamily || 'Arial',
                            fontSize: field.fontSize || '12',
                            fontColor: field.fontColor || '#000000',
                            fontWeight: field.fontWeight || 'normal',
                            fontStyle: field.fontStyle || 'normal',
                            textDecoration: field.textDecoration || 'none',
                            textAlign: field.textAlign || 'left',
                          }}
                          onHtmlChange={(html) => {
                            syncInlineEditorHtmlToField(field.id, html);
                          }}
                          onFieldTypographyChange={(patch) => {
                            applyEditorFieldLevelTypography(field.id, patch);
                          }}
                          onCancel={cancelInlineReadOnlyValueEditor}
                          onCommit={commitInlineReadOnlyValueEditor}
                        />
                      )}
                      {/* Resize handles */}
                      <div 
                        className="resize-handle resize-handle-n"
                        style={{ backgroundColor: field.recipientColor || '#6366f1' }}
                        onMouseDown={(e) => {
                          if (ignoreMouseFromTouchRef.current) return;
                          beginFieldResize(e, field, 'n', fieldWidth, fieldHeight);
                        }}
                        onTouchStart={(e) => beginFieldResize(e, field, 'n', fieldWidth, fieldHeight)}
                      ></div>
                      <div 
                        className="resize-handle resize-handle-s"
                        style={{ backgroundColor: field.recipientColor || '#6366f1' }}
                        onMouseDown={(e) => {
                          if (ignoreMouseFromTouchRef.current) return;
                          beginFieldResize(e, field, 's', fieldWidth, fieldHeight);
                        }}
                        onTouchStart={(e) => beginFieldResize(e, field, 's', fieldWidth, fieldHeight)}
                      ></div>
                      <div 
                        className="resize-handle resize-handle-e"
                        style={{ backgroundColor: field.recipientColor || '#6366f1' }}
                        onMouseDown={(e) => {
                          if (ignoreMouseFromTouchRef.current) return;
                          beginFieldResize(e, field, 'e', fieldWidth, fieldHeight);
                        }}
                        onTouchStart={(e) => beginFieldResize(e, field, 'e', fieldWidth, fieldHeight)}
                      ></div>
                      <div 
                        className="resize-handle resize-handle-w"
                        style={{ backgroundColor: field.recipientColor || '#6366f1' }}
                        onMouseDown={(e) => {
                          if (ignoreMouseFromTouchRef.current) return;
                          beginFieldResize(e, field, 'w', fieldWidth, fieldHeight);
                        }}
                        onTouchStart={(e) => beginFieldResize(e, field, 'w', fieldWidth, fieldHeight)}
                      ></div>
                      <div 
                        className="resize-handle resize-handle-ne"
                        style={{ backgroundColor: field.recipientColor || '#6366f1' }}
                        onMouseDown={(e) => {
                          if (ignoreMouseFromTouchRef.current) return;
                          beginFieldResize(e, field, 'ne', fieldWidth, fieldHeight);
                        }}
                        onTouchStart={(e) => beginFieldResize(e, field, 'ne', fieldWidth, fieldHeight)}
                      ></div>
                      <div 
                        className="resize-handle resize-handle-nw"
                        style={{ backgroundColor: field.recipientColor || '#6366f1' }}
                        onMouseDown={(e) => {
                          if (ignoreMouseFromTouchRef.current) return;
                          beginFieldResize(e, field, 'nw', fieldWidth, fieldHeight);
                        }}
                        onTouchStart={(e) => beginFieldResize(e, field, 'nw', fieldWidth, fieldHeight)}
                      ></div>
                      <div 
                        className="resize-handle resize-handle-se"
                        style={{ backgroundColor: field.recipientColor || '#6366f1' }}
                        onMouseDown={(e) => {
                          if (ignoreMouseFromTouchRef.current) return;
                          beginFieldResize(e, field, 'se', fieldWidth, fieldHeight);
                        }}
                        onTouchStart={(e) => beginFieldResize(e, field, 'se', fieldWidth, fieldHeight)}
                      ></div>
                      <div 
                        className="resize-handle resize-handle-sw"
                        style={{ backgroundColor: field.recipientColor || '#6366f1' }}
                        onMouseDown={(e) => {
                          if (ignoreMouseFromTouchRef.current) return;
                          beginFieldResize(e, field, 'sw', fieldWidth, fieldHeight);
                        }}
                        onTouchStart={(e) => beginFieldResize(e, field, 'sw', fieldWidth, fieldHeight)}
                      ></div>
    </div>
  );
                })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Field Properties or Thumbnails */}
        {isMobileFieldLayout && propertiesDrawerOpen && selectedField && (
          <button
            type="button"
            className="properties-drawer-backdrop"
            aria-label="Close properties"
            onClick={() => {
              setPropertiesDrawerOpen(false);
            }}
          />
        )}
        <div className={`thumbnails-sidebar${propertiesDrawerOpen && isMobileFieldLayout ? ' is-open' : ''}`}>
          {selectedField ? (
            // Field Properties Panel
            <div className="field-properties-panel">
              <div className="field-properties-header">
                <h5 className="field-properties-title">
                  {selectedFields.length > 1
                    ? `${selectedFields.length} Fields Selected`
                    : selectedField.label}
                </h5>
                <Button 
                  variant="link" 
                  className="btn-close-properties"
                  onClick={() => {
                    if (isMobileFieldLayout) {
                      setPropertiesDrawerOpen(false);
                    } else {
                      setSelectedField(null);
                      setSelectedFields([]);
                    }
                  }}
                >
                  <i className="ri-close-line"></i>
                </Button>
              </div>
              
              <div className="field-properties-content">
                {!selectionPropertiesInSync ? (
                  <div className="p-4 text-center text-muted small">Loading properties…</div>
                ) : (
                <>
                {/* Recipient Selection - Always show for multi-select; for single, show for signee field types */}
                {signeeRecipients.length > 0 && 
                 selectedField && 
                 (selectedFields.length > 1 ||
                  selectedField.type === 'signature' || 
                  selectedField.type === 'initial' || 
                  selectedField.type === 'dateSigned' || 
                  selectedField.type === 'date' || 
                  selectedField.type === 'name' || 
                  selectedField.type === 'email' ||
                  selectedField.type === 'company' ||
                  selectedField.type === 'title' ||
                  selectedField.type === 'text' ||
                  selectedField.type === 'number' ||
                  selectedField.type === 'checkbox' ||
                  selectedField.type === 'dropdown' ||
                  selectedField.type === 'radio') && (
                  <div className="property-section mb-3">
                    <Form.Label className="small mb-2">
                      Recipient
                      {selectedFields.length > 1 && (
                        <span className="text-muted ms-2">({selectedFields.length} fields selected)</span>
                      )}
                    </Form.Label>
                    <Form.Select
                      key={fieldsSelectionKey}
                      size="sm"
                      value={
                        propertiesPanelRecipientKey === '__mixed__'
                          ? ''
                          : propertiesPanelRecipientKey
                      }
                      onChange={(e) => {
                        const selectedRecipientKey = e.target.value;
                        if (!selectedRecipientKey) return;
                        assignRecipientToSelectedFields(selectedRecipientKey);
                      }}
                      className="mb-3"
                    >
                      <option value="" disabled>
                        {propertiesPanelRecipientKey === '__mixed__'
                          ? 'Multiple recipients'
                          : 'Select recipient'}
                      </option>
                      {signeeRecipients.map((recipient, index) => (
                        <option key={index} value={getRecipientSelectionKey(recipient, index)}>
                          {recipient.name || recipient.email || getVariableSigneePlaceholder(recipient, index)}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                )}

                {/* Required Field - works for both single and multi-select */}
                <div className="property-section mb-3">
                  <Form.Check
                    type="checkbox"
                    id="required-field"
                    label="Required Field"
                    checked={selectedField.required !== false}
                    onChange={(e) => {
                      const isRequired = e.target.checked;
                      if (selectedFields.length > 1) {
                        saveToHistory(documentFields);
                        setDocumentFields(prevFields =>
                          prevFields.map(f =>
                            selectedFields.includes(f.id)
                              ? { ...f, required: isRequired }
                              : f
                          )
                        );
                        setSelectedField(prev => ({ ...prev, required: isRequired }));
                      } else {
                        const updated = { ...selectedField, required: isRequired };
                      updateFieldWithHistory(updated);
                      }
                    }}
                  />
                  
                  {/* Read Only option for specific field types - single selection only */}
                  {selectedFields.length <= 1 && (selectedField.type === 'title' || 
                    selectedField.type === 'company' || 
                    selectedField.type === 'dropdown' || 
                    selectedField.type === 'text' || 
                    selectedField.type === 'date' || 
                    selectedField.type === 'number' || 
                    selectedField.type === 'checkbox' || 
                    selectedField.type === 'radio') && (
                    <>
                      <Form.Check
                        type="checkbox"
                        id="readonly-field"
                        label="Read Only"
                        checked={selectedField.readOnly || false}
                        onChange={(e) => {
                          const updated = growReadOnlyFieldToFitContent(
                            applyReadOnlyChoiceFieldState(
                              selectedField,
                              e.target.checked
                            )
                          );
                          updateFieldWithHistory(updated);
                        }}
                        className="mt-2"
                      />
                      {fieldTypeAllowsSingleOptionOnly(selectedField.type) && (
                        <div className="mt-3">
                          <Form.Label className="small">Option</Form.Label>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={getFieldChoiceOptionLabel(selectedField)}
                            placeholder="Label next to field (optional)"
                            onChange={(e) => {
                              const updated = growReadOnlyFieldToFitContent(
                                normalizeFieldOptionsForType({
                                  ...selectedField,
                                  options: [e.target.value],
                                }),
                                e.target.value
                              );
                              updateFieldWithHistory(updated);
                            }}
                          />
                        </div>
                      )}
                      {/* Value input for read-only/inline fields (not checkbox/radio — those use "selected").
                          Text fields are edited inline on the canvas, so no sidebar value input. */}
                      {(selectedField.readOnly || fieldSupportsInlineTextEdit(selectedField))
                        && selectedField.type !== 'text'
                        && !fieldTypeAllowsSingleOptionOnly(selectedField.type) && (
                        <div className="mt-3">
                          <Form.Label className="small">Field Value</Form.Label>
                          {selectedField.type === 'dropdown' ? (
                            (selectedField.options && selectedField.options.length > 0) ? (
                              <Form.Select
                                size="sm"
                                value={selectedField.value || ''}
                                onChange={(e) => {
                                  const updated = { ...selectedField, value: e.target.value, valueHtml: '' };
                                  updateFieldWithHistory(updated);
                                }}
                              >
                                <option value="">Select value...</option>
                                {selectedField.options.map((option, index) => (
                                  <option key={index} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </Form.Select>
                            ) : (
                              <Form.Control
                                type="text"
                                size="sm"
                                value={selectedField.value || ''}
                                placeholder="Add options first"
                                disabled
                              />
                            )
                          ) : selectedField.type === 'number' ? (
                            <Form.Control
                              type="number"
                              size="sm"
                              value={selectedField.value || ''}
                              placeholder="Enter value"
                              onChange={(e) => {
                                const updated = growReadOnlyFieldToFitContent(
                                  { ...selectedField, value: e.target.value, valueHtml: '' },
                                  e.target.value
                                );
                                setDocumentFields(prevFields =>
                                  prevFields.map(f =>
                                    f.id === selectedField.id ? updated : f
                                  )
                                );
                                setSelectedField(updated);
                              }}
                            />
                          ) : selectedField.type === 'date' ? (
                            <Form.Control
                              type="date"
                              size="sm"
                              value={normalizeToDateISO(selectedField.value) || ''}
                              onChange={(e) => {
                                const next = { ...selectedField, value: e.target.value, valueHtml: '' };
                                const display = getReadOnlyFieldCanvasDisplayValue(next);
                                const updated = growReadOnlyFieldToFitContent(next, display);
                                setDocumentFields(prevFields =>
                                  prevFields.map(f =>
                                    f.id === selectedField.id ? updated : f
                                  )
                                );
                                setSelectedField(updated);
                              }}
                            />
                          ) : (
                            <Form.Control
                              type="text"
                              size="sm"
                              value={selectedField.value || ''}
                              placeholder="Enter value"
                              onChange={(e) => {
                                const updated = growReadOnlyFieldToFitContent(
                                  { ...selectedField, value: e.target.value, valueHtml: '' },
                                  e.target.value
                                );
                                setDocumentFields(prevFields =>
                                  prevFields.map(f =>
                                    f.id === selectedField.id ? updated : f
                                  )
                                );
                                setSelectedField(updated);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Formatting — single and multi-select */}
                {selectedField?.type !== 'stamp'
                  && !(selectedFields.length > 1 && selectedFieldsForProperties.every((f) => f.type === 'stamp'))
                  && (
                    selectedFields.length > 1
                      ? selectionHasFontFormatting
                      : true
                  ) && (
                  <div className="property-section mb-3">
                    <div 
                      className="property-section-header"
                      onClick={() => setExpandedSections(prev => ({ ...prev, formatting: !prev.formatting }))}
                    >
                      <h6 className="property-section-title">Formatting</h6>
                      <i className={`ri-arrow-${expandedSections.formatting ? 'up' : 'down'}-s-line property-arrow-icon`}></i>
                    </div>
                    {expandedSections.formatting && (
                      <div className="property-section-content">
                        {/* Scale for Signature and Initial — single selection only */}
                        {selectedFields.length <= 1
                          && (selectedField.type === 'signature' || selectedField.type === 'initial') && (
                          <>
                            <Form.Label className="small">Scale (%)</Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              min="10"
                              max="200"
                              value={selectedField.scale || 100}
                              onChange={(e) => {
                                const updated = { ...selectedField, scale: parseInt(e.target.value) || 100 };
                                updateFieldWithHistory(updated);
                              }}
                              className="mb-2"
                            />
                          </>
                        )}
                        
                        {/* Font options for text-like fields */}
                        {(selectedFields.length > 1
                          ? selectionHasFontFormatting
                          : (
                            selectedField.type === 'dateSigned' || 
                            selectedField.type === 'date' || 
                            selectedField.type === 'name' || 
                            selectedField.type === 'email' || 
                            selectedField.type === 'company' || 
                            selectedField.type === 'title' || 
                            selectedField.type === 'text' || 
                            selectedField.type === 'number' || 
                            selectedField.type === 'checkbox' || 
                            selectedField.type === 'dropdown' || 
                            selectedField.type === 'radio'
                          )) && (
                          <>
                            <Form.Label className="small">Font Family</Form.Label>
                            <Form.Select
                              size="sm"
                              value={
                                (() => {
                                  const shared = getSharedSelectedFieldValue('fontFamily', 'Arial');
                                  return shared === '__mixed__' ? '' : (shared || 'Arial');
                                })()
                              }
                              onChange={(e) => {
                                const fontFamily = e.target.value;
                                if (!fontFamily) return;
                                if (selectedFields.length > 1) {
                                  applyFormattingToSelectedFields({ fontFamily }, { types: FONT_FORMAT_FIELD_TYPES });
                                  return;
                                }
                                if (applyInlineEditorFormatAndSyncField(selectedField, 'font', fontFamily)) {
                                  return;
                                }
                                const updated = { ...selectedField, fontFamily };
                                updateFieldWithHistory(updated);
                              }}
                              className="mb-2"
                            >
                              {getSharedSelectedFieldValue('fontFamily', 'Arial') === '__mixed__' && (
                                <option value="" disabled>Multiple</option>
                              )}
                              <option value="Arial">Arial</option>
                              <option value="Helvetica">Helvetica</option>
                              <option value="Times New Roman">Times New Roman</option>
                              <option value="Courier New">Courier New</option>
                              <option value="Verdana">Verdana</option>
                              <option value="Georgia">Georgia</option>
                              <option value="Palatino">Palatino</option>
                              <option value="Garamond">Garamond</option>
                              <option value="Comic Sans MS">Comic Sans MS</option>
                              <option value="Trebuchet MS">Trebuchet MS</option>
                            </Form.Select>
                            
                            <Form.Label className="small">Font Size</Form.Label>
                            <Form.Select
                              size="sm"
                              value={
                                (() => {
                                  const shared = getSharedSelectedFieldValue('fontSize', '12');
                                  return shared === '__mixed__' ? '' : String(shared || '12');
                                })()
                              }
                              onChange={(e) => {
                                const fontSize = e.target.value;
                                if (!fontSize) return;
                                if (selectedFields.length > 1) {
                                  applyFormattingToSelectedFields({ fontSize }, { types: FONT_FORMAT_FIELD_TYPES });
                                  return;
                                }
                                if (applyInlineEditorFormatAndSyncField(selectedField, 'size', `${fontSize}px`)) {
                                  return;
                                }
                                const base = { ...selectedField, fontSize };
                                let updated = base;
                                if (
                                  fieldUsesContentHeightFitting(selectedField)
                                  && readOnlyFieldHasDisplayContent(base)
                                ) {
                                  const styleOverride = { fontSize };
                                  updated = fitReadOnlyFieldToContent(base, undefined, styleOverride);
                                }
                                updateFieldWithHistory(updated);
                              }}
                              className="mb-2"
                            >
                              {getSharedSelectedFieldValue('fontSize', '12') === '__mixed__' && (
                                <option value="" disabled>Multiple</option>
                              )}
                              {FIELD_FONT_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={String(size)}>{size}</option>
                              ))}
                            </Form.Select>
                            
                            <Form.Label className="small">Font Color</Form.Label>
                            <Form.Control
                              type="color"
                              size="sm"
                              value={
                                (() => {
                                  const shared = getSharedSelectedFieldValue('fontColor', '#000000');
                                  return shared === '__mixed__' ? '#000000' : (shared || '#000000');
                                })()
                              }
                              title={getSharedSelectedFieldValue('fontColor', '#000000') === '__mixed__' ? 'Multiple colors' : undefined}
                              onChange={(e) => {
                                const fontColor = e.target.value;
                                if (selectedFields.length > 1) {
                                  applyFormattingToSelectedFields({ fontColor }, { types: FONT_FORMAT_FIELD_TYPES });
                                  return;
                                }
                                if (applyInlineEditorFormatAndSyncField(selectedField, 'color', fontColor)) {
                                  return;
                                }
                                const updated = { ...selectedField, fontColor };
                                updateFieldWithHistory(updated);
                              }}
                            />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* === Single-field-only sections (hidden when multiple fields are selected) === */}
                {selectedFields.length <= 1 && (
                <>
                {/* Options Section for Dropdown */}
                {selectedField.type === 'dropdown' && (
                  <div className="property-section mb-3">
                    <div 
                      className="property-section-header"
                      onClick={() => setExpandedSections(prev => ({ ...prev, options: !prev.options }))}
                    >
                      <h6 className="property-section-title">Options</h6>
                      <i className={`ri-arrow-${expandedSections.options ? 'up' : 'down'}-s-line property-arrow-icon`}></i>
                    </div>
                    {expandedSections.options && (
                      <div className="property-section-content">
                        {(selectedField.options || []).map((option, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <Form.Control
                              type="text"
                              size="sm"
                              value={option}
                              placeholder={`Option ${index + 1}`}
                              onChange={(e) => {
                                const updatedOptions = [...(selectedField.options || [])];
                                updatedOptions[index] = e.target.value;
                                const updated = { ...selectedField, options: updatedOptions };
                                updateFieldWithHistory(updated);
                              }}
                              className="me-2"
                            />
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => {
                                const updatedOptions = [...(selectedField.options || [])];
                                updatedOptions.splice(index, 1);
                                const updated = { ...selectedField, options: updatedOptions };
                                updateFieldWithHistory(updated);
                              }}
                              title="Remove option"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="w-100 mt-2"
                          onClick={() => {
                            const updatedOptions = [...(selectedField.options || []), ''];
                            const updated = { ...selectedField, options: updatedOptions };
                            updateFieldWithHistory(updated);
                          }}
                        >
                          <i className="ri-add-line me-1"></i> Add Option
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Data Label Section */}
                <div className="property-section mb-3">
                  <div 
                    className="property-section-header"
                    onClick={() => setExpandedSections(prev => ({ ...prev, dataLabel: !prev.dataLabel }))}
                  >
                    <h6 className="property-section-title">Data Label</h6>
                    <i className={`ri-arrow-${expandedSections.dataLabel ? 'up' : 'down'}-s-line property-arrow-icon`}></i>
                  </div>
                  {expandedSections.dataLabel && (
                    <div className="property-section-content">
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Enter data label"
                        value={selectedField.dataLabel ?? selectedField.label ?? ''}
                        onChange={(e) => {
                          const nextLabel = e.target.value;
                          const updated = {
                            ...selectedField,
                            dataLabel: nextLabel,
                            label: nextLabel,
                          };
                          updateFieldWithHistory(updated);
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Tooltip Section */}
                <div className="property-section mb-3">
                  <div 
                    className="property-section-header"
                    onClick={() => setExpandedSections(prev => ({ ...prev, tooltip: !prev.tooltip }))}
                  >
                    <h6 className="property-section-title">Tooltip</h6>
                    <i className={`ri-arrow-${expandedSections.tooltip ? 'up' : 'down'}-s-line property-arrow-icon`}></i>
                  </div>
                  {expandedSections.tooltip && (
                    <div className="property-section-content">
                      <Form.Control
                        as="textarea"
                        rows={3}
                        size="sm"
                        placeholder="Enter tooltip text"
                        value={selectedField.tooltip || ''}
                        onChange={(e) => {
                          const updated = { ...selectedField, tooltip: e.target.value };
                          setDocumentFields(prevFields =>
                            prevFields.map(f =>
                              f.id === selectedField.id ? updated : f
                            )
                          );
                          setSelectedField(updated);
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Location Section */}
                <div className="property-section mb-3">
                  <div 
                    className="property-section-header"
                    onClick={() => setExpandedSections(prev => ({ ...prev, location: !prev.location }))}
                  >
                    <h6 className="property-section-title">Location</h6>
                    <i className={`ri-arrow-${expandedSections.location ? 'up' : 'down'}-s-line property-arrow-icon`}></i>
                  </div>
                  {expandedSections.location && (
                    <div className="property-section-content">
                      {(() => {
                        const pageInfo = getFieldPageInfo(selectedField, svgPageHeights);
                        const absoluteX = pageInfo ? (pageInfo.offsetX || 0) + selectedField.x : selectedField.x;
                        const absoluteY = pageInfo ? pageInfo.y + selectedField.y : selectedField.y;
                        
                        return (
                          <>
                            <Form.Label className="small">Pixels from Left</Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={Math.round(absoluteX)}
                              onChange={(e) => {
                                const newAbsoluteX = parseInt(e.target.value) || 0;
                                // Calculate relative X based on page offset
                                const relativeX = pageInfo ? newAbsoluteX - (pageInfo.offsetX || 0) : newAbsoluteX;
                                const updated = { ...selectedField, x: relativeX };
                                updateFieldWithHistory(updated);
                              }}
                              className="mb-2"
                            />
                            <Form.Label className="small">Pixels from Top</Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={Math.round(absoluteY)}
                              onChange={(e) => {
                                const newAbsoluteY = parseInt(e.target.value) || 0;
                                const placement = resolvePlacementFromAbsolutePoint(absoluteX, newAbsoluteY, svgPageHeights);
                                if (!placement.pageInfo || !placement.documentId) return;
                                const updated = {
                                  ...selectedField,
                                  y: placement.y,
                                  documentId: placement.documentId,
                                  apiDocumentId: placement.documentId,
                                  documentIndex: placement.documentIndex,
                                  page: placement.page,
                                };
                                updateFieldWithHistory(updated);
                              }}
                            />
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>

                </>
                )}
                {/* === End single-field-only sections === */}

                {/* Action Buttons - works for both single and multi-select */}
                <div className="property-actions mt-4">
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    className="w-100 mb-2"
                    onClick={() => {
                      if (selectedFields.length > 1) {
                        handleDeleteFields(selectedFields);
                        setSelectedFields([]);
                      } else {
                      handleDeleteField(selectedField.id);
                      }
                      setSelectedField(null);
                    }}
                  >
                    {selectedFields.length > 1 ? `DELETE ${selectedFields.length} FIELDS` : 'DELETE'}
                  </Button>
                </div>
                </>
                )}
              </div>
            </div>
          ) : (
            // Thumbnails Panel (default)
            <div className="sidebar-section">
              {uploadedFiles.map((file, index) => {
                const docDeleteRowKey = getDocumentCacheKey(file, index);
                const isDeletingThisThumbnailDoc = deletingDocumentRowKeys.includes(docDeleteRowKey);
                return (
                <div key={index} className="document-thumbnail-section mb-3">
                  <div 
                    className="document-section-header"
                    onClick={() => toggleDocumentExpansion(index)}
                  >
                    <h6 className="document-name mb-0">
                      {file.name}
                    </h6>
                    <i className={`ri-arrow-${isDocumentExpanded(index) ? 'up' : 'down'}-s-line document-arrow-icon`}></i>
                  </div>
                  {isDocumentExpanded(index) && (
                    <div className="document-section-content">
                      <p className="text-muted small mb-2">Pages: {documentPages[index] || 1}</p>
                      {/* Display all pages for this document */}
                      {documentPageSvgs[index] && Object.keys(documentPageSvgs[index]).sort((a, b) => parseInt(a) - parseInt(b)).map((pageNum) => {
                        // Get the SVG string and modify it to scale properly
                        const svgString = documentPageSvgs[index][pageNum];
                        // Extract width and height from SVG to create viewBox, then make it responsive
                        const widthMatch = svgString.match(/width="([^"]+)"/);
                        const heightMatch = svgString.match(/height="([^"]+)"/);
                        const width = widthMatch ? parseFloat(widthMatch[1]) : 200;
                        const height = heightMatch ? parseFloat(heightMatch[1]) : 280;
                        
                        // Replace SVG tag to make it responsive with viewBox
                        const scaledSvg = svgString
                          .replace(/width="[^"]+"/, '')
                          .replace(/height="[^"]+"/, '')
                          .replace(
                            /<svg([^>]*)>/,
                            `<svg$1 viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" style="width: 100%; height: auto; max-width: 100%; display: block;">`
                          );
                        return (
                          <div key={pageNum} className="page-thumbnail mb-2">
                            <div className="thumbnail-container">
                              <div 
                                className="thumbnail-page"
                                dangerouslySetInnerHTML={{ __html: scaledSvg }}
                              >
                              </div>
                              <span className="page-number">{pageNum}</span>
                            </div>
                          </div>
                        );
                      })}
                      {(!documentPageSvgs[index] || Object.keys(documentPageSvgs[index]).length === 0) && (
                        <div className="page-thumbnail mb-2">
                          <div className="thumbnail-container">
                            <div className="thumbnail-page">
                              <span className="text-muted small">Loading...</span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="d-flex justify-content-end align-items-center">
                        <div className="page-actions">
                          <Button 
                            variant="link" 
                            className="btn-icon-sm p-1 text-danger" 
                            title={isDeletingThisThumbnailDoc ? 'Deleting...' : 'Delete Document'}
                            disabled={isDeletingThisThumbnailDoc}
                            onClick={async () => {
                              await handleRemoveFile(index, { waitForApi: true });
                            }}
                          >
                            {isDeletingThisThumbnailDoc ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                              <i className="ri-delete-bin-line"></i>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="field-placement-footer">
        <div className="footer-left">
          <Dropdown>
            <Dropdown.Toggle variant="link" className="text-muted">
              English (US)
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item>English (US)</Dropdown.Item>
              <Dropdown.Item>Spanish</Dropdown.Item>
              <Dropdown.Item>French</Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
          <a href="#contact" className="text-muted text-decoration-none ms-3">Contact Us</a>
          <a href="#terms" className="text-muted text-decoration-none ms-3">Terms of Use</a>
          <a href="#privacy" className="text-muted text-decoration-none ms-3">Privacy</a>
          <a href="#ip" className="text-muted text-decoration-none ms-3">Intellectual Property</a>
          <a href="#trust" className="text-muted text-decoration-none ms-3">Trust</a>
          <span className="text-muted ms-3">Copyright © 2026 Volksign, Inc. All rights reserved</span>
        </div>
        <div className="footer-right">
          <Button 
            variant="outline-secondary" 
            size="lg"
            className="field-footer-back-btn"
            onClick={() => setCurrentStep(1)}
          >
            Back
          </Button>
          <Button 
            variant="primary" 
            size="lg" 
            className="ms-2 field-footer-send-btn"
            onClick={handleFinalAction}
            disabled={isSavingDraft || isFinalActionSubmitting}
          >
            {isFinalActionSubmitting
              ? (finalActionPendingKind === 'save' || isTemplateMode ? 'Saving...' : 'Sending...')
              : (isTemplateMode ? 'Save Template' : 'Send')}
          </Button>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.show && (
        <div
          ref={contextMenuRef}
          className="context-menu"
          style={{
            position: 'fixed',
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
            zIndex: 10000,
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            padding: '4px 0',
            minWidth: '180px',
            fontFamily: 'Arial, sans-serif',
            fontSize: '14px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              padding: '8px 16px',
              cursor: contextMenu.fieldId ? 'pointer' : 'not-allowed',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: contextMenu.fieldId ? '#000' : '#999',
            }}
            onMouseEnter={(e) => {
              if (contextMenu.fieldId) {
                e.target.style.backgroundColor = '#f0f0f0';
              }
            }}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            onClick={() => {
              if (contextMenu.fieldId) {
                handleCutField(contextMenu.fieldId);
              }
            }}
          >
            <span>Cut</span>
            <span style={{ color: '#999', fontSize: '12px', marginLeft: '16px' }}>Ctrl+X</span>
          </div>
          <div
            style={{
              padding: '8px 16px',
              cursor: contextMenu.fieldId ? 'pointer' : 'not-allowed',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: contextMenu.fieldId ? '#000' : '#999',
            }}
            onMouseEnter={(e) => {
              if (contextMenu.fieldId) {
                e.target.style.backgroundColor = '#f0f0f0';
              }
            }}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            onClick={() => {
              if (contextMenu.fieldId) {
                // Keep multi-selection when right-clicking one of the selected fields.
                if (!(selectedFields.length > 1 && selectedFields.includes(contextMenu.fieldId))) {
                  const field = documentFields.find(f => f.id === contextMenu.fieldId);
                  if (field) setSelectedField(field);
                }
                handleCopyField();
                setContextMenu({ show: false, x: 0, y: 0, fieldId: null });
              }
            }}
          >
            <span>Copy</span>
            <span style={{ color: '#999', fontSize: '12px', marginLeft: '16px' }}>Ctrl+C</span>
          </div>
          <div
            style={{
              padding: '8px 16px',
              cursor: (copiedField || copiedFields.length > 0) ? 'pointer' : 'not-allowed',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: (copiedField || copiedFields.length > 0) ? '#000' : '#999',
            }}
            onMouseEnter={(e) => {
              if (copiedField || copiedFields.length > 0) {
                e.target.style.backgroundColor = '#f0f0f0';
              }
            }}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            onClick={() => {
              if (copiedField || copiedFields.length > 0) {
                handlePasteField(false);
                setContextMenu({ show: false, x: 0, y: 0, fieldId: null });
              }
            }}
          >
            <span>Paste</span>
            <span style={{ color: '#999', fontSize: '12px', marginLeft: '16px' }}>Ctrl+V</span>
          </div>
          <div
            style={{
              padding: '8px 16px',
              cursor: (copiedField || copiedFields.length > 0) ? 'pointer' : 'not-allowed',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: (copiedField || copiedFields.length > 0) ? '#000' : '#999',
            }}
            onMouseEnter={(e) => {
              if (copiedField || copiedFields.length > 0) {
                e.target.style.backgroundColor = '#f0f0f0';
              }
            }}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            onClick={() => {
              if (copiedField || copiedFields.length > 0) {
                handlePasteField(true);
                setContextMenu({ show: false, x: 0, y: 0, fieldId: null });
              }
            }}
          >
            <span>Paste to Location</span>
          </div>
          <div
            style={{
              padding: '8px 16px',
              cursor: contextMenu.fieldId ? 'pointer' : 'not-allowed',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: contextMenu.fieldId ? '#000' : '#999',
            }}
            onMouseEnter={(e) => {
              if (contextMenu.fieldId) {
                e.target.style.backgroundColor = '#f0f0f0';
              }
            }}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            onClick={() => {
              if (contextMenu.fieldId) {
                const ids = resolveMultiSelectActionIds(contextMenu.fieldId);
                handleDeleteFields(ids);
                setContextMenu({ show: false, x: 0, y: 0, fieldId: null });
              }
            }}
          >
            <span>Delete</span>
            <span style={{ color: '#999', fontSize: '12px', marginLeft: '16px' }}>Delete</span>
          </div>
        </div>
      )}
    </div>
    )}

      {/* Discard confirmation (in-app — window.confirm is unreliable in mobile WebViews/iframes) */}
      <Modal
        show={showDiscardConfirmModal}
        onHide={() => {
          if (!isFinalActionSubmitting) setShowDiscardConfirmModal(false);
        }}
        centered
      >
        <Modal.Header closeButton={!isFinalActionSubmitting}>
          <Modal.Title>Discard draft?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to discard this draft? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDiscardConfirmModal(false)}
            disabled={isFinalActionSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDiscard}
            disabled={isFinalActionSubmitting}
          >
            {isFinalActionSubmitting ? 'Discarding...' : 'Discard'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Message Modal */}
      <Modal show={showEditMessageModal} onHide={() => setShowEditMessageModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MessageSection
            subject={subject}
            setSubject={setSubject}
            message={message}
            setMessage={setMessage}
            reminderFrequency={reminderFrequency}
            setReminderFrequency={setReminderFrequency}
          />
        </Modal.Body>
      </Modal>

      {/* Edit Recipients Modal */}
      <Modal
        show={showEditRecipientsModal}
        onHide={() => setShowEditRecipientsModal(false)}
        size="lg"
        fullscreen="sm-down"
        scrollable
        className="edit-recipients-modal"
        dialogClassName="edit-recipients-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Recipients</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <RecipientsSection
            recipients={recipients}
            setRecipients={setRecipients}
            onPatchRecipient={handlePatchRecipient}
            isOnlySigner={isOnlySigner}
            setIsOnlySigner={setIsOnlySigner}
            signingOrder={signingOrder}
            setSigningOrder={setSigningOrder}
            sequenceInputs={sequenceInputs}
            setSequenceInputs={setSequenceInputs}
            handleSequenceChange={handleSequenceChange}
            handleSequenceBlur={handleSequenceBlur}
            handleAddRecipient={handleAddRecipient}
            handleRemoveRecipient={handleRemoveRecipient}
            getRecipientColor={getRecipientColor}
            signingRoles={signingRoles}
            isTemplateMode={isTemplateMode}
            showRecipientFieldErrors={recipientFieldErrorsVisible}
            removingRecipientIndex={removingRecipientIndex}
            idPrefix="modal-"
            savedContacts={savedRecipientContacts}
          />
        </Modal.Body>
        <Modal.Footer className="edit-recipients-modal-footer">
          <Button
            variant="primary"
            onClick={() => setShowEditRecipientsModal(false)}
          >
            Done
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Documents Modal */}
      <Modal
        show={showEditDocumentsModal}
        onHide={() => {
          if (deletingDocumentRowKeys.length > 0) return;
          setShowEditDocumentsModal(false);
        }}
        size="lg"
      >
        <Modal.Header closeButton={deletingDocumentRowKeys.length === 0}>
          <Modal.Title>Edit Documents</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DocumentsSection
            fileInputRef={fileInputRef}
            uploadedFiles={uploadedFiles}
            uploadError={uploadError}
            setUploadError={setUploadError}
            isDragging={isDragging}
            documentPages={documentPages}
            documentPageSvgs={documentPageSvgs}
            getDocumentThumbnailSvg={getDocumentThumbnailSvg}
            handleFileInputChange={handleFileInputChange}
            handleDocumentAction={handleDocumentAction}
            handleUploadClick={handleUploadClick}
            handleDragEnter={handleDragEnter}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handleDropboxUpload={handleDropboxUpload}
            handleGoogleDriveUpload={handleGoogleDriveUpload}
            handleOneDriveUpload={handleOneDriveUpload}
            handleBoxUpload={handleBoxUpload}
            uploadOptions={uploadOptions}
            replaceFileIndex={replaceFileIndex}
            setReplaceFileIndex={setReplaceFileIndex}
            showCloudUpload={true}
            onReorderDocuments={handleReorderDocuments}
            idPrefix="modal-"
            deletingDocumentRowKeys={deletingDocumentRowKeys}
            isDocumentRowUploadInFlight={isDocumentRowUploadInFlight}
          />
        </Modal.Body>
      </Modal>

      {/* Recipient Without Fields Modal */}
      <Modal
        show={showRecipientWithoutFieldsModal}
        onHide={() => setShowRecipientWithoutFieldsModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Recipient without fields</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            One or more recipients do not have any fields assigned.
          </p>
          {recipientsWithoutFields.length > 0 && (
            <ul className="mb-0 ps-3">
              {recipientsWithoutFields.map((recipient, idx) => (
                <li key={`${recipient.email || recipient.placeholder || recipient.name || 'recipient'}-${idx}`}>
                  {recipient.name || recipient.email || recipient.placeholder || 'Unnamed recipient'}
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowRecipientWithoutFieldsModal(false);
              setCurrentStep(2);
            }}
          >
            Add Fields
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowRecipientWithoutFieldsModal(false);
              allowSendWithoutFieldsCheckRef.current = true;
              handleFinalSend();
            }}
          >
            Send Anyway
          </Button>
        </Modal.Footer>
      </Modal>

      <UpgradeQuotaModal
        show={showQuotaModal}
        onHide={() => setShowQuotaModal(false)}
        message={quotaModalMessage}
        onViewPlans={handleViewPlansFromEditor}
      />

      <PayPerEnvelopeModal
        show={showPayPerEnvelopeModal}
        onHide={() => setShowPayPerEnvelopeModal(false)}
        envelopeId={payPerEnvelopeEnvelopeId}
        message={quotaModalMessage}
        payPerEnvelope={getPayPerEnvelopeOptions(userSubscription)}
        onPaymentComplete={handlePayPerEnvelopeComplete}
        onViewPlans={handleViewPlansFromEditor}
      />
    </>
  );
};

export default CreateEnvelope;

